// ============================================================================
// ui/design-panel.js — the design control panel (Step 2, element model)
// ============================================================================
// Functional (not-yet-polished) panel that drives the V2 element model end to
// end: layout preset, 3-color palette, weight value/unit, and per-element
// controls (arched text / weight format / logo slot) with a fill + outline
// stack. Every control mutates state.js in place, then requests a re-render +
// a debounced history snapshot — so undo/redo + autosave + the 3D texture keep
// working unchanged. The polished customer wizard is a later phase.
// ============================================================================

import { h, replaceChildren } from './dom.js';
import { attachTip } from './tooltip.js';
import { openModal } from './modal.js';
import {
  setLayoutPreset,
  setBodyColor,
  markBodyColorUserSet,
  isBlackBody,
  inkContextKey,
  rememberInk,
  restoreInk,
  setFont,
  setAllCaps,
  updateElement,
  addElement,
  removeElement,
  setWeight,
  setUnit,
  addSwatch,
  swatchSlotsLeft,
  removeSwatch,
  recolorEverywhere,
  markStyleUserEdited,
  resetToOriginal,
  recascadeColorDefaults,
  noteOutlineEdit,
  applyOutlineEdits,
  adoptTextRings,
  shownTextRings,
  assignOutlineColors,
  correctIllegibleFill,
  outlineHexReads,
  designSnapshot,
  restoreSnapshot,
  effectivePlateHex,
  wornOutlineHexes,
  addedLogoRingColor,
  resyncAddedLogoRings as resyncAddedLogoRingsState,
  dropAutoOutlinesWhenFillReads,
  reseedIdentityIntoPlaceholders,
  weightNudgeKey,
  rememberWeightNudge,
  nudgeRecordFor,
  applyNudgeRecord,
  relLuminance,
  seatedFieldHex,
  syncLogoContrastRing,
} from './state.js';
import { applyLayoutPreset, getLayoutPreset, orderedLayoutPresetsForProduct, layoutPresetProduct, matchPresetToComposition, isPlaceholderText, presetEligibility } from '../layouts.js';
import { layoutFamilyFor, modelFor, productSupportsBodyColor, productSupportsMultiConfig, geomOptsFor, defaultWeightFor, designableWeights, designLadder, parseProductKey, BARBELL_HOUSE_SET_LB } from '../products.js';
import { renderDesignToSVGString, effectivePointAngle, slotBaseAngle, productSlots, paintedTextSize, paintedNumeralSize, sizeEdgeFor, seedDefaultSizes, maxOutlineTotal, ARC_TEXT_SCALE_DIAL_MAX, edgeOf } from '../render-engine.js';
import { usableAccents, isNearBlackHex, numberEdgeFractionFor, weightOpenPctFor, DEFAULT_FONT_ID, DEFAULT_ELEMENT_SIZE_SCALE, FONTS, FONT_CATEGORIES, FONT_CATEGORY_LABELS, TEXT_MAX_LENGTH, fontFamilyFor, fontWeightFor, resolvePaletteRef, normalizeHex, DEFAULT_BODY_COLOR, effectiveTextStyle, fillContrastRemap, significantLogoTones, shownLogoTones, outlineThicknessFor, LOGO_STROKE_FRAC_REF, LOGO_BOX_NOMINAL_VB, OUTLINES_MAX, DEFAULT_OUTLINE_THICKNESS, colorsConflict, colorsIndistinguishable, readsOn, contrastPoolFrom, bestContrastRef, SWATCH_CAP, DEFAULT_SIZE_PCT, displayWeightText, canNudgeWeight, clampBigNudge, WEIGHT_NUDGE_MAX, WEIGHT_NUDGE_STEP, colorWord, logoGapFromStructure } from '../design-profile.js';

// A swatch background split into N clean, evenly-divided vertical bands (hard gradient stops so
// there's no blending between them). One color → a solid fill. Used by the "Original" logo-fill
// chip so a 2-tone logo shows two halves, a 3-tone shows thirds, a 4-tone quarters, … (Ryan 2026-07-23).
// Always returns a linear-gradient (even for one colour) so it can be assigned via
// `background-image` — NOT the `background` shorthand, which would reset background-origin/-repeat
// back to their defaults and reintroduce the 1px tiling-wrap sliver under the border (Ryan 2026-07-23).
function dividedSwatchBg(colors) {
  const cols = (colors || []).filter(Boolean);
  const n = cols.length;
  if (n <= 1) { const c = cols[0] || '#8a8f96'; return `linear-gradient(90deg, ${c} 0 100%)`; }
  const stops = cols.map((c, i) => `${c} ${(i / n * 100).toFixed(2)}% ${((i + 1) / n * 100).toFixed(2)}%`).join(', ');
  return `linear-gradient(90deg, ${stops})`;
}
// The SAME two rules the raster pipeline applies, so the swatch can never disagree with the plate.
import { bandConflictView, bandViewOptions, bandRescuePool, contextualFillSwap, bandsVisibleOnPlate, presentedOuterOutline, renderPaletteFor, markWearsBlack, isBlackInk, isPlateMoat } from '../logo-outline.js';
import { isPhone } from './mobile-launch.js';
import { createStepper, attachHoldRepeat } from './stepper.js';
import { createColorPicker } from './color-picker.js';
import { seeThroughChooser } from './see-through-chooser.js';
import { pickColorWithEyedropper } from './eyedropper.js';

// Weight lockups (§2.7), rendered as mini-previews. `unit` = the small stacked
// line (null → number-only / inline). Ordered varsity-first.
const WEIGHT_LOCKUPS = Object.freeze([
  { fmt: 'stacked-dash', unit: '-LB-' },
  { fmt: 'stacked-lb', unit: 'LB' },
  { fmt: 'stacked-lbs', unit: 'LBS' },
  { fmt: 'stacked-lb-lower', unit: 'lb' }, // Ryan 2026-07-22: right after LBS
  { fmt: 'stacked-pounds', unit: 'POUNDS' },
  { fmt: 'inline-lb', unit: null },        // 45LB
  { fmt: 'ruled', unit: null },            // 45LB framed by a top + bottom rule (right after inline)
  { fmt: 'plain', unit: null },            // 45
]);
const INLINE_LABEL = { 'inline-lb': 'NN LB', plain: 'NN', ruled: 'NN LB with rules' };
// The grip plate's `left`/`right` are its two LOWER gaps, at roughly 7 and 5 o'clock — "Left" alone
// would point a buyer at 9 o'clock, which on that face is a hand cutout (2026-07-30).
const SLOT_LABELS = { top: 'Top', bottom: 'Bottom', left: 'Left', right: 'Right' };
const SLOT_LABELS_GRIP = { top: 'Top', left: 'Lower left', right: 'Lower right' };
// The dumbbell / fixed-barbell end cap adds `center`, which is a place no ring product has: the
// bumper keeps a steel hub there and the grip plate a bore (2026-07-30).
const SLOT_LABELS_CAP = { center: 'Centre', top: 'Top', bottom: 'Bottom', left: 'Left', right: 'Right' };
const slotLabelsFor = (product) => (product === 'grip' ? SLOT_LABELS_GRIP
  : (product === 'dumbbell' || product === 'barbell') ? SLOT_LABELS_CAP : SLOT_LABELS);
const OUTLINE_MIN = 0;
/* WD 485c (Ryan 11-01): every outline-thickness dial steps 0.1, not 0.5. The corpus passes matched outlines to the
   logo and many came out thin (0.3, a hairline edge copied at 0.3 - WD 476), and a 0.5 dial could neither show nor
   reach them: it opened on the nearest 0.5 and one tap jumped a 0.3 ring to 0.8. Hold-to-repeat covers the long runs. */
const OUTLINE_STEP = 0.1;
/* The HARD ceiling for one TEXT ring (Ryan 2026-08-07: *"the outline thickness toggle… set the max
   to 5"*). 5 is 2x `DEFAULT_OUTLINE_THICKNESS`. The REAL ceiling is usually lower and is geometric —
   see `outlineMaxFor`, which is what stops a stack of rings painting off the plate.
     ⚠ THIS NO LONGER GOVERNS THE LOGO RING, and that split is deliberate rather than drift. The two
   dials were merged onto one constant on 2026-08-05 to give them a single vocabulary, but the logo
   ring's SEEDED default is `0.06` — which is 6 of these units — so a shared 5 would have put the
   app's own default above the dial that sets it. Ryan chose to cap the text ring only; the logo dial
   keeps its own ceiling below. Re-merging them means re-pegging that seed first.
     5 → 7.5 on 2026-08-08, at Ryan's ask. Still well under the logo ring's 20, so the split above
   stands; `outlineMaxFor`'s geometric budget is untouched and is still the limit that actually binds
   on a multi-ring stack — this only raises how far a SINGLE ring may go when there is room for it. */
const OUTLINE_MAX = 7.5;
/* The logo ring's ceiling. Same units as `OUTLINE_MAX` (the dial shows text-ring units and stores a
   fraction — see the stepper), just a different limit.
     ⛔ 20 → 10 when the dial's exchange rate was corrected (Ryan 2026-08-15). `serializeProfile`
   clamps a stored ring to 0.2 of the logo, and 0.2 is now 10.4 units rather than the 19.2 the old
   1.85x-heavy rate implied — so a ceiling of 20 offered nearly a whole extra dial of travel that a
   save would silently take back. This is the SAME limit expressed in corrected units, not a new
   policy: the ring the buyer can reach is unchanged, the number over it is finally true.
     ⚠ Still comfortably above the seeded 0.06 (= 3.1 units), which is the constraint that split this
   ceiling from `OUTLINE_MAX` in the first place. */
const LOGO_RING_MAX = 10;
/* An ADDED logo ring's thickness is a FRACTION of the logo's own size (0.06 is the seeded default),
   not viewBox units like a text outline — so it gets its own bounds and a % readout.
     ⛔ 0.02 → 0 (Ryan 2026-08-09: *"logo outlines have a minimum thickness of 2, whereas text
   outlines have a minimum thickness of 0 technically"*). 0.02 read as **2** on the shared dial, so
   the two controls that were deliberately merged onto one vocabulary in 248 still disagreed about
   where their own scale ended — and the disagreement cost the logo ring the only gesture that
   retires a ring from inside the popover. Zero is not a special value to anything downstream:
   `addList()` and the renderer's `adds` BOTH already filter `thickness > 0`, so a ring dialled to
   nothing paints nothing and shows nothing either way. All this changes is that it can now be
   reached, and therefore meant. */
const LOGO_RING_MIN = 0;
// (`LOGO_RING_MAX = 0.2` retired 2026-08-05 — the dial moved onto the text ring's units and ceiling,
//  because 0.2 was over 3x the 0.06 the seeder itself enforces. The UNITS are still shared; the
//  CEILING split again on 2026-08-07 when the text cap dropped to 5 — see `LOGO_RING_MAX` above.)
const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

// The eyedropper lives INSIDE the custom color picker (color-picker.js). It's now our own
// element-aware picker (ui/eyedropper.js), not the native EyeDropper API, so it works in every
// browser — offered wherever the app runs (Ryan 2026-07-22).
const SUPPORTS_EYEDROPPER = typeof window !== 'undefined';

// No-logo starter chips (Fable §2.2 empty state; drawn from the §12.5 colorway bank).
const STARTER_CHIPS = Object.freeze(['#2145C4', '#A6192E', '#203731', '#F2B01E', '#3D1E6D', '#FFFFFF']);
// Buyer-facing labels for the three active color roles (Ryan 2026-07-20: numbered, not
// Primary/Secondary). The design uses these three; bench colors continue 4th, 5th, …

// "Coach's picks" — the curated set behind the **"Most popular"** heading, which LEADS the one master
// font list (Default → Most popular → every category). There is no longer a separate rail or a
// "Browse all fonts" tap: that was replaced 2026-07-20 because two stacked scrollers read as clunky.
// This is therefore the first thing a buyer sees; everything else is below the fold. IDs must exist
// in the manifest.
//
// ⚠ **A font appears exactly ONCE** — a pick is excluded from its own category below, so that the
// selected ✓ cannot have two homes. Every edit here therefore MOVES a font rather than copying it:
//   · `graduate` ("College All Star") was REMOVED 2026-07-30 so it could lead the Collegiate group.
//     Re-adding it would silently pull it back OUT of Collegiate.
//   · `collegebold` was ADDED 2026-07-30 because removing `graduate` left Most popular with ZERO
//     collegiate faces — on a store that sells to schools and ADs, the opening screen showed no
//     collegiate option at all. Bold is the heaviest cut and the cleanest under an outline. The
//     cost, accepted knowingly: the Collegiate group below now lists FOUR cuts, not five, because
//     Bold lives up here instead.
const COACH_PICKS = Object.freeze([
  'collegebold',
  'anton', 'archivoblack', 'staatliches', 'luckiestguy', 'titanone', 'bowlby', 'fugaz',
  'bebas', 'oswald', 'teko', 'leaguegothic', 'fjallaone', 'barlowcond',
  'alfaslab', 'ultra',
  'jost', 'outfit', 'manrope', 'montserrat2', 'marcellus', 'cinzel',
  'tektur', 'newamsterdam', 'moderustic', 'goldman',
]);
// Chain glyph for the linked top/bottom text-size toggle (§2.7).
const LINK_ICON =
  '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round"><path d="M9 17H7A5 5 0 0 1 7 7h2"/><path d="M15 7h2a5 5 0 0 1 0 10h-2"/><line x1="8" y1="12" x2="16" y2="12"/></svg>';
/** Uppercase-dedupe a list of hex strings, preserving order. */
// Shared Outer→Inner naming for BOTH the Text-outline and Logo-outline chips (Ryan
// 2026-07-21) — `i` is the position OUTER→INNER (0 = outermost) within `total`:
//   1 → Edge · 2 → Outer/Inner · 3 → Outer/Middle/Inner · 4+ → Outer/Middle 1…/Inner.
// Tier label for an outline swatch, indexed from the OUTSIDE in (0 = Outer). Returns '' for a lone
// outline (Ryan 2026-07-24, was 'Edge'): naming a single outline says nothing — a tier name only earns
// its space once there are siblings to tell apart. `.wd-cchip-badge` has a min-height, so the blank
// badge holds the row's alignment instead of collapsing it.
function outlineTierName(i, total) {
  if (total <= 1) return '';
  if (i === 0) return 'Outer';
  if (i === total - 1) return 'Inner';
  // ONE middle → no number. Several → numbered from the INSIDE OUT, so they ascend left-to-right in
  // the row and the picker's tab strip, both of which render inner→outer (Ryan 2026-07-24). `i` counts
  // from the outside, so the outermost middle gets the HIGHEST number: 4 outlines read
  // "Inner · Middle 1 · Middle 2 · Outer". Numbering by `i` directly put Middle 2 left of Middle 1.
  return total === 3 ? 'Middle' : `Middle ${total - 1 - i}`;
}
// Prose forms for tooltips / aria-labels / announcements, which still need a noun even when the badge
// is blank: capitalized for sentence-start, lowercase for mid-sentence after "the".
const outlineProse = (tier) => (tier ? `${tier} outline` : 'Outline');
const outlineProseMid = (tier) => (tier ? `${tier.toLowerCase()} outline` : 'outline');
// Compact tier name for the picker's TARGET TABS only (Ryan 2026-07-24). A pill is ~50–75px wide
// depending on the docked width, and "Middle 1" plus its colour dot ellipsizes at the narrow end while
// "Mid 1" fits everywhere. Only the NUMBERED middles shorten — a lone unnumbered "Middle" (the
// 3-outline case) already fits. Swatch badges keep the full word; they have their own column and wrap.
const outlineTierTab = (tier) => String(tier || '').replace(/^Middle (\d+)$/, 'Mid $1');

/**
 * Dedupe a colour list EXACTLY and then PERCEPTUALLY (Ryan 2026-07-25). Exact-hex alone let palette
 * extraction stack up colours the eye reads as one — a logo with anti-aliased white edges yields
 * #FDFDFD and #FEFEFE and both got their own chip, which looks like the same colour listed twice.
 *   `keep` is a set of hexes that must survive regardless: the colours the design is CURRENTLY
 * wearing. Without it, a buyer who nudges a swatch a hair via ✎ could have their new hex swallowed
 * by the one it came from, and then no chip in the row would match the surface — the row would show
 * nothing selected. Rare, but a dead end, so in-use colours are never merged away.
 */
function dedupeHex(hexes, keep) {
  const seen = new Set();
  const out = [];
  for (const hx of hexes) {
    const H = String(hx || '').toUpperCase();
    if (!/^#[0-9A-F]{6}$/.test(H) || seen.has(H)) continue;
    if (!(keep && keep.has(H)) && out.some((k) => colorsIndistinguishable(k, H))) continue;
    seen.add(H);
    out.push(H);
  }
  return out;
}

const CHEVRON_L =
  '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>';
const CHEVRON_R =
  '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>';

const S = {
  // The rule between one section and the next. Reads --wd-divider (weight-designer.css) rather than
  // carrying its own alpha — it was 0.08 and near-invisible, and it is the same hairline as the
  // View-all header, the assign menu and the picker's splits (Ryan 2026-07-25).
  // (Section spacing + divider moved WHOLESALE to the .wd-dsection CSS rule 2026-07-25. Inline styles
  // beat a stylesheet, so while the padding lived here the last section could not drop it and left a
  // dead 52px under the final control once the Advanced button was hidden.)
  // (The section eyebrow — "COLORS", "FONT", … — moved to the .wd-eyebrow CLASS 2026-07-24. It lived
  // here as an inline style object and was the last of the header/sub-header treatments not shared with
  // the rest of the tool, so a colour or size change had to be made in two places.)
  row: { display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' },
  card: { border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', padding: '10px', margin: '0 0 8px' },
  input: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: 'inherit', padding: '5px 7px', font: 'inherit', width: '100%' },
  chip: { padding: '5px 9px', borderRadius: '999px', border: '1px solid rgba(255,255,255,0.2)', background: 'transparent', color: 'inherit', cursor: 'pointer', font: 'inherit', fontSize: '12px' },
  small: { fontSize: '11px', opacity: '0.65' },
};

// Simple/Advanced tier (Fable §2.1). Off by default; the choice persists per
// session (sessionStorage). "Advanced" describes the USER, never the UI — both
// tiers stay clean; Advanced only unfolds extra controls in place.
const ADVANCED_KEY = 'wd:v2:advanced';
// ⏸ ADVANCED IS HIDDEN FOR LAUNCH (Ryan 2026-07-25). The per-piece controls under "Show advanced
// options" work, but they need a polish pass Ryan doesn't want to hold the launch for — so the
// BUTTON is hidden and nothing else is touched. `advancedBlock()`, `elementsSection()`, the reveal
// animation, the sessionStorage tier and every per-piece control stay exactly where they are.
//   Flip this back to `true` to restore it — that is the whole change, there is no other switch.
//   Note it also FORCES the tier off: a session that had already opened Advanced has '1' in
// sessionStorage, and without the force it would come back expanded with no way to close it.
const SHOW_ADVANCED_TOGGLE = false;
const TEXT_LINK_KEY = 'wd:v2:text-size-linked'; // top/bottom size chain (§2.7)
// Height the buyer dragged the typeface list to. Persisted because ANY commit rebuilds the panel —
// without this the box would snap back to its default on the next colour pick (Ryan 2026-07-25).
const FONTBOX_H_KEY = 'wd:v2:fontbox-h';
const FONTBOX_H_MIN = 160;
const FONTBOX_H_MAX = 620;

export function createDesignPanel(ctx) {
  const { state } = ctx;
  const root = h('div', { class: 'wd-design-panel', style: { fontSize: '13px' } });

  let advanced = false;
  if (SHOW_ADVANCED_TOGGLE) {
    try { advanced = sessionStorage.getItem(ADVANCED_KEY) === '1'; } catch { /* storage off */ }
  }

  // Top & bottom text sizes are LINKED by default (§2.7 chain) — editing one size
  // moves both; unlink to size separately. Persists per session.
  let textSizeLinked = true;
  try { textSizeLinked = sessionStorage.getItem(TEXT_LINK_KEY) !== '0'; } catch { /* storage off */ }

  const valueCommit = () => {
    ctx.requestRender();
    ctx.recordHistory(); // debounced — coalesces rapid typing / slider drags
    /* ⛔ AND THE LAYOUT THUMBNAILS, BECAUSE THEY DRAW THE DESIGN TOO (Ryan 2026-08-07: *"if I click
       to change a weight's number style or unit of measurement, the SVGs don't update on the left
       side pane. If I click 'view all x layouts', they'll be updated in the slide-out menu, then
       when I close the menu they'll be updated on the narrower regular side pane — but they don't
       update in real time."*)
         Those tiles are the buyer's whole design rendered small, so anything that changes what the
       plate SAYS changes them. `View all` looked right for the reason it was never wrong: it derives
       on open. The carousel was built once and only ever redrawn by the paths that happened to
       remember to ask — the arc-text field did (which is why typing has always worked), a
       denomination change does (`refreshForWeight`), and the unit toggle and number-style tiles did
       not. Three controls, one rule, two of them missing it: CLAUDE.md §0b exactly.
         WIRED HERE RATHER THAN ON THE CONTROLS, so the next one added cannot forget. This is the
       shared commit for every value edit, and `scheduleThumbRefresh` is built to be called
       indiscriminately: it is debounced 450ms (so a slider drag or a burst of typing coalesces to
       one pass), it skips tiles that were never drawn and tiles nowhere near the viewport, and it
       draws what is left ONE PER FRAME. In the carousel that is the two or three cards on screen —
       the same work a denomination change has always done. */
    scheduleThumbRefresh();
  };
  // Discrete structural edits (add/remove element, change layout/slot, add/remove
  // outline) are committed to history IMMEDIATELY so each is individually
  // undoable — the debounce is only right for continuous value edits (WD-19).
  // A palette change (assign a role, swap, add custom) washes the stage tint too,
  // then commits as one undoable structural edit + re-renders the chips/badges.
  const structuralCommit = () => {
    ctx.requestRender();
    (ctx.recordHistoryNow || ctx.recordHistory)();
    render();
  };

  // Eyedropper sampler for the color picker — THE FALLBACK PATH ONLY as of 2026-08-02. Where
  // `window.EyeDropper` exists (Chromium desktop) the picker runs the native screen-wide one instead,
  // so a colour can be taken from another window, File Explorer or any other program; this runs on
  // Firefox / Safari / mobile, where it is the only thing that can sample at all.
  //   It squares the 3D plate up + holds, then opens the ELEMENT-AWARE eyedropper
  // (ui/eyedropper.js), which reads the TRUE colour under the cursor — a colour swatch → its own hex
  // (unaffected by the swatch's glass gradient, and WITHOUT restyling the swatch), the plate → its
  // rendered pixel. Resolves the picked hex (the picker applies it) or null on cancel. Square-up must
  // finish + paint first (Ryan 2026-07-22) — the native path CANNOT await that, see onScreenPickStart.
  async function eyedropSample() {
    await (ctx.squareUpForColorPick?.() ?? Promise.resolve());
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    const wd = (root.closest && root.closest('.weight-designer')) || document.body;
    let hex = null;
    try { hex = await pickColorWithEyedropper(wd); } catch (_) { /* cancelled — no color */ }
    ctx.endColorPick?.();
    return hex;
  }

  // ONE shared color picker popover (ui/color-picker.js) — SV area + hue slider + preview
  // + eyedropper + HEX + RGB. DIRECT-edit only: a target's color updates live as you drag
  // and commits on release, via `cpApply(hex, live)`. Used by every color chip — the Text
  // fill / each outline / the plate body / the "⋯ add" buttons / the Advanced per-piece
  // chips. `cpSwatch` (if set) live-tints as you go. On close, the panel rebuilds (deferred
  // + guarded) so a freshly ADDED color shows up in its list (Ryan 2026-07-20).
  let cpApply = null;
  let cpSwatch = null;
  /* Did a colour actually MOVE while this picker was up? Set by `onChange` — the one path that
     writes a live hex onto the anchor swatch — and read once by `onClose` to decide whether the
     dismissal owes the panel a rebuild. See the note there. */
  let cpMoved = false;
  const colorPicker = createColorPicker({
    supportsEyedropper: SUPPORTS_EYEDROPPER,
    onEyedrop: eyedropSample,
    // The native screen picker's half of the same choreography. It cannot AWAIT the square-up (that
    // would spend the click's user activation and the open() would throw), so the tween is started
    // and left to run behind the magnifier — by the time the eye is on a target the plate is flat,
    // still, and framed to fill, which is everything the await used to guarantee.
    onScreenPickStart: () => { try { ctx.squareUpForColorPick?.()?.catch?.(() => {}); } catch { /* never block the pick */ } },
    onScreenPickEnd: () => { ctx.endColorPick?.(); },
    onClose: () => {
      // Rebuild after the picker dismisses so a newly added color appears + the active
      // highlight updates. Deferred to the next tick (so it never remounts a chip mid-
      // click) and skipped if another picker opened in the meantime.
      /* ⛔ …AND ONLY IF A COLOUR ACTUALLY MOVED (Ryan 2026-08-09: the close-side flash, *"only to the
         canvas/editable area of the weight model"*). MEASURED: on a dismissal this fired a full
         `render()` at +135ms and swapped the whole plate SVG out of `.wd-canvas-stage` at +253ms —
         on EVERY close, including one where the buyer opened the editor, looked, and left.
           The rebuild exists for one narrow job: `onChange` writes the live hex straight onto
         `cpSwatch.style.background` for instant feedback, so a dismissed preview leaves that swatch
         wearing a colour the design never took. Nothing else here needs it — every committing path
         (confirm, swap, assign, "Add color") ends in `structuralCommit`, which renders on its own,
         and `onCancel` renders after restoring its snapshot. So the flag IS the condition, and it is
         a fact about what happened to the swatch rather than a guess about what might have changed.
           Cleared here rather than at each `open()`: close always fires, so the next picker starts
         from false without three call sites having to remember. */
      const moved = cpMoved;
      cpMoved = false;
      if (!moved) return;
      setTimeout(() => { if (!colorPicker.isOpen()) render(); }, 0);
    },
    onChange: (hex, live) => {
      cpMoved = true;
      if (cpSwatch) cpSwatch.style.background = hex; // instant swatch feedback
      if (typeof cpApply === 'function') cpApply(hex, live); // direct-edit
    },
  });

  // WHERE the picker mounts decides whether a Save/Share card can ever paint over it — this is a
  // STACKING-CONTEXT question, not a z-index one (Ryan 2026-07-25, third report of the same bug).
  //   `.ds-canvas` is `position: relative` + `z-index: 2`, so it CREATES a stacking context. The
  // Save/Share cluster is a child of it (`canvasEl.appendChild` in shell/canvas-actions.js), which
  // seals the cluster's z-index INSIDE that context. Mounting the picker on `.weight-designer` put
  // it a level ABOVE, so the browser never compared the cluster's 1100 to the picker's 1000 — it
  // compared the CANVAS's 2 to the picker's 1000, and the picker won every time. Two earlier fixes
  // raised the cluster's z-index (17 → 1100) and could not possibly have worked; the test that
  // passed them compared `getComputedStyle().zIndex` on the two elements, which cannot see this.
  //   Mounting into `.ds-canvas` puts the picker in the SAME context as the cluster, so the plain
  // 1100 vs 1000 comparison is finally the one the browser makes. It is also where the picker
  // belongs: it docks to the pane's edge, and every other overlay in the shell (pane, tool cluster,
  // zoom popover, status bar) is canvas chrome already. The picker is `position: fixed`, so the
  // canvas's `overflow: hidden` does NOT clip it — a fixed box's containing block is the viewport,
  // and `.ds-canvas` has no transform/filter/contain to change that. Verified, not assumed:
  // `tools/probe-card-picker-stack.mjs` measures the real winner with elementFromPoint.
  //   Root-level overlays that must stay ABOVE the picker still do, since each already outranks the
  // canvas's 2: modal scrim 100, view-all scrim 1000, assign menu 1001, eyedropper overlay 100000.
  /* ⛔ …EXCEPT ON A PHONE, WHERE IT MOUNTS ONE LEVEL OUT (Ryan 2026-08-09: *"whenever I open a color
   * slide-up menu, the whole edit window and 3D model and tab at the top etc. all flash quickly"*).
   *
   * 🔑 MEASURED, after two reasoned guesses were wrong. `tools/probe-picker-flash.mjs` records every
   * DOM mutation either side of an open, timestamped against the click, and the open is EMPTY: at
   * +19ms the card is appended, positioned and lit, and then nothing until the layout carousel's own
   * background thumbnail build ticks over at +320ms — at the identical ~110ms cadence it keeps while
   * idle. No canvas resize, no `webglcontextlost`, no rebuild of anything. So whatever the eye is
   * catching is not work the app is doing; it is the browser's response to the card ARRIVING.
   *
   * And the arrival is unusually expensive here. `.ds-canvas` is `overflow: hidden` and hosts the
   * live WebGL canvas, and in sheet mode the card is `position: fixed`, `z-index: 1000`, with both
   * `opacity` and `transform` transitions — a new animated layer that escapes its parent's clip.
   * Chrome has to rebuild the clip/layer tree for that subtree, and the subtree is the 3D stage.
   *   The `.ds-canvas` mount is a DESKTOP requirement (the Save/Share cards must be able to paint
   * over the picker, which needs them to be siblings — see the stacking note above). On a phone the
   * card covers the SHEET and nothing paints over it, so the requirement does not apply.
   *
   * ⚠ `.weight-designer`, NEVER `document.body`. Every sheet-mode rule in the stylesheet is written
   * `.weight-designer .wd-cp--sheet ...`, so hoisting to the body would silently drop the lot —
   * memory `hoisting-to-body-loses-scoped-css-vars`, which cost a round on the quote modal.
   *   Position is unaffected either way: both mounts sit inside `.weight-designer` and `.ds-canvas`
   * has no transform/filter/contain, so the fixed containing block is the same node in both cases. */
  const pickerMount = () => (phoneMQ() ? (root.closest('.weight-designer') || document.body)
    : (root.closest('.ds-canvas') || root.closest('.weight-designer') || document.body));

  // Open the picker in DIRECT-edit mode: `apply(hex, live)` receives every change (live
  // true mid-drag, false on the commit) so the target updates live + commits on release.
  /* ── AND NOTHING IS KEPT UNTIL YOU KEEP IT (Ryan 2026-08-08) ────────────────────────────────────
   * *"if you don't save, it simply stays on what it was when you opened the pop-up"* — asked for
   * every CD pop-up, Advanced per-piece included.
   *
   * ⚠ THIS IS A REAL BEHAVIOUR CHANGE ON THIS PATH, not a bug fix. These two editors used to COMMIT
   * on every gesture end — no confirm button existed, so releasing the hue slider wrote a history
   * entry and there was no way back except undo. They now preview like every other picker on the tab
   * and commit once, on the button.
   *
   * The three pieces, and why each is where it is:
   *   · `apply(hex, live)` still belongs to the caller — it knows WHICH element and WHICH field —
   *     but it now only ever writes and renders. The commit moved out of it.
   *   · the snapshot is taken here, once, on the first change rather than at open: a popover that is
   *     opened and dismissed untouched must not spend a `designSnapshot`, and `onCancel` fires on
   *     every dismissal including that one.
   *   · `valueCommit` is the commit for this tab (history + the debounced card refresh), so confirm
   *     calls exactly what the old gesture-end path called — the committed result is unchanged.
   */
  function openEditPicker(anchorEl, seed, apply, opts = {}) {
    let editUndo = null;
    cpApply = (hex, live) => {
      if (!editUndo) editUndo = designSnapshot(state);
      apply(hex, live);
      ctx.requestRender();   // the plate AND the 3D, without rebuilding the panel under the popover
    };
    cpSwatch = anchorEl;
    colorPicker.setColor(normalizeHex(seed) || '#000000');
    colorPicker.open(anchorEl, pickerMount(), {
      confirm: {
        label: opts.confirmLabel || 'Update color',
        onConfirm: () => { editUndo = null; valueCommit(); },
        onCancel: () => {
          // Untouched dismissal: nothing to undo, and nothing to repaint.
          if (!editUndo) return;
          restoreSnapshot(state, editUndo);
          editUndo = null;
          ctx.requestRender();
          render();
        },
      },
    });
  }

  // Open the picker in ADD mode (Ryan 2026-07-20): craft a color (SV area / hue slider /
  // eyedropper) with a LIVE preview on the target, then an explicit "Add color" button
  // makes it official — appends it to the END of your colors + commits (undoable). A
  // light-dismiss / Escape CANCELS: the live preview is reverted and nothing is added.
  //   preview(hex) → cheap live re-render (no history, no pool write)
  //   confirm(hex) → apply + addSwatch + commit
  //   cancel()     → restore the pre-edit state
  function openAddPicker(anchorEl, seed, { preview, confirm, cancel, label }) {
    cpApply = (hex /* , live */) => { preview(hex); };
    cpSwatch = anchorEl;
    colorPicker.setColor(normalizeHex(seed) || '#000000');
    colorPicker.open(anchorEl, pickerMount(), {
      confirm: { label: label || 'Add color', onConfirm: confirm, onCancel: cancel },
    });
  }

  /* ── "CHOOSE A COLOR TO EDIT" — the strip, rebuilt (Ryan 2026-08-15) ──────────────────────────
   * *"the color pop-up on the plate color and text color on the Customize Design tab doesn't include
   * these [the Add Logo tab's swatch tabs]. I think it should."*
   *
   * ⛔ THIS EXISTED AS `poolTargets()` AND HE HAD IT DELETED TEN DAYS EARLIER, so the two reasons it
   * went are the specification for putting it back:
   *   · it read as the twin of the SWAP row while meaning the opposite — one says "edit this one",
   *     the other "use this one", and two identical swatch strips with opposite verbs is a coin flip;
   *   · on a row that already renders the whole pool as clickable chips, it duplicated the row.
   *
   * 🔑 WHAT CHANGED IS THE FIRST ONE: both these popovers dropped their swap rows in the same pass,
   * so this is now the ONLY strip in either of them. The 2026-08-05 fault was the pairing, not the
   * strip — and it is captioned here, which the deleted version never was.
   *
   * 🔑 AND THE SECOND OBJECTION ANSWERS A QUESTION NOBODY IS ASKING. The row behind does show these
   * colours, but its verb is APPLY. Without this strip the ✎ can only ever edit the colour the row is
   * already on, so editing any other one means applying it, editing it, and putting the design back —
   * on the Add Logo tab you simply flip to it. That gap is the ask.
   *
   * ⚠ The pool is the DESIGN's, not the row's — `designColorPool` is the logo's roles plus the saved
   * swatches, which is the same set both rows draw their own chips from, and the same set
   * `assignOutlineColors` picks from. `recolorEverywhere` then means what Ryan asked for: the edit
   * lands on the 3D, the swatch, the logo artwork, the text and the outlines at once.
   * ⚠ The active colour is FORCED IN if the pool has somehow lost it, because a strip with nothing
   * lit is a strip that has mislaid the thing you pressed ✎ on.
   */
  function editTargets(activeHex) {
    const active = normalizeHex(activeHex);
    const out = designColorPool().map((c) => ({ key: c.hex, label: c.label, hex: c.hex }));
    if (active && !out.some((t) => t.key === active)) out.unshift({ key: active, label: active, hex: active });
    return out;
  }
  const EDIT_TARGETS_LABEL = 'Choose a color to edit';


  // EDIT an existing swatch's hex — the row-level ✎ on any colour row. On "Update color" the colour
  // itself CHANGES, everywhere the design wears it.
  //
  // GLOBAL since 2026-07-26, reversing the LOCAL scoping of 2026-07-23. Local meant "re-point THIS
  // surface at a new colour", so editing the text fill's green left the plate's green alone — each
  // surface independently tweakable. In practice that reads as the wrong verb: Ryan, on the ✎, "the
  // color I HAD selected doesn't actually get edited, it just adds a new color altogether… it should
  // edit the color I had selected and it should cascade down to all the same colors on all the
  // components/swatches". Which is what "Edit" says on the tin — the local behaviour was
  // indistinguishable from the row's "+ Add", right down to leaving a second chip on the row, because
  // `applyLocal` ends in `addSwatch`. Re-pointing ONE surface is still available: pick a different
  // swatch, or use "+ Add" to make a new one.
  //
  // `recolorEverywhere` is the same helper the Add Logo tab's swatches use, so "edit a colour" means
  // one thing in this tool no matter which screen you say it from — including repainting the logo
  // artwork, which is the part `applyLocal` could never do.
  //
  // Commit-only (no live preview — a recolour can touch the logo raster); Escape cancels.
  // `extraOpts` (optional) — passed straight through to the picker. Used by the outline rows' ✎ to
  // supply `targets` / `activeTargetKey` / `onTargetChange`, i.e. the tab strip that says WHICH ring
  // is being edited (Ryan 2026-07-24). With a global recolour those tabs now choose which colour is
  // being EDITED rather than which ring gets written to, so the tab strip has to move `from` — hence
  // the wrapped `onTargetChange` below. Reading a stale `from` would recolour the wrong colour.
  /* The colours this design ALREADY uses — the logo's palette plus the saved swatches, deduped by
     resolved hex and in that order (Ryan 2026-08-05, both confirmed). Deliberately the SAME pool
     `assignOutlineColors` draws from, so the one-click row offers exactly what the auto-derivation
     would have picked: on the Bauer's Elite logo, "swap the near-black ring for the blue" is one tap
     rather than a hex hunt. */
  /**
   * @param {(hex:string)=>boolean} [fits] keep only colours that READ in the slot being edited.
   * @param {string} [keepHex] the colour currently in that slot — always survives the filter.
   *
   * ⛔ THE ROW MUST NOT OFFER WHAT THE DERIVATION WOULD REFUSE (Ryan 2026-08-05). Unfiltered, it
   * handed over the raw pool: on a blue plate with black text and a grey ring, the two other chips
   * were black (merges with the text) and blue (merges with the plate) — *"is it worth, in this
   * example, even having the swatch section at the top of the color pop-up?"* No: a shortcut whose
   * only options are ones `assignOutlineColors` would never pick is worse than no shortcut.
   *   `keepHex` always survives because it is the "you are here" marker. A buyer who deliberately set
   * black-on-black must still see black lit — a row that drops the CURRENT colour is lying about the
   * state, which is a worse fault than offering a poor option.
   *   Filtering here removes CONVENIENCE, never capability: the full picker sits directly below and
   * still dials anything. That is the difference from greying out swatches on the main colour rows,
   * which would remove the only way to reach a colour (and which we deliberately did NOT do — 239 §3).
   */
  function designColorPool(fits, keepHex) {
    const p = state.designProfile;
    const pal = p.palette || {};
    const out = [];
    const seen = new Set();
    const keep = normalizeHex(keepHex);
    const add = (hex, label) => {
      const h2 = normalizeHex(hex);
      if (!h2 || seen.has(h2)) return;
      if (typeof fits === 'function' && h2 !== keep && !fits(h2)) return;
      seen.add(h2);
      out.push({ hex: h2, label: label || h2 });
    };
    ['primary', 'secondary', 'tertiary'].forEach((r) => add(resolvePaletteRef(r, pal), `Logo ${r}`));
    (Array.isArray(p.swatches) ? p.swatches : []).forEach((sw) => add(resolvePaletteRef(sw, pal)));
    return out;
  }

  function openSwatchEditPicker(anchorEl, oldHex, extraOpts) {
    let from = normalizeHex(oldHex);
    if (!from) return;
    const opts = extraOpts || {};
    // Taken at the FIRST preview, spent on commit or on dismiss. A preview mutates the live design so
    // the 3D can show it, so walking away has to put it back exactly — not approximately.
    let swapUndo = null;
    const targets = Array.isArray(opts.targets) ? opts.targets : [];
    /* ── THE PLATE SHOWS THE COLOUR YOU ARE TRYING (Ryan 2026-08-08) ────────────────────────────
     * *"whenever you're editing a colour pop-up and clicking different colours, I want to see those
     * colours appear on the 3D model itself — that's the whole point of keeping the model visible
     * while editing colours… but if you don't save, it simply stays on what it was when you opened
     * the pop-up."*
     *
     * This used to be `cpApply = () => {}` — *"no live recolor"* — so the swatch under your thumb
     * changed and the product did not. The swap chips beside it have previewed since 2026-07-25, so
     * the machinery was already here and only the picker's own colour was excluded.
     *
     * ⚠ IT REPLAYS FROM THE SNAPSHOT, IT DOES NOT CHAIN. `recolorEverywhere(from → to)` finds the
     * colour by VALUE, so a second preview would search for a `from` the first preview had already
     * replaced and quietly do nothing. Restoring first means every preview is computed against the
     * design as it was when the popover opened — which is also what makes "walk away and it goes
     * back" exact rather than approximate, and it leaves `onConfirm` below completely unchanged: it
     * still replays `from → to` over the original state and commits ONE history entry.
     *
     * ⚠ ON GESTURE END, NOT PER FRAME (`!live`), matching the Add Logo tab's picker exactly — the
     * surface Ryan says already behaves. A snapshot restore plus a full recolor on every pointermove
     * of a hue drag is real work over a live WebGL stage; a discrete tap on a swatch reports `false`
     * anyway, so clicking colours — his actual words — previews immediately.
     *
     * ⛔ `ctx.requestRender()`, NEVER `structuralCommit()`. The latter calls `render()`, which
     * rebuilds this panel and tears the popover's own anchor out of the DOM mid-edit. That is the
     * same rule the swap chips are already written to, stated on `swap.onPreview` below. */
    const previewColor = (hex) => {
      const to = normalizeHex(hex);
      if (!to || to === from) return;
      if (!swapUndo) swapUndo = designSnapshot(state);
      else restoreSnapshot(state, swapUndo);
      /* ⛔ THE OUTLINE EDITORS PREVIEW SCOPED, NOT BY VALUE (Ryan 2026-08-18, the Hanover white
       * plate: *"adjusting this color changes the plate color and the logo outline at the same
       * time which is super weird"*). `recolorEverywhere` finds the colour by VALUE — the right
       * meaning for the pool ✎s, and a trap the moment two different THINGS share a hex: the
       * mark's white keyline and the white plate dye are the same #F1F3F3 on that configuration,
       * so dialling the band dragged the plate with it. A caller that passes `scopedConfirm`
       * routes preview AND commit through its own scoped apply (`onSwapPick` — the per-band /
       * per-ring writes), which is what the swap chips beside the dial have always done. */
      if (opts.scopedConfirm && typeof opts.onSwapPick === 'function') {
        opts.onSwapPick(to, { preview: true });
      } else {
        recolorEverywhere(state, from, to);
      }
      ctx.requestRender();
    };
    cpApply = (hex, live) => { if (!live) previewColor(hex); };
    cpSwatch = anchorEl;
    colorPicker.setColor(from);
    colorPicker.open(anchorEl, pickerMount(), {
      ...opts,
      // The picker reseeds ITS colour from the target's `hex`; this keeps our `from` in step with it,
      // then lets the host do whatever it did before (re-pointing which ring `applyLocal` aims at).
      onTargetChange: (key) => {
        const t = targets.find((x) => x && x.key === key);
        const h = t && normalizeHex(t.hex);
        if (h) from = h;
        if (typeof opts.onTargetChange === 'function') opts.onTargetChange(key);
      },
      /* Only when the caller gives the chips something to DO — see color-picker's note on why a swap
         cannot borrow the confirm handler's "recolour everywhere" meaning.
           `onPreview` writes the slot and renders WITHOUT `structuralCommit`, which is the whole
         trick: `structuralCommit` calls `render()`, that rebuilds the design panel, and the rebuild
         takes this popover's anchor with it. `ctx.requestRender()` repaints the plate and the 3D and
         leaves the DOM alone, so the popover survives and you can compare colours in place. */
      swap: opts.onSwapPick ? {
        colors: designColorPool(opts.swapFits, from),
        onPreview: (hex) => {
          if (!swapUndo) swapUndo = designSnapshot(state); // first preview only — the way back
          opts.onSwapPick(hex, { preview: true });
          ctx.requestRender();
        },
        /* SPEND THE UNDO POINT FIRST. `onSwapPick` ends in `structuralCommit`, which calls `render()`
           — that rebuilds the design panel, which tears this popover out of the DOM and fires
           `onCancel` SYNCHRONOUSLY, before the next statement runs. Clearing `swapUndo` afterwards
           therefore cleared it too late: the cancel path had already restored the snapshot over the
           commit, so "Update color" previewed, committed, and silently reverted itself. */
        onPick: (hex) => { swapUndo = null; opts.onSwapPick(hex, { preview: false }); },
      } : null,
      confirm: {
        /* ── THE VERB NAMES WHAT YOU ARE EDITING (Ryan 2026-08-08) ─────────────────────────────
           *"the 'Update color' button isn't relevant [when changing thickness]. It should instead
           read 'Update outline'."* The popover grew a thickness dial for outlines, so on those two
           editors the button commits a ring, not a colour, and the old label described half of what
           it did. Caller's choice rather than derived from `opts.extra` being present: the LOGO
           editor shows that dial only for an ADDED ring and not for a detected band, and the button
           must not rename itself as the buyer switches tabs inside one popover. */
        label: opts.confirmLabel || 'Update color',
        onConfirm: (hex) => {
          const to = normalizeHex(hex);
          /* ⛔ PUT THE PREVIEW BACK BEFORE COMMITTING IT. `recolorEverywhere` finds the colour by
             VALUE, and a live preview has already replaced `from` on the design — so without this
             the search below would find nothing, return falsy, and the whole commit block would be
             skipped: the popover would appear to work right up until you pressed the button that
             makes it permanent. Restoring first means the commit runs against exactly the state it
             was written for, and lands as ONE history entry instead of a preview plus a commit. */
          if (swapUndo) { restoreSnapshot(state, swapUndo); swapUndo = null; }
          /* ⚠ THE COLOUR IS NO LONGER THE ONLY REASON TO PRESS THIS. Two bare `return`s used to stand
             here, which was honest while a colour was all the popover could change — the outline
             editor's thickness dial has since made "confirm with the hex untouched" a real and
             meaningful press. So the colour work became CONDITIONAL and `onConfirmExtra` runs either
             way; see the outline editors, which use it to retire a ring dialled down to 0. */
          // Scoped commit for the outline editors — see the note in `previewColor`. `onSwapPick`'s
          // non-preview path already marks, commits and announces.
          if (to && to !== from && opts.scopedConfirm && typeof opts.onSwapPick === 'function') {
            opts.onSwapPick(to, { preview: false });
            from = to;
          } else if (to && to !== from && recolorEverywhere(state, from, to)) {
            // Their colour, everywhere the design used ours — the broadest hand edit on the tab.
            markStyleUserEdited(state.designProfile);
            /* ⚠ A SWAP IS A DELIBERATE PICK, so it is marked `userSet` (Ryan 2026-08-05, confirmed).
               That is the difference between "my choice stuck" and "it moved again on the next hop":
               `userSet` is exactly what makes a ring immune to `assignOutlineColors` and
               `dropAutoOutlinesWhenFillReads`. Without it, tapping the blue on a black plate would
               be re-derived away by the very pass that should have picked blue in the first place. */
            structuralCommit();
            /* ⛔ …AND THE OTHER TAB'S SWATCHES, WHICH `structuralCommit` DOES NOT REACH (Ryan
               2026-08-15: *"if a swatch like this is edited on the Customize Design tab, the swatch
               on the Add Logo tab needs to change color accordingly as well"*).
                 `recolorEverywhere` already moves `state.ui.palette`, which is the source those chips
               are built from — but building them is `paintLandedColorChips`, and `structuralCommit`
               re-renders THIS panel and nothing on that tab. So the data was right and the DOM was a
               fortnight old: the Add Logo chips still showed the uploaded artwork's tones.
                 This is the third instance of one shape — project CLAUDE.md §0b, "a rule computed in
               one UI surface and cached in another" — and `repaintColors` is the hook that was added
               for the SECOND one (Reset colors, 2026-08-12) and then not put on this path. Same
               commit beat, same omission, so it goes here rather than in a new helper.
               ⚠ Placed at the level of `openSwatchEditPicker` rather than at the Plate/Text call
               sites, so every ✎ that can recolour globally — the outline rows and the logo row
               included — is on the hook by construction. */
            ctx.logoStep?.repaintColors?.();
            ctx.announce?.(`Updated ${from} to ${to} everywhere it was used.`);
            from = to; // the popover stays open in keepOpen mode; the slot now holds `to`
          }
          if (typeof opts.onConfirmExtra === 'function') opts.onConfirmExtra();
        },
        onCancel: () => {
          // Dismissed with a preview still on the plate — restore it. Same snapshot machinery the
          // cross-product remap's Undo toast uses, so "put it back" means one thing in this app.
          if (swapUndo) { restoreSnapshot(state, swapUndo); swapUndo = null; ctx.requestRender(); render(); }
          /* ⛔ SOME OF WHAT THE POPOVER CHANGED WAS NEVER A PREVIEW (Ryan 2026-08-09: *"if you select
             0 then swipe down or close the color menu, the text outline swatch that you just edited
             should be removed"*).
               The colour IS a preview — it lives on the plate and nowhere else until "Update
             outline" commits it, which is why a dismissal reverts it. The THICKNESS dial is not: it
             writes through `valueCommit` on every notch, so by the time you let go of the sheet the
             ring is already 0. Dismissing left the design holding a ring that paints nothing and a
             swatch row still advertising it.
               So the zero check runs on BOTH exits, and that is the honest shape of Ryan's rule —
             *"setting an outline to 0 is effectively the same thing as clicking on the ✕"*. A ✕ does
             not ask which button you pressed afterwards. Ordered after the snapshot restore so it
             reads the state the buyer is actually left with. */
          if (typeof opts.onDismissExtra === 'function') opts.onDismissExtra();
        },
      },
    });
  }

  // Text fill "+" (Ryan 2026-07-20) — craft a NEW fill color, preview it live, then
  // "Add color" appends it to your colors (at the END) + sets it as the fill. Cancel
  // restores the prior fill. Switching to an existing fill = clicking one of the swatches.
  function openFillAddPicker(anchorEl) {
    const before = snapshotFills(); // per-piece fillRefs, to restore on cancel
    openAddPicker(anchorEl, normalizeHex(repFillHex()) || STARTER_CHIPS[0], {
      preview: (hex) => { setFillAll(hex); ctx.requestRender(); },
      confirm: (hex) => {
        setFillAll(hex);
        addSwatch(state, hex, { userPicked: true }); // lands at the END of the shared pool (Text fill + Plate color)
        syncFillContrastOutline(); // if the new fill would vanish on the plate, add a contrasting ring
        assignOutlineColors(state.designProfile, effectivePlateHex(state)); // …and re-balance the rings so none matches the fill
        structuralCommit(); // render + history + panel rebuild → the new swatch appears last
        ctx.announce?.(`Text fill set to ${hex}, added to your colors.`);
      },
      cancel: () => { restoreFills(before); ctx.requestRender(); },
    });
  }
  // Advanced per-piece fill / outline — same picker, but writes to ONE element.
  /* ⚠ THESE TWO NOW ONLY WRITE. The `if (live) return; valueCommit();` tail that used to close each
     of them WAS the old behaviour — commit on every gesture end — and leaving it in would have made
     the new confirm button decorative: the colour would already be permanent by the time you reached
     it, and Cancel would restore a snapshot over a change already in history. The commit lives on the
     button, once, in `openEditPicker`. */
  function openElementFillPicker(element, anchorEl) {
    openEditPicker(anchorEl, resolvePaletteRef(element.fillRef, state.designProfile.palette), (hex) => {
      const H = normalizeHex(hex); if (H) updateElement(state, element.id, { fillRef: H });
    });
  }
  function openElementOutlinePicker(element, i, anchorEl) {
    const cur = (element.outlines || [])[i];
    openEditPicker(anchorEl, resolvePaletteRef(cur && cur.colorRef, state.designProfile.palette), (hex) => {
      const H = normalizeHex(hex); const o = (element.outlines || [])[i]; if (H && o) o.colorRef = H;
      // "Update outline", not "Update color" — the same rule the swatch editors follow: the verb
      // names what the button commits (Ryan 2026-08-08).
    }, { confirmLabel: 'Update outline' });
  }

  // Craft a custom PLATE-BODY color (B6 §4.1) — same ADD flow: preview the dye live, then
  // "Add color" dyes the plate + appends the color to the END of your colors. Cancel
  // restores the prior plate color (Ryan 2026-07-20).
  function openBodyPicker(anchorEl) {
    const before = state.designProfile.bodyColor || DEFAULT_BODY_COLOR;
    openAddPicker(anchorEl, normalizeHex(before) || '#0b0c0f', {
      preview: (hex) => { setBodyColor(state, hex); ctx.requestRender(); },
      confirm: (hex) => {
        // Stamp the ink belonging to the plate we are LEAVING, before the cascade re-derives it.
        // Keyed off `before`, NOT the live bodyColor: `preview` above has already dragged that
        // around, while the elements still wear the ink of the colour this picker opened on.
        rememberInk(state, inkContextKey(state, { body: before }));
        setBodyColor(state, hex);
        // A deliberate dye — latched, so the logo-derived defaults never overwrite it (state.js
        // `applyDefaultPlateColors`). Black counts: choosing it is not the same fact as never
        // having chosen, which is the whole reason the latch exists rather than a colour test.
        markBodyColorUserSet(state, state.ui.product);
        // `userPicked` — a hand-chosen white/black is a brand colour, not a derived one, and without
        // it this silently no-ops while the toast below still claims success (Ryan 2026-08-12).
        if (!isBlackBody(hex)) addSwatch(state, hex, { userPicked: true }); // joins the shared library at the END
        masterSwitchCascade(); // plate colour = master switch → re-cascade the contrast defaults
        structuralCommit(); // rebuild the dyed texture + rings + history + panel
        ctx.announce?.(isBlackBody(hex) ? 'Plate color set to black.' : `Plate dyed ${hex}, added to your colors.`);
      },
      cancel: () => { setBodyColor(state, before); ctx.requestRender(); },
    });
  }

  // The shared "your colors" pool, ORDERED (Ryan 2026-07-20): the logo-derived ROLES come
  // first — SORTED by hex so a "Swap colors" (which just exchanges 1st⇄2nd) never reshuffles
  // them — then the USER-ADDED swatches in the order they were added (newest LAST), so a
  // freshly "Add color"ed swatch always lands at the END of the list. `added` (a Set of
  // those user swatches) drives the removable ✕: roles are the extracted identity and
  // re-derive from the art, so only the added colors are removable.
  // Every hex the design is currently WEARING — these are exempt from the perceptual merge above, so
  // the active swatch in a row always has a chip to light up.
  // `includeBody: false` asks the narrower question "what are the ELEMENTS wearing" — the plate has its
  // own row, so its dye must not be injected into the ink rows by the homeless pass in `orderedPool`.
  const inUseHexes = (includeBody = true) => {
    const p = state.designProfile;
    const pal = p.palette || {};
    const out = new Set();
    const add = (h) => { const H = normalizeHex(h); if (H) out.add(H.toUpperCase()); };
    if (includeBody) add(p.bodyColor);
    for (const e of p.elements || []) {
      if (e.type === 'arctext' || e.type === 'weight') {
        add(resolvePaletteRef(e.fillRef, pal));
        for (const o of e.outlines || []) add(resolvePaletteRef(o.colorRef, pal));
      } else if (e.type === 'logo') {
        add(e.logoCenterColor);
        for (const k of Object.keys(e.logoBandColors || {})) add(e.logoBandColors[k]);
        const rings = Array.isArray(e.logoAddOutline) ? e.logoAddOutline : (e.logoAddOutline ? [e.logoAddOutline] : []);
        for (const r of rings) add(r && r.color);
      }
    }
    return out;
  };
  /** @param {{alwaysNeutrals?: boolean}} [opts] `alwaysNeutrals` is the PLATE row's flag — see the
   *  note on the neutral gate below for why that one row is exempt from it. */
  const orderedPool = ({ alwaysNeutrals = false } = {}) => {
    const pal = state.designProfile.palette;
    const keep = inUseHexes();
    const roles = dedupeHex([pal.primary, pal.secondary, pal.tertiary], keep).sort();
    const swatches = dedupeHex(Array.isArray(state.designProfile.swatches) ? state.designProfile.swatches : [], keep);
    // House neutrals close the row, after the brand roles and the buyer's own bench. They are NOT in
    // `added`, so they carry no ✕ — there is nothing to un-add (Ryan 2026-07-25).
    //   `usableAccents` drops any that would read as a colour already on the row: an exact-hex filter
    // let a NEAR-black logo (#0B0C0F) sit beside #000000 as two indistinguishable chips.
    //   ONE-TONE MARKS ONLY (Ryan 2026-07-25). The neutrals exist because a single-colour logo gives
    // the buyer nothing to contrast against — that was the whole point of the original rule. Once the
    // mark supplies two or more tones of its own the row is already rich enough, and padding it with
    // colours the brand does not contain is just noise: a green+yellow logo was being handed a white
    // chip it never asked for. Applies at 2 tones and up (Ryan: "also for 3-tone, 4-tone, etc").
    //   Keyed off the LOGO's palette roles, not the buyer's bench, so adding a custom colour to a
    // one-tone design can't yank the neutrals out from under them mid-edit.
    /* ⛔ "RICH ENOUGH" IS A QUESTION ABOUT THE PLATE, NOT A TONE COUNT (Ryan 2026-08-12: *"should the
       tool automatically add white as a default colour on upload, knowing that the black + colour
       won't be enough for most folks?"*).
         The rule above is right in spirit — once the mark supplies two or more tones of its own the
       row is rich enough, and padding it with colours the brand does not contain is noise. It counted
       tones without asking whether they are USABLE. Cherry Valley's two are #C38F82 and BLACK, and
       black IS the plate: on a training bumper the buyer really has one ink and the rule believed
       they had two, so the house white was withheld from a design that had nothing to reach for.
         Counting only the roles that survive against the effective plate gives the neutral back
       exactly where it is needed and nowhere else — measured: white plate 2 usable → withheld
       (unchanged), black plate 1 usable → white offered, green dye 2 usable → withheld (unchanged).
         ⭐ DERIVED, NOT BENCHED, and that is the whole point of answering it here. Auto-`addSwatch`ing
       white on upload would store a colour the buyer never chose — the exact behaviour entry 65
       removed — burn a cap slot, and carry a ✕ offering to un-add something nobody added. A neutral
       that appears because the plate needs it also LEAVES when the plate no longer does. */
    /* ⛔ …AND THE PLATE ROW IS EXEMPT FROM ALL OF IT (Ryan 2026-08-17: *"picking FFCA06 spawns
       FEF7DC; picking FEF7DC spawns FFCD06"* — a picker whose options are a function of your pick).
         The gate below counts the roles that clear THE CURRENT PLATE. That is the right question for
       the Text and Logo rows, where "what ink have I got left to work with" genuinely depends on the
       surface. On the PLATE row it is self-referential: the row is choosing the very thing it
       measures against, so dyeing the plate one of a two-tone design's own tones drops the tally to
       one and a house neutral is born on the row you just clicked. Measured, walking every chip:
       black plate → 3 chips, dye it the cream → 4, and the white never leaves again because by then
       it is the plate and `keep` holds it.
         ⭐ Ryan's call: always offer the neutrals there. BLACK is already unconditional on that row
       and nobody finds it odd, because a plate row is a list of body colours you can actually order
       — not an ink row — so "don't pad with colours the brand does not contain" simply does not
       apply to it. One extra chip, and the list stops moving under the buyer's hand.
         ⚠ SCOPED, because `orderedPool` feeds FOUR surfaces. Making the neutrals unconditional for
       everyone would put a white chip back on the Text and Logo rows, which is exactly the padding
       the 2026-07-25 rule removed. Only `plateColorRow` passes the flag. */
    const platedRoles = roles.filter((r) => !colorsConflict(r, effectivePlateHex(state)));
    const neutrals = (!alwaysNeutrals && platedRoles.length >= 2)
      ? []
      : dedupeHex(usableAccents([...roles, ...swatches]), keep);
    // ── THE ✕ IS FOR COLOURS THE BUYER ADDED, AND ONLY THOSE (Ryan 2026-07-28) ───────────────────
    // "the white swatches had the ✕ on their top right corner all the way down — is this because it's
    // forgetting the swatch is part of the logo?" Exactly that.
    //   `added` drives the ✕, and it used to be the whole bench, which was sound while the bench only
    // ever held things the buyer put there. Seating the logo's own tones (so a third colour finally
    // had a home) quietly broke that equivalence: the bench now holds colours from the ARTWORK too,
    // and offering to un-add one is offering to undo something nobody did. Worse than untidy — the
    // house neutrals are withheld once a logo has two tones of its own, so deleting a white that came
    // from the logo removes it from the row with nothing standing behind it.
    //   Derived, not stored: a bench colour that is currently one of the logo's tones is a logo tone.
    // That stays true for free through a recolour, since `recolorEverywhere` moves the bench entry and
    // the extractor's palette in the same pass — a stored "is own tone" flag would need a third thing
    // kept in step with those two.
    const own = new Set(shownLogoTones(state.ui.palette));
    // The keyed-out school colour is a logo tone too — it was the mark's own field before the key
    // stripped it (Ryan 2026-08-18, Hanover's navy). Without this it wore a ✕ offering to un-add
    // a colour nobody added. Derived through `seatedFieldHex` so a field the buyer has since
    // recoloured or evicted goes back to counting as theirs.
    { const f = seatedFieldHex(state); if (f) own.add(f); }
    const addedByBuyer = swatches.filter((s) => !own.has(normalizeHex(s)));
    /* ⛔ A COLOUR THE DESIGN IS WEARING ALWAYS GETS A CHIP (Ryan 2026-08-01: *"I added a color of
       white. The things that were supposed to turn white did — but the white swatch didn't show up in
       the swatches list."*)
         Three rules compounded to make white specifically unreachable. `addSwatch` refuses
       `DEFAULT_ACCENTS` (#FFFFFF/#000000) outright unless `ownTone`, so white never reaches the bench —
       and every caller discards its return value, so the "+" still applied the colour and still toasted
       success. The house neutral that was meant to stand behind that refusal is withheld the moment the
       logo has two roles (the line above). And `keep` looks like the safety net but is not one: it is
       consumed only as `dedupeHex(list, keep)`, where it merely exempts a hex from the perceptual
       merge — it can never PUT a hex on the row.
         So the design wore a colour with no chip: unselectable, and `fillEditable` false so the ✎ was
       greyed out too. Fixed at the row rather than by loosening `addSwatch`, because the invariant the
       buyer actually relies on is about the ROW ("I can see and re-pick what my design is wearing"),
       and stating it here covers black, a near-white, and whatever the next special case turns out to
       be. Appended last so the established roles → bench → neutrals order is untouched, and NOT in
       `added`, so it carries no ✕ — there is no bench entry behind it to un-add. */
    const base = dedupeHex([...roles, ...swatches, ...neutrals], keep);
    const shownHex = new Set(base.map((h) => (normalizeHex(h) || '').toUpperCase()));
    const homeless = [...inUseHexes(false)].filter((h) => h && !shownHex.has(h));
    return { list: [...base, ...homeless], added: new Set(addedByBuyer) };
  };
  // Remove a color from the SHARED pool (Ryan 2026-07-20). Because the pool feeds both the
  // plate and the text fill, deleting a color must revert EVERY surface currently wearing
  // it — no matter which row's ✕ was clicked: the plate falls back to black, the text fill
  // to the first color on the (now-shorter) list. You can't keep showing a color you just
  // deleted.
  const removePooledColor = (hex) => {
    const H = normalizeHex(hex);
    const bc = state.designProfile.bodyColor;
    const plateWasActive = !isBlackBody(bc) && normalizeHex(bc) === H;
    const fillWasActive = normalizeHex(repFillHex()) === H;
    removeSwatch(state, H);
    if (plateWasActive) {
      // Deleting the swatch the plate is wearing reverts it to black — a plate change the buyer did
      // not ask for, and one of three paths that do this silently (the others: re-uploading a logo,
      // and the cascade). It must not eat the ink they chose for the dye, so stamp that, then adopt
      // black's own set — the one every non-dyeable product has been wearing all along.
      rememberInk(state, inkContextKey(state));
      setBodyColor(state, DEFAULT_BODY_COLOR);
      restoreInk(state, inkContextKey(state));
    }
    if (fillWasActive) { const first = orderedPool().list[0]; if (first) setFillAll(first); }
    // A logo whose center was flattened to the removed color reverts to its original art.
    for (const e of state.designProfile.elements || []) if (e.type === 'logo' && normalizeHex(e.logoCenterColor) === H) { e.logoCenterColor = null; e.logoCenterAuto = false; }
    structuralCommit(); // one atomic paint (tint + texture + logo) + history + panel rebuild
    ctx.announce?.('Color removed from your colors.');
  };

  // Plate-color row (B6 §4.1) — the FIRST row of Colors. Black (default, "fastest")
  // + palette/extracted chips + an Advanced custom chip. Picking a dye applies
  // Ryan's §4.2 palette logic as one undoable change + a friendly toast.
  function plateColorRow() {
    const cur = normalizeHex(state.designProfile.bodyColor) || normalizeHex(DEFAULT_BODY_COLOR);
    const black = isBlackBody(cur);
    // Quick dye options = the shared, ordered "your colors" pool (roles then added swatches),
    // so a custom plate color and the design colors are one and the same library.
    // `alwaysNeutrals` — the plate row's options must not change as you pick from it.
    const { list, added } = orderedPool({ alwaysNeutrals: true });
    /* ⛔ THE PLATE'S OWN DYE ALWAYS GETS A CHIP — the plate-row twin of the `homeless` pass in
       `orderedPool` (Ryan 2026-08-12). That pass rescues a colour the ELEMENTS are wearing but
       deliberately asks `inUseHexes(false)`, because the plate has its own row and its dye must not
       be injected into the ink rows. Correct — and it left this row with no equivalent, so a plate
       could wear a colour that no chip on its own row showed.
         `userPicked` above closes the "+" route that produced Cherry Valley's missing white. This
       closes the rest of them: an auto-dye, a restored design, a product hop that copies `bodyColor`,
       or a bench at its cap can all seat a dye that never reached the pool. Stated as the row's
       invariant rather than patched per route — the same reasoning the ink-row rescue gives.
         Appended LAST so the roles → bench → neutrals order is untouched, capped so the row can
       still only render 8, and the current colour survives the cap rather than being sliced off the
       end. It carries no ✕ (it is not in `added`) — there may be no bench entry behind it to un-add. */
    const dyed = list.filter((hx) => !isBlackBody(hx));
    const curShown = black || !cur || dyed.some((hx) => normalizeHex(hx) === cur);
    const hexes = curShown ? dyed.slice(0, 8) : [...dyed.slice(0, 7), cur];

    const pick = (hex, isBlack) => {
      // The plate we are leaving, stamped before `masterSwitchCascade` below re-derives its ink.
      rememberInk(state, inkContextKey(state));
      setBodyColor(state, isBlack ? DEFAULT_BODY_COLOR : hex);
      markBodyColorUserSet(state, state.ui.product);   // see the note in `openBodyPicker`
      // MASTER SWITCH (Ryan 2026-07-23): re-cascade auto defaults over everything below, dropping
      // any prior one-off fill/outline/logo tweaks so the new plate colour drives the whole design.
      masterSwitchCascade();
      // Text/number contrast peel happens at render time (§4.2, reversible + non-cumulative).
      structuralCommit(); // one atomic paint (dyed texture + rings + tint + logo) + history + panel re-render
      ctx.announce?.(isBlack ? 'Plate color set to black.' : `Plate dyed ${hex}.`);
    };

    // `onRemove` (added swatches only) hangs a corner ✕ on the chip — same badge as the
    // outline chips (Ryan 2026-07-20). Black + logo-role chips pass none.
    //   The BADGE TEXT is not a sentinel (Ryan 2026-07-25). This used to branch on `badge === 'Custom'`
    // to render the old add-chip: tool styling, a '+' glyph, and an .wd-adv-inline wrapper. That chip is
    // now built directly below, so the branch was dead — but it was still live enough to fire the moment
    // the dyed-plate caption was renamed to "Custom", which turned every selected colour swatch into a
    // dashed '+' tool chip with its caption hidden. Display copy must never steer behaviour; a caption
    // is free to say anything.
    const platechip = (bg, selected, onClick, title, badge, onRemove) => {
      const btn = h('button', { type: 'button', class: 'wd-platechip' + (selected ? ' is-selected' : '') + (isNearBlackHex(bg) ? ' wd-platechip--dark' : ''), style: { background: bg }, title, 'aria-label': title, onClick });
      const body = onRemove
        ? h('div', { class: 'wd-outline-chip' }, btn, h('button', { type: 'button', class: 'wd-ol-x', 'aria-label': `Remove ${title}`, title: 'Remove from your colors', onClick: (e) => { e.stopPropagation(); onRemove(); } }, '✕'))
        : btn;
      /* ── THE LEAD TIME IS A NUMBER OVER A UNIT (Ryan 2026-08-09) ────────────────────────────────
         *"change it to '4-6' on top and 'weeks' below, and add it onto the swatch itself, vs below,
         on mobile… then the other swatches that say 'Custom', add '6+' on top and 'weeks' below…
         this will allow the row of swatches below (if they wrap) to be closer to the top row."*
         *"and use this same verbiage on desktop too please."*

         🔑 THE WORDING CHANGES EVERYWHERE, THE PLACEMENT ONLY ON THE PHONE. So the two halves are
         split by MEDIUM: the copy is built here, once, for both breakpoints, and CSS decides whether
         the badge sits under the chip or over it. Emitting a different caption per viewport would put
         the phone's layout decision inside the builder, where a resize could not reach it.
         ⚠ `badge` NOW CARRIES THE NUMBER ONLY and "weeks" is furniture. It stays a plain string at the
         call sites, which keeps the rule the comment above this function insists on: the badge is
         DISPLAY COPY and must never steer behaviour. Nothing branches on it; the empty string still
         means "no caption" exactly as before, and `.wd-platechip-badge:empty` still matches, because
         an empty badge emits no children at all. */
      /* ── THE BADGE PICKS ITS INK FROM THE PLATE (Ryan 2026-08-09) ───────────────────────────────
         *"the text color needs to be dynamic so it's white on dark colors and dark on light
         colors."* Unavoidable once the caption sits ON the chip: the colour underneath is one the
         BUYER chose, so no fixed ink can be right — a white "6+" on a white dyed plate is invisible,
         and that plate is one tap away at all times.
         ⛔ THE THRESHOLD IS 0.179, NOT 0.5, AND THAT IS NOT A TASTE VALUE. It is the exact luminance
         where black ink and white ink give equal WCAG contrast: contrast against black is
         (L+0.05)/0.05, against white is 1.05/(L+0.05), and they cross at √(1.05×0.05) − 0.05 ≈
         0.179. Half-way up the curve "feels" like the midpoint and is far too high — every mid-tone
         from a mid-blue to a mid-green sits below 0.5 and kept WHITE ink on a colour bright enough to
         swallow it, which is what Ryan hit (*"instead of the label becoming dark on top…"*).
         ⚠ `relLuminance` linearises the channels first and weighs green 0.7152 against blue 0.0722,
         so a saturated blue correctly reads dark while a yellow of the same apparent brightness reads
         light. A channel average would get both wrong. */
      const onLight = relLuminance(bg) > 0.179;
      return h('div', { class: `wd-platechip-wrap${onLight ? ' is-onlight' : ''}` }, body, h(
        'span',
        { class: 'wd-platechip-badge' },
        badge ? h('span', { class: 'wd-platechip-badge__n' }, badge) : null,
        badge ? h('span', { class: 'wd-platechip-badge__u' }, 'weeks') : null,
      ));
    };

    // Lead-time sub-label sits UNDER the CHOSEN chip only (Ryan 2026-07-20). Unselected chips show
    // no sub-text, so it reads as the stock tier by default and swaps to the dyed tier on the color
    // you pick. (Replaces the old made-to-order footnote line.)
    //
    // ⛔ THE REAL FIGURES, CONFIRMED BY THE FACTORY 2026-08-31 (Ryan): *"14-16 weeks pertains only to
    // urethane bumpers in a non-black color… everything else is 4-6 weeks."* So there are exactly two
    // tiers and only ONE product can reach the slow one: a competition URETHANE plate with a dyed
    // body. Rubber is compounded black and cannot be dyed at all, which is also why this whole row
    // only renders for the one dyeable product now.
    // ⚠ The old note here claimed black reads "6 weeks" and dyed reads "15+ weeks"; the code had said
    // '4-6' and '6+' since 2026-08-09 and the comment had simply gone stale.
    // Caption reads STOCK STATUS, not a duration (Ryan 2026-07-25). "15+ weeks" led with an
    // open-ended number, which reads as a penalty and pushed people off dyed plates; "Custom" says
    // the same thing the way premium goods say it, and stays short enough to sit under a 38px chip
    // without overhanging into its neighbours. The weeks stay in the TOOLTIP —
    // unchanged and still exact — so anyone planning against a season date still finds them, and
    // nobody is surprised by a ~4-month lead time only after a rep calls them.
    const chips = [platechip('#0b0c0f', black, () => pick(null, true), 'Black plate — in stock, ships in 4-6 weeks (fastest)', black ? '4-6' : '')];
    hexes.forEach((hex) => {
      const selected = !black && normalizeHex(hex) === cur;
      const removable = added.has(hex);
      // 1st click dyes; 2nd click on the ALREADY-dyed chip opens the edit picker (global recolor).
      // Tapping a swatch only ever CHOOSES (Ryan 2026-07-24). The old "tap the selected chip again to
      // edit" is gone — editing is the row's ✎ button, so one gesture has one meaning and an accidental
      // double-tap (easy on touch) can't pop a picker nobody asked for.
      /* ⛔ THE TOOLTIP CARRIES THE EXACT FIGURE, THE BADGE CARRIES RYAN'S CHOSEN SHORTHAND.
         The note above this block says the weeks "stay in the TOOLTIP, unchanged and still exact,
         so anyone planning against a season date still finds them". That was not true: the tooltip
         said "6+ weeks" and the real figure is 14-16 (the factory, 2026-08-31). It is exact now.
         The '6+' BADGE is deliberately left alone — it is Ryan's own wording (2026-08-09), it is
         still true, and 2026-07-25 records why an open-ended number was chosen over a big one.
         ⚠ RAISED WITH RYAN 2026-08-31 AND NOT YET RULED ON: a `title` attribute has no hover on a
         touch device, so on a phone the badge is the ONLY lead time a buyer ever sees, and "6+"
         against a real 14-16 is the surprise this block's own comment set out to prevent. */
      chips.push(platechip(hex, selected, () => { if (!selected) pick(hex, false); }, selected ? `Plate ${hex} — current, custom made (ships in 14-16 weeks) — use ✎ Edit to change it` : `Dye the plate ${hex} — custom made, ships in 14-16 weeks`, selected ? '6+' : '', removable ? () => removePooledColor(hex) : null));
    });
    // ✎ Edit — edits the CHOSEN plate color (Ryan 2026-07-24), the row-level twin of the Text-fill
    // ✎; see that comment for why it isn't a corner badge. Disabled when BLACK is chosen: black is
    // the stock undyed default, not a pooled color — there's no hex to recolor, and dyeing means
    // picking a color or hitting "+". So the greyed state here reads "nothing editable is chosen"
    // even though a chip is highlighted.
    const plateEditable = !black && !!cur;
    const plateEditBtn = h('button', {
      type: 'button', class: 'wd-platechip wd-platechip--tool wd-flat-glass wd-platechip--edit',
      disabled: plateEditable ? null : true,
      title: plateEditable ? `Edit the plate color (${cur})` : 'Dye the plate a color first — black has nothing to edit',
      'aria-label': plateEditable ? `Edit the current plate color ${cur}` : 'Edit plate color — the plate is black (undyed)',
      onClick: (e) => {
        e.stopPropagation();
        if (!plateEditable) return;
        // …and cascade, exactly as picking a plate swatch does (Ryan 2026-07-25). Editing the plate
        // through the ✎ is still a PLATE CHANGE, so everything below it has to re-derive; this path
        // was setting the colour and skipping the master switch, so the same dye reached the design
        // differently depending on which control you used to get there.
        // Tabs for the row's dyed colours, lit on the chosen one. BLACK is deliberately not a tab:
        // it is the stock undyed default rather than a pooled colour, which is the same reason this
        // button is disabled when black is chosen — there is no hex to recolour.
        /* The "choose a colour to edit" strip is BACK on this row (Ryan 2026-08-15) — see
           `editTargets` for what changed since it was removed on 2026-08-05 and why the objection
           that removed it no longer applies here. This popover has no swap row, so the strip is the
           only one in it, and it is captioned.
             ⚠ The tabs reach the WHOLE design pool, not just the plate's dyed colours — the point is
           to edit a colour without first having to wear it, and a logo tone is the commonest thing
           you would want to fix while looking at the plate it clashes with. */
        openSwatchEditPicker(plateEditBtn, cur, {
          targets: editTargets(cur),
          activeTargetKey: normalizeHex(cur),
          targetsLabel: EDIT_TARGETS_LABEL,
          targetStyle: 'swatch',
        });
      },
    }, '✎');
    chips.push(toolWrap(plateEditBtn, 'Edit', 'platechip'));
    // "+" — add a NEW custom plate color to the list (always available, no longer Advanced-
    // gated). It dyes the plate now AND joins your colors so it stays in the list for easy
    // switching, instead of overwriting a default swatch (Ryan 2026-07-20).
    // --more so this '+' matches the '+' on every swatch row (Ryan 2026-07-25) — it shares .wd-cchip--more.
    // Greys out at the shared pool's cap (Ryan 2026-07-25) so the limit is visible. Before this,
    // addSwatch silently evicted the oldest colour and the button looked like it still worked.
    const platePoolFull = swatchSlotsLeft(state.designProfile) <= 0;
    const customBtn = h('button', { type: 'button', class: 'wd-platechip wd-platechip--tool wd-flat-glass wd-platechip--more', disabled: platePoolFull ? true : null, title: platePoolFull ? `That's the ${SWATCH_CAP}-colour limit — remove one to add another` : 'Add a custom plate color', 'aria-label': platePoolFull ? 'Add a custom plate color — colour limit reached' : 'Add a custom plate color', onClick: (e) => { e.stopPropagation(); if (!platePoolFull) openBodyPicker(customBtn); } }, '+');
    chips.push(toolWrap(customBtn, 'Add', 'platechip'));

    return h('div', { style: { margin: '0 0 20px' } },
      subHead('Plate'),
      h('div', { class: 'wd-platechip-row' }, chips),
    );
  }

  // ── whole-plate outlines (Fable §2.5) ────────────────────────────────────────
  // Simple gets ONE "+ Add outline" applied to ALL text + weight pieces (Advanced
  // cards keep per-element overrides). The first text/weight piece is the
  // representative stack; every edit fans out to every text/weight piece.
  const textWeightEls = () => state.designProfile.elements.filter((e) => e.type === 'arctext' || e.type === 'weight');
  const repOutlines = () => { const e0 = textWeightEls()[0]; return e0 && Array.isArray(e0.outlines) ? e0.outlines : []; };
  const eachTextWeight = (fn) => textWeightEls().forEach((e) => { if (!Array.isArray(e.outlines)) e.outlines = []; fn(e.outlines, e); });
  /**
   * The most ring `idx` may thicken before the STACK's ink leaves the paintable band.
   *
   * `OUTLINE_MAX` alone is a lie once there is more than one ring: strokes are painted at twice the
   * cumulative thickness and centred on the path, so the ink grows outward by the SUM of every ring
   * on every side. Four rings at 20 is 80u of growth per side on a band with ~76.5u of usable width
   * — and the weight number's own ring-band cap can't rescue that, it just shrinks the lettering
   * toward its floor while the rings paint past the ring edge (measured 2026-07-26).
   *
   * So each ring is offered what the band has LEFT after its siblings. `maxOutlineTotal` owns the
   * geometry — the panel must not carry a second copy of it, which is how the number's own cap and
   * this one would drift apart.
   *   A single ring still reaches the full 20 (nothing a buyer can do today gets tighter), so no
   * existing design is clamped; the cap only bites on a stack.
   */
  const outlineMaxFor = (outs, idx) => {
    const total = maxOutlineTotal(state.designProfile, {
      product: modelFor(state.ui.product),
      weight: state.ui.weight,
    });
    if (!(total > 0)) return OUTLINE_MAX; // nothing measurable → don't invent a limit
    const others = (Array.isArray(outs) ? outs : [])
      .reduce((a, o, i) => a + (i === idx ? 0 : Math.max(0, Number(o && o.thickness) || 0)), 0);
    // Never below what this ring ALREADY is: a design that arrives over the cap (an older save, or a
    // sibling thickened first) must still be adjustable DOWN rather than frozen by its own value.
    const mine = Math.max(0, Number((outs && outs[idx] && outs[idx].thickness)) || 0);
    return Math.max(OUTLINE_MIN, mine, Math.min(OUTLINE_MAX, total - others));
  };
  // Flat Fill + Outlines model (Ryan 2026-07-20): the text/number FILL and each OUTLINE
  // carry a LITERAL HEX after a user edit (resolvePaletteRef resolves hexes as-is), so
  // every outline is an independent color. Auto-seeded refs (roles from extraction/B1)
  // still resolve fine; the UI shows the RESOLVED color and, on edit, writes the chosen
  // hex to EVERY text + weight piece (Simple fans out; Advanced keeps per-piece control).
  const repFillHex = () => { const e0 = textWeightEls()[0]; return e0 ? resolvePaletteRef(e0.fillRef, state.designProfile.palette) : null; };
  // The plate body color the RENDER actually uses (non-dyeable products always render black).
  const plateBodyNow = () => (productSupportsBodyColor(state.ui.product) ? (normalizeHex(state.designProfile.bodyColor) || DEFAULT_BODY_COLOR) : DEFAULT_BODY_COLOR);
  // Does the logo have NATURAL outline bands? Selects the matching-plate rule (Ryan 2026-07-23):
  // no natural outlines → "solid, no outline"; outlined logo → keep the layered text stack.
  const logoHasOutlines = () => { const ls = state.designProfile.logoStructure; return !!(ls && Array.isArray(ls.bands) && ls.bands.length > 0); };
  // The render palette + its two hints (pool for the loaded-swatch fallback, logoOutlined for the
  // matching-plate rule) — so the panel's effective-style math matches the renderer exactly.
  /* ⛔ THE SAME BUILDER THE RASTER USES — never a hand-built copy (see `renderPaletteFor`). The
     panel's swatch mirror and the plate have to agree about every hint, and this WAS a second copy
     that had to be edited in lockstep with render-engine's. `effectivePlateHex` is the panel's own
     plate resolution (a product that cannot be dyed renders black) and stays the caller's job. */
  const renderPalette = () => renderPaletteFor(state.designProfile, effectivePlateHex(state));
  // The fill hex as RENDERED — after the reversible conflict-peel view (effectiveTextStyle)
  // auto-swaps a fill that would vanish on the dyed plate. The swatch highlight must track
  // THIS, not the stored ref, or the picker shows the wrong color selected (Ryan 2026-07-23).
  const effFillHex = () => {
    const e0 = textWeightEls()[0];
    if (!e0) return normalizeHex(repFillHex());
    // Same render palette the renderer uses (pool + logoOutlined), so the swatch highlight tracks
    // the fill actually rendered after the plate-conflict view (Ryan 2026-07-23).
    const rp = renderPalette();
    const eff = effectiveTextStyle(e0, rp, plateBodyNow());
    return normalizeHex(resolvePaletteRef(eff.fillRef, rp));
  };
  // Choosing a fill LOCKS it (Ryan 2026-07-23): a manual pick overrides the plate-dye auto-recolor
  // and sticks (green fill stays green on a green plate) until the buyer picks a plate colour again
  // (re-cascade) or hits Reset. The lock rides the fill snapshot so a cancelled "Add color" reverts it.
  // fillLocked = swap immunity for THIS plate (resets on a re-cascade); fillUserSet = the buyer chose
  // this colour (survives one). Both set here because picking a fill is a buyer action (Ryan 2026-07-24).
  /* ⚠ EVERY HELPER BELOW THAT WRITES A COLOUR OR AN OUTLINE ALSO SETS THE HAND-STYLED LATCH (Ryan
     2026-08-08). They are the panel's buyer-facing chokepoints — nothing but a tap reaches them — and
     the latch is what stops the Add Logo tab's holes flip re-deriving over a stack somebody chose.
     See `markStyleUserEdited` and, for the rule itself, `styleUserEdited` in design-profile.js.
     ⛔ ADD THE CALL WHEN YOU ADD A HELPER. A new hand edit that skips it is invisible until the day
     a buyer flips that checkbox and watches their outlines re-derive. */
  const setFillAll = (hex) => { const H = normalizeHex(hex); if (H) { markStyleUserEdited(state.designProfile); textWeightEls().forEach((e) => { e.fillRef = H; e.fillLocked = true; e.fillUserSet = true; }); } };
  const snapshotFills = () => textWeightEls().map((e) => [e.id, e.fillRef, e.fillLocked === true]);
  const restoreFills = (snap) => { const m = new Map(snap.map(([id, ref, lock]) => [id, { ref, lock }])); textWeightEls().forEach((e) => { if (m.has(e.id)) { e.fillRef = m.get(e.id).ref; e.fillLocked = m.get(e.id).lock; } }); };
  // Enable/disable outline `i` across every text+weight piece — the on/off toggle grays it
  // out instead of deleting, so it's one tap to bring back (Ryan 2026-07-21). Only the
  // DEFAULT (auto-seeded) outlines gray; an ADDED outline is REMOVED outright (below).
  const setOutlineEnabledAll = (i, on) => { markStyleUserEdited(state.designProfile); eachTextWeight((outs) => { if (outs[i]) outs[i].enabled = on; }); };
  // LOCK/unlock outline `i` across pieces (Ryan 2026-07-23) — a locked ring is exempt from the
  // plate-dye auto-peel (the buyer forced it on). Cleared when a plate colour is picked / on Reset.
  const setOutlineLockedAll = (i, on) => { eachTextWeight((outs) => { if (outs[i]) outs[i].locked = on; }); };
  // Mark ring `i` as the BUYER's colour choice — survives a plate re-cascade, unlike `locked`, which is
  // only peel immunity for the current plate (Ryan 2026-07-24).
  const setOutlineUserSetAll = (i) => { markStyleUserEdited(state.designProfile); eachTextWeight((outs) => { if (outs[i]) outs[i].userSet = true; }); };
  // Recolor outline `i` (literal hex) across pieces — a LOCAL swatch edit (Ryan 2026-07-23).
  const setOutlineColorAll = (i, hex) => { const H = normalizeHex(hex); if (H) { markStyleUserEdited(state.designProfile); eachTextWeight((outs) => { if (outs[i]) outs[i].colorRef = H; }); } };
  // Re-derive the TEXT outline for the chosen fill on a dyed plate (Ryan 2026-07-23) — an OUTLINE-
  // LESS logo only, since its ring is synthetic. A fill that would VANISH (merges with the plate)
  // gets ONE contrasting ring (green fill on green plate → yellow ring, locked so the peel keeps it);
  // a fill that reads fine gets NONE (solid). This is the same "solid vs outline" symmetry the auto
  // view uses, applied to the locked CHOICE — so switching the fill back to a visible colour strips
  // the ring again. Outlined logos keep their layered stack; an undyed/black plate leaves it alone.
  const syncFillContrastOutline = () => {
    if (logoHasOutlines()) return;
    const base = normalizeHex(plateBodyNow());
    // Same veto removed as on the logo's ring below (Ryan 2026-07-28) — this is the TEXT half of the
    // identical rescue and carried the identical hole: pick a near-black lettering colour on the
    // default black plate and it vanished, because "the plate is black" was being read as "nothing
    // can clash with it". Fixed here as well rather than waiting for the same report about text,
    // since one guard in two places is one bug in two places.
    if (!base) return;
    const rep = textWeightEls()[0];
    if (!rep) return;
    const rp = renderPalette();
    const fillHex = normalizeHex(resolvePaletteRef(rep.fillRef || 'primary', rp));
    if (fillHex && colorsConflict(base, fillHex)) {
      // BRAND COLOURS FIRST (Ryan 2026-07-25). `bestContrastRef` optimises purely for contrast with
      // the PLATE, so a white plate always answered BLACK — and on the GGR mark black is not a brand
      // colour at all, it is just the default plate finish that happens to sit in the pool. Red and
      // white are the logo's colours, so white text on a white plate should be rescued with a RED
      // ring, not a black one.
      //   Same ordering `assignOutlineColors` already uses ("house neutrals LAST — a real brand
      // colour always outranks them"), which this path simply wasn't following. A candidate still has
      // to clear both jobs: readable against the PLATE, and distinct from the FILL it outlines.
      // bestContrastRef remains the fallback for a design with no brand colour that can do both.
      const prof = state.designProfile;
      const brand = [prof.palette.primary, prof.palette.secondary, prof.palette.tertiary,
        ...(Array.isArray(prof.swatches) ? prof.swatches : [])].map(normalizeHex).filter(Boolean);
      /* RULE 4 (Ryan 2026-09-20): *"If there is black on the logo... then it's fine to add to the
         weight number too. If there is no black on the logo due to none of those 3 things being
         true, then do NOT add any black to the weight number."* His three channels are the
         artwork's own colour, a recolour that produced black, and a black ring the tool added to
         the mark - all of them in `markWearsBlack`.
           This fallback is where the black came from: `bestContrastRef` optimises purely for
         contrast with the PLATE, so a red or a white plate always answers BLACK. McDonald's is red
         and gold, its white is not on the swatch row, so no brand colour cleared both jobs and the
         number got a black ring around a gold 45.
           /!\ null means the mark has NO census - unmeasured, not black-free - and then this
         changes nothing, because refusing black on a mark we cannot read is a guess. */
      const wearsBlack = markWearsBlack(state.designProfile, base);
      const blackBanned = wearsBlack === false;
      const works = (hx) => !!hx && hx !== fillHex && !colorsConflict(fillHex, hx) && !colorsConflict(base, hx)
        && !(blackBanned && isBlackInk(hx) && !isPlateMoat(hx, base));
      let ringHex = brand.find(works) || normalizeHex(resolvePaletteRef(bestContrastRef(rp, base), rp));
      if (blackBanned && isBlackInk(ringHex) && !isPlateMoat(ringHex, base)) {
        // the other house neutral, then anything on the row that reads - never black
        ringHex = ['#FFFFFF', '#000000'].filter((h) => !isBlackInk(h)).find(works)
          || brand.find(works) || null;
      }
      if (ringHex && ringHex !== fillHex) {
        eachTextWeight((outs) => { outs.length = 0; outs.push({ colorRef: ringHex, thickness: outlineThicknessFor(state.designProfile), locked: true }); });
        addSwatch(state, ringHex);
        return;
      }
    }
    // A MULTI-TONE MARK KEEPS ITS RING (Ryan 2026-07-28) — the fill-pick half of the same widening
    // `dropAutoOutlinesWhenFillReads` just took, so a plate pick and a fill pick can't disagree about
    // whether this design is outlined. Deliberately HERE and not at the top of the function: the
    // rescue above must still run, because a fill that vanishes on the plate needs its ring no matter
    // how many tones the logo has. Only the STRIP is suppressed. See that function for the reasoning
    // and for why 3 is the line.
    if ((state.designProfile.logoToneCount || 0) >= 3) return;
    eachTextWeight((outs) => { outs.length = 0; }); // fill reads fine on the plate → solid, no outline
  };
  // The plate colour is the MASTER SWITCH (Ryan 2026-07-23): picking one re-derives the text
  // fill/outlines AND the logo from the auto-styled defaults, so the new plate's contrast cascade
  // re-applies cleanly over the whole design. It is NOT a reset, though (Ryan 2026-07-24): every
  // colour the buyer explicitly chose survives it — a custom logo fill no longer reverts the moment
  // you dye the plate. See recascadeColorDefaults for the kept-vs-derived split. The full wipe
  // (restoreColorDefaults, via resetToOriginal) still backs the "Reset colors" button below.
  // Re-cascade, then re-colour the auto rings against the NEW plate (Ryan 2026-07-24) so a ring never
  // lands on the plate's own colour and get greyed by the peel — prevention rather than a grey swatch.
  // Logo mirror of syncFillContrastOutline (Ryan 2026-07-23): flattening the logo to a tone that
  // would VANISH on a dyed plate auto-adds a contrasting outer ring (green logo on green plate →
  // yellow ring); a visible flatten — or "Original" (multi-tone, which the colour-swap handles) —
  // clears the auto ring. Outline-less logos only; an undyed plate leaves it alone.
  //
  // PANEL-SCOPE, not inside the logo-fill row (Ryan 2026-07-25). It used to close over that row's
  // `logoEl`, so the only caller that could reach it was a logo-fill PICK — a plate pick could not.
  // That left the one gap the contextual-contrast system had: a MANUAL flatten suppresses the
  // fill-swap remap by design (syncLogoRaster gates `fillRemap` on `!center`), so a logo flattened
  // to yellow and then put on a yellow plate had neither the remap nor a ring and simply vanished.
  // recascadeColorDefaults drops the `auto: true` ring on every plate change but never re-derived one
  // for the NEW plate; this is what re-derives it. Exactly the same shape as the text-side fix.
  /* HOISTED to ui/state.js as `syncLogoContrastRing` (2026-08-18) so the config-switch path in
     main.js can run the same derivation — Ryan walks his set with the arrows, and that path ran a
     slimmer cascade, so the logo's rescue ring only ever appeared after a manual plate-chip pick.
     All the rules and their history moved with the body; this stays a name local callers know. */
  const syncLogoContrastOutline = () => syncLogoContrastRing(state);

  // Then DROP any re-derived ring the new plate doesn't need (Ryan 2026-07-24): an outline is a
  // contrast device, so a fill that already reads on the plate wants none — the plate-pick half of
  // the same solid-vs-outline symmetry syncFillContrastOutline applies on a fill pick. Runs BEFORE
  // assignOutlineColors so the colour pass sees the final stack instead of colouring a ring that is
  /* ── THE ADDED LOGO RING'S COLOUR, AND KEEPING IT TRUE (Ryan 2026-08-05) ──────────────────────
     *"I set comp bumper plate color to black, added an outline to the logo, it defaulted to gray…
     I then set the plate color to gray — should it not automatically change the logo outline color?"*
     Yes. The chooser below always knew how to answer; it was only ever RUN when the ring was created,
     so a ring stayed whatever colour the OLD plate had earned it. Ryan spotted the proof himself:
     delete the ring and re-add it on the grey plate and it comes back blue — same function, right
     answer, just never re-asked.

     ⚠ HOISTED TO PANEL SCOPE, and that is the actual fix. It lived inside `logoOutlineBlock()`, so
     the only caller that could reach it was the "+ Add" button — a PLATE PICK could not. That is
     word-for-word the bug `syncLogoContrastOutline` was hoisted out of the logo-fill row to fix
     (see its note above); the same trap, the same row, a fortnight apart. */
  // WD 487: hoisted to ui/state.js (also checks the LOGO's own edge, and runs on the weight-hop path).
  const nextAddedRingColor = (arr) => addedLogoRingColor(state, arr);
  const resyncAddedLogoRings = () => resyncAddedLogoRingsState(state);

  // about to go (and so it can't land on the unsatisfiable case that left a greyed swatch).
  const masterSwitchCascade = () => {
    recascadeColorDefaults(state.designProfile, { plateChange: true }); // WD 495: hand outline edits are per plate
    // The logo's own contrast ring, re-derived for the NEW plate. recascadeColorDefaults just
    // dropped the old `auto` ring; without this a manually-flattened logo got no ring and no
    // fill-swap (the swap is gated off a manual flatten) and vanished into a matching plate.
    syncLogoContrastOutline();
    // …and the rings the buyer ADDED to the logo, which `recascadeColorDefaults` deliberately keeps
    // (structure is theirs) but never recoloured — so a grey ring survived onto a grey plate.
    resyncAddedLogoRings();
    dropAutoOutlinesWhenFillReads(state.designProfile);
    assignOutlineColors(state.designProfile, effectivePlateHex(state));
    // ⚠ AND A PLATE COLOUR THE BUYER HAS ALREADY INKED GETS ITS OWN INK BACK (Ryan 2026-08-01).
    // Everything above is the re-derive, and that is the right answer for a colour seen for the
    // first time. It is the wrong answer the second time: dye yellow, ink it black, dye something
    // else, come back to the same yellow — the re-derive would hand back a colour they had already
    // rejected. This runs LAST so a remembered set wins the cascade rather than racing it.
    //   (Amends changelog 80's "a plate pick re-baselines everything below it" — see
    //   `recascadeColorDefaults`' header, which carries the full history of this rule.)
    restoreInk(state, inkContextKey(state));
    /* WD 486 (Ryan 11-01, option 1): the buyer's deletions + thicknesses on the app's own text rings win over the
       per-plate memory too. `restoreInk` re-creates a plate colour's remembered ring COUNT (the 08-05 floor), so a
       ring deleted on black came back on a yellow visited earlier; replaying the edits last makes the latest edit
       hold on every plate. Colours stay per plate (untouched here). */
    for (const el of textWeightEls()) applyOutlineEdits(el);
  };
  // Delete outline `i` from every text+weight piece — for user-ADDED outlines, whose −
  // removes them completely rather than graying them (Ryan 2026-07-22).
  const removeOutlineAll = (i) => {
    markStyleUserEdited(state.designProfile);
    // WD 486: an app ring the buyer deletes stays deleted across plate changes (noteOutlineEdit)
    eachTextWeight((outs, el) => { if (i >= 0 && i < outs.length) { noteOutlineEdit(el, outs[i], { removed: true }); outs.splice(i, 1); } });
    assignOutlineColors(state.designProfile, effectivePlateHex(state)); // the survivors have new neighbours now
  };
  // The NEXT outline to add — reproduces the logo's ORIGINAL outline SEQUENCE (color +
  // thickness), CYCLING, indexed by how many outlines the target already has (Ryan
  // 2026-07-20). So the logo's thin-green → thick-red gives: add #1 thin-green, #2
  // thick-red, #3 thin-green, #4 thick-red — and deleting all then re-adding reproduces
  // exactly that. Falls back to a palette-ref cycle + default thickness when the logo had
  // no detected structure (no autoStyle). `autoStyle.outlines` = [{colorRef, thickness}].
  function nextOutlineSpec(count) {
    const as = state.designProfile.autoStyle;
    const seq = as && Array.isArray(as.outlines) && as.outlines.length ? as.outlines : null;
    if (seq) { const o = seq[count % seq.length]; return { colorRef: o.colorRef, thickness: o.thickness }; }
    const prof = state.designProfile;
    const pal = prof.palette;
    if (normalizeHex(pal.secondary)) {
      /* ⛔ THE ROLES BRANCH OFFERED A RING BLIND TO THE FILL IT WRAPS (Ryan 2026-08-23, Amherst
         on the black plate: text set to WHITE, "+ Add" offered a WHITE outline — *"with 1 outline
         around the text it should default to purple"*). The one-tone branch below has always
         filtered against the effective fill and the plate; this branch cycled raw roles. Now:
         roles that merge with the EFFECTIVE fill are out entirely, plate-clearing ones lead, and
         when nothing fits the concrete-hex pool below takes over. The chain re-balance that runs
         after every Add still recolours a SECOND ring against its real inner neighbour, which is
         how purple-then-white falls out of two presses without this function knowing depth. */
      const fillNow = effFillHex();
      const plateNow = normalizeHex(plateBodyNow());
      const refs = pal.tertiary ? ['secondary', 'tertiary', 'primary'] : ['secondary', 'primary'];
      const fits = refs.filter((r) => { const h = normalizeHex(pal[r]); return h && !(fillNow && colorsConflict(h, fillNow)); });
      if (fits.length) {
        const ordered = [
          ...fits.filter((r) => !(plateNow && colorsConflict(normalizeHex(pal[r]), plateNow))),
          ...fits.filter((r) => plateNow && colorsConflict(normalizeHex(pal[r]), plateNow)),
        ];
        return { colorRef: ordered[count % ordered.length], thickness: outlineThicknessFor(prof) };
      }
      // no role clears the fill → fall through to the concrete-hex pool logic below
    }
    // NO SECOND ROLE (a one-tone logo) — Ryan 2026-07-25. This used to hand back `colorRef:
    // 'secondary'` regardless, a ref to a role that does not exist: resolvePaletteRef quietly
    // resolves it through its #FFFFFF fallback, so "+ Add" produced an outline colour nobody chose
    // and the swatch could disagree with the plate about what it was. Cycling to 'primary' instead
    // is no better — on a one-tone design primary IS the logo colour, which is how a green ring
    // landed on a green plate.
    //   Pick a CONCRETE hex that reads against the text fill, from the buyer's own pool (which
    // carries black + white for exactly this case). A literal hex can't be re-resolved by anything.
    //   Contrast against the EFFECTIVE fill — what the plate actually paints — not the stored ref.
    // On a green-text-on-green-plate design the renderer already swaps the fill to something legible,
    // so measuring against the stored green picks white, which is invisible against the white the
    // buyer is looking at (and then gets dropped as a ring that reads no differently from its fill).
    const fillHex = effFillHex() || '#000000';
    const pool = contrastPoolFrom(pal, prof.swatches);
    const plate = normalizeHex(plateBodyNow());
    // Avoid the PLATE too, not just the fill: a lone ring is both the innermost and outermost band,
    // so it has to clear the colour on each side of it.
    const usable = pool.filter((h) => !colorsConflict(h, fillHex) && !(plate && colorsConflict(h, plate)));
    if (usable.length) return { colorRef: usable[0], thickness: outlineThicknessFor(prof) };
    return { colorRef: bestContrastRef({ ...pal, pool }, fillHex), thickness: outlineThicknessFor(prof) };
  }
  // "+ Add" is RESTORE-then-append (Ryan 2026-07-24). Because ✕ now REMOVES a text outline outright
  // rather than graying it, Add is the only way back — so it first reinstates the lowest MISSING
  // autoStyle default (at its own depth, with its own colour), and only once the default stack is whole
  // does it append a brand-new user outline from the sequence. Restoring by `autoIdx` rather than by
  // position is what makes deleting inside-out safe: appending by count would hand back the wrong
  // colour and orphan the removed one.
  function addPlateOutline() {
    if (repOutlines().length >= 4) return;
    /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 487, 2026-11-01) — THE FIRST TEXT RING MATCHES THE LOGO'S. Ryan: *"if a
       text outline exists on a logo but not the text, adding one to the text should automatically match the logos
       outline thickness"*. Read BEFORE the add: only when the text is bare and the logo presents an outer edge
       ring. The colour is still the panel's own choice; this sets the width only. */
    const textWasBare = shownTextRings(state, textWeightEls()[0]).filter((o) => o && o.enabled !== false).length === 0;
    const logoEdgeW = textWasBare ? Number(renderPalette().logoOuterWeight) : NaN;
    markStyleUserEdited(state.designProfile);
    /* WD 488 (UX run 2, Ryan "hand-edit = text's own"): the press takes the rings the plate shows over first, then
       appends ONE new outermost ring. The old restore-a-missing-app-slot path is gone from here: once the text's rings
       are the buyer's there are no app slots, and on Drop Bear's cream plate that path minted a ring the re-balance
       deleted in the same tick (bug 1 - "nothing happens"). */
    adoptTextRings(state);
    /* WD 488 (UX run 2, Ryan: on black, logo + gave the light ring, then text + gave "a dark one, instead of the light
       one we just added to the logo"): the new text ring takes a ring the LOGO wears that the text does not yet - the
       logo's stack read inner -> outer from the ring count on - when it reads against the fill, the ring inside it and
       the plate. `userSet` so the re-balance keeps it. Nothing fits -> the old sequence pick, re-balanced as before. */
    const rpNow = renderPalette();
    const logoRings = (Array.isArray(rpNow.logoStack) ? rpNow.logoStack : []).map((x) => normalizeHex(x && x.hex)).filter(Boolean).reverse();
    const plateNow = normalizeHex(effectivePlateHex(state));
    const fillNow = effFillHex();
    eachTextWeight((outs) => {
      if (outs.length >= 4) return;
      const sp = nextOutlineSpec(outs.length);
      const worn = outs.map((o) => normalizeHex(resolvePaletteRef(o.colorRef, rpNow)));
      const inner = worn.length ? worn[worn.length - 1] : fillNow;
      // `inner` is the fill when the text is bare - the fill only touches the INNERMOST ring, so it is not asked again.
      const fits = (hx) => hx && !worn.some((w) => w && colorsConflict(w, hx)) && !(inner && colorsConflict(inner, hx))
        && !(plateNow && colorsConflict(plateNow, hx));
      // ...and when the logo wears nothing to copy, the colour the LOGO's own + would pick here (Ryan: text + first on a
      // bare black plate went dark; the logo + gives the light ring the design started with), so both buttons agree.
      // Seen first: a ring that READS against the one inside it (Ryan: black round maroon "becomes twice as thick").
      const sees = (hx) => fits(hx) && !(inner && !readsOn(inner, hx));
      const order = [...logoRings.slice(outs.length), ...logoRings.slice(0, outs.length)];
      const lp = normalizeHex(addedLogoRingColor(state, worn.filter(Boolean).map((c) => ({ color: c }))));
      const match = order.find(sees) || (sees(lp) ? lp : null) || order.find(fits) || (fits(lp) ? lp : null);
      outs.push(match ? { colorRef: match, thickness: sp.thickness, added: true, userSet: true } : { colorRef: sp.colorRef, thickness: sp.thickness, added: true });
      if (Number.isFinite(logoEdgeW) && logoEdgeW > 0 && outs.length === 1 && outs[0]) outs[0].thickness = Math.round(logoEdgeW * 10) / 10;
    });
    // Re-balance the whole chain, not just the new ring: with a green plate + green text and two
    // colours, the ONLY valid 3-ring arrangement is yellow/green/yellow, so the clashing colour has
    // to migrate to the middle. Choosing a colour for the new slot alone can't get there.
    assignOutlineColors(state.designProfile, effectivePlateHex(state));
    structuralCommit();
    ctx.announce?.('Outline added.');
  }

  // A sub-heading inside a section ("Plate color", "Text fill", "Text outline(s)", …). Deliberately
  // does NOT spread S.small any more (Ryan 2026-07-24): S.small carries opacity 0.65, which stacked
  // with the old 11px made these labels small AND washed out. Now the shared sub-head token at full
  // text colour; S.small stays for genuinely secondary captions.
  // EVERY sub-heading in this panel must come through here (Ryan 2026-07-24). Several were
  // hand-rolled copies of this exact inline style — "Plate color" among them — so bumping the
  // sub-head size left them behind at the old 11px/0.65 and the Colors rows disagreed with each
  // other. `margin` is the only thing a caller may vary.
  const subHead = (txt, margin) => h('div', { class: 'wd-subhead', style: margin ? { margin } : {} }, txt);

  // Flat Fill + Outlines model (Ryan 2026-07-20). The Colors section reads, top to bottom:
  //   Plate color (the dyeable product only) · Text fill · Text outlines · Saved colors.
  // The fill + outline chips show the ACTUAL colors on the plate; editing one writes that
  // hex to every text + number piece. Saved colors is the library you pull from.
  function paletteSection() {
    const hasText = textWeightEls().length > 0; // logo-only layouts have no text to color

    // ── Text fill — a ROW of your colors to switch between + "+" to add a new one ─
    // (Ryan 2026-07-20). Tap a swatch to set the fill; the active one wears a ring. "+"
    // opens the ADD picker (craft → "Add color" appends to the END of the shared pool,
    // which also feeds Plate color). Added colors carry a ✕ to remove; logo roles don't.
    const fillHex = effFillHex(); // highlight the swatch that's actually RENDERED (post-peel)
    const { list: pool, added } = orderedPool();
    const fillOptionChip = (hex) => {
      const active = normalizeHex(hex) === fillHex;
      const removable = added.has(hex);
      const btn = h('button', {
        type: 'button', class: 'wd-cchip' + (active ? ' is-active' : '') + (isNearBlackHex(hex) ? ' wd-cchip--dark' : ''), style: { background: hex },
        title: active ? `Text fill ${hex} — current (use ✎ Edit to change it)` : `Set text fill to ${hex}`, 'aria-label': `Set text fill to ${hex}${active ? ' (current)' : ''}`,
        // Choose only — editing is the row's ✎ button (Ryan 2026-07-24). Tapping the already-active
        // swatch is a deliberate no-op rather than a hidden second meaning.
        // `assignOutlineColors` re-balances the RINGS against the new fill (Ryan 2026-07-25). It owns
        // the "innermost ring ≠ fill" rule — a ring the same colour as the thing it outlines is
        // invisible — but nothing on the fill-pick path was calling it: `syncFillContrastOutline`
        // handles the DYED-plate case and returns immediately on black. So on a black plate, GGR's
        // red text with its white outline went white-on-white the moment you picked white. It skips
        // any ring the buyer set themselves, so only auto rings move.
        onClick: (e) => { e.stopPropagation(); if (active) return; setFillAll(hex); syncFillContrastOutline(); assignOutlineColors(state.designProfile, effectivePlateHex(state)); structuralCommit(); ctx.announce?.(`Text fill set to ${hex}.`); },
      });
      const body = removable
        ? h('div', { class: 'wd-outline-chip' }, btn, h('button', { type: 'button', class: 'wd-ol-x', 'aria-label': `Remove ${hex} from your colors`, title: 'Remove from your colors', onClick: (e) => { e.stopPropagation(); removePooledColor(hex); } }, '✕'))
        : btn;
      return h('div', { class: 'wd-cchip-wrap' }, body);
    };
    const fillChips = pool.map(fillOptionChip);
    // ✎ Edit — a ROW-LEVEL tool chip that edits the ACTIVE swatch (Ryan 2026-07-24). This is now the
    // primary edit path, replacing the undiscoverable "tap the active swatch a second time": tapping a
    // swatch means CHOOSE, ✎ means EDIT what you chose. Row-level rather than a corner ✎ on the chip
    // because added swatches already carry a corner ✕ — two badges on a 34px chip is unreadable. This
    // row is single-select by construction (active === effFillHex), so "the active swatch" is never
    // ambiguous and no switcher is needed. The old second-tap still works as a silent accelerator.
    const fillEditable = pool.some((hex) => normalizeHex(hex) === fillHex);
    const fillEditBtn = h('button', {
      type: 'button', class: 'wd-cchip wd-cchip--tool wd-flat-glass wd-cchip--edit', disabled: fillEditable ? null : true,
      title: fillEditable ? `Edit the text fill (${fillHex})` : 'Pick a fill color first',
      'aria-label': fillEditable ? `Edit the current text fill ${fillHex}` : 'Edit text fill — no fill color selected',
      onClick: (e) => {
        e.stopPropagation();
        if (!fillEditable) return;
        openSwatchEditPicker(fillEditBtn, fillHex, {
          /* ── THE TARGET-TAB STRIP IS BACK ON THIS ROW (Ryan 2026-08-15) ────────────────────────
             It was removed on 2026-08-05 — *"this brings up a pop-up which has a top row of swatches
             which are for switching between default colors and then editing them… this isn't needed.
             Get rid of that top row of swatches, and just keep the bottom row"* — and the "bottom
             row" he kept was the SWAP row, which was then itself removed in the very same pass (see
             the note directly below). So the compromise that justified dropping the tabs did not
             survive the day: what was left is a ✎ that can only edit the colour the text is already
             wearing.
               Reinstated with the one thing it never had — a caption — and, because the swap row is
             gone, as the only strip in the popover. Full reasoning at `editTargets`.
             ⚠ The outline rows' tabs are a DIFFERENT KIND: Inner/Middle/Outer are genuinely separate
             slots, so those stay named rather than swatch-styled. */
          targets: editTargets(fillHex),
          activeTargetKey: normalizeHex(fillHex),
          targetsLabel: EDIT_TARGETS_LABEL,
          targetStyle: 'swatch',
          /* ⛔ NO SWAP ROW ON THIS ROW (Ryan 2026-08-05: *"the whole 'use a color from your design'
             feature is pointless on text color selection since you're supposed to just click the
             visible swatch you want to use, since all the colors from your design are visible right
             there"*).
               Exactly right, and it is the rule for the whole feature: the row belongs ONLY where the
             pool is NOT already on screen. The Text fill row renders every pooled colour as a
             clickable chip, so a second copy inside the ✎ is a duplicate control one layer deeper —
             and worse, one whose verb ("use this") competes with the ✎'s own ("edit this").
               Where it IS needed: text outlines, logo outlines and the logo colour row, whose rows
             show the CURRENT colours (per ring / per band) rather than the pool, so reaching another
             one has no other route. Those three now carry it. */
        });
      },
    }, '✎');
    fillChips.push(toolWrap(fillEditBtn, 'Edit'));
    const fillAddBtn = h('button', {
      type: 'button', class: 'wd-cchip wd-cchip--tool wd-flat-glass wd-cchip--more',
      disabled: swatchSlotsLeft(state.designProfile) <= 0 ? true : null,
      title: swatchSlotsLeft(state.designProfile) <= 0 ? `That's the ${SWATCH_CAP}-colour limit — remove one to add another` : 'Add a new fill color',
      'aria-label': swatchSlotsLeft(state.designProfile) <= 0 ? 'Add a new fill color — colour limit reached' : 'Add a new fill color',
      onClick: (e) => { e.stopPropagation(); if (swatchSlotsLeft(state.designProfile) > 0) openFillAddPicker(fillAddBtn); },
    }, '+');
    fillChips.push(toolWrap(fillAddBtn, 'Add'));
    // "Text", not "Text fill" — the outline row nested under it belongs to the same group, and the
    // pairing is what tells the two identically-shaped blocks apart. Same for "Logo" below.
    //   The SIZE steppers that used to sit on this row are gone (Ryan 2026-07-25): Arch moved to the
    // Text section beside the wording it scales, Number to the Weight section beside LB/KG and
    // Number style, and Logo size to the Add Logo tab. Each control now lives in the section that
    // owns the thing it resizes, rather than all three being pooled here under one "Size & color"
    // heading — which is also why that heading is just "Color" again.
    const fillBlock = h('div', { style: { margin: '0 0 20px' } }, subHead('Text'),
      h('div', { class: 'wd-cchip-row wd-fill-row' }, fillChips));

    // ── Text outlines — one chip per outline, INNER → OUTER, named Inner/Middle/Outer.
    // ✕ REMOVES an outline outright — it does not gray it (Ryan 2026-07-24). "+ Add" is the way back:
    // it restores the lowest missing autoStyle default at its own depth before it appends anything new
    // (see addPlateOutline). That leaves the grayed/dashed treatment meaning exactly ONE thing here —
    // "the plate-conflict peel is hiding this ring" — instead of doubling as a manual off state.
    // NOTE the logo-band row deliberately still grays: a detected band lives in the logo's own artwork,
    // so it can only be hidden, never removed, and its Add button adds a NEW outer ring rather than
    // restoring a band — a vanished band swatch would be unrecoverable.
    /* WD 488 (UX run 2): the row lists the rings the PLATE shows - until the buyer takes them over (`ringsOwn`) those are
       the logo's mirror, which the stored stack does not hold. Same order as storage once adopted (inner→outer). */
    const repEl = textWeightEls()[0];
    const ringsOwned = !repEl || repEl.ringsOwn === true;
    const outlines = shownTextRings(state, repEl); // inner→outer
    const total = outlines.length;
    const maxed = total >= 4;
    // Which stored outlines are currently HIDDEN by the plate-conflict peel — a reversible render-
    // time view (effectiveTextStyle). The swatch must mirror it: a peeled outline reads as OFF
    // (grayed + green "+"), not "active", while it's invisible on the plate (Ryan 2026-07-23). Peels
    // the outermost ENABLED outlines that merge with the dyed plate, inward, stopping at the first
    // distinct ring — the exact rule effectiveTextStyle uses. Black/undyed → nothing peeled, so a
    // return to a black plate restores every ring automatically.
    const peeledSet = (() => {
      const set = new Set();
      if (!ringsOwned) return set; // WD 488: the shown mirror is already the post-peel view
      const base = normalizeHex(plateBodyNow());
      if (!base || isBlackBody(base)) return set;
      const pal = state.designProfile.palette;
      for (let i = outlines.length - 1; i >= 0; i--) {
        const o = outlines[i];
        if (!o || o.enabled === false) continue; // disabled: already off; the peel passes through it
        if (o.locked === true) break;            // locked: the buyer forced it on → stops the peel
        if (colorsConflict(base, resolvePaletteRef(o.colorRef, pal))) set.add(i);
        else break; // first distinct enabled ring stops the outside-in peel
      }
      // Mirror effectiveTextStyle's SOLID rule (Ryan 2026-07-23) — ONLY for a no-natural-outline
      // logo: the FILL recolors and any SURVIVING outline now that same colour is dropped as
      // redundant, so gray those swatches too. An outlined logo keeps its stack, so skip this.
      const rep = textWeightEls()[0];
      const fillRef = (rep && rep.fillRef) || 'primary';
      if (!logoHasOutlines() && rep && rep.fillLocked !== true && colorsConflict(base, resolvePaletteRef(fillRef, pal))) {
        const rp = renderPalette();
        const newHex = normalizeHex(resolvePaletteRef(bestContrastRef(rp, base), rp));
        for (let j = 0; j < outlines.length; j++) {
          const o = outlines[j];
          if (!o || o.enabled === false || set.has(j)) continue;
          if (normalizeHex(resolvePaletteRef(o.colorRef, pal)) === newHex) set.add(j);
        }
      }
      return set;
    })();
    // The corner −/+ circles are GONE (Ryan 2026-07-24). Every outline swatch now reads like the
    // rest of the tab — white ring = on, dimmed/dashed + no ring = off — and carries ONE corner
    // badge: the same ✕ the added fill colors use. What the ✕ does depends on where the outline
    // came from, which is the `added` flag already on every outline:
    //   • DEFAULT (auto-seeded from the logo's own colors, autoStyle.outlines) → ✕ turns it OFF:
    //     grayed, kept, tap the swatch to bring it back. These are NOT disposable — "+ Add" can't
    //     restore one, because nextOutlineSpec picks by POSITION in the sequence (seq[count % len]),
    //     so deleting the white ring on a red/white/blue logo and re-adding hands you the next
    //     cycle position, not white. Destroying a logo-derived outline is unrecoverable.
    //   • ADDED (you made it) → ✕ REMOVES it outright. Nothing derived is lost, and "+ Add" makes
    //     another the same way it made this one.
    // The ✕ shows only on an ACTIVE swatch — a grayed default has nothing to turn off, so it's
    // bare and the swatch body restores it. That keeps the badge's meaning legible: it always acts
    // on something that's currently on, and the grayed-but-present swatch teaches the difference
    // in one use.
    /* ⛔ REMOVING THE LAST RING CAN STRAND THE FILL (Ryan 2026-08-05: *"gray plate with gray text with
       blue outline, and I press the 'x' to delete the outline — shouldn't the text then default to
       another color instead of staying gray so we don't run into gray on gray?"*).
         Yes. The ring was the only thing holding those two apart, so deleting it changes what the FILL
       is sitting against — and nothing on this path was re-asking that question. `correctIllegibleFill`
       already knows the rule; it simply was not called here (it ran only on a product hop).
         `force: true` because the buyer's own tap created the conflict: a `fillUserSet` grey would
       otherwise be honoured into invisibility. Announced, and undo puts both back together. */
    const removeOutlineNow = (oi, prose) => {
      adoptTextRings(state); // WD 488: the press takes the shown rings over first (display index == stored index)
      removeOutlineAll(oi);
      const moved = correctIllegibleFill(state.designProfile, effectivePlateHex(state), { force: true });
      structuralCommit();
      ctx.announce?.(moved
        ? `${prose} removed — text recolored so it still reads on the plate.`
        : `${prose} removed.`);
    };
    // Display INNER → OUTER, left to right (Ryan 2026-07-24, flipped from outer→inner). Three
    // reasons: (1) "+ Add" appends a new OUTERMOST outline, so the new chip now appears next to
    // the button that made it instead of jumping to the far left; (2) it continues the outward
    // sweep from the "Text fill" row above (fill is the innermost thing on the plate); (3) it
    // matches storage order, so the display index IS the storage index.
    // Tier labels describe the rings that are actually ON THE PLATE, so they're computed over the
    // ENABLED stack — not all four slots (Ryan 2026-07-24). Turning the Inner of two off used to leave
    // the survivor still labelled "Outer" even though it was the only ring left; now it goes unlabelled
    // (a lone outline has no inner/outer to distinguish), and a 3-stack with its middle off reads
    // Inner · Outer. A DISABLED outline gets no tier at all — it isn't a ring, so it has no position.
    //   Deliberately keyed off `enabled`, NOT the effective `on`: the plate-conflict peel is a
    // render-time VIEW, so a dye must not silently renumber the buyer's stack. Only a manual on/off —
    // a real structural change — relabels.
    const liveOrder = outlines.map((o, i) => (o && o.enabled !== false ? i : -1)).filter((i) => i >= 0); // inner→outer
    const tierFor = (oi) => {
      const pos = liveOrder.indexOf(oi);
      if (pos < 0) return '';
      return outlineTierName(liveOrder.length - 1 - pos, liveOrder.length); // outlineTierName counts from the outside
    };
    const outChips = [];
    // Active outlines in display order (inner→outer) so the row's ✎ has a target — see the Edit
    // chip below for why it aims at the OUTERMOST one (Ryan 2026-07-24).
    const activeOutlines = [];
    for (let d = 0; d < total; d++) {
      const oi = d; // display order == storage order (both inner→outer)
      const o = outlines[oi];
      const isAdded = !!o.added;
      const manualOff = o.enabled === false;
      const peeled = peeledSet.has(oi);      // hidden by the plate-conflict view (still enabled)
      const on = !manualOff && !peeled;      // EFFECTIVE on: enabled AND actually rendered
      const hex = resolvePaletteRef(o.colorRef, state.designProfile.palette);
      // `tier` is '' for a lone outline or a disabled one — the badge goes blank and the prose forms
      // supply the noun. See tierFor above for why it's the ENABLED stack that decides.
      const tier = tierFor(oi);
      const prose = outlineProse(tier);
      const proseMid = outlineProseMid(tier);
      const sw = h('button', {
        type: 'button', class: 'wd-cchip' + (on ? ' is-active' : ' wd-cchip--off') + (isNearBlackHex(hex) ? ' wd-cchip--dark' : ''), style: { background: hex },
        title: on
          ? `${prose} ${hex} — tap to remove${isAdded ? '' : ' (＋ Add brings it back)'}`
          : `${prose} (${peeled ? 'hidden — matches the plate' : 'off'}) — tap to turn on`,
        'aria-label': on ? `${prose} ${hex}. Tap to remove.` : `${prose} (${peeled ? 'hidden, matches the plate' : 'off'}). Tap to turn on.`,
        // THE WHOLE SWATCH IS THE ✕ (Ryan 2026-07-25). It cost nothing to give it away: since the
        // 07-24 pass moved editing onto the row's ✎, an ON swatch was a deliberate no-op — 34px of
        // dead target sitting under a 16px corner badge. Now the two states are symmetric — on →
        // click anywhere removes, off → click anywhere restores — and the ✕ stays as the affordance
        // that says which one you're about to get.
        //   Scoped to OUTLINE chips on purpose. The added fill / plate swatches share `.wd-ol-x` but
        // their body click SELECTS that colour, so wiring the body to the badge there would delete a
        // colour every time someone tried to pick one.
        //   Undo covers a mis-tap: removeOutlineNow → structuralCommit files a history entry.
        // PEELED (or an old save's manually-off ring) → click anywhere on the grayed swatch to force it
        // on + LOCK it, so it overrides the plate-dye peel and sticks (Ryan 2026-07-23).
        onClick: (e) => {
          e.stopPropagation();
          if (on) { removeOutlineNow(oi, prose); return; }
          adoptTextRings(state); // WD 488
          setOutlineEnabledAll(oi, true); setOutlineLockedAll(oi, true); setOutlineUserSetAll(oi); structuralCommit(); ctx.announce?.(`${prose} on.`);
        },
      });
      // ✕ = remove, for defaults and added alike (Ryan 2026-07-24). Shown only while the ring is
      // actually on — a peel-hidden swatch isn't offering to delete something you can't see.
      const body = on
        ? h('div', { class: 'wd-outline-chip wd-outline-chip--xbody' }, sw, h('button', {
            type: 'button', class: 'wd-ol-x',
            'aria-label': `Remove the ${proseMid}`,
            title: isAdded ? 'Remove' : 'Remove (＋ Add brings it back)',
            onClick: (e) => { e.stopPropagation(); removeOutlineNow(oi, prose); },
          }, '✕'))
        : sw;
      if (on) activeOutlines.push({ oi, hex, prose, tier });
      outChips.push(h('div', { class: 'wd-cchip-wrap' }, body, h('span', { class: 'wd-cchip-badge' }, tier)));
    }
    // ✎ Edit — every colour row carries one, so the control set reads the same everywhere
    // (Ryan 2026-07-24). Unlike the single-select pool rows, SEVERAL outlines can be on at once, so
    // the button needs a target: it aims at the OUTERMOST active ring (the last chip in this
    // inner→outer row, and the most visually prominent one on the plate). The tooltip and aria-label
    // name which ring that is, so it's never a guess. Tapping any individual swatch still edits that
    // specific one directly — the ✎ is the row-level shortcut, not the only path. Greys out when
    // nothing is on.
    const outlineEditTarget = activeOutlines.length ? activeOutlines[activeOutlines.length - 1] : null;
    const outlineEditBtn = h('button', {
      type: 'button', class: 'wd-cchip wd-cchip--tool wd-flat-glass wd-cchip--edit', disabled: outlineEditTarget ? null : true,
      title: outlineEditTarget ? `Edit the ${outlineEditTarget.prose.toLowerCase()} (${outlineEditTarget.hex})` : 'Turn on an outline first',
      'aria-label': outlineEditTarget ? `Edit the ${outlineEditTarget.prose.toLowerCase()} ${outlineEditTarget.hex}` : 'Edit outline — no outline is on',
      onClick: (e) => {
        e.stopPropagation();
        if (!outlineEditTarget) return;
        adoptTextRings(state); // WD 488: ✎ is a hand edit - the shown rings become the buyer's (same indices)
        // `target` is MUTABLE and the apply closure reads it at confirm time, so switching tabs
        // re-points where "Update color" lands (Ryan 2026-07-24). Tabs appear only when 2+ outlines
        // are on; with one, this is exactly the single-target open it was before.
        let target = outlineEditTarget;
        // THICKNESS rides in the picker, under the tabs (Ryan 2026-07-25): it has to act on the ring
        // the lit tab names, and the row itself has no notion of a selected ring. Unlike text/logo
        // SIZE — which lives out in its row because it must stay reachable — a thickness control is
        // meaningless with no outline on, which is exactly when this ✎ is disabled. So nothing is
        // stranded behind a greyed button. Rebuilt on every tab change so the readout tracks it.
        const thickHost = h('div');
        const fillThickness = () => {
          thickHost.textContent = '';
          // The host labels its own `extra` control — the picker deliberately does not know what this
          // is. Unlabelled, it read as the popover's headline feature and hid the colour row below it.
          thickHost.append(h('div', { class: 'wd-cp__rowlabel' }, 'Outline thickness'), sizeRow(createStepper({
            /* ⚠ CLAMPED TO THE SAME CEILING AS `max`, not to the flat constant. `outlineMaxFor`
               never reports below a ring's CURRENT thickness, so a design saved before the cap came
               down to 5 keeps its real value and stays adjustable; clamping the value to the
               constant while the max said otherwise would have shown 5 on a ring that is really 12,
               and written that 5 back on the next nudge. Latent while the cap was 20 — every stored
               value was under it — and load-bearing the moment it moved. */
            value: clamp(Number(repOutlines()[target.oi]?.thickness) || 0, OUTLINE_MIN, outlineMaxFor(repOutlines(), target.oi)),
            // Geometric ceiling, not the flat constant — see `outlineMaxFor`.
            min: OUTLINE_MIN, max: outlineMaxFor(repOutlines(), target.oi), step: OUTLINE_STEP,
            ariaLabel: `${target.prose} thickness`,
            onInput: (v) => {
              markStyleUserEdited(state.designProfile);
              // WD 486: and a thickness dialled on an app ring survives the next plate pick
              eachTextWeight((outs, el) => { if (outs[target.oi]) { outs[target.oi].thickness = v; noteOutlineEdit(el, outs[target.oi], { thickness: v }); } });
              // The confirm button is gated on the COLOUR moving; a thickness edit is a change too.
              colorPicker.syncExtra?.();
              valueCommit();
            },
          })));
        };
        fillThickness();
        /* ── A RING DIALLED TO ZERO IS A RING THE BUYER DELETED (Ryan 2026-08-08, widened 08-09) ──
           *"if you set the outline thickness to 0 then save it, the outline swatch should be removed
           back on the main menu, as if someone had clicked the 'x' on it."* — then, a day later:
           *"…or swipe down or close the color menu (on mobile or desktop)."*
             `OUTLINE_MIN` is 0, so the dial can already reach a ring that paints nothing, and leaving
           its swatch on the row afterwards is the panel claiming an outline that is not there.
           `removeOutlineNow` is the ✕'s own handler, so this really is the same operation and not a
           lookalike: the fill re-checks its legibility, one history entry is filed, and the
           announcement says what happened.
             ONE closure, handed to both exits — see `onDismissExtra` in `openSwatchEditPicker` for
           why a dismissal has to be one of them. `target` is mutable, so this reads the ring the lit
           tab names at the moment the popover actually closes. */
        const retireIfZero = () => {
          const t = Number((repOutlines()[target.oi] || {}).thickness);
          if (t > 0) return;
          removeOutlineNow(target.oi, target.prose || 'Outline');
        };
        openSwatchEditPicker(
          outlineEditBtn, target.hex,
          {
            targets: activeOutlines.map((o) => ({ key: String(o.oi), label: outlineTierTab(o.tier) || 'Outline', hex: o.hex })),
            activeTargetKey: String(target.oi),
            onTargetChange: (key) => { target = activeOutlines.find((o) => String(o.oi) === key) || target; fillThickness(); },
            extra: thickHost,
            confirmLabel: 'Update outline',
            /* What the gate compares the dial against. Read at CALL time so it follows `target`,
               which the tab strip re-points — the same mutable-`target` contract the swap row and
               the confirm handler already use. */
            extraValue: () => (repOutlines()[target.oi] || {}).thickness ?? null,
            onConfirmExtra: retireIfZero,
            onDismissExtra: retireIfZero,
            /* Re-point ONLY the ring the lit tab names (`target` is mutable, read at click time —
               same contract the confirm handler uses). `userSet` because a swap is a deliberate
               pick: it is what stops `assignOutlineColors` re-deriving the choice away on the next
               hop. Nothing here touches the palette, the swatch pool or the artwork. */
            // The ring the lit tab names decides what reads — `target` is mutable, so this is read at
            // open time for the row and again on a tab change (the picker rebuilds its row per open).
            swapFits: (hex) => outlineHexReads(state.designProfile, textWeightEls()[0], target.oi, hex, effectivePlateHex(state)),
            // Same scoped routing as the logo-outline editor — dialling a ring colour must never
            // recolour by VALUE (a ring can share its hex with the plate or another surface).
            scopedConfirm: true,
            onSwapPick: (hex, o) => {
              eachTextWeight((outs) => {
                if (!outs[target.oi]) return;
                outs[target.oi].colorRef = hex;
                // `userSet` only on the COMMIT — a previewed colour the buyer walks away from must
                // not be left marked as their deliberate choice.
                if (!o || !o.preview) outs[target.oi].userSet = true;
              });
              if (o && o.preview) return;      // the picker renders; no rebuild, no history
              structuralCommit();
              ctx.announce?.(`Outline set to ${hex}.`);
            },
          },
        );
      },
    }, '✎');
    outChips.push(toolWrap(outlineEditBtn, 'Edit'));
    const addOutlineBtn = h('button', {
      type: 'button', class: 'wd-cchip wd-cchip--tool wd-flat-glass wd-cchip--more', disabled: maxed ? true : null,
      title: maxed ? '4 outlines max' : 'Add an outline', 'aria-label': 'Add an outline',
      onClick: (e) => { e.stopPropagation(); if (!maxed) addPlateOutline(); },
    }, '+');
    // (An empty row carried a "None — the text is solid." line; removed at Ryan's request
    // 2026-07-25. The heading over a lone "+ Add" is self-explanatory.)
    const outlineBlock = h('div', { style: { margin: '0 0 20px' } }, subHead('Text outlines'),
      h('div', { class: 'wd-cchip-row wd-outline-row' }, ...outChips, toolWrap(addOutlineBtn, 'Add')));

    // ── Logo outlines (Ryan 2026-07-21) — manage the logo's OWN outlines:
    //   • REMOVE/restore any band (B) INDEPENDENTLY, like the text: a disabled INNER band
    //     is recolored to its nearest enabled outer neighbour (so the outer replaces it),
    //     a disabled outermost band peels (logoBandsOff = the disabled indices). The
    //     auto-conflict peel on a dyed plate is separate (render-time) and NOT reflected
    //     in these chips — same as the text model (its editor shows the full stack).
    //   • ADD an outer ring (C) — a fresh silhouette outline (default white) so a logo
    //     whose edge is lost on a matching plate reads full-size again.
    // Shown whenever a logo is in the layout; the band chips only when it was confidently
    // structured, the "+ Add" always (it's a pure silhouette op).
    function logoOutlineBlock() {
      const ls = state.designProfile.logoStructure;
      const logoEl = state.designProfile.elements.find((e) => e.type === 'logo' && e.logo && e.logo.dataUrl);
      if (!logoEl) return null;
      /* ⛔ A BAND THE PEEL CANNOT REACH GETS NO CHIP (Ryan 2026-08-15, the Elite Edge lockup: the
         white outline chip whose ✕ measurably did nothing). `bandPeelable` is computed at analysis
         time by the very flood these controls act through — see `applyLogoStructure`. Filtering
         here, at the only place that renders them, rather than in the stored `bands`: the colour is
         still a real colour of the mark and every other consumer (the palette, the text auto-style,
         the logo-colour cascade) is right to keep seeing it. What was wrong was offering a control.
         ⚠ INDEX-PRESERVING. `toggleBand`, `logoBandsOff`, `logoBandColors` and `bandTouchesFill` are
         all keyed by a band's position in `ls.bands`, so the chips carry their ORIGINAL index and
         the filter must never renumber them — the same rule `seedOutlinesFrom` states for roles. */
      const peelable = ls && Array.isArray(ls.bandPeelable) ? ls.bandPeelable : null;
      const bandUsable = (i) => !peelable || peelable[i] !== false;
      const hasBands = ls && ls.confidence === 'high' && Array.isArray(ls.bands)
        && ls.bands.some((_, i) => bandUsable(i));
      const bands = hasBands ? ls.bands : []; // outer→in
      // Each band toggles independently (like the text outlines): a disabled INNER band
      // gets replaced by its outer neighbour's color at render time; a disabled outermost
      // band peels. Stored as a set of disabled indices (Ryan 2026-07-21).
      const offSet = new Set((Array.isArray(logoEl.logoBandsOff) ? logoEl.logoBandsOff : []).map((i) => i | 0));
      // `prose` is the already-built noun phrase ("Logo outer outline" / "Logo outline") — a lone band
      // has no tier name, same as a lone text outline (Ryan 2026-07-24).
      const toggleBand = (i, wasOff, prose) => {
        markStyleUserEdited(state.designProfile);
        adoptTextRings(state); // WD 488: a logo-outline press never changes the text's rings (Ryan run 2)
        const s = new Set(offSet);
        if (s.has(i)) s.delete(i); else s.add(i);
        logoEl.logoBandsOff = [...s].sort((a, b) => a - b);
        structuralCommit();
        ctx.announce?.(wasOff ? `${prose} on.` : `${prose} off.`);
      };
      // Per-band color overrides (Ryan 2026-07-22): the swatch shows the buyer's chosen color
      // if they recolored this band, else the detected one; clicking an ON band opens a local
      // edit picker → a raster remap repaints just that band.
      const bandColorsMap = (logoEl.logoBandColors && typeof logoEl.logoBandColors === 'object') ? logoEl.logoBandColors : null;
      // Recoloring a band is LOCAL (Ryan 2026-07-23): only THIS band gets a per-band raster override
      // — sibling bands / other swatches of the same colour are untouched.
      // Factored out of editBand so the row's ✎ tab strip can dispatch to a band OR a ring through
      // one confirm handler (Ryan 2026-07-24).
      const applyBandColor = (i, to) => {
        markStyleUserEdited(state.designProfile);
        adoptTextRings(state); // WD 488: a logo-outline press never changes the text's rings (Ryan run 2)
        addSwatch(state, to);
        if (!logoEl.logoBandColors || typeof logoEl.logoBandColors !== 'object') logoEl.logoBandColors = {};
        logoEl.logoBandColors[i] = to;
        // Mark it as the BUYER's choice, so the logo-fill cascade below never overwrites it. Same
        // split the text side uses (locked vs userSet): derived colours re-cascade, chosen ones don't.
        const us = new Set(Array.isArray(logoEl.logoBandUserSet) ? logoEl.logoBandUserSet : []);
        us.add(i | 0);
        logoEl.logoBandUserSet = [...us].sort((a, b) => a - b);
      };
      // Active bands, outer→in (bands[0] is the outermost), so the row's ✎ can aim at the outermost
      // one when there's no added ring outside it (Ryan 2026-07-24).
      const activeBands = [];
      // Tiers over the bands still ON, same rule as the text outlines (see tierFor there): turning one
      // off relabels the survivors, and a lone remaining band goes unlabelled. `bands` is outer→in, so
      // a band's position among the live ones IS its outside-in index — no inversion needed here.
      // ⚠ Unreachable bands are out of the TIER count too, or a two-band mark with one dead band
      // would name its only chip "Outer" and leave the buyer looking for the inner one.
      const liveBands = bands.map((_, i) => i).filter((i) => !offSet.has(i) && bandUsable(i));
      const bandTierFor = (i) => {
        const pos = liveBands.indexOf(i);
        return pos < 0 ? '' : outlineTierName(pos, liveBands.length);
      };
      // `.map` then drop, NOT `.filter` first — see `bandUsable`: `i` is the band's identity.
      const bandChips = bands.map((origHex, i) => {
        if (!bandUsable(i)) return null;
        const off = offSet.has(i);
        const tier = bandTierFor(i);
        const prose = tier ? `Logo ${tier.toLowerCase()} outline` : 'Logo outline';
        const hex = (bandColorsMap && normalizeHex(bandColorsMap[i])) || origHex; // show the override if any
        const sw = h('button', {
          type: 'button', class: 'wd-cchip' + (off ? ' wd-cchip--off' : ' is-active') + (isNearBlackHex(hex) ? ' wd-cchip--dark' : ''), style: { background: hex },
          title: off ? `${prose} — tap to restore` : `${prose} ${hex} — tap to turn off (it's kept)`,
          'aria-label': `${prose} ${hex}${off ? ' (off). Tap to restore.' : '. Tap to turn off.'}`,
          // The whole swatch is the ✕ (Ryan 2026-07-25) — see the text-outline chip above for why
          // that space was free. Here it's a pure toggle in both directions, and `toggleBand` already
          // flips whichever way the band currently sits, so one unconditional call covers both.
          onClick: (e) => { e.stopPropagation(); toggleBand(i, off, prose); },
        });
        // A detected band is logo-derived like a default text outline → ✕ turns it off (kept,
        // grayed); tap the grayed swatch to restore. No ✕ while it's already off.
        const body = off ? sw : h('div', { class: 'wd-outline-chip wd-outline-chip--xbody' }, sw, h('button', {
          type: 'button', class: 'wd-ol-x',
          'aria-label': `Turn off the ${prose.toLowerCase()}`, title: 'Turn off (kept — tap the swatch to bring it back)',
          onClick: (e) => { e.stopPropagation(); toggleBand(i, false, prose); },
        }, '✕'));
        if (!off) activeBands.push({ i, hex, prose, tier, sw });
        return h('div', { class: 'wd-cchip-wrap' }, body, h('span', { class: 'wd-cchip-badge' }, tier));
      // Rendered INNER → OUTER to match the text-outline row (Ryan 2026-07-24). `bands` is stored
      // outer→in, so map first (keeping `i` the true outer-relative band index that offSet,
      // logoBandColors and toggleBand all key on) and reverse only the resulting chips. This also
      // makes the whole row monotonic: the added rings that follow sit OUTSIDE every band, so
      // before the flip the row ran outward, then inward, then jumped back outside.
      }).reverse();

      // Default the added ring to a LOADED colour, NOT a hard-coded white that isn't in this
      // palette (Ryan 2026-07-23). The ring's job is to make the logo edge POP against the PLATE,
      // so it contrasts against the plate colour (a green plate → yellow) — unambiguous, vs. a
      // 2-tone logo's arbitrary "dominant". bestContrastRef only escapes to white/black when no
      // loaded swatch is distinct enough from the plate.
      // (hoisted to panel scope alongside `nextAddedRingColor`.)
      // ── WHAT COLOUR SHOULD THE NEXT ADDED RING BE? (Ryan 2026-08-03) ──────────────────────────
      // Three reports, one cause: the ring nearest the plate was chosen without ever looking at the
      // plate, and no ring ever looked at what the rest of the design was wearing.
      //   *"On 2nd 'Add' click it goes with white… odd choice, as this adds a white outline on the
      // outside when it's a white plate."* — exactly right. The 2nd ring's old rule was
      // `bestContrastRef(palette, last)`: maximise contrast against the PREVIOUS RING and nothing
      // else. Against a dark-blue ring 1 the best answer is white, and because the outermost ring is
      // the one that touches the plate, white is the one colour that cannot work there.
      //   ⚠ THE TEXT PATH ALREADY KNEW THIS. `nextOutlineSpec` states it: "Avoid the PLATE too, not
      // just the fill: a lone ring is both the innermost and outermost band, so it has to clear the
      // colour on each side of it." The logo path is a second implementation that never inherited it,
      // which is the real lesson — the chain rule belongs to the STACK, not to one element type.
      //   So an added ring now clears BOTH neighbours (the ring inside it, and the plate outside it),
      // and among the colours that qualify it PREFERS one already worn as an outline elsewhere, so a
      // logo and its lettering read as one object instead of two unrelated decisions.
      // (hoisted to panel scope — see `nextAddedRingColor` up by `masterSwitchCascade`.)
      // ── HOW THICK? ────────────────────────────────────────────────────────────────────────────
      // Ryan 2026-08-03: *"when adding the outline to the logo, it's MUCH thicker than the outline
      // added to the text."* It was hard-coded at `0.06` — 6% of the logo — while the TEXT ring
      // derives from `logoStrokeFrac`, the artwork's own measured stroke width, and is capped so it
      // only ever lightens.
      //   ⚠ THE DENOMINATOR MOVED UNDER THIS (WD 438). `addOutlineDataUrl` used to paint
      // `round(min(w,h) * frac)` px and now paints `round(max(w,h) * frac)`, because every other
      // measurement in the app — `logoStrokeFrac` included, which is what this function clamps — is
      // a fraction of the LONG edge. On a square mark nothing moves. On a wordmark the ring used to
      // come out at the mark's aspect ratio TOO THIN: bobcats' 0.023 measured stroke painted 0.23
      // viewBox units, under the 1.0 floor a ring needs to read at all. So the bounds below are
      // unchanged and now mean what they always claimed to.
      //   The smoking gun is that the app's own reference for a logo's characteristic linework is
      // `LOGO_STROKE_FRAC_REF = 0.026`. The added ring was pinned at **more than double** it, and
      // ignored the per-logo measurement entirely — the very input that exists to stop finely-ruled
      // marks being over-weighted. Anchoring to the artwork's own stroke is what makes the ring read
      // as part of the logo instead of a band wrapped around it.
      //   Clamped: never heavier than the old constant (so nothing a buyer already likes gets bolder)
      //   and never so thin it stops reading at plate scale. Unmeasured logos fall back to the
      //   reference rather than the old 0.06.
      const LOGO_RING_FRAC_MIN = 0.02;
      const LOGO_RING_FRAC_MAX = 0.06;
      const addedRingThickness = () => {
        const frac = state.designProfile.logoStrokeFrac;
        const base = Number.isFinite(frac) && frac > 0 ? frac : LOGO_STROKE_FRAC_REF;
        return Math.max(LOGO_RING_FRAC_MIN, Math.min(LOGO_RING_FRAC_MAX, base));
      };
      // Added outer rings (C) — up to a TOTAL of OUTLINES_MAX with the bands (WD 487; was two), inner→outer (Ryan 2026-07-23). Normalize a legacy
      // single-object save to an array so the rest is uniform.
      if (logoEl.logoAddOutline && !Array.isArray(logoEl.logoAddOutline)) logoEl.logoAddOutline = [logoEl.logoAddOutline];
      const addList = () => (Array.isArray(logoEl.logoAddOutline) ? logoEl.logoAddOutline : []).filter((a) => a && normalizeHex(a.color) && a.thickness > 0);
      // Recolor ring `idx` via the shared picker (commit-only — a ring regen is heavy).
      // An added ring has no "off" state — it's removed outright, from the picker footer where
      // its corner − used to be (Ryan 2026-07-24).
      /* ⚠ BY IDENTITY, NOT BY INDEX, and that is what makes it safe to call from the picker. `addList`
         filters `thickness > 0`, so the moment the dial reaches 0 the ring drops OUT of that list and
         every index after it shifts — an index captured when the popover opened would then name the
         buyer's other ring. The object reference cannot go stale that way. (`derived-list-in-a-const-
         goes-stale`, one turn further on: the list is already a function here, and the INDEX into it
         was the thing with the shelf life.) */
      const dropRing = (ring) => {
        if (!ring) return;
        markStyleUserEdited(state.designProfile);
        adoptTextRings(state); // WD 488: a logo-outline press never changes the text's rings (Ryan run 2)
        /* ⛔ AND A DERIVED RING STAYS GONE (Ryan 2026-09-21). Removing an `auto` ring used to leave
           no trace, so `syncLogoContrastRing` re-derived it on the very next plate pick — see the
           long note at that function for why the two-state model could not express this. The latch
           is design-wide and survives until a new logo or "Reset colors".
           ⚠ Only a DERIVED ring arms it. Deleting a ring the buyer added themselves needs no latch:
           nothing re-creates a manual ring, and arming it there would silently refuse the rescue on
           a later plate that genuinely needs one. */
        if (ring.auto === true) state.designProfile.logoRingSuppressed = true;
        const all = Array.isArray(logoEl.logoAddOutline) ? logoEl.logoAddOutline : [];
        logoEl.logoAddOutline = all.filter((a) => a !== ring);
        structuralCommit(); ctx.announce?.('Added outline removed.');
      };
      const removeAddedRing = (idx) => dropRing(addList()[idx]);
      // (editBand / openAddOutlinePicker were removed 2026-07-24: an active swatch is a no-op now, so the
      // only way into the picker is the row's ✎, which applies via applyBandColor / applyRingColor.)
      const rings = addList();
      const addChips = rings.slice(0, OUTLINES_MAX).map((a, idx) => {
        const colorHex = normalizeHex(a.color);
        // The whole swatch removes the ring (Ryan 2026-07-25) — recolouring is still the row's ✎.
        // This chip was the purest case of the dead target the change fixes: its click handler did
        // nothing but stopPropagation.
        const sw = h('button', { type: 'button', class: 'wd-cchip is-active', style: { background: colorHex }, title: `Added outline ${colorHex} — tap to remove`, 'aria-label': `Added logo outline ${colorHex}. Tap to remove.`, onClick: (e) => { e.stopPropagation(); removeAddedRing(idx); } });
        const x = h('button', { type: 'button', class: 'wd-ol-x', 'aria-label': 'Remove added outline', title: 'Remove', onClick: (e) => { e.stopPropagation(); removeAddedRing(idx); } }, '✕');
        return h('div', { class: 'wd-cchip-wrap' }, h('div', { class: 'wd-outline-chip wd-outline-chip--xbody' }, sw, x), h('span', { class: 'wd-cchip-badge' }, 'Added'));
      });
      const tail = [];
      /* WD 487: a total of OUTLINES_MAX (4) like the text - the logo's own outlines still showing count toward it. */
      if (activeBands.length + rings.length < OUTLINES_MAX) {
        // A NEW ring stands out from the plate; a 2nd stands out from the 1st so they're distinct.
        const addBtn = h('button', { type: 'button', class: 'wd-cchip wd-cchip--tool wd-flat-glass wd-cchip--more', title: 'Add an outer ring to the logo so its edge stands out', 'aria-label': 'Add an outer outline to the logo', onClick: (e) => {
          e.stopPropagation();
          markStyleUserEdited(state.designProfile);
          adoptTextRings(state); // WD 488: a logo-outline press never changes the text's rings (Ryan run 2)
          const arr = addList().slice();
          const color = nextAddedRingColor(arr);
          /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 487, 2026-11-01) — THE LOGO'S FIRST ADDED RING MATCHES THE TEXT'S
             OUTER RING. Ryan, DB Logo: the added ring came in at 1.4 beside a 2.5 text outline - *"if the text has one
             but the logo doesn't and we click add, then it should match"*. The app's own auto ring already matches
             (2.5 = 0.048 x LOGO_BOX_NOMINAL_VB); only this button used the stroke-derived width. A bare text, or a
             second logo ring, keeps the old width. */
          const textOuter = arr.length ? null : shownTextRings(state, textWeightEls()[0]).filter((o) => o && o.enabled !== false && o.thickness > 0).slice(-1)[0];
          // ...and every later ring matches the one before it (WD 487: rings 2-4 came in at the old thin width).
          const prev = arr.length ? arr[arr.length - 1] : null;
          const thickness = prev && prev.thickness > 0 ? prev.thickness
            : (textOuter ? textOuter.thickness / LOGO_BOX_NOMINAL_VB : addedRingThickness());
          arr.push({ color, thickness }); logoEl.logoAddOutline = arr; addSwatch(state, color); structuralCommit(); ctx.announce?.('Outer logo outline added.');
        } }, '+');
        tail.push(toolWrap(addBtn, 'Add'));
      }

      // Recoloring an added ring, as a plain apply (the ✎ commits on "Update color" rather than the
      // live-preview path the ring swatch itself uses — a silhouette regen is expensive, so
      // commit-only is the better mode here anyway).
      const applyRingColor = (idx, to) => {
        const a = addList()[idx]; if (!a) return;
        markStyleUserEdited(state.designProfile);
        adoptTextRings(state); // WD 488: a logo-outline press never changes the text's rings (Ryan run 2)
        a.color = normalizeHex(to) || a.color;
        // `userSet` so `resyncAddedLogoRings` leaves it alone on the next plate change — the same
        // split the text rings and the logo bands already use: derived colours re-derive, chosen
        // ones don't. Without it, picking a ring colour would survive exactly until the next dye.
        a.userSet = true;
        addSwatch(state, to);
      };
      // ✎ Edit — the logo row's twin of the Text-outline ✎ (Ryan 2026-07-24). Targets are listed in
      // DISPLAY order (bands inner→outer, then the added rings that sit outside them all), so the tab
      // strip reads left-to-right exactly like the row above it. `activeBands` is collected outer→in,
      // hence the reverse. It opens on the OUTERMOST target — the most visually prominent ring — and
      // the tabs let you reach any other one that's currently on.
      const logoTargets = [
        ...activeBands.slice().reverse().map((b) => ({ key: `band:${b.i}`, label: outlineTierTab(b.tier) || 'Outline', hex: b.hex })),
        ...rings.slice(0, OUTLINES_MAX).map((a, idx) => ({ key: `ring:${idx}`, label: rings.length > 1 ? `Added ${idx + 1}` : 'Added', hex: normalizeHex(a.color) })),
      ];
      const logoEditInit = logoTargets.length ? logoTargets[logoTargets.length - 1] : null;
      const logoEditBtn = h('button', {
        type: 'button', class: 'wd-cchip wd-cchip--tool wd-flat-glass wd-cchip--edit', disabled: logoEditInit ? null : true,
        title: logoEditInit ? `Edit the ${logoEditInit.label.toLowerCase()} logo outline (${logoEditInit.hex})` : 'Turn on a logo outline first',
        'aria-label': logoEditInit ? `Edit the ${logoEditInit.label.toLowerCase()} logo outline ${logoEditInit.hex}` : 'Edit logo outline — none is on',
        onClick: (e) => {
          e.stopPropagation();
          if (!logoEditInit) return;
          let key = logoEditInit.key; // mutable — a tab switch re-points the confirm handler
          // Thickness under the tabs, same as the text outline row — but ONLY for an ADDED ring.
          // A detected BAND is part of the uploaded artwork: it can be recoloured or hidden, never
          // re-measured, so offering a thickness dial on a band tab would be a control that lies.
          const thickHost = h('div');
          /* The ring the lit tab names, held as an OBJECT for as long as the popover is open. Every
             other reader here re-derives it from `addList()[n]`, which stops being the same ring the
             instant the dial reaches 0 and the filter drops it — see `dropRing`. Null on a band tab,
             which is exactly the "nothing to move" answer `extraValue` wants. */
          let activeRing = null;
          const fillThickness = () => {
            thickHost.textContent = '';
            activeRing = null;
            const [kind, n] = String(key).split(':');
            if (kind !== 'ring') return;
            const ring = addList()[Number(n)];
            if (!ring) return;
            activeRing = ring;
            /* ── THE SAME DIAL AS THE TEXT OUTLINE'S (Ryan 2026-08-05) ────────────────────────
               *"The outline thickness toggle on logo outlines looks different, has a different upper
               limit, and has no header when compared to text outline thickness toggle."*

               All three came from one thing: a logo ring is STORED as a fraction of the logo's own
               size, a text ring in viewBox units. So this dial stepped 0.01 at a time and read "6%"
               while its twin stepped 0.5 and read "2.5" — two controls doing the same job in two
               vocabularies, side by side in the same popover.
                 The two scales already have an exchange rate: `outlineThicknessFor` converts the
               artwork's stroke fraction into text-ring units via `LOGO_STROKE_FRAC_REF` ↔
               `DEFAULT_OUTLINE_THICKNESS`. That is the same conversion, so the dial now SHOWS units
               and stores the fraction — meaning the same number on either dial is the same apparent
               weight, which is what the 2026-08-03 fix ("MUCH thicker than the outline added to the
               text") was chasing in the first place.
                 ⚠ The ceiling was the real outlier: this allowed 0.2 — **more than three times** the
               0.06 that same fix capped a SEEDED ring at, so the dial offered a weight the app itself
               considered wrong. It moved onto the text ring's units, and shared its ceiling until
               2026-08-07, when the text cap dropped to 5 and this dial kept 20 as `LOGO_RING_MAX` —
               a shared 5 would sit BELOW this ring's own seeded 0.06 (= 6 units). */
            /* ⛔ THE EXCHANGE RATE WAS THE WRONG NUMBER, SO THE PARITY THIS DIAL PROMISES WAS A LIE
               BY ~1.85x (Ryan 2026-08-15). `DEFAULT_OUTLINE_THICKNESS / LOGO_STROKE_FRAC_REF` reads
               as a conversion and is not one: it divides a ring weight by a CORPUS MEDIAN of how
               finely logos are drawn, and calls the result units-per-fraction. A ring stored as a
               fraction of the logo's own size paints that fraction of the LOGO BOX, so the box is
               the rate — measured at 52 units, not the 96 that constant implied. Same fix, same
               cause, as the text ring's seed one file over; both now read one measured number. */
            const U = LOGO_BOX_NOMINAL_VB; // fraction of the logo → text-ring units
            const toUnits = (frac) => Math.round((Number(frac) || 0) * U * 10) / 10; // the dial's 0.1 grid (WD 485c)
            thickHost.append(h('div', { class: 'wd-cp__rowlabel' }, 'Outline thickness'), sizeRow(createStepper({
              value: clamp(toUnits(ring.thickness), toUnits(LOGO_RING_MIN), LOGO_RING_MAX),
              min: toUnits(LOGO_RING_MIN), max: LOGO_RING_MAX, step: OUTLINE_STEP,
              ariaLabel: 'Logo outline thickness',
              onInput: (v) => {
                adoptTextRings(state); // WD 488 (idempotent - only the first nudge copies)
                ring.thickness = (Number(v) || 0) / U; ring.auto = false;
                colorPicker.syncExtra?.();   // re-compare against the baseline — see the text dial above
                valueCommit();
              },
            })));
          };
          fillThickness();
          /* ⚠ A SWEEP, NOT A LOOK AT THE LIT TAB. One popover can visit several tabs, so "dial the
             Added ring to 0, switch to a band to check it against the artwork, then swipe down"
             would leave a zeroed ring behind if this only asked about the tab you happened to end
             on. The question is about the DESIGN — are any added rings at nothing? — not about the
             route taken through the popover. */
          const retireRingIfZero = () => {
            const all = Array.isArray(logoEl.logoAddOutline) ? logoEl.logoAddOutline : [];
            const kept = all.filter((a) => a && Number(a.thickness) > 0);
            if (kept.length === all.length) return;
            markStyleUserEdited(state.designProfile);
            adoptTextRings(state); // WD 488: a logo-outline press never changes the text's rings (Ryan run 2)
            logoEl.logoAddOutline = kept;
            structuralCommit();
            ctx.announce?.('Added outline removed.');
          };
          openSwatchEditPicker(logoEditBtn, logoEditInit.hex, {
            targets: logoTargets,
            activeTargetKey: key,
            // Bands and added rings alike — this popover commits an OUTLINE. See the text editor.
            confirmLabel: 'Update outline',
            /* A BAND has no thickness dial (it is measured off the artwork, see `fillThickness`), so
               this answers null on those tabs — null against null is "unchanged", which is exactly
               right: there is nothing there to move. `activeRing` rather than a fresh `addList()[n]`
               because a ring dialled to 0 leaves that list; re-deriving would compare this ring's
               dial against a DIFFERENT ring's number at the one moment the answer matters. */
            extraValue: () => (activeRing ? activeRing.thickness : null),
            /* Parity with the text outline's ✕ rule (Ryan 2026-08-09) — reachable at all only because
               `LOGO_RING_MIN` came down to 0 in the same pass. A band tab holds `activeRing` null and
               falls straight through, which is right: a band is measured off the artwork and has no
               thickness of its own to retire. */
            onConfirmExtra: retireRingIfZero,
            onDismissExtra: retireRingIfZero,
            onTargetChange: (k) => { key = k; fillThickness(); },
            extra: thickHost,
            /* This row lists the outline's CURRENT colours (one chip per active band / added ring),
               never the pool — so without this there is no route from here to another of the design's
               colours except dialling the hex by hand. The plate is the neighbour that matters for a
               silhouette ring; the bands' own stacking is the artwork's, not ours to re-derive. */
            swapFits: (hex) => !colorsConflict(plateBodyNow(), hex),
            // The dial + confirm route through the same scoped apply as the swap chips — a band or
            // ring edit must never recolour BY VALUE (the keyline can share the plate's own hex).
            scopedConfirm: true,
            onSwapPick: (hex, o) => {
              const [kind, n] = String(key).split(':');
              if (kind === 'band') applyBandColor(n | 0, hex); else applyRingColor(n | 0, hex);
              // A preview mutation is safe to make in full: `openSwatchEditPicker` snapshots the whole
              // profile before the first preview, so a dismiss restores the band's `userSet` too.
              if (o && o.preview) return;
              structuralCommit();
              ctx.announce?.(`Logo outline set to ${hex}.`);
            },
          });
        },
      }, '✎');
      const editChip = toolWrap(logoEditBtn, 'Edit');
      // (An empty row carried a "None — the logo has no outline." line; removed at Ryan's request
      // 2026-07-25. The heading over a lone "+ Add" is self-explanatory.)
      return h('div', { style: { margin: '0 0 20px' } }, subHead('Logo outlines'),
        h('div', { class: 'wd-cchip-row' }, ...bandChips, ...addChips, editChip, ...tail));
    }

    // ── Logo fill (Ryan 2026-07-22, renamed from "Logo center" to match "Text fill") —
    // flatten the logo's INTERIOR (fill + any detail bits, e.g. purple with flecks of
    // black/green) to ONE chosen tone, keeping the outline bands + the cutouts. An
    // "Original" chip (the untouched multi-color art) is selected by default; tap a pool
    // swatch to flatten, tap the ACTIVE swatch again to EDIT its hex (global), "+" crafts a
    // new color. Shown whenever a logo is in the layout.
    function logoFillBlock() {
      const logoEl = state.designProfile.elements.find((e) => e.type === 'logo' && e.logo && e.logo.dataUrl);
      if (!logoEl) return null;
      const ls = state.designProfile.logoStructure;
      // A genuinely single-tone fill (Ryan 2026-07-22): drop the "Original" chip — that swatch
      // just IS the fill colour, so highlight it in the list instead. Multi-tone (or unknown) →
      // keep the "Original" chip, since no single swatch represents the original interior.
      //   Structure detection returns LOW confidence on a one-tone mark ("too-few-swatches"), so `ls`
      // is null and this could not tell it was uniform — it rendered an "Original" chip that is, for a
      // single-colour logo, THE SAME COLOUR as the swatch beside it: two identical unlabelled chips,
      // and worse in near-black where the row read as three dark squares (Ryan 2026-07-25 audit).
      // Fall back to the palette: one role and no detected bands IS a uniform fill.
      const soleTone = (() => {
        const pal = state.designProfile.palette;
        if (normalizeHex(pal.secondary) || normalizeHex(pal.tertiary)) return null;
        if (ls && Array.isArray(ls.bands) && ls.bands.length) return null;
        return normalizeHex(pal.primary);
      })();
      // "Uniform" has to mean the WHOLE MARK is one tone, not just its CENTRE (Ryan 2026-07-25).
      //   `ls.fillUniform` only describes the interior — every significant layer that isn't a border
      // band. That was the right test back when this row painted the fill alone, but the row now
      // paints the whole mark, bands included, so the "Original" it reverts to is the whole mark's
      // original colours. Garage Gym Reviews is red with a white lettering RING: the ring is detected
      // as a band, so the centre read as uniform red, the split "Original" chip was suppressed
      // entirely, and the row offered a plain red swatch for a logo that is red AND white. The
      // Wallabies mark carries both its tones in the centre, which is why it split correctly and GGR
      // looked like a one-off.
      const uniformHex = (() => {
        if (!ls || !ls.fillUniform) return soleTone;
        const fillHex = normalizeHex(ls.fillHex);
        const bandTones = (Array.isArray(ls.bands) ? ls.bands : []).map(normalizeHex).filter(Boolean);
        // A band in the SAME colour as the fill adds no second tone — still uniform.
        return bandTones.some((b) => b !== fillHex) ? null : fillHex;
      })();
      const center = normalizeHex(logoEl.logoCenterColor); // null = original art
      // Contextual-contrast collapse (Ryan 2026-07-23): on a dyed plate the auto fill-swap can
      // fold a 2+/3-tone OUTLINE-LESS logo down to ONE effective tone (green+yellow → all yellow
      // on a green plate). When it does, highlight THAT tone instead of "Original", so the swatch
      // matches what's rendered. Mirrors syncLogoRaster's gate (no bands, no manual flatten).
      const effTone = (() => {
        if (center || uniformHex) return null;
        if (ls && Array.isArray(ls.bands) && ls.bands.length) return null;
        // ⚠ A colour-borne mark never collapses, because nothing collapses it any more — the fill
        // swap that this predicts is now skipped for exactly these marks (see main.js). Without this
        // the panel goes on predicting a swap that will not happen, lights a swatch that is not even
        // on the row, and takes the ring off the multi-tone chip to do it: Ryan 2026-07-28, "the
        // 4-tone swatch loses its white outline so something seemingly invisible is getting selected".
        if (ls && ls.colorBorneDetail === true) return null;
        const pal = state.designProfile.palette;
        const cands = [pal.primary, pal.secondary, pal.tertiary].map(normalizeHex).filter(Boolean);
        const remap = fillContrastRemap(cands, plateBodyNow());
        if (!remap.length) return null;
        const mapped = new Set(cands.map((c) => { const s = remap.find((r) => r.from === c); return s ? s.to : c; }));
        return mapped.size === 1 ? [...mapped][0] : null; // collapsed to a single tone
      })();
      const shown = center || uniformHex || effTone; // the colour highlighted as active (null = multi-original)
      const { list, added } = orderedPool();
      // Picking a logo colour paints the WHOLE mark, bands included (Ryan 2026-07-25).
      //   The flatten only ever repainted pixels that aren't part of a detected band, so on a logo
      // whose outer layer is a ring of lettering — "GARAGE GYM REVIEWS" — choosing red recoloured the
      // barbell and the two diamonds and left the words white. That reads as a broken control: the row
      // is called "Logo" and offers one tone, so it should produce one tone.
      //   A band the buyer has explicitly recoloured is NEVER touched (`logoBandUserSet`), which is the
      // escape hatch for the case Ryan flagged — a logo whose keyline should stay put while the fill
      // changes. Set that band's colour first and the cascade leaves it alone; or set it afterwards,
      // since the outlines row still overrides. "Original" and Reset clear both, so nothing is lost.
      const cascadeFillToBands = (hex) => {
        const bands = state.designProfile.logoStructure && Array.isArray(state.designProfile.logoStructure.bands)
          ? state.designProfile.logoStructure.bands : [];
        if (!bands.length) return;
        const chosen = new Set(Array.isArray(logoEl.logoBandUserSet) ? logoEl.logoBandUserSet : []);
        if (!hex) {
          // Back to "Original": drop only the colours the cascade wrote, keeping the buyer's own.
          const kept = {};
          for (const k of Object.keys(logoEl.logoBandColors || {})) if (chosen.has(k | 0)) kept[k] = logoEl.logoBandColors[k];
          logoEl.logoBandColors = Object.keys(kept).length ? kept : null;
          return;
        }
        // Skip any band that ABUTS the fill (Ryan 2026-07-25). Painting a keyline the same colour as
        // the shape it borders dissolves it — the band exists precisely to separate the two. A band
        // with clear space around it has no such job, so it takes the colour. Detected from the
        // artwork (logoStructure.bandTouchesFill), not guessed from the logo's type.
        //   The buyer keeps both overrides either way: recolour a spared band from the outlines row,
        // or ✕ it for a clean single-tone silhouette.
        const touches = Array.isArray(state.designProfile.logoStructure.bandTouchesFill)
          ? state.designProfile.logoStructure.bandTouchesFill : [];
        if (!logoEl.logoBandColors || typeof logoEl.logoBandColors !== 'object') logoEl.logoBandColors = {};
        for (let i = 0; i < bands.length; i++) if (!chosen.has(i) && touches[i] !== true) logoEl.logoBandColors[i] = hex;
      };
      // `logoCenterAuto = false` marks this flatten as the buyer's rather than one
      // `applyVanishingLogoDefaults` derived — including when they pick "Original" (hex null), which
      // is an explicit choice to stop flattening, not an absence of one.
      //   ⚠ It does NOT buy survival across a plate change, and this comment used to say it did
      // ("so the plate-colour re-cascade must preserve it"). Changelog 80 the following day made a
      // plate pick re-baseline every colour under it — structure survives, colour re-derives — so
      // `recascadeColorDefaults` clears the fill unconditionally. Corrected 2026-07-29. The flag
      // still earns its keep in the paths that DO read it (the vanishing check re-runs on a derived
      // flatten but leaves a chosen one alone).
      const setFillColor = (hex) => { markStyleUserEdited(state.designProfile); logoEl.logoCenterColor = hex; logoEl.logoCenterAuto = false; cascadeFillToBands(hex); syncLogoContrastOutline(); structuralCommit(); ctx.announce?.(hex ? `Logo set to ${hex}.` : "Logo back to its own colors."); };
      const openFillAddPicker2 = (anchorEl) => openAddPicker(anchorEl, shown || STARTER_CHIPS[0], {
        preview: () => {}, // no live recolor — the interior flatten is a heavy raster op; commit only
        confirm: (hex) => { addSwatch(state, hex, { userPicked: true }); setFillColor(normalizeHex(hex)); },
        cancel: () => {},
      });
      const chips = [];
      // ⚠ ITS CLICK IS UNCONDITIONAL (Ryan 2026-07-28: "on the navy and orange plate colours it won't
      // even let me select the 3-in-1 logo swatch"). It used to act only `if (center)` — only when a
      // flatten existed to clear — which is defensible right up until something ELSE decides the chip
      // is not the active one. Then it renders unlit and does nothing when pressed: a control that
      // looks available, is not, and gives no reason. `setFillColor(null)` is idempotent and states
      // the buyer's intent ("this mark wears its own colours") whether or not there was anything to
      // undo, so pressing it is always answerable.
      // ⚠ NO LONGER CALLED "ORIGINAL" (Ryan 2026-07-28). It shows the logo's tones AS THEY ARE NOW —
      // it follows an edit made on the Add Logo tab, which is what he asked for — and pressing it does
      // not restore the uploaded artwork either: it clears the manual flatten, so the mark goes back
      // to wearing its OWN colours rather than one picked one. "Original" was true of neither by then,
      // and a label describing a state the control neither shows nor reaches is how the next person
      // gets this wrong.
      // The multi-tone chip — ONLY when the fill is multi-tone (the logo's own colors). The chip is
      // now split into CLEAN even bands, one per detected tone (2 → halves, 3 → thirds, 4 →
      // quarters, …), from the logo's real extracted colors (Ryan 2026-07-23) — not a blurry
      // gradient. Reflects the ORIGINAL art, so it stays constant when a plate dye recolors things.
      if (!uniformHex) {
        const pal = state.designProfile.palette;
        // The bands are WHAT THE ADD LOGO TAB SHOWED (Ryan 2026-07-28) — see `shownLogoTones` for why
        // this cannot be `significantLogoTones`'s answer: that one merges at ΔE 24 and needs 6%, so a
        // third tone with its own chip could still be missing from the swatch claiming to show the
        // same logo.
        //   The old call survives as the FALLBACK, and earns its place: a resumed session restores the
        // design profile but not necessarily the extractor palette, so `shownLogoTones` correctly
        // returns nothing there and the roles are all that is left to draw.
        //   ⚠ `shownTones`, NOT `shown`. There is already a `shown` in the enclosing scope — the
        // COLOUR currently highlighted as active, where null means "the multi-tone original" — and
        // naming this array `shown` shadowed it for the rest of this block. The chip's own active test
        // is `!shown`, and an array is always truthy, so the selected ring could never appear on it
        // (Ryan 2026-07-28: "the 3-in-1 swatch, when selected, isn't getting the white outline").
        // The chip was correct about everything except being able to say it was chosen.
        const shownTones = shownLogoTones(state.ui.palette);
        const tones = shownTones.length >= 2
          ? shownTones
          : significantLogoTones(state.ui.palette && state.ui.palette.all, [pal.primary, pal.secondary, pal.tertiary]);
        /* ⛔ THE CHIP SHOWS WHAT IS ON THE PLATE, NOT WHAT WAS UPLOADED (Ryan 2026-08-12: *"the one
           highlighted is the split swatch which shows C38F82 on the left and black on the right,
           despite the logo's actual appearance on the plate being C38F82 and white"*).
             This chip used to draw the ORIGINAL art on purpose — "so it stays constant when a plate
           dye recolors things". That is right for what the chip MEANS: it is a choice ("use the
           logo's own colours", i.e. do not flatten), not a status readout. But it also wears the
           `is-active` ring, and a highlighted chip is a claim about what you are looking at. On a
           black plate the rescue repaints the merging keyline white, so the claim was false.
             So the MEANING is unchanged and only the rendering moves: the tones are pushed through
           the same view the raster applies — `bandConflictView` for a banded mark, `contextualFillSwap`
           for a band-less one — via the shared helpers rather than a second copy of those rules
           (CLAUDE.md §0b). A peeled band is dropped from the chip entirely, because it is not on the
           plate either.
             ⚠ THE TRADEOFF, ACCEPTED: the chip now changes when the plate does. That is the churn the
           original note was avoiding, and it is truthful churn — the mark genuinely looks different —
           and this chip is the row's only readout of the mark's current colours. Nothing is stored, so
           un-dyeing restores the original bands with nothing to undo. */
        const platedTones = (() => {
          const src = tones.length ? tones : [pal.primary || '#8a8f96', pal.secondary || '#b8bcc4'];
          const prof = state.designProfile;
          const plate = effectivePlateHex(state);
          const bands = (prof.logoStructure && Array.isArray(prof.logoStructure.bands)) ? prof.logoStructure.bands : [];
          // The bands as they currently render — the buyer's manual override, else the detected
          // colour. Same first step the raster takes, so the conflict is judged on the live colour.
          const manual = (logoEl && logoEl.logoBandColors && typeof logoEl.logoBandColors === 'object') ? logoEl.logoBandColors : {};
          const effBands = bands.map((c, i) => normalizeHex(manual[i]) || normalizeHex(c) || c);
          // Same options as the raster — the chip is a claim about what the plate shows.
          const view = bandConflictView(effBands, plate, bandViewOptions(prof));
          const bandMap = new Map();       // original band hex -> repainted hex, or null when peeled
          Object.keys(view.repaint).forEach((i) => bandMap.set(normalizeHex(effBands[i]), normalizeHex(view.repaint[i])));
          view.peel.forEach((i) => bandMap.set(normalizeHex(effBands[i]), null));
          const swap = new Map(contextualFillSwap(prof, plate).map((s) => [normalizeHex(s.from), normalizeHex(s.to)]));
          return src
            .map((t) => {
              const H = normalizeHex(t);
              if (bandMap.has(H)) return bandMap.get(H);   // may be null → peeled, drop it
              return swap.get(H) || t;
            })
            .filter(Boolean);
        })();
        const origBg = dividedSwatchBg(platedTones.length ? platedTones : (tones.length ? tones : [pal.primary || '#8a8f96', pal.secondary || '#b8bcc4']));
        chips.push(h('div', { class: 'wd-cchip-wrap' },
          // No caption (Ryan 2026-07-25): the chip is already unmistakable — it renders the logo's
          // real tones as clean even bands, so it can't be read as a solid swatch, and its tooltip
          // and aria-label still say "Original logo colors". The empty span is gone too, not just
          // its text: with the badge min-height removed it only contributed the wrap's 3px column
          // gap, which would leave the Logo row's stepper sitting looser than the Text row's.
          h('button', { type: 'button', class: 'wd-cchip' + (!shown ? ' is-active' : ''), style: { backgroundImage: origBg }, title: "The logo's own colors", 'aria-label': `Logo fill — the logo's own colors${!shown ? ' (current)' : ''}`, onClick: (e) => { e.stopPropagation(); setFillColor(null); } })));
      }
      // ── A SOLID FILL IS NOT ON OFFER FOR EVERY MARK (Ryan 2026-07-28) ─────────────────────────
      // "it took a 4-colour logo which has no gaps in it at all and decided to create 4 solid-fill
      // versions of it… you're reducing an intricate detailed badge into a solid fill circle. No one
      // will ever want that."
      //   The row's colour swatches all do one thing: repaint the WHOLE mark in that colour. For a
      // wordmark that is a real choice — the letterforms survive, only their colour changes. For a
      // badge whose ring, field, lettering and mascot exist only as colour boundaries inside one
      // contiguous shape, every one of those swatches produces the same thing: a plain disc. Four
      // controls, four ways to destroy the logo, and no colour that makes any of them acceptable.
      //   `colorBorneDetail` is measured at upload from the artwork itself (logo-structure.js) rather
      // than guessed from the tone count — a three-colour wordmark and a three-colour badge look
      // identical to a counter and completely different to this. When it is set the row keeps the
      // multi-tone chip and the ✎, which are the two controls that can still say something true: the
      // mark as it is, and per-colour editing.
      //   This is also what retires most of the plate-contrast patching underneath it. A flatten that
      // is never offered cannot be auto-applied either — the rescue paths all write the same
      // `logoCenterColor` these swatches do.
      const flattenRuins = !!(state.designProfile.logoStructure && state.designProfile.logoStructure.colorBorneDetail);
      /* ⛔ A FLATTEN THE BUYER HAS ALREADY CHOSEN MUST STILL BE VISIBLE (Ryan 2026-08-05: *"I clicked
         '+ Add' to add a new color on the Logo line… the logo changed color, but no swatch for the new
         color was added to the logo swatch row — it was only added to the plate color and text color
         rows."*).
           `flattenRuins` (a `colorBorneDetail` logo — one whose detail IS its colours, so any flatten
         renders a plain disc) suppressed the whole swatch list. That is right for OFFERING flattens:
         the row should not present a dozen ways to destroy the mark. It was applied half-way, though —
         the row hid the swatches and kept the **"+ Add" button, which is itself a flatten**. So the
         buyer could pick a colour, watch the logo change, and find nothing on the row saying so and
         nothing to tap to get back. The state existed and the UI denied it.
           Showing the ACTIVE colour only is the narrow fix: no menu of destructive options returns,
         but whatever is currently on the mark is on the row, selectable and reversible (the "original
         logo colors" chip beside it is the way back). ⚠ The alternative — disabling "+ Add" under
         `flattenRuins` — is arguably more consistent with the row's stated contract, but it removes a
         capability rather than surfacing its result, so it is Ryan's call, not mine to assume. */
      (flattenRuins ? (shown ? [shown] : []) : list).forEach((hex) => {
        const H = normalizeHex(hex);
        const on = H === shown;
        const isFillHex = uniformHex && H === uniformHex; // the single-tone fill's own swatch
        const removable = added.has(hex);
        const btn = h('button', {
          type: 'button', class: 'wd-cchip' + (on ? ' is-active' : '') + (isNearBlackHex(hex) ? ' wd-cchip--dark' : ''), style: { background: hex },
          title: on ? `Logo fill ${hex} — current (use ✎ Edit to change it)` : `Set logo fill to ${hex}`,
          'aria-label': `Set logo fill to ${hex}${on ? ' (current)' : ''}`,
          onClick: (e) => {
            e.stopPropagation();
            if (on) {
              // Choose only — editing is the row's ✎ (Ryan 2026-07-24). This branch MUST stay ahead of
              // the isFillHex one below even as a no-op: when a single-tone logo is on its own colour,
              // BOTH are true, and falling through would revert to the original art on a plain re-tap.
              return;
            } else if (isFillHex && !colorsConflict(plateBodyNow(), H)) {
              // Picking the fill's own colour normally = revert to the original art: on a plate the
              // colour reads against, "original" and "flattened to its own tone" render identically,
              // so the simpler state wins.
              //   NOT WHEN IT CONFLICTS WITH THE PLATE (Ryan 2026-07-28: "the logo refuses to go green
              // and automatically get a white outline"). The two stop being equivalent exactly then,
              // because `null` is what the rescue machinery reads as "no deliberate choice here":
              // `syncLogoRaster` gates `autoRemap` on `!center`, so the contextual swap runs — and for
              // a ONE-TONE mark it has no second brand colour to swap to and falls back to a NEUTRAL.
              // Green logo, green plate, no other tone → white. Meanwhile `syncLogoContrastOutline`
              // needs a `center` to know what the ring must contrast with, so it adds none either.
              //   Recording the hex instead makes it a real flatten: the remap is suppressed and the
              // ring is derived, which is precisely what the TEXT side already does with the same
              // pick — and why the text went green-with-a-white-outline while the logo went white.
              //   Reproduced before/after: plate #168C3C + logo #168C3C gave centre `null`, ring
              // `null`; it now gives centre `#168C3C` with an auto white ring.
              setFillColor(null);
            } else {
              setFillColor(H);
            }
          },
        });
        const body = removable
          ? h('div', { class: 'wd-outline-chip' }, btn, h('button', { type: 'button', class: 'wd-ol-x', 'aria-label': `Remove ${hex} from your colors`, title: 'Remove from your colors', onClick: (e) => { e.stopPropagation(); removePooledColor(hex); } }, '✕'))
          : btn;
        chips.push(h('div', { class: 'wd-cchip-wrap' }, body));
      });
      // ✎ Edit — third pool row to get one (Ryan 2026-07-24), same rationale as Text fill. Disabled on
      // "Original": a multi-tone original has no single hex to recolor, so there's nothing to edit until
      // the buyer flattens to a tone. Editing here is LOCAL to the logo fill, matching the 2nd-tap path.
      const fillEditBtn2 = h('button', {
        type: 'button', class: 'wd-cchip wd-cchip--tool wd-flat-glass wd-cchip--edit', disabled: shown ? null : true,
        title: shown ? `Edit the logo fill (${shown})` : 'Flatten the logo to one color first — the original art has no single color to edit',
        'aria-label': shown ? `Edit the current logo fill ${shown}` : 'Edit logo fill — the logo is showing its original colors',
        onClick: (e) => {
          e.stopPropagation();
          if (!shown) return;
          // `flattenRuins ? [] : list` — the SAME expression the chips are built from, so the strip can
          // never offer a colour this row is deliberately not showing. On a badge whose detail is
          // colour-borne the solid fills are withheld, and tabs for them would put back the exact
          // controls that pass was added to remove.
          /* The `poolTargets` strip is gone from here too — the third and last instance of the
             "choose a colour to edit" pattern on the Customize Design tab, which now lives only on
             the Add Logo tab where that is genuinely the job (Ryan 2026-08-05). In its place, the
             swap row: on a `colorBorneDetail` logo this row shows only the active colour, so it was
             the surface with the least visible pool and the most need for a way to reach one. */
          openSwatchEditPicker(fillEditBtn2, shown, {
            swapFits: (hex) => !colorsConflict(plateBodyNow(), hex),
            onSwapPick: (hex, o) => {
              logoEl.logoCenterColor = hex;
              logoEl.logoCenterAuto = false;
              cascadeFillToBands(hex);
              if (o && o.preview) return; // a colour they are only looking at is not a choice yet
              markStyleUserEdited(state.designProfile);
              syncLogoContrastOutline();
              structuralCommit();
              ctx.announce?.(`Logo set to ${hex}.`);
            },
          });
        },
      }, '✎');
      chips.push(toolWrap(fillEditBtn2, 'Edit'));
      const logoPoolFull = swatchSlotsLeft(state.designProfile) <= 0;
      const addBtn = h('button', { type: 'button', class: 'wd-cchip wd-cchip--tool wd-flat-glass wd-cchip--more', disabled: logoPoolFull ? true : null, title: logoPoolFull ? `That's the ${SWATCH_CAP}-colour limit — remove one to add another` : 'Add a custom logo-fill color', 'aria-label': logoPoolFull ? 'Add a custom logo-fill color — colour limit reached' : 'Add a custom logo-fill color', onClick: (e) => { e.stopPropagation(); if (!logoPoolFull) openFillAddPicker2(addBtn); } }, '+');
      chips.push(toolWrap(addBtn, 'Add'));
      // (Logo SIZE moved to the Add Logo tab 2026-07-25 — see `logoSizeBlock` in ui/logo-step.js.
      // It sat on this row because ✎ is disabled while the fill reads "Original" (multi-tone) and
      // resizing an un-flattened logo has to stay possible; that constraint is unchanged, it's just
      // satisfied on the tab that owns the logo now.)
      return h('div', { style: { margin: '0 0 20px' } }, subHead('Logo'),
        h('div', { class: 'wd-cchip-row' }, chips));
    }

    // Plate color is offered ONLY on a product that can actually be dyed — since 2026-08-31 that
    // is the competition URETHANE bumper alone (rubber cannot take dye; see products.js) — and is
    // hidden entirely elsewhere. Named by the predicate rather than by tier, so a change to the
    // catalogue's dye rule reaches this row without an edit here. Same gate the render uses.
    const canDyePlate = productSupportsBodyColor(state.ui.product);

    // "Reset to original" (Ryan 2026-07-21) — the easy escape hatch back to the
    // just-uploaded design (black plate, extracted colors, auto-styled outlines, no
    // added swatches / logo peel / added ring). One undoable step.
    const resetBtn = h('button', {
      type: 'button', class: 'wd-reset-btn wd-flat-glass wd-flat-glass--flush',
      title: 'Put every colour back to your original uploaded design — sizes and text are kept',
      onClick: (e) => { e.stopPropagation(); resetToOriginal(state); structuralCommit(); ctx.logoStep?.repaintColors?.(); ctx.announce?.('Colors reset to your original design.'); },
    }, '↺ Reset colors');

    return h(
      'div',
      {},
      // "Size & color" → "Color" (Ryan 2026-07-25). The section was renamed when the size steppers
      // arrived; all three have now left for the sections that own what they resize, so the heading
      // goes back to describing what's actually in here.
      h('div', { class: 'wd-eyebrow' }, 'Color'),
      // NESTED (Ryan 2026-07-25): each "Outline(s)" row is indented under the thing it outlines, so
      // the section reads as three groups — Plate · Text(+outlines) · Logo(+outlines) — instead of
      // five flat rows. This is also what finally disambiguates the two identically-titled
      // "Outline(s)" blocks entry 59 left open: it noted the fix was GROUPING, not longer titles.
      canDyePlate ? plateColorRow() : null, // Plate — competition bumpers only (+ "+" to add)
      hasText ? fillBlock : null,           // Text — swatches + size
      hasText ? outlineBlock : null,        // Text outlines — chips; thickness lives in the ✎
      logoFillBlock(),                      // Logo — swatches + size
      logoOutlineBlock(),                   // Logo outlines — bands + added rings
      resetBtn,                             // Reset styling — undo all size/color/outline edits
    );
  }

  // ── SECTION RESETS (Ryan 2026-07-28) ──────────────────────────────────────────────────────────
  // "add a 'Reset text' button on mobile, at the bottom of the text pill menu, and also add 'Reset
  // weight' to the bottom of the weight pill menu."
  //
  // Each one is scoped to EXACTLY what its own section contains, which is the lesson "Reset colors"
  // taught the hard way: it was called "Reset all styling", was renamed, and then had to be made
  // colour-only because the label had started lying (changelog 108). A section reset that reaches
  // outside its section is the same bug waiting to happen, so these two touch nothing the buyer
  // cannot see in the pane above the button.
  //
  // ⚠ NEITHER TOUCHES THE UNIT. LB/KG sits in the Weight section, but it is not styling — it is a
  // fact about what is being ordered, and silently flipping a KG buyer back to LB would change the
  // product rather than its look. Deliberately left out; say the word if it should go in.
  //
  // Phone only, per the request. Desktop stacks all four sections in one pane, where four reset
  // buttons down a single column would read as clutter rather than as per-section actions.
  const phoneMQ = () => typeof window.matchMedia === 'function'
    && window.matchMedia('(max-width: 768px) and (pointer: coarse)').matches;
  const sectionResetBtn = (label, title, onReset) => h('button', {
    type: 'button', class: 'wd-reset-btn wd-flat-glass wd-flat-glass--flush', title,
    onClick: (e) => { e.stopPropagation(); onReset(); },
  }, `↺ ${label}`);

  /**
   * Font, lettering case and arc size — the text's STYLING.
   *
   * ⚠ THE WORDS ARE DELIBERATELY NOT TOUCHED, and the first version of this was wrong about that.
   * It cleared `textUserSet` to hand the lines back to the identity's auto-split — which sounds like
   * "reset the text" until you follow what the identity IS by then: typing in a Wording field writes
   * back to it (`syncIdentityFromArcs`, last-edit-wins across the two screens). So the split it
   * re-derives from is the buyer's own typed words. Measured: type "MY OWN WORDS", press reset, get
   * "MY OWN WORDS" — the destructive half of the operation achieved nothing, while still handing a
   * hand-written line back to a splitter that may lay it out differently on the next layout change.
   *   Resetting styling and leaving content alone is also the rule "Reset colors" settled on after it
   * was caught resetting sizes it did not name (changelog 108). The words have their own field, two
   * inches above this button.
   */
  const resetTextSection = () => {
    const p = state.designProfile;
    setFont(state, DEFAULT_FONT_ID);
    p.allCaps = true;
    for (const e of p.elements || []) {
      if (e.type !== 'arctext') continue;
      e.sizeScale = DEFAULT_ELEMENT_SIZE_SCALE;
      e.sizeUserSet = false;
    }
    seedDefaultSizes(p, { product: modelFor(state.ui.product), arcPct: 100 });
    structuralCommit();
    ctx.announce?.('Text style reset — font, lettering case and size back to their defaults.');
  };

  /**
   * Logo SIZE and POSITION — the two things the Logo section offers (Ryan 2026-07-29: "in the same way
   * we have a reset colors, reset text, and reset weight button on mobile, I think we need a reset logo
   * button that resets its size and position").
   *
   * ⚠ THE LOGO ITSELF AND ITS COLOURS ARE NOT TOUCHED, and the scoping rule is what decides that
   * rather than taste: each of these buttons resets exactly what the buyer can see in the pane above
   * it, which is the lesson "Reset colors" learned the hard way when it was still called "Reset all
   * styling" (changelog 108). The logo's art has its own "Upload different logo" tile on the Add Logo
   * tab; its colours are re-derived by "Reset colors" at the foot of the Colors section, which
   * deliberately leaves sizes alone for the mirror-image reason. The two buttons partition the logo
   * cleanly and neither reaches into the other's half.
   *
   * SIZE goes back by clearing the hand-sized flag rather than by writing a number: `syncLogoSize`
   * re-seeds DEFAULT_SIZE_PCT on the next refresh for any logo not flagged `sizeUserSet`, so setting a
   * literal here would put the mark at a raw scale no default ever chose AND suppress the seeding that
   * would have corrected it — exactly the bug the note in `resetToOriginal` records.
   */
  const resetLogoSection = () => {
    const p = state.designProfile;
    for (const e of p.elements || []) {
      if (e.type !== 'logo') continue;
      e.sizeScale = DEFAULT_ELEMENT_SIZE_SCALE;
      e.sizeUserSet = false;
      // Cleared on EVERY logo element, not only the badge ones the pad is shown for. A layout switch
      // away from a Badge leaves the offset sitting on the element unread; "reset" that left it there
      // to reappear on the way back would be the same surprise as a size that outlived its reset.
      e.nudgeU = 0;
      e.nudgeV = 0;
    }
    structuralCommit();
    ctx.announce?.("Logo reset — size and position back to their defaults. Your logo and its colors are unchanged.");
  };

  /** Number style + size — everything the Weight section offers except the unit (see above). */
  const resetWeightSection = () => {
    const p = state.designProfile;
    for (const e of p.elements || []) {
      if (e.type !== 'weight') continue;
      e.weightFormat = 'stacked-dash';
      e.sizeScale = DEFAULT_ELEMENT_SIZE_SCALE;
      e.sizeUserSet = false;
      // ⛔ AND THE POSITION, which this section now also owns (Ryan 2026-08-09). A section reset that
      // left the number where the buyer had pushed it would be the same lie "Reset colors" would tell
      // if it kept `savedInk` — the button resets what the section controls, and the nudge pad is in
      // it. The per-product RECORD goes with it below, or the next hop would hand the offset back.
      e.nudgeU = 0;
      e.nudgeV = 0;
    }
    rememberWeightNudge(state, weightNudgeKey(state));
    seedDefaultSizes(p, { product: modelFor(state.ui.product), pct: DEFAULT_SIZE_PCT });
    structuralCommit();
    ctx.announce?.('Weight number reset to the default style, size and position.');
  };

  // ── font (one for the whole plate; each option previewed in its own face) ────
  // ONE master scroll list (Ryan 2026-07-20): a "Most popular" section leads (where
  // people start), then the rest of the ~105-font library by category below — scroll
  // past Most popular to go deeper. (Replaces the old "Coach's picks rail + a separate
  // Browse-all list" — two stacked scrollers read as clunky.) Selection updates IN
  // PLACE so the scroll position is never lost.
  function fontSection() {
    // Apply a font live: highlight the selected option + re-render the preview + record
    // history (debounced, so arrow-key scrubbing coalesces into one undo step).
    const applyFontSelection = (f) => {
      setFont(state, f.id);
      root.querySelectorAll('.wd-font-opt').forEach((b) => {
        const on = b.getAttribute('data-font') === f.id;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      /* ── THE TWO SURFACES THAT DRAW THE DESIGN IN ITS OWN FONT (Ryan 2026-08-02) ────────────────
       * *"when you change fonts, the 8x number style cards/buttons do not update with the new font.
       * If however you change fonts, then change the unit from LB to KG, THEN the cards update."*
       * Exactly right, and the unit is the tell: the unit toggle calls the Weight section's
       * `renderAll`, which rebuilds the grid and picks the new font up as a side effect. A font
       * change had no such path.
       *
       * ⛔ NEITHER OF THESE MAY BE A PANEL REBUILD. `applyFontSelection` runs on every ↑/↓ of the
       * arrow-key scrub, and `render()` would tear down the font list being scrubbed — losing its
       * scroll position and the focus ring mid-keypress. Same reason the wording input refreshes the
       * thumbs in place rather than committing structurally. So both updates are surgical.
       *
       * ⚠ AND THEY HAVE VERY DIFFERENT COSTS, which is why one is immediate and one is debounced.
       * The number-style tiles are plain DOM — a `font-family` on eight buttons, so they can just be
       * re-stamped on the spot, no rebuild, no reflow of anything else. The LAYOUT cards are full
       * SVG renders of the buyer's design with the logo embedded ("tens of milliseconds" each), so
       * they go through `scheduleThumbRefresh`'s 450ms debounce — which also skips off-screen cards.
       * Scrub through ten fonts and you pay for ONE redraw of the visible ones, not ten. */
      const fam = fontFamilyFor(f.id);
      const fw = fontWeightFor(f.id);
      // Scoped to the number-style grid. `.wd-lockup` is shared with the SIZE ladder's tiles, which
      // are a denomination list rather than a preview of the plate's lettering and do not follow the
      // design font — a bare `.wd-lockup` here would silently re-font them too.
      root.querySelectorAll('.wd-lockup-grid .wd-lockup').forEach((b) => {
        b.style.fontFamily = fam;
        b.style.fontWeight = fw;
      });
      scheduleThumbRefresh();
      valueCommit();
      ctx.announce?.(`Font: ${f.label}`);
    };

    // Arrow-key scrubbing (Ryan 2026-07-20): click a font, then ↑/↓ (or ←/→, Home/End)
    // steps through the list, each one loading live on the plate — like picking a font in a
    // desktop app. Navigation is per-box (Coach's picks OR the full library), clamps at the
    // ends, skips category headers (we only walk `.wd-font-opt`), and keeps the focused
    // option scrolled into view.
    const NAV_KEYS = new Set(['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft', 'Home', 'End']);
    const onFontNavKey = (box) => (e) => {
      if (!NAV_KEYS.has(e.key)) return;
      // Only VISIBLE options (Ryan 2026-07-25): with the search filter on, arrowing through hidden
      // entries would jump the focus ring to nothing and skip past the matches on screen.
      const opts = [...box.querySelectorAll('.wd-font-opt')].filter((b) => !b.hidden);
      if (!opts.length) return;
      const active = document.activeElement;
      const cur = active && active.classList && active.classList.contains('wd-font-opt')
        ? opts.indexOf(active)
        : opts.findIndex((b) => b.classList.contains('is-selected'));
      let idx;
      if (e.key === 'Home') idx = 0;
      else if (e.key === 'End') idx = opts.length - 1;
      else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') idx = cur < 0 ? 0 : Math.min(opts.length - 1, cur + 1);
      else idx = cur < 0 ? 0 : Math.max(0, cur - 1); // ArrowUp / ArrowLeft
      const btn = opts[idx];
      if (!btn || btn === active) { e.preventDefault(); return; }
      e.preventDefault();
      btn.focus();
      btn.scrollIntoView({ block: 'nearest' });
      const f = FONTS[btn.getAttribute('data-font')];
      if (f) applyFontSelection(f);
    };

    // ── TAP A FONT AND THE LIST RE-CENTRES ON IT (Ryan 2026-07-28) ──────────────────────────────
    // "when you click on a font, please have it auto-scroll to the middle of the list. that way you
    // can easily keep tapping fonts at the bottom and have the list auto-scroll up after each tap."
    // Auditioning fonts is a rapid back-and-forth, and the box is only ~230px — so the option you
    // just tried is often the LAST one in view, and the next few candidates are off-screen below.
    // Re-centring turns each tap into its own scroll, so the list feeds itself.
    //
    // Arithmetic on `scrollTop`, NOT `scrollIntoView({ block: 'center' })`: that walks up and scrolls
    // EVERY scrollable ancestor, so on a phone it would also drag the sheet's own section scroller
    // (`.wd-dsection-scroll`) — the pane would lurch while the font list did the right thing.
    // Touching `.wd-font-box` directly is the only way to keep the movement inside the box.
    //
    // ⚠ BOTH PLATFORMS NOW (Ryan 2026-07-28: "need to implement the same thing on desktop too — it's
    // a good feature that desktop visitors will also appreciate"). It shipped phone-only on the
    // reasoning that "on desktop the list is taller, tapping is not the audition loop being fixed,
    // and moving the list under a mouse that is about to click again would be actively worse."
    //   The first half was wrong about the behaviour rather than the pixels: auditioning fonts is the
    // same rapid back-and-forth with a mouse as with a thumb, and a taller list only means more
    // scrolling per lap, not less need for the list to feed itself.
    //   The second half is a real cost and it is worth naming rather than pretending away: the
    // pointer does not move with the list, so the next click lands on whatever font slid under it.
    // That is a fair trade for a control where every option is a preview and nothing is destructive —
    // clicking the "wrong" font shows you that font, and the next click fixes it. It would not be an
    // acceptable trade on a row of buttons that did something.
    const centreFontInList = (btn) => {
      if (!btn) return;
      const box = btn.closest('.wd-font-box');
      if (!box) return;
      const b = btn.getBoundingClientRect();
      const r = box.getBoundingClientRect();
      // Both ends clamp naturally — the first fonts cannot centre without scrolling past 0, and the
      // last cannot without scrolling past the end. `scrollTo` already refuses both.
      const top = box.scrollTop + (b.top - r.top) - (box.clientHeight - b.height) / 2;
      const smooth = !(typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      box.scrollTo({ top: Math.max(0, top), behavior: smooth ? 'smooth' : 'auto' });
    };

    const fontBtn = (f) => {
      const isCur = f.id === state.designProfile.font;
      return h('button', {
        type: 'button',
        class: 'wd-font-opt' + (isCur ? ' is-selected' : ''),
        'data-font': f.id,
        title: f.label,
        role: 'radio',
        'aria-checked': isCur ? 'true' : 'false',
        style: { fontFamily: f.family, fontWeight: f.weight },
        // Centre BEFORE applying: `applyFontSelection` commits, and a commit can rebuild the panel —
        // which would replace this very button and leave the scroll aimed at a detached node.
        //   The camera leans in on the arch too (Ryan 2026-07-28) — a typeface is a decision you make
        // by LOOKING at it, and at the default framing the arc text is a few millimetres of ring.
        // Only the font buttons do this, not every control in the Text section: the Wording fields
        // are typed into, and pulling the camera around on each keystroke would be seasickness.
        onClick: (e) => { centreFontInList(e.currentTarget); ctx.focusTextZoom?.(); applyFontSelection(f); },
      }, f.label);
    };

    // ONE master list: "Most popular" (the curated picks, in order) leads, then the rest
    // of the library by category (each pick appears ONCE — popular fonts are excluded from
    // their category below to avoid duplicates). Arrow-key scrubbing now spans the whole
    // list — no per-box boundary.
    // The RADIOGROUP holds only the options — the scroll box around it (.wd-font-box) also holds the
    // sticky search field, and a searchbox inside a radiogroup is the wrong shape for a screen reader.
    const masterList = h('div', { role: 'radiogroup', 'aria-label': 'Fonts', class: 'wd-font-list', onKeyDown: (e) => onFontNavKey(masterList)(e) });
    // Alphabetical, like every category below (Ryan 2026-07-25). COACH_PICKS was rendered in ARRAY
    // order — a curated ranking — which made a 28-item list hard to scan for a known name. The array
    // order is now purely WHICH fonts are picked, not the order they appear in.
    // ── "Default" LEADS THE LIST (Ryan 2026-07-28) ──────────────────────────────────────────────
    // "the category is 'Default' and the only font on the list is Montserrat… that way, when you land
    // on the font list, you see the default font actually highlighted."
    //   A one-entry category earns its heading here: an untouched design IS on this font, and burying
    // it alphabetically inside 28 "Most popular" entries meant the list opened with nothing visibly
    // selected — the tool looked like it had no opinion about what you were already looking at.
    //   Taken out of "Most popular" AND out of its own category below, so it appears exactly once. A
    // font listed twice would give the selected state two homes and the ✓ would land on whichever the
    // buyer was not looking at.
    const defaultFont = FONTS[DEFAULT_FONT_ID];
    const placed = new Set(defaultFont ? [defaultFont.id] : []);
    if (defaultFont) {
      masterList.append(h('div', { class: 'wd-subhead wd-font-cat' }, 'Default'));
      masterList.append(fontBtn(defaultFont));
    }
    // Alphabetical, like every category below (Ryan 2026-07-25). COACH_PICKS was rendered in ARRAY
    // order — a curated ranking — which made a 28-item list hard to scan for a known name. The array
    // order is now purely WHICH fonts are picked, not the order they appear in.
    const popular = COACH_PICKS.map((id) => FONTS[id])
      .filter((f) => f && !placed.has(f.id))
      .sort((a, b) => a.label.localeCompare(b.label));
    popular.forEach((f) => placed.add(f.id));
    if (popular.length) {
      masterList.append(h('div', { class: 'wd-subhead wd-font-cat' }, 'Most popular'));
      popular.forEach((f) => masterList.append(fontBtn(f)));
    }
    // ── `order` LEADS ITS CATEGORY, then alphabetical (Ryan 2026-07-30) ─────────────────────────
    // "slot Varsity Block into the same section of 4 fonts, at the top, listed as 'College All Star'".
    // Alphabetical alone put it LAST ("Spread" sorts after "Semi-condensed"), which buried the face
    // the other four are meant to be read against. `order` is optional and absent on all but that
    // one font, so every other category is unchanged — same escape hatch as "Default" leading the
    // list above, not a new concept.
    FONT_CATEGORIES.forEach((cat) => {
      const fonts = Object.values(FONTS)
        .filter((f) => f.category === cat && !placed.has(f.id))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.label.localeCompare(b.label));
      if (!fonts.length) return;
      masterList.append(h('div', { class: 'wd-subhead wd-font-cat' }, FONT_CATEGORY_LABELS[cat] || cat));
      fonts.forEach((f) => masterList.append(fontBtn(f)));
    });

    // ── Search (Ryan 2026-07-25) ────────────────────────────────────────────────────────────────
    // 132 fonts across 8 categories is past the point where scanning works, and the only way to find
    // a name was to know its category first.
    //   Filters by TOGGLING VISIBILITY rather than re-rendering: a rebuild on every keystroke would
    // blow away the input's own focus and caret. Category headings hide when their group empties, and
    // ALL headings hide while a query is active — they are noise once the list is four items long.
    //   It never changes the selection. If the current font is filtered out it stays applied; this is
    // a finding aid, not a picker.
    //   The "no matches" line lives INSIDE the list box, not above it (Ryan 2026-07-25). The box is a
    // fixed-height scroller now — only its CONTENTS change as you type — so a message stacked above it
    // would be the one thing that still moved the panel around.
    const noHits = h('div', { class: 'wd-font-nohit', hidden: true }, 'No fonts match');
    masterList.append(noHits);
    const applyFontFilter = (raw) => {
      const q = String(raw || '').trim().toLowerCase();
      const opts = [...masterList.querySelectorAll('.wd-font-opt')];
      let hits = 0;
      for (const b of opts) {
        const f = FONTS[b.getAttribute('data-font')];
        const show = !q || (f && f.label.toLowerCase().includes(q));
        b.hidden = !show;
        if (show) hits += 1;
      }
      // A heading is shown only with no query AND at least one visible option beneath it.
      for (const head of masterList.querySelectorAll('.wd-font-cat')) {
        let visible = false;
        for (let n = head.nextElementSibling; n && !n.classList.contains('wd-font-cat'); n = n.nextElementSibling) {
          if (n.classList.contains('wd-font-opt') && !n.hidden) { visible = true; break; }
        }
        head.hidden = !!q || !visible;
      }
      noHits.hidden = !q || hits > 0;
    };
    const searchBox = h('input', {
      type: 'search', class: 'wd-font-search', placeholder: 'Search fonts…', 'aria-label': 'Search fonts',
      autocomplete: 'off', spellcheck: 'false',
      onInput: (e) => { applyFontFilter(e.target.value); syncClear(); },
      // Enter/arrows hand off to the list without the field swallowing them.
      onKeyDown: (e) => {
        if (e.key === 'ArrowDown' || e.key === 'Enter') {
          const first = [...masterList.querySelectorAll('.wd-font-opt')].find((b) => !b.hidden);
          if (first) { e.preventDefault(); first.focus(); }
        }
      },
    });
    // OUR OWN clear ✕, not `input[type=search]`'s native one (Ryan 2026-07-25). Chrome only reveals
    // that one while the pointer is over the field, draws it tiny and grey, and offers no hook to
    // restyle it — so it read as "is something even there?". This one is visible whenever the field
    // has TEXT regardless of the pointer, grey at rest and red on hover, and a few px bigger.
    const clearBtn = h('button', {
      type: 'button', class: 'wd-font-clear', 'aria-label': 'Clear font search', title: 'Clear search', hidden: true,
      onClick: () => { searchBox.value = ''; applyFontFilter(''); syncClear(); searchBox.focus(); },
    }, '×');
    const syncClear = () => { clearBtn.hidden = !searchBox.value; };

    // The scroll box, drag-resizable by its bottom-right corner (Ryan 2026-07-25) so a buyer can open
    // it up and see more of the library at once. CSS gives the grabber; the height it lands on is
    // remembered here, because a commit re-renders the whole panel and would otherwise reset it.
    const fontBox = h('div', { class: 'wd-font-box' },
      h('div', { class: 'wd-font-searchwrap' }, searchBox, clearBtn),
      masterList);
    let savedH = 0;
    try { savedH = parseInt(sessionStorage.getItem(FONTBOX_H_KEY) || '', 10) || 0; } catch { /* storage off */ }
    if (savedH >= FONTBOX_H_MIN && savedH <= FONTBOX_H_MAX) fontBox.style.height = `${savedH}px`;
    // ── THE LIST OPENS ON THE FONT YOU CHOSE (Ryan 2026-07-28) ─────────────────────────────────
    // "any time someone changes fonts, should they also go to a new tab then return to the font list
    // later, I want the font they've chosen to be centered and highlighted, as opposed to the list
    // re-scrolling you to the top of it."
    //   Every panel rebuild hands back a BRAND-NEW box at scrollTop 0, so a font 90 entries down was
    // simply gone from view — the selection was never lost, only the sight of it.
    //   Deferred a frame because the box is not in the document yet: it is being returned to the
    // caller that will mount it, and it has no height to scroll within until then.
    //   ONLY WHEN IT IS ACTUALLY OFF-SCREEN. Montserrat now sits in "Default" at the very top, where
    // a fresh box already shows it, and scrolling a list that is already correct is a jump for
    // nothing. `auto`, not smooth: this is where the list STARTS, not a move the buyer asked to watch.
    requestAnimationFrame(() => {
      if (!fontBox.isConnected) return;
      const sel = fontBox.querySelector('.wd-font-opt.is-selected');
      if (!sel) return;
      const b = sel.getBoundingClientRect();
      const r = fontBox.getBoundingClientRect();
      if (b.top >= r.top && b.bottom <= r.bottom) return; // already in view — leave the scroll alone
      const top = fontBox.scrollTop + (b.top - r.top) - (fontBox.clientHeight - b.height) / 2;
      fontBox.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
    });
    if (typeof ResizeObserver === 'function') {
      let t = null;
      new ResizeObserver(() => {
        clearTimeout(t);
        t = setTimeout(() => {
          const px = Math.round(fontBox.getBoundingClientRect().height);
          if (px >= FONTBOX_H_MIN && px <= FONTBOX_H_MAX) {
            try { sessionStorage.setItem(FONTBOX_H_KEY, String(px)); } catch { /* storage off */ }
          }
        }, 200);
      }).observe(fontBox);
    }

    // Caps toggle (Advanced) — ALL CAPS (default) vs "As typed" (§2.7).
    return h(
      'div',
      {},
      // "FONT" → "TEXT" (Ryan 2026-07-25): the section owns the arch WORDING now, not just how it's
      // set. Content first, then how it's lettered.
      h('div', { class: 'wd-eyebrow' }, 'Text'),
      ...archTextFields(),
      // SIZE, between the wording and how it's lettered (Ryan 2026-07-25) — moved here from the
      // colour rows, so the control that scales the arch sits with the words it scales. It reads
      // down as content → size → case → face.
      //   It MUST stay above the font list: that box is a tall scroller, so anything below it is
      // effectively out of reach (the same reason Lettering case sits above it).
      //   No "Arch" label on the row — the Number stepper it needed telling apart from now lives in
      // the Weight section, so this is the only stepper here and the section header already says
      // what it acts on. (sizeRow's own rule: label only where a group has two.)
      //   VISIBLE even with no arch text on the plate, greyed rather than gone (Ryan 2026-07-26,
      // same call as the Wording fields above): a control that disappears reads as a control the
      // tool doesn't have. It comes alive the moment the Wording fields put words on the plate.
      // Dropped entirely only where no layout in the family HAS an arc to size.
      (hasPieceType('arctext') || familyHasArcs())
        ? [subHead('Size', '12px 0 6px'), sizeRow(textSizeStepper('arctext', 'Arch text', { enabled: hasPieceType('arctext') }))]
        : null,
      // Mirrors the Weight section's shape: sub-head + segmented toggle, then sub-head + the picker.
      // `12px 0 6px` — the standard gap a sub-head takes when it FOLLOWS a control, and the same one
      // "Font" below and "Number style" in the Weight section use. It carried the default `0 0 6px`
      // while it was the first thing in the section; with the Size stepper now above it, that put
      // the heading flush against the stepper's bottom edge (Ryan 2026-07-25). The clearance belongs
      // to the sub-head rather than to `.wd-sizerow`, because that's where every other group in the
      // panel puts it — a bottom margin on the row would double up wherever a sub-head follows.
      // ── LETTERING CASE GOES WITH THE WORDING (Ryan 2026-07-30) ────────────────────────────────
      // *"neither does ANY of the TEXT section b/c we're on a layout WITHOUT any text."*
      //
      // `allCaps` is read by `arcFontSize`, `arcHalfExtentDeg` and `drawArcText` — and by nothing
      // else. It cannot change a weight number, whose unit marks are literals (`-LB-`), so on a
      // layout with no arc text this toggle genuinely does nothing and belongs with the wording.
      //
      // ⚠ THE FONT PICKER BELOW DOES NOT GO WITH IT, and that is the one part of his ask left
      // standing. There is ONE font for the whole plate (2026-07-11) and this section is the only
      // place it can be chosen — the Weight section carries Unit, Size and Number style, no face. So
      // hiding the whole section would leave a Classic Grip with no way to change the typeface of
      // "45 -LB-", which is the only lettering on that plate. Worth a flagged deviation rather than
      // a silently-removed control.
      familyHasArcs() ? subHead('Lettering case', '12px 0 6px') : null,
      familyHasArcs() ? letteringCaseToggle() : null,
      // "Typeface" → "Font" (Ryan 2026-07-25) — plainer word for the same list, and the section
      // eyebrow above is "TEXT" now, so there's no longer a FONT heading for it to collide with.
      subHead('Font', '12px 0 6px'),
      // Search sits INSIDE the scroll box, frozen to its top above "Most popular" (Ryan 2026-07-25) —
      // it was a separate control above the box, so scrolling the list carried you away from the one
      // thing that narrows it.
      fontBox,
      phoneMQ()
        ? sectionResetBtn('Reset text', 'Put the font, lettering case and text size back to their defaults — your wording is kept', resetTextSection)
        : null,
    );
  }

  // ── SUPPRESS THE ANDROID AUTOFILL STRIP (Ryan 2026-07-28) ──────────────────────────────
  // "an additional strip populates above the keyboard which includes a credit card symbol, a key
  // symbol, and a map location symbol… makes it so much taller and more intrusive."
  //
  // That row is Chrome's autofill bar, and it shows because Chrome has GUESSED these are address or
  // payment fields. The guess is not unreasonable — one of them is literally labelled "Organization",
  // which is a real `autocomplete` address token — so the fix is to say outright that none of them
  // are: `autocomplete="off"` plus a `name` its heuristics cannot pattern-match. Correction and
  // capitalisation go too while we are here: these are team names and logos, not prose, and
  // autocorrect "helping" with BARBOS helps nobody.
  //
  // ⚠ BEST EFFORT, NOT A GUARANTEE. Chrome deliberately ignores `autocomplete="off"` on some
  // heuristic paths, and the buyer's own Android settings can put the bar back. This removes the
  // reasons Chrome has to show it; it cannot revoke Chrome's right to.
  const NO_AUTOFILL = Object.freeze({
    autocomplete: 'off',
    autocorrect: 'off',
    autocapitalize: 'off',
    spellcheck: 'false',
    // A name the address/payment matchers have nothing to say about. An UNNAMED field gets guessed
    // from its label and placeholder instead — which is how "Organization" got here to begin with.
    name: 'wd-freetext',
  });

  // ── arch wording (Ryan 2026-07-25) ───────────────────────────────────────────
  // The OVERRIDE for what tab 2's "Who's this for?" split out. deriveArcText balances a name across
  // the two arcs — "Big Johns Gym" becomes BIG / JOHNS GYM — which is a reasonable guess and a
  // lopsided one, and until now the only way to change it was to open Advanced and find the piece
  // card. These sit at the top of the Text section, one per arc slot the CURRENT layout actually has.
  //   Typing marks the line `textUserSet` so a later edit to the org name on tab 2 can't re-derive
  // over it; EMPTYING the field clears that flag, handing the line back to the auto-split. That
  // round trip is the whole reason the flag is cleared on empty rather than latched forever.
  // `left`/`right` are the GRIP PLATE's two lower gaps (2026-07-30), named for where they sit on the
  // plate rather than by their slot key — a buyer reading "Left text" next to a plate whose word is
  // down at 7 o'clock would reasonably wonder which one it meant.
  const ARC_SLOT_LABEL = { top: 'Top arch text', bottom: 'Bottom arch text' };
  // The GRIP plate names its three gaps by WHERE THEY SIT, not by their slot keys, and drops the word
  // "arch" (Ryan 2026-07-30: *"placeholder text in 'Top arch text' should read 'Upper text'"*). Two
  // reasons it is per-product rather than a global rename: on a bumper "Top arch" is exactly right and
  // has been since launch, and on a grip plate the word is wrong twice over — Tri-Word's lower gaps
  // are not arched at all, and "Left" alone would point a buyer at 9 o'clock, which on that face is a
  // hand cutout rather than a place to put anything.
  const ARC_SLOT_LABEL_GRIP = { top: 'Upper text', left: 'Lower left text', right: 'Lower right text' };
  // The cap's Sidelines layout runs words DOWN THE SIDES of the face, which is not an arch and is
  // not upper/lower either — so it names them for where they sit, as the grip's do (2026-07-30).
  const ARC_SLOT_LABEL_CAP = { top: 'Top arch text', bottom: 'Bottom arch text', left: 'Left side text', right: 'Right side text' };
  const arcSlotLabel = (slot) => {
    const model = modelFor(state.ui.product);
    const map = model === 'grip' ? ARC_SLOT_LABEL_GRIP
      : (model === 'dumbbell' || model === 'barbell') ? ARC_SLOT_LABEL_CAP : ARC_SLOT_LABEL;
    return map[slot] || 'Arch text';
  };
  /** The character cap for arc text — ONE number, every product (Ryan 2026-07-30). The grip plate
   *  had its own 22 against the bumper's 24, so a line one product's field accepted was a line the
   *  other's would not let you extend. See TEXT_MAX_LENGTH: the cap is taste, and the thing that
   *  actually keeps a name on the plate is the fit, which no longer truncates on any product. */
  const arcMaxLen = () => String(TEXT_MAX_LENGTH);
  // The layout a line typed on a TEXT-LESS design promotes into (Ryan 2026-07-26) — the same
  // twin-arch preset smartStart picks when a name arrives on the Add Logo tab, so "I gave it a
  // name" lands in the same place whichever screen it was typed on.
  const ARCH_PROMOTE_PRESET = 'logo-l-weight-r-arch-tb';
  // …and the grip plate's own, since its three gaps can't wear a bumper preset.
  // ⚠ THE ID, NOT THE NAME. This is the layout DISPLAYED as "Tri-Arch"; the preset that used to hold
  // the id `grip-tri-arch` was deleted on 2026-07-30 and pointing here at the name would resolve to
  // `LAYOUT_PRESETS[0]` — a bumper layout — and quietly turn a grip plate into a 450mm disc the first
  // time someone typed a school name on the Customize tab.
  const ARCH_PROMOTE_PRESET_GRIP = 'grip-tri-word';
  /**
   * Does the design in front of the buyer have arched text to edit?
   *
   * Was `layoutFamilyFor(product) === 'plate'`, which answered a DIFFERENT question — "could this
   * product ever have arcs" — and was right only for as long as the bumper was the only product with
   * any. The grip plate's Tri- layouts (2026-07-30) put arc text on a `grip` family, so the product
   * test hid the Wording fields on a plate that was visibly wearing three words.
   *   Asking the LAYOUT is also strictly more honest for the bumper: a Badge has no arcs either, and
   * that case was already handled one layer down by `archTextOfferFields`.
   */
  const familyHasArcs = () => state.designProfile.elements.some((e) => e.type === 'arctext')
    || layoutFamilyFor(state.ui.product) === 'plate';
  function archTextFields() {
    const arcs = state.designProfile.elements.filter((e) => e.type === 'arctext');
    if (!arcs.length) return archTextOfferFields(); // Badge / Minimal have no arc slots — offer them
    // Down the plate, not in element order: top first, then the grip plate's lower-left and
    // lower-right gaps in the order a reader meets them. `bottom` shares rank 1 with `left` because
    // no layout has ever had both — the bumper's arcs are top/bottom, the grip's top/left/right.
    const order = { top: 0, bottom: 1, left: 1, right: 2 };
    arcs.sort((a, b) => (order[a.slot] ?? 3) - (order[b.slot] ?? 3));
    return [
      subHead('Wording', '10px 0 6px'),
      // No per-field caption (Ryan 2026-07-25). Which arc is which is only ever a question when a
      // field is EMPTY — full ones say what they are by their own content — so the caption is the
      // placeholder. Two sub-heads for two inputs was just repeating the section header twice.
      // `wd-keep-typing`: the two arcs are each other's counterpart, so both survive the phone's
      // typing-mode collapse while the 'Wording' sub-head above them goes (main.js, TYPING MODE).
      ...arcs.map((el) => h('input', {
        type: 'text', class: 'wd-arctext-field wd-keep-typing', maxlength: arcMaxLen(), value: el.text || '',
        placeholder: arcSlotLabel(el.slot), 'aria-label': arcSlotLabel(el.slot),
        ...NO_AUTOFILL,
        // Look comes from the shared .wd-arctext-field rule, which .wd-font-search reads too — so the
        // Wording boxes and the font search are identical by construction, not by two copies of the
        // same hexes (Ryan 2026-07-25).
        'data-arctext': el.id,
        onInput: (e) => {
          const v = e.target.value;
          // ── THE WORD'S LENGTH IS THE ROOM (Ryan 2026-07-28) ─────────────────────────────────────
          // "go from Denver to Denver Broncos and the font naturally gets smaller (good), but go back
          // to Denver and it stays small while the size still reads 100%."
          //   Arc text is fitted to its ARC SPAN, so the longest scale that changes anything moves
          // with the number of characters — a long word walls out early, a short one runs to the top
          // of the dial. That wall IS the room, exactly as the number styles were, and `sizeScale` is
          // an absolute multiplier that means different things on either side of it. Nothing re-pegged
          // it when the words changed, so a scale seeded against "Denver Broncos" kept capping
          // "Denver" long after the reason had gone.
          //   And the READOUT went stale with it: the dial caches its edge, typing deliberately never
          // rebuilds the panel (that would tear out the field being typed in), so it went on dividing
          // by the long word's wall and reporting a confident 100%.
          //   Same share-preserving conversion the number styles use, so a hand-sized arc keeps its
          // own percentage rather than being reset to the default.
          const sizeOpts = { product: modelFor(state.ui.product), weight: state.ui.weight };
          const beforeEdge = sizeEdgeFor(state.designProfile, 'arctext', sizeOpts);
          const arcs = textWeightEls().filter((x) => x.type === 'arctext');
          const shares = beforeEdge > 0 ? arcs.map((x) => (x.sizeScale ?? 1) / beforeEdge) : null;
          updateElement(state, el.id, { text: v, textUserSet: v.trim() !== '' });
          if (shares) {
            const afterEdge = sizeEdgeFor(state.designProfile, 'arctext', sizeOpts);
            if (afterEdge > 0) {
              arcs.forEach((x, i) => {
                const next = Math.round(Math.min(ARC_TEXT_SCALE_DIAL_MAX, shares[i] * afterEdge) * 1000) / 1000;
                if (next > 0) x.sizeScale = next;
              });
            }
          }
          // Re-probe the dial against the new wall. `setValue` exists for exactly this and says so in
          // its own comment; it simply had no caller on this path.
          if (arcSizeStepper) arcSizeStepper.setValue(textScaleOf('arctext'));
          syncArcInputs(el.id, v, e.target);
          // WRITE BACK to the identity the Add Logo tab shows (Ryan 2026-07-25). The two surfaces
          // edit the same words from opposite ends: tab 2 asks for an Organization and a Mascot and
          // derives the arcs; here you edit the arcs directly. Only the first direction was wired,
          // so a line changed here left tab 2 still showing the original name — two screens
          // disagreeing about the same plate. Top arch is the organisation, bottom the mascot,
          // matching how deriveArcText lays them out in the first place. Last edit wins, whichever
          // side it came from.
          // Mirror BOTH lines every time, not just the one being typed in. Writing only the edited
          // field left the other half of the identity stale: delete "Windows" from the bottom arch
          // and `mascot` cleared, but `orgName` still said "Microsoft Windows" — the whole name that
          // deriveArcText had split across the two arcs. The Add Logo tab kept showing the deleted
          // word, and worse, anything that re-derives from the identity (a layout switch, a preset
          // preview with one arch, a resume) put it straight back on the plate.
          //   The rule is simply: the identity IS what the arches say. Read both, write both.
          syncIdentityFromArcs();
          // The layout thumbnails render the buyer's ACTUAL design, so they have to re-draw when the
          // words change. `valueCommit` only repaints the plate + files history — it deliberately
          // doesn't rebuild the panel, because that would tear down the very field being typed in.
          // So refresh the thumbs in place instead: no input is touched, focus and caret survive.
          scheduleThumbRefresh();
          valueCommit();
        },
      })),
    ];
  }

  /**
   * The Wording fields for a design that has NO arc-text slots — the buyer skipped "Who's this
   * for?" on the Add Logo tab, so smartStart opened them on Fieldhouse (logo + weight, no text).
   *
   * They are shown anyway (Ryan 2026-07-26): "in case they forgot or missed the opportunity to do
   * so on the previous tab". Hiding them made Customize Design a dead end for the one thing it is
   * named after — from here there was no way to add a school name at all, short of finding the
   * layout picker and guessing which of sixteen layouts has room for words. The controls a design
   * COULD have belong on the panel; whether they currently do anything is what greying is for.
   *
   * These two can't be greyed, because they are the way OUT of that state: typing promotes the
   * design into the twin-arch layout and drops the line onto that arc.
   */
  function archTextOfferFields() {
    // ⚠ THE DUMBBELL AND FIXED BARBELL USED TO BE EXCLUDED HERE, on the grounds that their family had
    // "no arched-text layouts at ALL, so there is nothing to offer and nothing to promote into". That
    // stopped being true on 2026-07-30: Crest, Signet and Sidelines all carry words. The offer now
    // follows `familyHasArcs()`, which asks the LAYOUT rather than the product — the same correction
    // that function's own comment records having made once already.
    //
    // ⚠ AND THE GRIP PLATE IS BACK IN THAT CATEGORY (Ryan 2026-07-30: *"when we're on the grip plate
    // on a layout WITHOUT text on it, the 3 wording boxes don't need to be displayed"*). It briefly
    // offered three, added by inference when its Tri- layouts shipped — he never asked for them.
    //   This is NOT the same call as the bumper's, and the difference is real rather than an
    // inconsistency: the bumper's `smartStart` can land a buyer on a text-less Fieldhouse without
    // their ever having seen a wording field, so those offers are the only way IN (2026-07-26). On a
    // grip plate the layout picker is right there, its Tri- layouts are named for their text, and
    // nothing steers anyone onto Classic Grip by accident.
    if (!familyHasArcs()) return [];
    return [
      subHead('Wording', '10px 0 6px'),
      ...['top', 'bottom'].map((slot) => h('input', {
        type: 'text', class: 'wd-arctext-field wd-keep-typing', maxlength: arcMaxLen(), value: '',
        placeholder: arcSlotLabel(slot), 'aria-label': arcSlotLabel(slot),
        ...NO_AUTOFILL,
        onInput: (e) => promoteToArchText(slot, e.target.value, ARCH_PROMOTE_PRESET),
      })),
    ];
  }
  /**
   * Give a text-less design somewhere to put the words that were just typed.
   *
   * The sibling arc is left EMPTY rather than carrying the preset's seeded "GOES HERE" — one line
   * was asked for, so one line is what appears — unless the buyer's stored identity re-seeded a
   * real word into it on the way through (they named the plate, then chose a Badge layout, and are
   * now typing their way back to text; that word is theirs and stays).
   *
   * Rebuilding the panel is what swaps these placeholder inputs for the real per-element ones, so
   * focus has to be handed to the field that replaces the one being typed in — otherwise the second
   * character of a school name goes nowhere.
   */
  function promoteToArchText(slot, value, preset = ARCH_PROMOTE_PRESET) {
    if (!setLayoutPreset(state, preset)) return;
    const arcs = (state.designProfile.elements || []).filter((e) => e.type === 'arctext');
    for (const e of arcs) {
      if (e.slot === slot) { e.text = value; e.textUserSet = value.trim() !== ''; }
      else if (isPlaceholderText(e.text)) { e.text = ''; }
    }
    syncIdentityFromArcs();
    structuralCommit();
    const mine = arcs.find((e) => e.slot === slot);
    const field = mine && root.querySelector(`[data-arctext="${CSS.escape(String(mine.id))}"]`);
    if (field) {
      field.focus();
      const n = field.value.length;
      try { field.setSelectionRange(n, n); } catch { /* not a text input in some browsers */ }
    }
  }

  // The same line is editable in two places (here and the Advanced piece card), so whichever one is
  // typed into pushes the value to the other. Without this the second field keeps whatever it was
  // built with and silently overwrites on its next keystroke.
  /**
   * Keep `orgName` / `mascot` equal to what the arches actually read. They are two views of the
   * same words — tab 2 collects a name and a mascot and derives the arches; the Wording fields edit
   * the arches directly — so the profile must not carry a version of them that is no longer on the
   * plate. A layout with only ONE arch re-derives from `orgName`, which is exactly where a stale
   * value resurfaces as the full "Microsoft Windows" on a single arch.
   */
  /**
   * ⛔ THE ARCS SEED THE IDENTITY; THEY NEVER OVERWRITE ONE (Ryan 2026-07-30: *"when the wording is
   * changed on this model, it will affect the wording on bumper and grip plate models too — when it
   * shouldn't"*).
   *
   * This wrote the buyer's arc text straight into `orgName` / `mascot`, which are SHARED by every
   * product. That was correct when the bumper was the only face with lettering — "the identity IS
   * what the arches say", 2026-07-25 — and it silently became a cross-product leak as the grip plate
   * and then the cap grew arcs of their own: every family without its own remembered wording
   * re-derives from the identity on arrival, so retyping one word on a dumbbell rewrote the bumper
   * and the grip plate on the next hop.
   *
   * ⚠ `savedArcText` DOES NOT COVER THIS, which is worth being precise about because it looks as
   * though it should. It remembers a line a family was GIVEN and left behind — and it works: the
   * state-level round trip is provably clean. What leaked was upstream of it, in the shared SOURCE
   * those families derive from when they have nothing remembered, which is most of the time (a
   * derived line is deliberately not remembered — see `applyLayoutPreset`).
   *
   * So the rule is now: **the Wording fields edit this product's lettering, and the Add Logo tab's
   * Organization / Mascot fields edit the identity.** The one exception is a buyer who never filled
   * those in — typing into Wording still SEEDS them, so the way in from that screen is unchanged and
   * nothing downstream has an empty name to work from.
   *
   * The cost, stated rather than buried: editing a bumper arch no longer updates the Organization
   * field on the Add Logo tab. That mirror was the 2026-07-25 ask, and it cannot coexist with
   * per-product wording — one of them has to give, and Ryan has now asked for the other twice.
   */
  function syncIdentityFromArcs() {
    // Empty means empty on BOTH halves: a buyer who typed only an organisation still has a mascot
    // slot the arcs must not fill in for them.
    const p0 = state.designProfile;
    const seeded = String(p0.orgName || '').trim() !== '' || String(p0.mascot || '').trim() !== '';
    if (seeded) return;
    const arcs = (state.designProfile.elements || []).filter((e) => e.type === 'arctext');
    const top = arcs.find((e) => e.slot === 'top');
    const bot = arcs.find((e) => e.slot === 'bottom');
    // ── THREE GAPS READ BACK AS A NAME, NOT AS A NAME-AND-A-MASCOT (2026-07-30) ────────────────────
    // The identity is a PAIR and the grip plate's Tri- layouts are a TRIPLE, so there is no honest
    // 1:1 mapping. Joining all three into `orgName` and leaving the mascot alone is the reading that
    // round-trips: `deriveArcTextTri` cuts a name three ways, so the join is its exact inverse and
    // typing across the gaps then hopping layouts returns the same words.
    //   Guessing instead — say, third gap → mascot — would be wrong the moment someone spells out
    // "DENVER / NORTH CITY / HIGH SCHOOL", turning "High School" into their mascot and then printing
    // it on the bottom arch of every bumper layout they visit afterwards.
    if (arcs.length === 3 && top && !bot) {
      const said = ['top', 'left', 'right']
        .map((s) => String((arcs.find((e) => e.slot === s) || {}).text || '').trim())
        .filter((t) => t && !isPlaceholderText(t));
      // DEDUPED, which is what makes a MIRRORED group read back as one line instead of three.
      // Tri-Arch repeats the same words in every gap, so joining them verbatim would set the
      // organisation to "Arcadia Arcadia Arcadia" — and the Add Logo tab would then show it, and the
      // next layout change would derive from it. On Tri-Word the three gaps genuinely differ, so
      // nothing is dropped there and the join is unchanged.
      const joined = [...new Set(said)].join(' ');
      if (joined) state.designProfile.orgName = joined;
      return;
    }
    let name = top ? String(top.text || '') : String(state.designProfile.orgName || '');
    let mascot = bot ? String(bot.text || '') : String(state.designProfile.mascot || '');
    // ⚠ THE PAIR MUST NOT CONTAIN THE SAME WORDS TWICE (Ryan 2026-07-28: "the only text I input
    // ANYWHERE was 'Denver Broncos', but… I'm looking at a Varsity Arched theme where it says
    // 'Denver Broncos Broncos'").
    //   The arcs and the identity are not the same shape. The identity is a PAIR — an organisation
    // and a mascot — while the arcs are wherever those words happened to land. Type the whole name
    // into the TOP field while the bottom still holds the auto-split's second half and this reads
    // back `{ name: "Denver Broncos", mascot: "Broncos" }`: a mascot that is the TAIL of its own
    // name. Nothing downstream can tell that apart from a genuine pair, so every later join prints
    // the word twice — and the one-arc layouts join, which is how a duplicate reaches the plate
    // without anyone typing it. The same happens with `bot` absent entirely, where the old code left
    // a stale mascot beside a name that had just swallowed it.
    //   BOTH DIRECTIONS. Ryan's actual plate read "Denver Denver Broncos", which is `"Denver"` +
    // `"Denver Broncos"` — the NAME a prefix of the MASCOT, the mirror of the case above. Whichever
    // half swallowed the other, the longer one is the real identity and the shorter is its echo, so
    // the rule is symmetric: keep the longer, drop the other. The split re-derives the missing half
    // on the next two-arc layout, and the buyer's own arc text is untouched either way (`textUserSet`
    // outranks all of this). Word-boundary matched, so "Broncos" cannot clear itself out of
    // "Superbroncos".
    const n = name.trim().toLowerCase();
    const m = mascot.trim().toLowerCase();
    if (n && m && n !== m) {
      if (n.endsWith(` ${m}`)) mascot = '';               // "Denver Broncos" / "Broncos"
      else if (m.startsWith(`${n} `)) { name = mascot; mascot = ''; } // "Denver" / "Denver Broncos"
    }
    state.designProfile.orgName = name;
    state.designProfile.mascot = mascot;
  }

  /**
   * Re-draw every layout thumbnail currently on screen — the pane's carousel slides and, if it is
   * open, the View-all grid. Debounced: rendering a thumb renders the whole design to SVG, and doing
   * that per keystroke would be wasteful while someone is mid-word. Targets `[data-wd-thumb]`
   * wherever it lives, so it needs no handle on either component.
   */
  /**
   * Mark every layout tile on screen locked or unlocked, wherever it lives.
   *
   * Both pickers stamp `data-wd-thumb="<presetId>"` on their tiles, so one pass over that attribute
   * reaches the carousel's slides and the View-all grid's cards alike — and neither needs a handle on
   * the other. That shared selector is also what guarantees they agree: a layout greyed in one place
   * and live in the other is worse than either answer on its own.
   *
   * ONLY the class and the reason line are touched, never the drawing — the thumbnail of a locked
   * layout is still the right picture of what it would look like, and seeing it is most of the reason
   * to want it.
   */
  function paintLayoutLocks(scope) {
    const where = scope || (root.closest && root.closest('.weight-designer')) || document;
    where.querySelectorAll('[data-wd-thumb]').forEach((el) => {
      const id = el.getAttribute('data-wd-thumb');
      if (!id) return;
      const gate = presetEligibility(id, state.designProfile);
      el.classList.toggle('is-locked', !gate.ok);
      if (gate.ok) el.removeAttribute('aria-disabled'); else el.setAttribute('aria-disabled', 'true');
      if (!gate.ok) el.setAttribute('title', gate.reason); else el.removeAttribute('title');
      const line = el.querySelector('.wd-layout-card__locked');
      if (line) line.textContent = gate.ok ? '' : gate.reason;
    });
  }

  /**
   * Send the buyer to the thing that would unlock the layout they just tapped.
   *
   * A locked card that merely refuses is a dead end — it says what is missing and leaves them hunting
   * for where to supply it. The Text section's first Wording box IS that place, so the tap opens the
   * panel there and puts the caret in it.
   */
  function focusWording() {
    const field = root.querySelector('.wd-arctext-field');
    if (!field) return;
    if (field.scrollIntoView) field.scrollIntoView({ behavior: 'smooth', block: 'center' });
    try { field.focus({ preventScroll: true }); } catch (_) { field.focus(); }
  }

  let thumbRefreshTimer = null;
  function scheduleThumbRefresh() {
    clearTimeout(thumbRefreshTimer);
    thumbRefreshTimer = setTimeout(() => {
      const scope = (root.closest && root.closest('.weight-designer')) || document;
      // WHICH layout the carousel is showing has to catch up too, not just the drawing inside it
      // (Ryan 2026-07-26) — emptying an arch changes the composition, and the composition IS the
      // selection. Do it first: a rebuilt window draws its own slides, so the loop below skips them
      // rather than rendering four SVGs (each embedding the logo) a second time.
      const rebuilt = resyncLayoutPicker ? resyncLayoutPicker() === true : false;
      // …and so does WHICH layouts are available at all (2026-07-30). Eligibility reads the wording,
      // and this timer is the one that already fires on every keystroke into it — so typing the third
      // word of a school name is what un-greys the grip plate's Tri- layouts, live, without the buyer
      // having to close and reopen the picker to discover that it worked.
      paintLayoutLocks(scope);
      scope.querySelectorAll('[data-wd-thumb]').forEach((el) => {
        if (rebuilt && el.classList.contains('wd-layout-slide')) return; // just drawn
        const id = el.getAttribute('data-wd-thumb');
        // Skip the ones that haven't been built yet — both the carousel and the View-all list fill
        // those in lazily as they scroll into range, and they'll pick up the new text when they do.
        if (!id || !el.dataset.wdDrawn) return;
        // ⚠ AND SKIP THE ONES NOBODY CAN SEE. The carousel now holds all sixteen slides rather than a
        // window of four, so once a buyer has scrolled the whole strip this loop would re-render every
        // one of them — sixteen full designs, each embedding the logo — on a 450ms fuse WHILE THEY ARE
        // TYPING. A slide well outside the window keeps its stale drawing until it is next approached,
        // which nobody can perceive and which costs nothing.
        const r = el.getBoundingClientRect();
        if (r.right < -240 || r.left > window.innerWidth + 240) return;
        drawThumbInto(el, id);
      });
    }, 450);
  }

  function syncArcInputs(id, value, except) {
    root.querySelectorAll(`[data-arctext="${CSS.escape(String(id))}"]`).forEach((i) => {
      if (i !== except && i.value !== value) i.value = value;
    });
  }

  // Lettering case (ALL CAPS / As typed). PROMOTED out of Advanced into the basic Font section
  // (Ryan 2026-07-24): it's a typography decision, so it belongs beside the typeface rather than two
  // clicks deeper — and the Weight section already sets the pattern this mirrors (sub-head + segmented
  // toggle, sub-head + grid). Returns just the toggle; the Font section owns the heading.
  // Sized like the LB/KG toggle (--lg), but NOT --gridalign: there's no tile grid under it to line up
  // with, so that width maths would be meaningless here.
  function letteringCaseToggle() {
    const capsWrap = h('div', { class: 'wd-unit-toggle wd-unit-toggle--lg', role: 'group', 'aria-label': 'Lettering case' });
    const renderCaps = () => {
      const on = state.designProfile.allCaps !== false;
      replaceChildren(capsWrap, [['ALL CAPS', true], ['As typed', false]].map(([label, val]) =>
        h('button', {
          type: 'button',
          class: 'wd-unit-btn' + (val === on ? ' is-active' : ''),
          'aria-pressed': val === on ? 'true' : 'false',
          onClick: () => { if ((state.designProfile.allCaps !== false) === val) return; setAllCaps(state, val); renderCaps(); valueCommit(); },
        }, label)));
    };
    renderCaps();
    return capsWrap;
  }

  // ── layout presets ───────────────────────────────────────────────────────────
  // Render a 2D drawing of the buyer's CURRENT design arranged in `presetId`, so
  // each layout choice previews what THEIR plate looks like in that arrangement
  // (Ryan 2026-07-13 — visual layout picker, GLD "Platforms" style). Cloned so we
  // never mutate the live profile; a per-preset uid keeps SVG def ids from
  // colliding when several thumbnails render on the page at once.
  /**
   * Draw a preset's thumbnail into a tagged tile, keeping anything the tile wears ON TOP of it.
   *
   * ⚠ THE THUMBNAIL IS THE TILE'S CONTENT, BUT IT IS NOT ALL OF IT (Ryan 2026-07-28: "the card is
   * still blue but the checkmark is gone"; then again on a resume). A carousel slide carries its
   * selected tick as a CHILD, and `innerHTML =` deletes every child — while `is-selected`, being a
   * class on the node, survives untouched. So the blue stayed and the tick did not.
   *
   * THREE separate places redraw these tiles, and the first fix only taught one of them. That is why
   * this is a function and not a guard: the tick's survival cannot depend on which caller happened to
   * redraw it. Overlays declare themselves with `data-wd-keep` rather than being listed here, and the
   * same NODES are re-appended, so a tile can gain a badge or a lock later without anyone revisiting
   * this — or the two callers nobody would think to check.
   */
  /* ── THE ONE GATE (Ryan 2026-08-02) ─────────────────────────────────────────────────────────────
   * `thumbsBusy` is set by main.js across a product hand-off, because building one of these is a
   * synchronous SVG render with the buyer's logo embedded — "tens of milliseconds", per the carousel's
   * own measured note — and the hand-off needs those frames for a 420ms spin out, a synchronous
   * geometry rebuild and a 1s settle in.
   *
   * ⛔ IT GATES HERE, AT THE FUNNEL, BECAUSE FIVE CALLERS REACH IT: the carousel drain, the View-all
   * drain, `scheduleThumbRefresh`'s 450ms timer, a slide's no-observer fallback, and `refreshThumbs`.
   * Gating only the carousel drain measured 0 renders inside the animation on one run and 1–2 on the
   * next three — the timer and `resyncLayoutPicker` were walking straight past it.
   *
   * ⚠ DEFERRED, NEVER DROPPED. A refused tile is remembered and drawn on release, so a card cannot be
   * left blank or — worse — stale from the PREVIOUS product, which is what skipping would have done
   * to every tile whose `wdDrawn` was already set.
   */
  let thumbsBusy = false;
  const deferredThumbs = new Map();   // el → presetId, deduped by node, newest id wins
  let resumeThumbDrain = () => {};
  function setThumbsBusy(on) {
    const next = !!on;
    if (next === thumbsBusy) return;
    thumbsBusy = next;
    if (thumbsBusy) return;
    // Release: redraw what was refused, ONE PER FRAME. Doing them in a burst here would move the
    // stall rather than remove it — straight into the frame after the settle, which is exactly the
    // "all reappearing at once" Ryan called out on the denomination ladder (changelog 204).
    const pending = [...deferredThumbs.entries()];
    deferredThumbs.clear();
    const step = () => {
      const entry = pending.shift();
      if (!entry) { resumeThumbDrain(); return; }
      const [el, id] = entry;
      if (el.isConnected) drawThumbInto(el, id);
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  function drawThumbInto(el, presetId) {
    if (thumbsBusy) { deferredThumbs.set(el, presetId); return; }
    const overlays = [...el.children].filter((c) => c.hasAttribute('data-wd-keep'));
    el.innerHTML = presetThumbSVG(presetId);
    if (overlays.length) el.append(...overlays);
    // "This tile has a drawing in it" — the one honest test now that a tile can hold an overlay
    // BEFORE it holds a thumbnail. A carousel slide carries its tick from birth, so `firstChild` says
    // yes about a slide that has never been drawn, and a redraw pass would have built every one of
    // them on the spot — defeating the lazy build it was written alongside.
    el.dataset.wdDrawn = '1';
  }
  /* ══ THE THUMBNAIL LOGO — ONE SMALL SHARED RASTER (Ryan 2026-08-02) ═══════════════════════════
   * Measured on a *17KB* test crest: **77% of every thumbnail SVG string was the logo**, re-embedded
   * as base64 in each card (29KB of which 22KB was image). A real school crest is 1–3MB, at which
   * point each drawn card is a multi-megabyte string the browser must parse, and each one decodes
   * its own private copy of a 1000px+ PNG in order to paint it at roughly forty pixels. The lazy
   * drawing keeps that to the ~6 visible cards rather than all 48, which is the only reason it has
   * been survivable.
   *
   * So thumbnails get ONE downscaled raster, referenced by a `blob:` URL: one decode, one copy in
   * memory, and the markup per card collapses to the ~7KB that is actually geometry and text.
   *
   * ⛔ THUMBNAILS ONLY. NEVER THE DEFAULT RENDER PATH. Two independent reasons, either of which is a
   * shipped bug on its own:
   *   · a `blob:` URL is scoped to this document and is DEAD the moment the SVG leaves it — the
   *     design-sheet PDF, the packet plates, the OG image and the share render all serialise the
   *     string and use it elsewhere, and would resolve no logo at all;
   *   · the raster is deliberately soft, and a 128px crest in a customer's proposal PDF is worse
   *     than a slow one.
   * This lives here, applied to the CLONE `presetThumbSVG` already makes, precisely so it cannot
   * reach `renderDesignToSVGString`'s other callers. Do not "promote" it into the engine.
   *
   * ⚠ ASYNC, WITH AN HONEST FALLBACK. Downscaling needs an image decode, so the first draw after a
   * logo lands uses the full data URL exactly as before and the thumbs are refreshed once the small
   * one exists. Never a blank card, never a wait.
   */
  const THUMB_LOGO_PX = 128; // the card paints it at ~40px; 128 covers 2-3x DPR with room to spare
  let thumbLogo = { key: null, url: null };
  function ensureThumbLogo(src) {
    if (!src) return null;
    if (thumbLogo.key === src) return thumbLogo.url;   // ready, or pending (null) — either is fine
    // A new logo (or a new peel/recolour composite) — the old object URL is now garbage. Revoking
    // is not optional: without it every upload leaks a full-size raster for the life of the session.
    if (thumbLogo.url) URL.revokeObjectURL(thumbLogo.url);
    thumbLogo = { key: src, url: null };
    const pendingKey = src;
    const img = new Image();
    img.onload = () => {
      // A later logo landed while this was decoding — its result is stale, drop it on the floor.
      if (thumbLogo.key !== pendingKey) return;
      try {
        const scale = Math.min(1, THUMB_LOGO_PX / Math.max(img.width || 1, img.height || 1));
        const w = Math.max(1, Math.round((img.width || 1) * scale));
        const h = Math.max(1, Math.round((img.height || 1) * scale));
        const cv = document.createElement('canvas');
        cv.width = w; cv.height = h;
        const cx = cv.getContext('2d');
        cx.imageSmoothingQuality = 'high';
        cx.drawImage(img, 0, 0, w, h);
        cv.toBlob((blob) => {
          if (!blob || thumbLogo.key !== pendingKey) return;
          thumbLogo.url = URL.createObjectURL(blob);
          // Redraw with the cheap raster. Debounced + off-screen-skipping + hand-off aware, so this
          // costs the visible cards once and nothing else.
          scheduleThumbRefresh();
        }, 'image/png');
      } catch { /* canvas tainted or OOM — the full data URL keeps working */ }
    };
    img.onerror = () => { /* keep the fallback; a broken raster is the render engine's problem */ };
    img.src = src;
    return null;
  }

  function presetThumbSVG(presetId) {
    // SHALLOW clone: applyLayoutPreset only REPLACES elements/savedLogo/layoutPreset
    // (it reads the old elements read-only and copies content into fresh objects — it
    // never mutates a shared element or the big base64 logo string). A shallow copy is
    // therefore safe AND avoids a full JSON deep-clone of the multi-MB logo data URL on
    // every thumbnail — which janked "View all" (15 clones) once a large logo was up.
    const clone = { ...state.designProfile };
    try { applyLayoutPreset(clone, presetId, { preserve: true }); } catch (_) { /* fall back to as-is */ }
    /* ⛔ AND THE OFFSETS THIS LAYOUT REMEMBERS (Ryan 2026-08-09: *"nudged it 10 clicks to the right,
     * went to the 2D SVG to look at it, and it was unchanged"*).
     *
     * 🔑 `applyLayoutPreset` REBUILDS the elements, and the nudge is deliberately not migrated any
     * more — it became a per-layout memory the moment Ryan asked for Sidelines to be unaffected by
     * what he does on Full Face. Correct for the live design and silently wrong here: the clone came
     * out with the offsets stripped, so a card could never show a nudge, on any layout, ever. It
     * looked like staleness and was not; a rebuild would have redrawn exactly the same wrong picture.
     *   Each card asks the store for ITS OWN preset's offsets, which is also the honest preview —
     * card 13 shows where you put things on Big Badge, card 14 where you put them on the Flip, and
     * the card for the layout you are on now matches the plate.
     *   ⚠ SAFE TO MUTATE: these are the fresh elements `applyLayoutPreset` just built on the clone,
     * never the live profile's — the shallow-clone note above is what guarantees that. */
    applyNudgeRecord(clone, nudgeRecordFor(state.designProfile, weightNudgeKey(state, { preset: presetId })));
    // Fill the freshly-seeded placeholder slots with the buyer's own name, exactly as picking this
    // preset for real would (Ryan 2026-07-25). applyLayoutPreset can only MIGRATE text out of pieces
    // the current layout has, so from a Badge (no text at all) every text preset previewed
    // YOUR TEXT / GOES HERE — and then showed the real name once picked. orgName/mascot persist
    // through a text-less layout, so the preview can derive from them the same way the plate does.
    // Re-seed from what the ARCHES SAY, not from the stored identity (Ryan 2026-07-25 — his own
    // diagnosis: "these 2D renders are keying off the text on the AL tab, whereas the actual 3D
    // model is keying off the text from the CD tab").
    //   `orgName`/`mascot` are the Add-Logo tab's version of these words and `reseedIdentityIntoPlaceholders`
    // derives from them, so a preset with ONE arch re-derived the whole "Microsoft Windows" onto it
    // long after the buyer had deleted "Windows" from the bottom arch. The plate never did that — it
    // renders the elements. Overriding the identity on the CLONE (never the real profile) points
    // both at the same source, so a thumbnail can only ever show words that are actually on the
    // design. Empty arches contribute nothing, which is what makes a deleted line stay deleted.
    const liveArcs = (state.designProfile.elements || []).filter((e) => e.type === 'arctext');
    const arcAt = (slot) => {
      const e = liveArcs.find((x) => x.slot === slot);
      return e && !isPlaceholderText(e.text) ? String(e.text || '') : '';
    };
    const topLive = arcAt('top');
    const botLive = arcAt('bottom');
    if (liveArcs.length === 3 && !botLive) {
      // Three gaps (grip Tri- layouts) — join what the plate SAYS into the name, the same reading
      // `syncIdentityFromArcs` takes and the exact inverse of `deriveArcTextTri`'s cut. Reading only
      // top+bottom here would have handed every thumbnail a one-word `orgName` and previewed a
      // three-gap layout with two gaps blank.
      clone.orgName = [...new Set(['top', 'left', 'right'].map(arcAt).filter(Boolean))].join(' ');
      clone.mascot = '';
    } else if (liveArcs.length) { clone.orgName = topLive; clone.mascot = botLive; }
    reseedIdentityIntoPlaceholders(clone);
    // The thumbnail must use the PRESET's own family, not the current product's — it
    // renders every preset in the picker, and each now draws its OWN 2D face (bumper
    // ring / dumbbell-barbell end-cap / grip cutouts) so a preset never advertises the
    // wrong product (Ryan 2026-07-21). The preset's `product` IS the layout family, and
    // family === the render-engine MODEL id (plate / grip / dumbbell) for chrome + zone.
    const family = layoutPresetProduct(getLayoutPreset(presetId));
    // The thumbnail's CHROME + body colour must match the CURRENT product's variant, not just
    // its family (Ryan 2026-07-22): the TRAINING bumper draws the economy face (no zinc hub),
    // the RUBBER dumbbell/barbell recesses the art, and a NON-DYEABLE product (everything but
    // the competition bumper) forces the plate black — so a competition dye never leaks into a
    // training/grip/dumbbell/barbell preview.
    /* ── SWAP IN THE SMALL SHARED RASTER (see ensureThumbLogo) ─────────────────────────────────
     * ⚠ ON THE CLONE'S ELEMENTS, VIA FRESH `logo` OBJECTS — never in place. `applyLayoutPreset`
     * copies element CONTENT into new element objects but the `logo` it carries is still the SAME
     * OBJECT the live design holds (that is the whole point of the shallow clone above: not deep-
     * copying a multi-MB data URL per thumbnail). Mutating `el.logo.dataUrl` here would therefore
     * replace the buyer's real artwork with a 128px stub — on the plate, in the PDF, in the packet.
     * Assigning a NEW object leaves the original untouched.
     *   `renderUrl` AND `dataUrl` both get it: the engine prefers `renderUrl` (the recoloured /
     * peeled composite) and falls back to `dataUrl`, so setting one and not the other would leave
     * the full-size copy reachable on exactly the designs that have a composite.
     */
    const liveLogo = (state.designProfile.elements || []).find((e) => e.type === 'logo' && e.logo);
    const smallSrc = liveLogo ? ensureThumbLogo(liveLogo.logo.renderUrl || liveLogo.logo.dataUrl) : null;
    if (smallSrc) {
      clone.elements = (clone.elements || []).map((e) => (
        e.type === 'logo' && e.logo
          ? { ...e, logo: { ...e.logo, dataUrl: smallSrc, renderUrl: smallSrc } }
          : e
      ));
    }
    const geom = geomOptsFor(state.ui.product) || {};
    return renderDesignToSVGString(clone, {
      size: 320,
      uid: `wd-thumb-${presetId}`,
      product: family || 'plate',
      /* ⚠ THE DENOMINATION, or the grip previews the wrong FACE (Ryan 2026-08-02). Everywhere else
         `weight` only moves the printed numeral, so leaving it to `p.weightValue` was harmless.
         The grip family is two different faces under one product — 2.5 and 5 lb are cutout-free —
         and the picker's own list is already filtered by this same weight, so without it the six
         small-grip presets were being drawn on a plate with three hand holes, with Four Corners
         placing art at `bottom`: a hand cutout on the face shown, a solid annulus on the real one. */
      weight: state.ui.weight,
      economy: geom.economy === true,
      rubber: geom.rubber === true,
      bodyColorOverride: productSupportsBodyColor(state.ui.product) ? undefined : DEFAULT_BODY_COLOR,
    });
  }

  function applyPreset(id) {
    // (WD-01) On a blank plate the default preset is "current" but nothing is
    // seeded — so always (re)apply rather than no-op on an equal id.
    // A real PICK, so it is worth remembering against every face that can wear it (see main.js's
    // `rememberLayoutChoice`). Recorded here and in `selectSlide` — the two places a buyer chooses
    // — rather than inside `setLayoutPreset`, which healing and product hops also go through.
    ctx.rememberLayoutChoice?.(id);
    setLayoutPreset(state, id);
    structuralCommit();
    ctx.announce?.(`Layout: ${getLayoutPreset(id).name}`);
  }

  // Full-window "view all" grid — every layout as a square with its name + one-line
  // descriptor, GROUPED by family (Arched · Minimal · Badge — Badge last) with sticky headers
  // (Fable §2.6). Click one to apply + close. Mounts into the scoped
  // .weight-designer root so the wd-* styles apply; Esc / scrim / ✕ close it.
  function openLayoutViewAll() {
    // Scoped to the live 3D product (Fable §9.1) — a bumper buyer never sees the dumbbell's set.
    // Hoisted above `cur` because the selection is derived FROM this list.
    // ⛔ THE ORDERED LIST, NOT THE RAW ONE. `layoutPresetsForProduct` returns them in the array's own
    // money-makers-first order; the GRID has to match the carousel, and the carousel uses
    // `orderedLayoutPresetsForProduct` (see the note at its own call site). Using the raw list here
    // is what let the two disagree about which heading comes first.
    const presets = orderedLayoutPresetsForProduct(layoutFamilyFor(state.ui.product), state.ui.weight);
    // Highlight what the plate LOOKS like, not what was last picked (Ryan 2026-07-25). Clearing one
    // arc-text turns a Varsity Arch into a Marquee on screen; the picker used to keep pointing at
    // Varsity Arch, which read as the tool disagreeing with its own preview. Falls back to the
    // stored id when the composition matches no preset exactly (e.g. a piece was removed by hand).
    const cur = matchPresetToComposition(state.designProfile, presets) || state.designProfile.layoutPreset;
    const mount = (root.closest && root.closest('.weight-designer')) || document.body;
    let scrim = null;
    const prevOverflow = document.body.style.overflow;
    // ── IT SLIDES, IT DOES NOT SNAP (Ryan 2026-07-28) ──────────────────────────────────────────
    // "opening and closing this menu on mobile is a snap… sort of like when you open and close the
    // cart. It's got a really cool open and close animation. Worth repurposing that code."
    //
    // Repurposed from the theme's own drawer (`main.css` — `.drawer` / `.drawer[open]`): a transform
    // on `cubic-bezier(0.19, 1, 0.22, 1)`, the ease-out-expo that gives the cart its fast-departure,
    // long-settle feel. Only the AXIS changes — the cart comes in from the right because it is a side
    // panel; this is a bottom sheet on a phone, so it comes up from below, which is also the direction
    // the pane it was launched from lives in.
    //
    // Two details are taken from the theme rather than reinvented, because both are the difference
    // between an animation and a flicker:
    //  · the panel must be MOUNTED and laid out before the open class lands, or there is no start
    //    value to move from — hence the rAF pair (one frame to attach, one to let style settle);
    //  · it must stay in the DOM for the whole of the CLOSING transition. The theme does this by
    //    delaying `visibility` 0.6s; here the element is removed by script, so the removal itself is
    //    what waits — `CLOSE_MS`, with the listeners and the body lock released IMMEDIATELY so a
    //    second Escape or tap can never land on a sheet that is already leaving.
    // Must outlast the LONGEST close transition. Removing the node early would cut the slide-out off
    // mid-flight — the snap this exists to remove, arriving on the way out instead of the way in.
    /* ⚠ 460 → 600 → 900 ON 2026-08-08, chasing the phone sheet's duration each time it moved. First
       Ryan asked it to slide *"using the same speed/style as the other menus"* (0.55s, so 460 would
       have cut it off ~90ms early); then, seeing that the shared DURATION still left this one visibly
       quicker — it translates a whole screen where the pane only grows ~412px — he asked for the
       shared SPEED, ~1.13s; then he tried that and settled on **0.8s**. This tracks whatever that
       lands on, with ~100ms of headroom.
         This is the timer that detaches the node after a close, so it must always outlast the longest
       close transition. Erring long is the safe direction: the extra time is spent on an off-screen
       node that is already pointer-inert with its listeners released, and desktop (0.38s) simply
       waits longer for something nobody can see.
       ⛔ IT LIVES IN A DIFFERENT FILE FROM THE DURATION IT TRACKS (`--wd-viewall-slide`,
       weight-designer.css). Both notes point at each other; retune them together or the sheet gets
       torn off the screen mid-slide, which is the exact defect the animation exists to prevent. */
    /* ⛔ MOVES WITH `--wd-viewall-slide` (0.8s → 0.4s on 2026-08-09, matching the help panel). This
       is the timer that detaches the node after a close: shorter than the animation and the sheet is
       torn off screen part-way down, which is the exact defect the animation exists to prevent,
       arriving on the way out. Kept a beat longer than the slide so a slow frame cannot clip it. */
    const CLOSE_MS = 500;
    let closing = false;
    /**
     * The part of closing that is the same whichever surface this opened on: stop building
     * thumbnails and drop the Escape listener. Split out because the desktop pane takeover below
     * shares every line of it and none of the scrim/body-lock teardown.
     *
     * ⚠ THE 3D IS NOT IN HERE, because the two surfaces disagree about WHEN it may resume — see the
     * note on `close`'s `after` parameter.
     */
    const releaseShared = () => {
      document.removeEventListener('keydown', onKey, true);
      if (thumbObserver) thumbObserver.disconnect();
      thumbQueue = [];        // stop any pending raster work the moment it goes
    };
    /**
     * Close, and then — once it is SAFE TO DO WORK — run `after`.
     *
     * That second half is the whole reason this takes a callback rather than the callers doing it
     * themselves, and the two surfaces answer "when is it safe" completely differently:
     *
     *  · THE DIALOG closes on `transform` + `opacity`, which the browser hands to the COMPOSITOR.
     *    Two frames is enough for the transition to be handed over; after that the main thread can
     *    block for as long as it likes and the close still plays out smoothly beside it.
     *  · THE PANE closes on `width`, which cannot be composited — it is laid out and painted on the
     *    main thread, every frame. So the same two frames buy nothing at all, and anything expensive
     *    running beside it does not merely compete with the animation, it stops it dead (Ryan
     *    2026-08-07: *"the pane goes to slide shut, it gets 80% there, then it freezes and stalls b/c
     *    something must be loading simultaneously"*). Its work waits for the slide to FINISH, which
     *    is a thing only the host can time — so it hands the callback on and main.js runs it.
     *
     * ⚠ `let`, and the cards close over the BINDING rather than the value. The pane-takeover branch
     * further down swaps this for its own close AFTER `layoutCard` has already built every card,
     * which is only safe because every reference resolves at click time.
     */
    let close = (after) => {
      if (!scrim || closing) return;
      closing = true;
      releaseShared();
      ctx.suspend3D?.(false); // the plate is visible again — let it breathe
      document.body.style.overflow = prevOverflow;
      document.body.classList.remove('wd-overlay-open'); // the hamburger comes back

      const leaving = scrim;
      scrim = null;                       // nothing else may act on it from here
      leaving.classList.remove('is-open');
      // Pointer-inert while it leaves, so a tap during the slide-out cannot hit a card behind it or
      // re-trigger anything inside it.
      leaving.style.pointerEvents = 'none';
      setTimeout(() => leaving.remove(), CLOSE_MS);
      // Two frames — the dialog's answer to "when is it safe to do work", per the note above.
      if (typeof after === 'function') requestAnimationFrame(() => requestAnimationFrame(after));
    };
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); close(); } };
    // Thumbnails are built LAZILY (Ryan 2026-07-25: "it loads slowly / lags as you scroll").
    //   `presetThumbSVG` renders the buyer's whole design to an SVG string, and that string embeds
    // their logo as a base64 data URL. Building all 16 up front therefore handed the browser 16
    // copies of a multi-MB raster to decode and rasterise before the overlay could paint — with a
    // big logo that is most of the delay, and it is why the wait scaled with logo size.
    //   An IntersectionObserver builds each thumb as its card approaches the viewport, with a
    // generous rootMargin so it is ready before it is seen. The first screenful still costs what it
    // costs; everything below the fold is spread across the scroll instead of paid for at open.
    //   Building is also DRAINED ONE PER FRAME rather than in the observer callback. Rendering a
    // thumb is synchronous and not cheap, so doing the whole visible screenful in one go blocked
    // the first paint — the dialog took ~2.6s to appear at all with a large logo. Yielding between
    // each lets the overlay paint immediately and the thumbs arrive over the next handful of
    // frames, which also keeps the main thread free while the buyer is scrolling.
    let thumbQueue = [];
    let draining = false;
    const drainThumbs = () => {
      const el = thumbQueue.shift();
      if (!el) { draining = false; return; }
      const id = el.getAttribute('data-wd-thumb');
      if (id && !el.firstChild && el.isConnected) drawThumbInto(el, id);
      requestAnimationFrame(drainThumbs);
    };
    const queueThumb = (el) => {
      thumbQueue.push(el);
      if (draining) return;
      draining = true;
      requestAnimationFrame(drainThumbs);
    };
    const thumbObserver = typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver((entries, obs) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          obs.unobserve(e.target);
          queueThumb(e.target);
        }
      }, { root: null, rootMargin: '300px 0px', threshold: 0 })
      : null;
    const layoutCard = (p) => {
      const thumb = h('span', { class: 'wd-layout-card__thumb', 'data-wd-thumb': p.id });
      // No observer (very old browser) → build it now, exactly as before.
      if (thumbObserver) thumbObserver.observe(thumb); else thumb.innerHTML = presetThumbSVG(p.id);
      const gate = presetEligibility(p, state.designProfile);
      if (!gate.ok) {
        return h(
          'button',
          {
            type: 'button',
            class: 'wd-layout-card is-locked',
            'aria-disabled': 'true',
            title: gate.reason,
            // NOT `disabled`. A disabled button fires no events, and this card's whole job when
            // tapped is to take the buyer to the thing that would unlock it — a dead card would
            // leave them re-tapping something that says what is wrong but not where to fix it.
            onClick: () => close(() => focusWording()),
          },
          thumb,
          h('span', { class: 'wd-layout-card__name' }, p.name),
          h('span', { class: 'wd-layout-card__label wd-layout-card__locked' }, gate.reason),
        );
      }
      return h(
        'button',
        {
          type: 'button',
          class: 'wd-layout-card' + (p.id === cur ? ' is-active' : ''),
          // ⚠ THE COMMIT WAITS FOR THE CLOSE, AND THAT IS THE WHOLE FIX (Ryan 2026-07-28: "when you
          // open it up and choose a card, it snaps shut again — it should smoothly animate/close
          // when you select an option").
          //   `close()` only asks for the animation; it is the next PAINT that starts it. Running
          // `applyPreset` in the same task meant `structuralCommit` — a panel rebuild plus the 3D
          // texture — blocked the main thread across that paint, so by the time anything reached the
          // screen most of the 380ms had already elapsed and only the last frames of the close were
          // ever shown. The sheet was animating perfectly; nobody was watching.
          //   HOW LONG TO WAIT IS THE SURFACE'S BUSINESS, NOT THIS BUTTON'S — a dialog needs two
          // frames (its close is composited), a pane needs the whole slide (its close is a layout
          // property). That is why the work is HANDED to `close` rather than scheduled here: this
          // handler used to own a `requestAnimationFrame` pair, which was silently the dialog's
          // answer being applied to both. See `close`.
          onClick: () => close(() => applyPreset(p.id)),
        },
        thumb,
        h('span', { class: 'wd-layout-card__name' }, p.name),
        h('span', { class: 'wd-layout-card__label' }, p.blurb),
      );
    };
    // ── ONE SECTION PER FAMILY, IN THE ORDER THE LIST IS ALREADY IN ────────────────────────────
    // ⛔ This walked `LAYOUT_FAMILIES` — the GLOBAL order — and filtered the presets into it, which
    // threw away the per-product ordering entirely. So the grid opened the grip plate on ARCHED and
    // the cap on ARCHED, while the carousel beside it opened both on the layout the product actually
    // uses (Ryan 2026-07-30: *"make the grip category appear before the arched category for grip
    // plates, and the minimal category before the arched category for dumbbells"* — he was reading
    // the grid, and the two pickers genuinely disagreed).
    //   Grouping RUNS of the ordered list instead means the order has exactly one source, which is
    // what the note at the carousel's call site has asked for since 2026-07-23. A heading can only
    // move by the presets moving.
    const sections = [];
    for (const preset of presets) {
      const last = sections[sections.length - 1];
      if (!last || last.family !== preset.family) sections.push({ family: preset.family, items: [preset] });
      else last.items.push(preset);
    }
    const sectionEls = sections.flatMap((sec) => [
      h('h3', { class: 'wd-layout-family' }, sec.family),
      h('div', { class: 'wd-layout-grid' }, sec.items.map(layoutCard)),
    ]);
    /* ══ DESKTOP: THE PANE *BECOMES* THE PICKER (Ryan 2026-08-07) ════════════════════════════════
     * *"should the left side pane slide out fully like it does when you go to the Request Quote tab,
     * and then all the existing stuff on it gets dropped for the layout cards instead… The 'x' button
     * can be in the top right corner to then close it when done, which results in the cards being
     * removed and it sliding back to its smaller size and the old buttons/toggles/cards re-rendering
     * back on it again? Just like when you go to the Request Quote tab from say the CD tab then back
     * again. Basically all the same information disseminated but with a different way of visualizing
     * it."*
     *
     * Exactly that, and the point of it is that NOTHING here is a new surface. A modal over a pane
     * that could simply BE the thing costs a second window's worth of chrome — a scrim, a dialog rim,
     * its own header and its own open/close animation — for content that already has a home. The app
     * has spent two sessions making the pane's width change read well (travel empty, freeze the body,
     * hold the thumbnail drain); the picker inherits all of it instead of maintaining a rival.
     *
     * ⛔ THE PHONE KEEPS THE FULL-SCREEN SHEET. There the pane IS a bottom sheet, so a takeover would
     * be indistinguishable from the surface it was launched from, and the slide-up is the motion Ryan
     * chose for it (2026-07-28). `ctx.canTakeOverPane()` is the host's own answer to "is there a
     * desktop-style pane here", so the two can never disagree about what desktop means.
     *
     * ⛔ THE EXIT IS UNCONDITIONAL, three ways over (memory: *never enter a screen whose exit is
     * conditional*), and not one of them depends on this content: the ✕ in the header, Escape, and
     * the step tabs — which live OUTSIDE `.ds-canvas` and so cannot be covered at any pane width.
     * `goToStep` drops a live takeover on its way through, so all three land in the same place.
     */
    if (ctx.canTakeOverPane?.()) {
      const paneBody = h('div', { class: 'wd-layout-inpane' }, sectionEls);
      // The pane header's OWN chrome (`.ds-side-panel-btn` + flat glass), not the dialog's square
      // chip: it is sitting in the pane's header row, where the collapse chevron would be — and the
      // chevron is `display: none` at full width, so this lands as the rightmost control.
      const closeBtn = h(
        'button',
        {
          type: 'button',
          class: 'ds-side-panel-btn wd-pane-close wd-flat-glass wd-flat-glass--flush',
          'aria-label': 'Close',
          title: 'Close',
          onClick: () => close(),
        },
        '✕',
      );
      const took = ctx.openPaneTakeover({
        title: 'Choose a layout',
        body: paneBody,
        headerAction: closeBtn,
        /* Fired by the host whenever the takeover ENDS — the ✕, Escape, or a step tab that dropped it
           out from under us. Teardown ONLY: `close` below is what ASKS for the pane back, and calling
           it from in here would ask a second time for a pane that is already on its way. */
        onClose: () => { if (!closing) { closing = true; releaseShared(); } },
        // Focus lands with the content, not at the start of the slide — focusing a card inside a
        // pane that is still 320px wide would scroll the grid it is about to stop being.
        onShown: () => {
          const first = paneBody.querySelector('.wd-layout-card');
          if (first) first.focus({ preventScroll: true });
        },
      });
      if (took) {
        /* `after` is handed straight on: only the host knows when its width transition has landed,
           and on a main-thread animation that is the ONLY moment a preset commit is free (see the
           note on `close`). The 3D is the host's too — it parked the stage on the way in and is the
           one thing that can be sure to un-park it however the takeover ends, including exits this
           picker never hears about, like a step tab. */
        close = (after) => {
          if (closing) return;
          closing = true;
          releaseShared();
          ctx.closePaneTakeover?.(after);
        };
        document.addEventListener('keydown', onKey, true);
        return;
      }
      /* Refused — the host asked twice and the viewport changed in between. Fall through to the
         dialog, which builds its own scroller from the SAME `sectionEls` and simply re-parents them
         out of the discarded `paneBody` on the way. Never left unshown. */
    }

    const scroller = h('div', { class: 'wd-layout-viewall__body' }, sectionEls);
    const head = h(
      'div',
      { class: 'wd-layout-viewall__head' },
      h('h2', { class: 'wd-layout-viewall__title' }, 'Choose a layout'),
      h('button', { type: 'button', class: 'wd-layout-viewall__close', 'aria-label': 'Close', onClick: close }, '✕'),
    );
    /* ── ON A PHONE THIS BAR IS A GRAB HANDLE (Ryan 2026-08-08) ─────────────────────────────────
     * *"allow the user to put their finger in the top middle and tap or swipe down in the same way
     * they can on other menus, to have it slide down that way too — just like they can with the
     * colour editing slide-up menus."*
     *
     * Deliberately the SAME shape as `color-picker.js`'s sheet head rather than a shared helper:
     * both are ~15 lines, and the two differ in the one place that matters — the picker must not let
     * a gesture become a second way to COMMIT, while this dialog has nothing to commit. Factoring
     * them together would mean parameterising that difference, which is more surface than the code
     * it saves. If a third sheet ever wants this, extract it then.
     *
     * ⚠ A TAP CLOSES TOO, and that is why `moved` exists: the ✕ is a 34px target in the corner of a
     * bar the thumb is already resting on, so the whole bar answers. The ✕ still owns its own click —
     * a press that starts on it is not the start of a swipe.
     * ⚠ DOWNWARD ONLY. There is no second state to swipe UP to, so an upward drag must do nothing
     * rather than close; `e.clientY - dragY` stays negative and never reaches the threshold.
     * `DRAG_COMMIT_PX` is 12 for the same reason the sheet raised it to 12 on 2026-08-09 — it decides
     * an OUTCOME on the first pixel past it, so a thumb rolling a few px while tapping must not
     * commit. */
    const VIEWALL_DRAG_COMMIT_PX = 12;
    let vaDragY = null;
    let vaMoved = false;
    head.addEventListener('pointerdown', (e) => {
      if (e.target.closest && e.target.closest('.wd-layout-viewall__close')) return;
      vaDragY = e.clientY;
      vaMoved = false;
      try { head.setPointerCapture(e.pointerId); } catch { /* not capturable — the swipe still works */ }
    });
    head.addEventListener('pointermove', (e) => {
      if (vaDragY == null) return;
      if (e.clientY - vaDragY < VIEWALL_DRAG_COMMIT_PX) return;   // still a tap, or heading up
      vaDragY = null;                                             // spent — the rest of the travel is inert
      vaMoved = true;
      try { head.releasePointerCapture(e.pointerId); } catch { /* already released */ }
      close();
    });
    head.addEventListener('pointerup', () => {
      if (vaDragY == null) return;   // a committed swipe already closed it
      vaDragY = null;
      if (!vaMoved) close();         // a tap on the bar is the same exit as the ✕
    });
    head.addEventListener('pointercancel', () => { vaDragY = null; });
    const panel = h(
      'div',
      { class: 'wd-layout-viewall', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Choose a layout' },
      head,
      scroller,
    );
    scrim = h('div', { class: 'wd-layout-viewall-scrim', onClick: (e) => e.target === scrim && close() }, panel);
    document.body.style.overflow = 'hidden';
    // Tells the page a full-screen overlay owns the screen. The phone CSS uses it to stand the
    // hamburger down — it lives on `<body>` at a near-maximum z-index, so it cannot be out-stacked
    // from in here, and once this sheet is full-bleed the two would sit in the same corner offering
    // different exits. Removed in `close()`, including the Escape and scrim-click paths.
    document.body.classList.add('wd-overlay-open');
    document.addEventListener('keydown', onKey, true);
    // The overlay covers the stage completely, so nothing behind it is worth a frame. Without this
    // the plate kept idle-swaying at full rate under an opaque panel, competing for the main thread
    // and the GPU with the very scrolling this dialog exists to do.
    ctx.suspend3D?.(true);
    mount.append(scrim);
    // Two frames: one for the mount to lay out at the closed position, one for that to be the
    // style the transition starts FROM. A single frame is enough on desktop and drops the animation
    // on a loaded phone, which is the case that asked for it.
    const opening = scrim;
    requestAnimationFrame(() => requestAnimationFrame(() => opening.classList.add('is-open')));
    const firstBtn = scroller.querySelector('.wd-layout-card');
    // ⚠ `preventScroll` — focusing a card inside a sheet that is still translated down makes the
    // browser scroll to reveal it, which fights the transform and lands the sheet part-scrolled.
    if (firstBtn) firstBtn.focus({ preventScroll: true });
  }

  // Set by layoutSection() on every build — the live carousel's "catch up with the plate" hook.
  // Panel-scope because the thing that moves the composition (typing in a Wording field) lives in
  // a different section entirely and must not rebuild the panel to make this happen.
  let resyncLayoutPicker = null;
  /* The live Layout section and the ELIGIBLE LIST it was built from — so a denomination change can
     tell whether the choice on offer actually moved. See `refreshForWeight`. Both re-set on every
     build, like `resyncLayoutPicker` above. */
  let layoutBoxEl = null;
  let layoutSectionKey = '';
  /** The ids the picker should be offering right now, as one comparable string. */
  const layoutKeyNow = () => orderedLayoutPresetsForProduct(layoutFamilyFor(state.ui.product), state.ui.weight)
    .map((p) => p.id).join(',');

  function layoutSection() {
    // A COMPACT PEEK carousel (Ryan 2026-07-23): the current layout sits large at the LEFT with the
    // NEXT layout peeking in from the right edge (mobile "Multi-Column" style) so it's obvious more
    // layouts are one arrow away. A pre-rendered 4-slide track translates by one step per arrow —
    // nothing re-rasters mid-slide — and the 3D + panel commit is DEFERRED to the END of the slide,
    // so the motion has the main thread all to itself (kills the old halfway hitch). Arrows flank
    // the "View all" button BELOW the preview. Infinite wrap via a window of [idx-1 … idx+2], so a
    // peek is present in BOTH directions during and after a slide. Scoped to the live 3D product.
    // Order MUST match the View-all grid (Ryan 2026-07-23) → shared orderedLayoutPresetsForProduct.
    const presets = orderedLayoutPresetsForProduct(layoutFamilyFor(state.ui.product), state.ui.weight);
    // Same derivation the View-all grid uses (Ryan 2026-07-25) — the pane's own preview card has to
    // agree with the plate too, or the buyer is looking at a Marquee while the card under it insists
    // it's a Varsity Arch. Emptying an arc-text is what moves it; typing the line back moves it home.
    const cur = matchPresetToComposition(state.designProfile, presets) || state.designProfile.layoutPreset;
    const idx = Math.max(0, presets.findIndex((p) => p.id === cur));
    const n = presets.length;

    const viewport = h('div', { class: 'wd-layout-viewport', role: 'button', tabindex: '0', 'aria-label': 'Choose this layout', title: 'Tap a layout to use it' });
    const track = h('div', { class: 'wd-layout-track' });
    viewport.appendChild(track);
    // Window of 4 real presets around the current one: [prev, current, next, next+1]. Slot 1 is
    // "home" (the big current preview at the left); slot 2 peeks on the right; slot 0 waits fully
    // off-canvas left (so a PREV click has something to bring in from that side).
    // The preview owns its OWN index (Ryan 2026-07-25). It used to read `idx` from the profile at
    // build time and only ever move by a full panel render(), so ONE picker instance could serve
    // exactly ONE slide: `sliding` latched true and was never cleared, and the only thing that made
    // the arrows live again was the rebuild at the end of the commit. That commit waited out the
    // 0.36s transition and then did the logo raster + 3D texture + panel rebuild — so the arrow was
    // genuinely dead until the 3D caught up. Ryan: "the 2D has to scroll fast regardless of what's
    // finished loading on the 3D."
    // ── IT IS A REAL SCROLLER NOW (Ryan 2026-07-28) ─────────────────────────────────────────────
    // The velocity flick was the small answer and he rejected it on feel: "I don't like it, go with
    // the bigger option." So the window, the transform and the hand-written physics are gone, and all
    // `n` slides sit in one horizontally-scrolling, snap-aligned track.
    //
    // WHY THIS COULD NOT BE DONE BEFORE, AND WHY IT CAN NOW. The 4-slide window existed to cap
    // RENDERING, not to shape the gesture: each slide is the buyer's whole design rendered to SVG with
    // their logo embedded as base64, and building sixteen up front is what made the View-all sheet
    // take seconds to open with a large logo. That sheet solved it — an IntersectionObserver builds
    // each tile as it approaches, drained one per frame so the first paint never waits. The same
    // machinery is used here, so sixteen slides cost about what four did: the ones you can see.
    //
    // WHAT THE BROWSER NOW OWNS: momentum, rubber-banding, the snap, and the fact that a hard flick
    // travels further than a gentle one — all of it tuned by the platform, per device, for free. That
    // is the thing hand-written physics cannot match, and the reason "1 flick = 1 card" felt wrong.
    //
    // WHAT WAS GIVEN UP: the infinite wrap. A native scroller has two ends, so the arrows clamp at the
    // first and last layout instead of cycling. That is the honest trade — a list of sixteen with a
    // "View all" beside it is a list, not a carousel of unknown length.
    const COMMIT_IDLE = 240;    // quiet time before the expensive tail runs, once

    // The track GROWS during a burst instead of being recycled between steps (Ryan 2026-07-25).
    //   A recycle re-centres by rebuilding the 4-slide window and snapping the transform back to
    // home with no transition. Done once per click that reads as a JUMP, not a slide — Ryan: "after
    // the first click I'm not seeing the 2D physically slide on the 2nd+ clicks". It also rebuilt
    // four thumbnails per click, which is the expensive part.
    //   Instead: each click APPENDS the one new slide it needs and moves the track one more step
    // along. Re-pointing a running CSS transition interpolates from wherever the track currently is,
    // so a burst reads as one continuous glide rather than a series of restarts, and each click
    // costs exactly one thumbnail. The compact window is rebuilt only once the burst SETTLES, at a
    // moment when it is visually identical — same slide at home, same offset — so it never shows.
    // ── SCROLLING BROWSES; CLICKING CHOOSES (Ryan 2026-07-28) ───────────────────────────────────
    // "require the one you want to go with to be clicked on, instead of just scrolled to… when it's
    // clicked, format it like the category cards which get clicked on, on the welcome screen (blue
    // glass, blue check in top right corner)."
    //
    // Until now the carousel WAS the commit: every arrow, swipe and wheel step called
    // `setLayoutPreset` and dragged the 3D along behind it. That reads as a scrubber, and a scrubber
    // has no way to say "let me look at this one without taking it" — the buyer could not pass THROUGH
    // a layout to reach another. So `slide()` is now pure navigation and this is the only thing that
    // commits, which also means one deferred 3D update per DECISION instead of one per step.
    //
    // `selectedId` is tracked here rather than read back off `state.designProfile.layoutPreset` for
    // the reason `resyncLayoutPicker` documents below: the stored preset and the composition can
    // legitimately disagree (emptying an arc-text moves the plate without moving the stored id), and
    // the tick has to follow what the buyer actually picked.
    let selectedId = presets[idx] ? presets[idx].id : cur;
    const CHECK_SVG = '<svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" '
      + 'stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 8.5l3 3 7-7"/></svg>';
    // ── THUMBS ARE BUILT AS THEY APPROACH, ONE PER FRAME ────────────────────────────────────────
    // Lifted wholesale from the View-all sheet, where it is already proven against the worst case (a
    // multi-MB logo × sixteen tiles). Two halves matter and neither is optional: the observer decides
    // WHEN, with a screenful of lead time so a slide is ready before it is seen; the frame-by-frame
    // drain decides HOW MANY AT ONCE, because rendering a thumb is synchronous and doing a screenful
    // in one callback blocks the very scroll it is trying to keep ahead of.
    let thumbQueue = [];
    let draining = false;
    // ⚠ NOT WHILE THE STRIP IS MOVING (Ryan 2026-07-28: "if I get it going it'll keep going
    // indefinitely, but as soon as it stops, if I re-scroll, it wants to only go 2 or so").
    //   Rendering a thumb is one synchronous SVG build with the buyer's logo embedded — tens of
    // milliseconds, once per frame, exactly while the fling is trying to run. It does not slow the
    // scroll itself (that is composited) but it delays `scroll` EVENT delivery, which is what the
    // idle detector below reads, and a fling that looks idle gets cancelled by the settle. Hence the
    // asymmetry he found: the first throw crosses undrawn cards and dies; keep flicking and you are
    // crossing cards that are already drawn, so nothing blocks the thread and it runs free.
    //   Deferring costs a beat of blank card during a fast throw and nothing else — which is how
    // every native image list behaves, and better than a throw that stops short.
    let scrolling = false;
    /* ⚠ THE SECOND REASON TO HOLD OFF — A PRODUCT HAND-OFF IS RUNNING (Ryan 2026-08-02: *"whenever
       you change models, things are a little glitchy and laggy… the old model spinning out, the new
       model spinning in, and the layout 2D SVG renderings all switching over"*).
         It is the SAME cost the scroll pause above exists for, hitting the same frames from a
       different direction. A product switch changes the layout FAMILY, so `refreshControls` rebuilds
       the panel and all ~16 carousel slides are fresh, undrawn and immediately intersecting — the
       observer queues the lot, and the drain then spends "tens of milliseconds, once per frame"
       (the note above, measured) building a full SVG design with the buyer's logo embedded in each.
       Those are precisely the frames `playHandoffExit`, the synchronous geometry rebuild inside
       `buildReveal` and `playHandoffEnter` need, so the two set-pieces the switch exists to show
       are competing with sixteen renders nobody is looking at yet.
         Holding them costs a beat of blank card while the buyer is watching the model — the same
       trade the scroll pause already makes, and the same one every native image list makes. */
    /* ⚠ THE PAUSE ITSELF LIVES AT PANEL SCOPE (`thumbsBusy`), NOT HERE, AND THAT IS THE WHOLE POINT.
       The first cut of this gated only the drain below — and measured a clean zero on one run and
       one-to-two renders inside the animation on the next three, because the drain is one of FIVE
       callers of `drawThumbInto`: `scheduleThumbRefresh`'s 450ms timer and `resyncLayoutPicker` both
       draw directly, and a switch fires them. That is `drawThumbInto`'s own header, verbatim —
       *"THREE separate places redraw these tiles, and the first fix only taught one of them"* —
       repeated by someone who had just read it. The gate belongs at the funnel; this flag is only
       here so the drain does not spin a pointless rAF chain while the funnel is refusing. */
    const drainThumbs = () => {
      if (scrolling || thumbsBusy) { draining = false; return; }  // resumed by the idle / not-busy handler
      const el = thumbQueue.shift();
      if (!el) { draining = false; return; }
      const id = el.getAttribute('data-wd-thumb');
      if (id && el.isConnected && !el.dataset.wdDrawn) drawThumbInto(el, id);
      requestAnimationFrame(drainThumbs);
    };
    const queueThumb = (el) => {
      thumbQueue.push(el);
      if (draining || scrolling || thumbsBusy) return;
      draining = true;
      requestAnimationFrame(drainThumbs);
    };
    /* Let the panel-scope release restart this drain. Reassigned on every carousel rebuild, which is
       why `setThumbsBusy` calls it through the variable rather than capturing it. */
    resumeThumbDrain = () => {
      if (scrolling || thumbsBusy) return;
      if (thumbQueue.length && !draining) { draining = true; requestAnimationFrame(drainThumbs); }
    };
    // `root: viewport` — the carousel is its own scrollport, so "approaching" means approaching along
    // ITS axis. A null root would ask about the window instead, and every slide in a horizontal
    // scroller is inside the window's box whether it is on screen or not.
    const slideObserver = typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver((entries, obs) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          obs.unobserve(e.target);
          queueThumb(e.target);
        }
      }, { root: viewport, rootMargin: '0px 400px', threshold: 0 })
      : null;
    const slideEl = (pi) => {
      const gate = presetEligibility(presets[pi], state.designProfile);
      const node = h('div', {
        class: 'wd-layout-slide' + (presets[pi].id === selectedId ? ' is-selected' : '')
          + (gate.ok ? '' : ' is-locked'),
        'data-wd-thumb': presets[pi].id,
        ...(gate.ok ? {} : { 'aria-disabled': 'true', title: gate.reason }),
      });
      if (slideObserver) slideObserver.observe(node); else drawThumbInto(node, presets[pi].id);
      // Appended AFTER construction — `html:` sets innerHTML, so a child passed to h() would be wiped.
      // `data-wd-keep` says the same thing to the OTHER writer of this node's innerHTML: the debounced
      // thumb redraw, which used to take the tick down with the old drawing (see scheduleThumbRefresh).
      node.appendChild(h('span', { class: 'wd-layout-slide__check', 'aria-hidden': 'true', 'data-wd-keep': true, html: CHECK_SVG }));
      // The reason rides ON the slide, not just in a tooltip: the carousel is the picker a phone
      // buyer actually uses, and a phone has no hover. `data-wd-keep` so the debounced thumb redraw
      // doesn't take it down with the old drawing — the same trap the tick above documents.
      node.appendChild(h('span', {
        class: 'wd-layout-card__locked wd-layout-slide__locked', 'data-wd-keep': true,
      }, gate.ok ? '' : gate.reason));
      /* WHERE AM I IN THE SET (Ryan 2026-08-02) — the storefront product gallery's image counter,
         brought to the layout carousel: *"each image on a product page has a '1/3' in the bottom
         right corner… wondering if we should add something similar, same font, to the bottom right
         corner of layout cards on the slide-out pane. No pill though."*
           Format and spacing follow the theme's `.media-ctrl__counter` verbatim — `1 / 16`, spaces
         around the slash — rather than the parenthesised shorthand, because the theme element IS
         the reference and matching it is the whole request. The pill is dropped as asked: the
         theme needs a fill to sit on a photograph, this sits on a card whose ground we control.
           ⚠ `pi`, NOT the track position. The strip holds THREE copies of the list for the wrap,
         so a counter taken off the DOM index would read "23 / 16" on the second copy. Off `pi` it
         says the same thing on all three, which is what makes the wrap invisible.
           ⚠ `data-wd-keep`, for the third time on this node. `drawThumbInto` writes innerHTML, so
         anything without it is swept away by the next debounced redraw — the trap the tick and the
         locked label each document above. Third occurrence; if a fourth arrives, the redraw should
         preserve children by default rather than by attribute.
           Hidden on a LOCKED slide, where `.wd-layout-slide__locked` already owns the bottom edge
         and the two would print over each other. */
      if (gate.ok) {
        node.appendChild(h('span', {
          class: 'wd-layout-slide__count', 'aria-hidden': 'true', 'data-wd-keep': true,
        }, `${pi + 1} / ${n}`));
      }
      return node;
    };
    /** Repaint the tick across the whole track — slides are appended mid-burst, so a class set at
     *  construction time is not enough on its own. */
    const paintSelection = () => {
      for (const c of track.children) c.classList.toggle('is-selected', c.dataset.wdThumb === selectedId);
    };
    // ── THE WRAP IS BACK, THREE COPIES DEEP (Ryan 2026-07-28: "I liked the wrap, not gonna lie") ──
    // A scroller has two ends and no amount of CSS changes that — so the LIST gets longer instead.
    // Three copies of every layout, opening in the middle one, which leaves a full sixteen cards of
    // runway in each direction. Whenever the strip goes idle, `recentre()` puts the scroll offset back
    // in the middle copy by adding or subtracting exactly one list-width. The content either side is
    // identical, so that jump is invisible by construction — there is nothing on screen to move.
    //   ⚠ ONLY WHEN IDLE, never mid-fling. Writing `scrollLeft` during momentum cancels it outright on
    // some platforms, which would be the old "1 flick = 1 card" problem reinvented in a worse place.
    // A full list of runway means no single fling can reach an end before the next idle moment.
    //   Below three layouts there is nothing to wrap around convincingly — the seam would be on screen
    // at the same time as its original — so short sets stay finite.
    const WRAP = n >= 3;
    const COPIES = WRAP ? 3 : 1;
    // Their SIZE comes from CSS (`--wd-slide-w` + `aspect-ratio`), not from their contents, so an
    // undrawn slide holds its place perfectly and the scroll length is right from the first frame —
    // no reflow as thumbs arrive, which is what makes the lazy drawing invisible here.
    track.append(...Array.from({ length: COPIES * n }, (_, k) => slideEl(k % n)));
    /** Where slide `i` of the middle copy lives. */
    const startAt = WRAP ? n + idx : idx;

    let commitTimer = null;

    // The expensive tail — logo raster, 3D texture, history, panel rebuild — coalesced to the END of
    // a burst. Ten fast clicks cost ONE 3D render instead of ten, and the 2D never waits on it.
    // One history entry per scrub is also the better undo story: a scrub is one decision.
    //
    // The PANEL REBUILD is the dangerous half (Ryan 2026-07-25, second report). `render()` throws the
    // whole panel away and builds a new one — including this picker — so if it lands while a slide is
    // animating it wipes the animation mid-flight. That is what "I click once, then click again while
    // the 3D is loading and I don't see the animation any longer" was: click #1's commit rebuilding
    // the panel underneath click #2's slide. So the rebuild is now (a) skipped outright if another
    // click has superseded it, and (b) held back while the track is still moving.
    // TWO separate debounces, because the two halves have very different costs to the buyer.
    //
    //   • The visual catch-up (`ctx.requestRender()` → logo raster → 2D plate → 3D texture) repaints
    //     the CANVAS and touches nothing in this panel. Short fuse — this is the "3D catches up"
    //     Ryan liked.
    //   • The PANEL REBUILD (`render()`) is `replaceChildren(root, build())` — it destroys the whole
    //     panel, this picker included. Long fuse, because a rebuild landing between two clicks is
    //     what broke the slide (Ryan 2026-07-25, third report).
    //
    // The failure it fixes: at ~1 click/sec, click #1's rebuild lands in the gap before click #2.
    // The 3D texture update is blocking the main thread at that moment, so clicks #2/#3 sit QUEUED —
    // and by the time they dispatch, the arrow they were aimed at is a DETACHED node. The handler
    // still runs (state advances, which is why the right layout eventually appears) but its track is
    // no longer in the document, so there is nothing to animate: one clean slide, then a silent snap
    // to the final image. At 3 clicks/sec every click landed before the rebuild, which is exactly why
    // that case already felt perfect.
    //   PANEL_IDLE is therefore longer than any realistic click cadence: while the buyer is still
    // scrubbing, the panel is simply left alone.
    const PANEL_IDLE = 1200;
    let panelTimer = null;
    let commitSeq = 0;
    const idle = (fn) => (typeof window.requestIdleCallback === 'function'
      ? window.requestIdleCallback(fn, { timeout: 400 }) : setTimeout(fn, 0));
    const scheduleCommit = () => {
      const mine = ++commitSeq; // any later click supersedes the pending panel rebuild
      if (commitTimer) clearTimeout(commitTimer);
      commitTimer = setTimeout(() => { commitTimer = null; ctx.requestRender(); }, COMMIT_IDLE);
      if (panelTimer) clearTimeout(panelTimer);
      panelTimer = setTimeout(() => {
        panelTimer = null;
        const finish = () => {
          if (mine !== commitSeq) return;                       // superseded — a newer click owns it
          (ctx.recordHistoryNow || ctx.recordHistory)?.();
          // Announces what was CHOSEN. It used to name `presets[curIdx]` — the slide sitting at home —
          // which was the same thing only because taking a layout also brought it home. Reading the
          // selection directly says what happened even if the strip has been scrolled since.
          ctx.announce?.(`Layout: ${getLayoutPreset(selectedId).name}`);
          render();
        };
        idle(finish);
      }, PANEL_IDLE);
    };

    // ── SCROLL HELPERS ──────────────────────────────────────────────────────────────────────────
    // One slide plus one gap, measured rather than computed from the token — the token is a
    // percentage and the gap a length, and only the browser knows what they came out as.
    const reducedMotion = typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    const stepPx = () => {
      const first = track.firstElementChild;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return (first ? first.getBoundingClientRect().width : viewport.clientWidth * 0.726) + gap;
    };
    /** Which slide is currently sitting at the start edge — the one the viewport is showing full-size.
     *  A TRACK index, not a preset index: with the wrap on, the same layout appears three times. */
    const homeIndex = () => {
      const step = stepPx();
      if (!(step > 0)) return 0;
      return Math.max(0, Math.min(track.children.length - 1, Math.round(viewport.scrollLeft / step)));
    };
    /** The copy of preset `pi` nearest to where the strip is now — so a correction never sends the
     *  buyer the long way round a list they are already standing in the middle of. */
    const nearestCopy = (pi) => {
      if (!WRAP) return pi;
      const home = homeIndex();
      let best = pi;
      for (let c = 0; c < COPIES; c += 1) {
        const cand = c * n + pi;
        if (Math.abs(cand - home) < Math.abs(best - home)) best = cand;
      }
      return best;
    };
    /**
     * Bring slide `i` to the start edge.
     *
     * Offsets are read from the DOM rather than multiplied out: `offsetLeft - track.offsetLeft` is
     * the slide's distance along the track, which is exactly the scrollLeft that puts it at the
     * content start — and it stays correct through the padding, the gap and any future change to
     * either, none of which a `i * step` calculation would notice.
     */
    const scrollToIndex = (i, smooth = true) => {
      const el = track.children[i];
      if (!el) return;
      const left = el.offsetLeft - track.offsetLeft;
      const reduce = reducedMotion && reducedMotion.matches;
      viewport.scrollTo({ left, behavior: smooth && !reduce ? 'smooth' : 'auto' });
    };
    // ⚠ THE OPENING POSITION HAS TO WAIT FOR A LAYOUT, AND SOMETIMES FOR A TAB.
    // `scrollLeft` on an element with no box does nothing and reports 0 — and on a phone this panel
    // is built while another section tab may be the visible one, so the carousel can be `display:
    // none` at build time. Setting it then would silently land on slide 0, i.e. the tool would open
    // insisting the first layout is the current one.
    //   So the alignment is armed and fires at the first moment the viewport actually has a width,
    // whenever that turns out to be. Once only: after that, where the strip sits is the buyer's.
    let aligned = false;
    const alignToCurrent = () => {
      if (aligned || !viewport.clientWidth) return;
      aligned = true;
      scrollToIndex(startAt, false); // no animation — this is the starting position, not a movement
      // ⚠ DRAW THE OPENING SCREENFUL DIRECTLY, don't wait to be told about it. The observer's root is
      // an element that was not in the document when the observer was made, and the slides it watches
      // were placed before the scroll position was. Queueing the few around the landing spot costs
      // nothing if the observer also reports them — `drainThumbs` skips anything already drawn — and
      // it is the difference between opening on the buyer's layout and opening on empty cards if it
      // does not. Everything past this screenful stays lazy, which is the point.
      for (let i = startAt - 1; i <= startAt + 2; i += 1) {
        const el = track.children[i];
        if (el && !el.dataset.wdDrawn) queueThumb(el);
      }
    };
    // ── WHAT HAPPENS WHEN THE STRIP STOPS MOVING ────────────────────────────────────────────────
    // Both corrections that must never touch a gesture in flight, on one idle timer.
    //
    //  1. RECENTRE — slide the offset back to the middle copy, ±exactly one list-width. Invisible:
    //     the pixels either side of that jump are the same cards.
    //  2. SETTLE — come to rest ON a card. This is now the ONLY snapping there is: CSS scroll-snap
    //     is off in both its modes, because either one makes the browser choose the fling's
    //     destination and damp the throw to reach it (Ryan measured it — 2 free scrolls in 20 under
    //     `proximity`, whose range is about a scrollport wide against cards half that size). Doing it
    //     here means the throw is never shortened: the fling runs its full natural distance, and the
    //     tidying happens afterwards, when the finger and the momentum are both already done.
    const IDLE_MS = 140;
    let idleTimer = 0;
    const recentre = () => {
      if (!WRAP) return;
      const list = n * stepPx();
      if (!(list > 0)) return;
      const x = viewport.scrollLeft;
      if (x < list * 0.5) viewport.scrollLeft = x + list;
      else if (x > list * 1.5) viewport.scrollLeft = x - list;
    };
    /* ── HOW FAR YOU HAVE TO PUSH BEFORE IT COMMITS (Ryan 2026-08-09) ───────────────────────────
     * *"can you release some of the stickiness of scrolling through the SVGs? I understand a little
     * stickiness is good, but I'm pretty sure you can loosen that so when you do swipe it, it's
     * inclined to move a fraction more freely before it gets grabbed and stopped."*
     *
     * 🔑 THE STICKINESS WAS NEVER IN THE THROW — it is in where the throw is allowed to COUNT. The
     * fling has been undamped since CSS snapping came out (see the viewport's note): the browser
     * runs it to its natural end and nothing shortens it. What grabs the strip is this settle, and
     * with a plain `Math.round` the boundary sits at half a card — so any swipe that moved less than
     * 50% of a step is rounded back to the card it started on and slides home. That return IS the
     * "grabbed and stopped", and it is why a small deliberate flick feels like it was refused.
     *
     * So the boundary moves to 28% IN THE DIRECTION YOU WERE TRAVELLING. Push about a quarter of a
     * card and it commits to the next one; drift back the other way and it still comes home. The
     * rest position is unchanged — it is a card, exactly as before — and only the question "which
     * card did you mean" is answered more generously.
     *
     * ⚠ SCOPED TO THE SETTLE, NOT TO `homeIndex`. That helper answers "which card is the buyer
     * looking at" for the arrows, for `selectSlide` and for `nearestCopy`'s shortest-path search, and
     * all three want the honest nearest — an arrow press that advanced by 0.72 of a card because the
     * strip was mid-drift would be a bug, not a courtesy.
     * ⚠ DIRECTION COMES FROM THE SCROLL ITSELF, not from a pointer gesture: the strip also moves
     * under momentum long after the finger is gone, and under `scrollToIndex`. `lastDir` holds 0
     * until something actually moves, and at 0 this is exactly the old `Math.round`. */
    const COMMIT_FRAC = 0.28;
    let lastDir = 0;
    const settleIndex = () => {
      const step = stepPx();
      if (!(step > 0)) return homeIndex();
      const f = viewport.scrollLeft / step;
      const frac = f - Math.floor(f);
      let i = Math.round(f);
      if (lastDir > 0 && frac >= COMMIT_FRAC) i = Math.ceil(f);
      else if (lastDir < 0 && frac <= 1 - COMMIT_FRAC) i = Math.floor(f);
      return Math.max(0, Math.min(track.children.length - 1, i));
    };
    const settleToNearest = () => {
      const el = track.children[settleIndex()];
      if (!el) return;
      const left = el.offsetLeft - track.offsetLeft;
      // 2px of slack: a scroller that has already snapped is "on a card", and re-issuing a scroll for
      // a sub-pixel difference would fight the browser every time it lands correctly by itself.
      if (Math.abs(left - viewport.scrollLeft) > 2) viewport.scrollTo({ left, behavior: 'smooth' });
    };
    // ⚠ A TIMER ALONE DOES NOT MEAN "STOPPED" — it means "no scroll event reached us recently", and
    // those are two different things whenever the main thread is busy. The scroll itself is
    // composited and keeps running; only the notifications are late. Acting on that is what cancelled
    // a live fling and replaced it with a short glide to the nearest card.
    //   So the position is the witness: if `scrollLeft` has moved since the timer was armed, the strip
    // is still going and the timer re-arms. Nothing settles until two readings agree.
    let idleAt = -1;
    const onIdle = () => {
      if (viewport.scrollLeft !== idleAt) {           // still moving — ask again later
        idleAt = viewport.scrollLeft;
        idleTimer = setTimeout(onIdle, IDLE_MS);
        return;
      }
      scrolling = false;
      recentre();
      settleToNearest();
      // The thumbs that were held back while it moved — now, with the thread to themselves.
      if (thumbQueue.length && !draining) { draining = true; requestAnimationFrame(drainThumbs); }
    };
    /* ⚠ THE DIRECTION IS SAMPLED HERE AND NOWHERE ELSE, and it must be read BEFORE `idleAt` is
       overwritten — that variable is the idle witness's previous reading, which is exactly the
       "where were we last time" this needs. A zero delta leaves `lastDir` alone rather than clearing
       it: the final scroll event of a fling often reports the same position twice, and forgetting the
       direction on that last tick would hand the settle a 0 and undo the commit it had earned. */
    let lastScrollX = -1;
    viewport.addEventListener('scroll', () => {
      const x = viewport.scrollLeft;
      if (lastScrollX >= 0 && x !== lastScrollX) lastDir = x > lastScrollX ? 1 : -1;
      lastScrollX = x;
      scrolling = true;
      clearTimeout(idleTimer);
      idleAt = x;
      idleTimer = setTimeout(onIdle, IDLE_MS);
    }, { passive: true });
    requestAnimationFrame(alignToCurrent);
    if (typeof ResizeObserver === 'function') {
      const ro = new ResizeObserver(() => { alignToCurrent(); if (aligned) ro.disconnect(); });
      ro.observe(viewport);
    }

    /**
     * Take the layout a slide is showing, and bring it to the start edge.
     *
     * Clicking the one PEEKING at the right edge has to leave it where the chosen layout lives, or
     * the tick ends up half off-screen — so selecting is also a navigation. Smooth, because this one
     * IS a movement the buyer asked for, unlike the opening alignment.
     * @param {Element} node a `.wd-layout-slide`
     */
    const selectSlide = (node) => {
      if (!node) return;
      // A LOCKED slide still SCROLLS to the start edge, then sends them to the Wording field instead
      // of committing. Bringing it forward first is deliberate: the buyer asked to look at this one,
      // and refusing to even move would read as a broken tap rather than as a requirement. What they
      // get is the layout centred, its reason legible, and the caret already in the box that fixes it.
      const at = [...track.children].indexOf(node);
      if (at >= 0) scrollToIndex(at);
      if (node.classList.contains('is-locked')) { focusWording(); return; }
      selectedId = node.dataset.wdThumb;
      ctx.rememberLayoutChoice?.(selectedId); // a pick — see applyPreset's note
      setLayoutPreset(state, selectedId);
      paintSelection();
      scheduleCommit();
    };

    // ── SCROLLING BROWSES, CLICKING CHOOSES — and the browser now does the scrolling ─────────────
    // Every pointer handler that used to live here is gone: pointerdown/move/up, the pointer capture,
    // the 1:1 transform, the release threshold, the spring-back, the velocity sampling, and the wheel
    // handler that translated deltaX into steps. A native scroll container does all of it, per
    // platform, including the momentum and rubber-band that no amount of hand-tuning was going to
    // match. Deleted rather than left dormant — a second gesture engine sitting behind the browser's
    // own is exactly how a carousel ends up fighting the finger.
    //
    // ALL that is left is the one thing the browser cannot decide: whether a press was a CLICK. It
    // matters on the mouse, where a press-drag-release still fires `click` on the element it began in
    // (Ryan 2026-07-28: "a click and drag is nothing, a click is a selection"). Touch needs no help —
    // a swipe that scrolls suppresses its own click.
    let downTarget = null;
    let pressed = false;
    let moved = false;
    const CLICK_SLOP_PX = 6;   // same tap/drag split the bottom sheet uses
    let startX = 0;
    let startY = 0;
    viewport.addEventListener('pointerdown', (e) => {
      downTarget = e.target; pressed = true; moved = false;
      startX = e.clientX; startY = e.clientY;
    });
    viewport.addEventListener('pointermove', (e) => {
      // Measured from the PRESS, not accumulated per move, so a drag that wanders and comes back is
      // still a drag rather than sneaking in as a click.
      if (pressed && !moved && Math.hypot(e.clientX - startX, e.clientY - startY) > CLICK_SLOP_PX) moved = true;
    });
    const endPress = () => { pressed = false; };   // `moved` survives, for the click that follows
    viewport.addEventListener('pointerup', endPress);
    viewport.addEventListener('pointercancel', endPress);
    viewport.addEventListener('click', (e) => {
      if (moved) { moved = false; downTarget = null; return; }
      const aimed = downTarget || e.target;
      downTarget = null;
      selectSlide(aimed.closest && aimed.closest('.wd-layout-slide'));
    });
    // Keyboard activation takes the slide at the start edge — the one the viewport is showing
    // full-size — since there is no pointer to say which of the two visible slides was meant.
    viewport.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      selectSlide(track.children[homeIndex()]);
    });

    // Acts on POINTERDOWN, not click (Ryan 2026-07-25). A `click` only fires if the press and the
    // release resolve to the same element — and `render()` replaces this whole panel, so a rebuild
    // landing between the two makes the release hit a BRAND-NEW button and no click event is
    // generated at all. That is the "I click and nothing happens" case: not a guard rejecting the
    // input, the input never arriving. Acting on press is immune to it, and feels more immediate.
    //   A click must NEVER do nothing — better a slide that starts from an awkward position than a
    // dead button (Ryan: "feels cheap from a UX perspective when you click and nothing happens").
    //   They scroll rather than drive a transform, and they wrap for free: the track holds three
    // copies, so `home ± 1` is always a real slide and the idle recentre keeps the strip parked in the
    // middle one. `step()` reads the live home index each press, so a press mid-flick continues from
    // wherever the strip actually is rather than from wherever the last press left it.
    const step = (dir) => scrollToIndex(Math.max(0, Math.min(track.children.length - 1, homeIndex() + dir)));
    const arrow = (dir, label) => h('button', {
      type: 'button', class: 'wd-layout-arrow wd-flat-glass wd-flat-glass--flush', 'aria-label': label,
      onPointerDown: (e) => { if (e.button === 0) { e.stopPropagation(); step(dir); } },
      // Keyboard activation still arrives as a SYNTHETIC click (detail 0). Honour that, and only
      // that, so a mouse press can't fire the step twice.
      onClick: (e) => { e.stopPropagation(); if (e.detail === 0) step(dir); },
    }, h('span', { 'aria-hidden': 'true', html: dir < 0 ? CHEVRON_L : CHEVRON_R }));

    /**
     * RE-POINT the carousel at whatever the plate now looks like (Ryan 2026-07-26).
     *
     * The selection is derived from the composition (entry 87) — but it was derived ONCE, here at
     * build time, and an arc-text edit deliberately never rebuilds this panel: `valueCommit` repaints
     * the plate and files history precisely because a rebuild would tear out the field being typed
     * in. So emptying the bottom arch turned the 3D into a Marquee while this card sat on Varsity
     * Arch, and `scheduleThumbRefresh` couldn't help — it redraws each slide's SVG for the preset id
     * that slide ALREADY had. The picture was current; the choice of layout was stale.
     *
     * "View all" looked right for the same reason it was never wrong: it derives on open.
     *
     * Deliberately does NOT store the derived id. The stored `layoutPreset` stays Varsity Arch, so
     * typing the line back walks the card home rather than stranding it — the composition is the
     * truth and this is a view of it.
     *
     * @returns {boolean} whether the window was rebuilt (its slides are freshly drawn — see caller)
     */
    resyncLayoutPicker = () => {
      const want = matchPresetToComposition(state.designProfile, presets) || state.designProfile.layoutPreset;
      const wantIdx = presets.findIndex((p) => p.id === want);
      if (wantIdx < 0 || presets[wantIdx].id === selectedId) return false;
      // The tick comes along. This is the card catching up with what the plate ALREADY is, so leaving
      // the tick on the old preset would put it on a slide that is no longer home — or off-screen
      // entirely. What is shown at the start edge and what is marked as chosen have to be the same.
      selectedId = presets[wantIdx].id;
      paintSelection();
      scrollToIndex(nearestCopy(wantIdx));
      // FALSE, deliberately, where the window version returned true: nothing was rebuilt, so the
      // slides still carry their OLD drawings and the caller's redraw pass is exactly what they need.
      // (It skips the ones never drawn and the ones nowhere near the viewport — see scheduleThumbRefresh.)
      return false;
    };

    const viewAll = h('button', { type: 'button', class: 'wd-layout-viewall-btn wd-flat-glass wd-flat-glass--flush', onClick: openLayoutViewAll }, `View all ${n} layouts`);
    // Arrows flank "View all", BELOW the preview (Ryan 2026-07-23).
    const nav = h('div', { class: 'wd-layout-nav' }, arrow(-1, 'Previous layout'), viewAll, arrow(1, 'Next layout'));
    // Stamped with the list this build was made FROM — `refreshForWeight` compares against it.
    layoutSectionKey = presets.map((p) => p.id).join(',');
    layoutBoxEl = h('div', {}, h('div', { class: 'wd-eyebrow' }, 'Layout'), viewport, nav);
    return layoutBoxEl;
  }

  // ── logo size (BORROWED from the Add Logo tab) ───────────────────────────────
  // Ryan 2026-07-29: "people who get onto the Customize Design tab easily see the way to adjust the
  // size of the text and numbers, but don't see the ability to adjust the logo size, and don't know
  // they need to go back to the 'Add Logo' tab to do it." So the dial comes to them, between Layout
  // and Colors. (The preview and the "Upload different logo" tile were in the first pass and Ryan cut
  // them: by this tab the buyer has settled on a logo, so both are redundant here.)
  //
  // ⚠ IT IS THE **SAME NODE** THE ADD LOGO TAB USES — appended here, which relocates it out of that
  // pane. That is the whole design: one instance can't fall out of sync with itself, so "an edit here
  // updates the Add Logo tab" needs no plumbing at all. The full argument and the re-adopt hook live
  // next to `logoLoan` in ui/logo-step.js.
  //   `ctx.logoStep` is read HERE, at render time, not at panel construction — main.js is still
  // assembling ctx when `createDesignPanel` runs (the same half-built-ctx trap the logo stepper's own
  // onInput documents).
  //   ⚠ STEP-GATED. `refreshControls()` calls designPanel.refresh() and logoStep.refresh() back to
  // back on EVERY commit, from whichever tab is showing — so without this the two of them would tug
  // the same node out of each other's pane forever, and a rebuild fired while Add Logo was visible
  // would strip the dial from under the buyer's cursor. Each end only takes it when it is the surface
  // being shown; `goToStep` sets `currentStep` before it calls `refresh()`, which is what makes the
  // handover land on the right side of the switch.
  function hasLogoSection() {
    return !!(state.ui.hasLogo && state.ui.currentStep === 3 && ctx.logoStep && ctx.logoStep.logoLoan);
  }
  /* ── "Let weight show through enclosed areas", the SECOND control (Ryan 2026-08-12) ────────────
   * *"Please add the … checkbox to the CD tab in the Logo section."*
   *
   * ⛔ NOT LOANED, AND NOT A COPY OF THE RULE EITHER. The size dial and the nudge pad above are
   * LOANED — one DOM node, relocated — because only one tab shows them at a time. This one has to
   * exist on both tabs at once, so it is a genuine second control; what it must NOT be is a second
   * implementation. `ctx.holesControl` (logo-step) owns the flip, the availability test and the
   * answer; this is a view that asks and reports. See the note at `flipHoles`.
   *
   * ⚠ BUILT ONCE, NOT PER SECTION. `logoSection()` runs on every panel build, and a fresh toggle
   * each time would register a fresh mirror each time — an unbounded set of dead controls all being
   * written to on every flip. */
  /** This pane's name for it. Short, like every other sub-head in here. */
  const HOLES_HEADING = 'Logo cutouts'; // was 'See Through Logo' - Ryan 10-31: "make it 'Logo Cutouts' instead" (both tabs)
  let holesUI = null;
  function holesToggle() {
    const hc = ctx.holesControl;
    if (!hc) return null;
    if (!holesUI) {
      // WD 482: the This-or-That (both pictures on a weight), the same view as the Add Logo tab's.
      holesUI = seeThroughChooser({
        // ⛔ NOT `hc.label`, AND THAT IS THE POINT (Ryan 2026-08-12): *"the text … should just be
        // 'See Through Logo', in the same font weight/size/color/spacing as 'Size' and 'Plate' and
        // 'Text' and 'Lettering case'."* Every control in this pane is headed by a `.wd-subhead` in
        // two or three words; the Add Logo tab's full sentence is right THERE, where the control is
        // an aside under a thumbnail, and wrong here, where it is a row in a settings list. Two
        // surfaces, one answer, two ways of naming it — see `HOLES_LABEL` in logo-step.
        caption: false,
        label: HOLES_HEADING,
        ariaLabel: 'Let the weight show through enclosed areas of the logo',
        onChange: (v) => hc.set(v),
      });
      hc.mirror(holesUI);
    }
    /* Same availability test as the Add Logo tab's — a logo the keyer never touched has nothing to
       flip, and asking it here would be a second answer to that question.
       ⚠ NOT APPENDED rather than hidden, because the sub-head above it is a SEPARATE node: hiding
       only the control would leave "See Through Logo" heading nothing. The section rebuilds on every
       panel build, so declining to append is exactly as good as hiding — and it cannot desync. */
    if (!hc.available()) return null;
    holesUI.el.hidden = false;
    holesUI.set(hc.get()); // silent; never re-enters the flip
    holesUI.setPreviews(hc.previews ? hc.previews() : null);
    return holesUI.el;
  }

  function logoSection() {
    const box = h('div', {}, h('div', { class: 'wd-eyebrow' }, 'Logo'));
    if (!hasLogoSection()) { box.hidden = true; return box; }
    // Headed "Size" so this reads exactly like the Weight section above its own dial. The borrowed
    // block carries its own "Logo size" label — right on the Add Logo tab, redundant under a LOGO
    // eyebrow — so the CSS hides that one here rather than either tab having to know about the other.
    box.append(subHead('Size'), ctx.logoStep.logoLoan.sizeBlock);
    // ── Position: the Badge nudge (Ryan 2026-07-29) ────────────────────────────────────────────
    // Loaned on the same terms as the dial — one instance, relocated, so the two tabs cannot drift.
    // The pad hides ITSELF when the layout isn't a Badge (logo-step's `syncNudge`), so this appends
    // unconditionally and the sub-head goes with it: gating here as well would put the decision in two
    // places, and they would disagree the first time a layout switch landed on one refresh and not the
    // other. The sub-head is inside the borrowed block's own hidden container for exactly that reason.
    const nudge = ctx.logoStep.logoLoan.nudgeBlock;
    if (nudge) box.append(nudge);
    // ⚠ AND RE-ASK WHETHER IT BELONGS HERE, every time this section is built (Ryan 2026-07-30: the pad
    // stayed visible after switching to a non-Badge layout, then vanished the instant an arrow was
    // clicked — and would not reappear on returning to a Badge layout without a reload).
    //   A layout change commits through `structuralCommit`, which re-renders THIS panel but never calls
    // `refreshControls` — so `logoStep.refresh()` and the `syncNudge()` inside it were not on the path.
    // The pad was simply re-appended with whatever `hidden` it last had. Calling it here puts the check
    // on the one path a layout change is guaranteed to take.
    //   It is logo-step's own function, not a copy of its rule: "which layouts get a nudge" is still
    // answered in exactly one place (`isBadgeLogo`), and that mattered more than where it is invoked
    // from — the arrow click proved the logic was right and only its reachability was broken.
    ctx.logoStep.logoLoan.syncNudge?.();
    // …and the SIZE dial, which was left behind when the pad was fixed and has the same failure: it
    // caches what 100% means, that meaning is a property of the LAYOUT, and this is the only path a
    // layout change is guaranteed to take. See `syncSize`.
    ctx.logoStep.logoLoan.syncSize?.();
    /* The show-through answer, under Size and Position because it is about the ARTWORK rather than
       its placement — and above the reset, which is the section's last row everywhere.
       ⚠ `12px 0 6px` is `letteringCaseToggle`'s heading verbatim, not a number picked by eye: Ryan
       asked for the same spacing as those sub-heads, and this is the app's own gap between two
       control groups inside one section. The default `.wd-subhead` margin (`0 0 6px`) is for a
       heading that OPENS a section; this one follows a stepper row, which is why it read as sitting
       right underneath the +/- (Ryan 2026-08-12). */
    /* WD 489 (Ryan 11-01: "yes, same change here"): the Solid / See-through pair is NOT shown here any more - it lives
       in the editor, above the big logo, exactly as on Add Logo. `holesToggle` stays for its mirror, unmounted. */
    const holes = null;
    /* WD 485 (queue 4a): "Edit see-through parts" here too - the Add Logo tab's editor and apply path (logo-step owns
       both), under the same heading. Offered on every logo once its reveal is done, even with no chooser (Mojoe's). */
    const hc = ctx.holesControl;
    const canEdit = !!(hc && hc.tapFixAvailable && hc.tapFixAvailable());
    if (holes || canEdit) box.append(subHead(HOLES_HEADING, '12px 0 6px'));
    if (holes) box.append(holes);
    if (canEdit) {
      // glows on an unsure logo until the editor is opened (logo-step's `syncEditGlow` keeps a live one in step)
      box.append(h('button', { type: 'button', class: 'wd-reset-btn wd-flat-glass wd-flat-glass--flush wd-tapfix-open wd-tapfix-open--cd' + (hc.editGlow && hc.editGlow() ? ' is-hinting' : ''), onClick: () => hc.openTapFix() },
        h('span', { class: 'wd-tapfix-open__icon', 'aria-hidden': 'true' }, '✎'), 'Clean up logo')); // WD 498
    }
    // Last in the section, same position and same control as "Reset colors" and "Reset weight" at the
    // foot of theirs — so it reads as "reset everything above me", which here is size and position.
    if (phoneMQ()) {
      box.append(sectionResetBtn('Reset logo size / position', "Put the logo's size and position back to their defaults — your logo and its colors are kept", resetLogoSection));
    }
    return box;
  }

  // ── weight unit ──────────────────────────────────────────────────────────────
  // No denomination picker (Ryan 2026-07-20): the plate/dumbbell/bar opens on a
  // sensible SAMPLE weight for its category (defaultWeightFor) and the buyer's real
  // weights + quantities are collected on the quote form later. All that stays here
  // is the LB/KG toggle — flipping it re-letters the mark AND swaps to that unit's
  // default sample weight for the current product.
  /**
   * WHICH DENOMINATION IS ON THE STAGE — the "does the 25 lb look like the 45?" section
   * (Ryan 2026-08-02).
   *
   * The designer opens on ONE representative sample per category (`DEFAULT_WEIGHT_BY_CATEGORY` —
   * 45 lb plate / 50 lb dumbbell / 60 lb bar) and has had no way to show any other since the
   * denomination picker was removed on 2026-07-20. That removal was right about the QUOTE — the
   * buyer's real mix is collected on the quote form, not here — and it left the buyer's actual
   * question unanswered, because on three of the four products the branded face genuinely changes
   * size with the load (the grip plate's whole diameter runs 10.63"→17.72"; a dumbbell's and a
   * fixed barbell's head grows with the steel).
   *
   * It sits ABOVE Layout because it names WHAT you are looking at rather than how it is decorated
   * — the same class of control as the product switcher, not a styling one.
   *
   * ⚠ NO EXPLANATORY COPY, deliberately (Ryan 2026-08-02: *"people are smart, and the reassurance
   * of seeing everything remain the same and them getting to then play spot the difference to
   * realize the number and thickness changed is enough for me… we don't do it elsewhere"*). A
   * bumper is 450 mm at EVERY denomination, so on that product this control moves the printed
   * number and the edge thickness and nothing else. That is the answer, not a dead control.
   *
   * ✅ THE LADDER NOW SHOWS IN KG TOO — the earlier "lb only, hide the section" decision is RETIRED
   * (Ryan 2026-08-02, later the same day). That call rested on a premise this change removes: that
   * a kg ladder would have to be a different SET of plates, so converting the lb rungs to
   * 20.4 / 15.9 / 11.3 would be inventing sizes nobody sells. But the unit is a LABEL now — every
   * rung is the same physical plate whichever way it is written, and Ryan's own conversion table
   * (*"45 lb = 20.4 kg"*) is exactly this list re-lettered. So the rungs stay POUNDS, always, and
   * only the tile's text goes through `displayWeightText`. There is no kg ladder to author.
   */
  function sizeLadder() {
    // ⚠ `null` from designableWeights means FREE ENTRY, not "no denominations" — a fixed barbell
    // has no chart of its own (its heads read the dumbbell's at whatever load the handle leaves).
    // So it offers the house rack, which is the same ten bars the send-to-project fan already
    // assumes a bar buyer is buying.
    // ⚠ `designLadder` OWNS BOTH RULES NOW — the barbell fallback and the lightest-first order —
    // because the quote form shows the same list and had neither. See products.js.
    const list = designLadder(state.ui.product);
    if (!list || list.length < 2) return [];
    return list;
  }

  /**
   * @returns {HTMLElement|null} `null` when there is nothing to choose between — NOT an empty box.
   *   Every `.wd-dsection` is given the divider rule, so an empty one would paint a hairline with
   *   no content above it. Reachable on any product whose chart has fewer than two rungs.
   */
  // The live ladder, so a denomination change can re-light the chosen rung WITHOUT a panel rebuild
  // — the same trick `arcSizeStepper` / `weightSizeStepper` use, and for the same reason. See
  // `refreshForWeight` at the foot of this module for why a rebuild is the thing being avoided.
  let sizeGridEl = null;
  // The Weight section's own re-render — see the note at its assignment.
  let weightSectionRender = null;
  // The Size section's own re-render, so the "View more rows" toggle and a denomination change can
  // both repaint the ladder without touching the rest of the panel.
  let sizeSectionRender = null;
  /* ── HOW MUCH LADDER SHOWS AT REST (Ryan 2026-08-02) ──────────────────────────────────────────
     *"we only show on load a max of 3 rows… then we have a white text link centered under the 3
     rows which reads 'View more rows' and then it changes to 'Hide rows'."*
       Three of the six product ladders fit inside that cap and never see the link at all (a
     competition bumper is one row, the grip and the training bumper two, a fixed barbell two). It
     exists for the dumbbells, which are 6 and 7 rows — enough to push Layout off the screen.
       PERSISTED ACROSS PANEL REBUILDS, deliberately: the panel re-renders on most edits, and an
     expanded ladder that silently re-collapsed every time the buyer changed a colour would read as
     the control fighting them. Session-scoped, like the mobile section tab. */
  const SIZE_COLS = 5;              // must match `.wd-size-grid`'s grid-template-columns
  /* TWO ROWS ON A PHONE, three on desktop (Ryan 2026-08-08). The cap exists to stop a 6–7 row
     dumbbell ladder pushing Layout off the screen, and a phone's pane is the short viewport where
     that bites hardest — the row it buys back is the difference between seeing the next section and
     not knowing it is there. The "View more rows" link is unchanged and still reveals the full set.
       ⚠ It moves the DEFAULT WEIGHT with it: `DEFAULT_WEIGHT_BY_CATEGORY.dumbbell` went 50 → 25 in
     products.js so the opening rung stays inside the rows that are visible at rest. Retune the two
     together — an opening denomination below the fold is the thing this cap must never create. */
  const SIZE_COLLAPSED_ROWS = isPhone() ? 2 : 3;
  let sizeRowsExpanded = false;
  function sizeSection() {
    const ladder = sizeLadder();
    if (!ladder.length) { sizeGridEl = null; sizeSectionRender = null; return null; } // one denomination, or kg → nothing to choose between
    const box = h('div', {});
    /* ⚠ THE NUMBER-STYLE TILE, NOT A PILL (Ryan 2026-08-02: *"I want them to have the UI like the
       number style buttons, but instead of 4 wide, I think we can go 5 wide in rows, as many as
       needed, until the full set's worth of numbers are visible"*).
         So this reuses `.wd-lockup` outright — the same flat-glass tile, the same 8px radius, the
       same `--wd-ring-selected` on the active one — rather than restating its look, which is the
       house rule for matching a shared control (`match-shared-ui-by-sharing-rules`: add your class
       to ITS rules, never copy the declarations).
         The COLUMN COUNT is the one thing that cannot be shared. `--wd-lockup-cols` is read by
       `--wd-toggle-w`, which is how the LB/KG toggle lands exactly over tiles 1 and 2 of the Number
       style grid — so overriding it here to 5 would silently resize a control two sections away.
       `.wd-size-grid` therefore states its own 5 and leaves the token alone. */
    /* The nouns the Size tooltips use mid-sentence.
       ⛔ NOT `categoryLabelOne()`, and this is not an oversight. That function answers a different
       question — the TITLE-CASE display label for chrome ("Fixed Barbell", "Bumper Plate") — and
       these are lowercase words inside a sentence, deliberately shorter: Ryan wrote *"barbell"*, not
       "fixed barbell", because "view this fixed barbell design as a 60lb fixed barbell" is the kind
       of sentence nobody reads twice. Two answers to two questions beats one answer bent to serve
       both; what would be wrong is deriving one from the other and pretending they agree.
       `target` is what the NUMBER sits on, which is why both plate families say "plate". */
    const TIP_NOUN = Object.freeze({
      bumper: { self: 'bumper plate', target: 'plate' },
      grip: { self: 'grip plate', target: 'plate' },
      dumbbell: { self: 'dumbbell', target: 'dumbbell' },
      barbell: { self: 'barbell', target: 'barbell' },
    });
    const tileFor = (w, live, held, hasSibling) => {
      const active = Number(w) === live;
      /* ⭐ A CLAIMED RUNG WEARS ITS SET'S COLOUR (Ryan 2026-08-10) — *"this strip of color needs to be
       * a shape fill instead… the active yet clicked on one will have a new color and a white
       * outline, the active but not clicked on ones will just have the inner glow."*
       *
       * ⛔ THE FILL GOES THROUGH `--wd-glass-fill`, NOT `background`. That variable is flat-glass's
       * one darkness knob; everything that makes the tile read as glass — the lit top hairline, the
       * light pooling from the top, the shadow at the bottom — is `box-shadow` inset layers that
       * survive whatever the fill is. Setting `background` directly would paint over the base and
       * leave the insets stranded on top of a flat rectangle. Going through the knob is literally
       * Ryan's "just have the inner glow", and it is the house rule about sharing a control's own
       * rules rather than overriding them.
       *
       * ⚠ ONLY WITH A SIBLING ON THE BOARD (`hasSibling`). A colour here exists to tell two SETS apart, so
       * with one configuration there is nothing to tell apart and the ladder stays exactly as it has
       * always looked — the same rule the switcher already uses to decide whether a pill gets a
       * colour dot. It also keeps every non-dyeable product untouched, since `siblingRungs` is empty
       * for anything that cannot hold two configurations.
       *
       * ⚠ THE LIVE SET'S OWN COLOUR IS NOT IN `held` — `siblingRungs` deliberately excludes the
       * product on the stage — so the active tile has to be coloured from the profile. And when the
       * buyer parks this configuration on a rung a sibling also holds, the tile is THEIRS: `active`
       * wins, so it wears the live colour, matching the `taken` rule directly below. */
      /* WD 496: a product that holds a LINEUP always colours its plates - the fill is now how "in your lineup" reads
         against a hollow "not added", so it cannot wait for a second set to exist. */
      const lineupMode = productSupportsMultiConfig(state.ui.product) && !!ctx.lineup;
      const dye = !(hasSibling || lineupMode) ? null
        : (active ? (normalizeHex(state.designProfile.bodyColor) || DEFAULT_BODY_COLOR)
          : (held ? held.color : null));
      /* Same 0.179 crossover the plate chip's badge uses, and the same `is-onlight` class name — it
         is the luminance where black and white ink give EQUAL WCAG contrast, not a taste value. The
         long derivation lives on that call site; do not re-derive it here. */
      const onLight = !!dye && relLuminance(dye) > 0.179;
      /* "Could this product ever be picked twice?" — the one question that decides whether any of the
         copy below is even true. Same predicate `siblingRungs` gates itself on, asked of the live
         product rather than of this tile. */
      const multiConfig = productSupportsMultiConfig(state.ui.product);
      /* ⭐ A RUNG ANOTHER COLOUR SET IS SITTING ON (Ryan 2026-08-09).
       *
       * ⚠ MARKED, NEVER DISABLED, and never hidden. Hiding it would make the same plate show two
       * different ladders depending on which configuration you were looking at, which reads as the
       * tool being broken rather than as the rung being spoken for. Disabling it would be enforcing
       * a partition on a surface that only decides WHICH PLATE YOU ARE LOOKING AT — a buyer is
       * entitled to see a green 45 before deciding the 45s should be white. The enforcement lives
       * on the quote form, where the number that gets billed is entered.
       *
       * ⚠ AND IT NEVER MARKS THE ACTIVE RUNG. If the buyer moves this configuration onto the rung a
       * sibling holds, the tile they are standing on must read as theirs — "active" and "someone
       * else has this" on one tile is a contradiction, and `is-active` is the more useful half. */
      const taken = !lineupMode && !!held && !active; // WD 496: in a lineup a held rung is a plate you can GO to
      /* WD 496 — the three states of a lineup tile, and whether the one you are on may be removed (never the last). */
      const inLineup = lineupMode && !!held && !active;
      const notAdded = lineupMode && !held && !active;
      const removable = lineupMode && active && (ctx.lineup.count() > 1);
      /* ── ONE PLACE OWNS THIS WORDING, IN BOTH TENSES ───────────────────────────────────────────
       * A desktop buyer HOVERS a tile and is told what a click would do; a phone buyer has no hover,
       * so they tap, it happens, and a pop-up tells them what just did (Ryan 2026-08-10: *"the
       * message would change slightly, obviously — instead of 'Click here to change…' it would be
       * 'You just changed…'"*). Same four situations, same nouns, same route out — only the tense
       * moves. Written twice they would drift the first time any of it is reworded.
       *
       * @param {boolean} done  the phone's after-the-fact voice
       * @param {number} [fromW] the rung we came FROM. ⚠ Must be captured BEFORE `changeWeight`,
       *   because by the time the pop-up opens `state.ui.weight` is already the new one — reading it
       *   live would produce "you just changed your 55lb plate to a 55lb plate".
       */
      const tipCopy = (done, fromW) => {
        const say = (n) => `${displayWeightText(n, state.designProfile.unit)}`
          + `${state.designProfile.unit === 'kg' ? 'kg' : 'lb'}`;
        // One string, several sentences — the route out is the same wherever the buyer is standing,
        // and copies of it are chances for one to drift the day the tab is renamed.
        const PICK_TAB = 'go to the Pick Weights tab (1) up above';
        if (!multiConfig) {
          /* ⭐ EVERYTHING THAT CANNOT BE PICKED TWICE — the dumbbells, the fixed barbells, the grip
             plate and the Training Rubber bumper. For these the ladder has only ever done one thing,
             so it is one sentence with no Pick Weights tail: there is no "separate one with its own
             design" to go and add.
             ⚠ TWO NOUNS, NOT ONE, and they are not the same word: *"view this grip plate design as a
             25lb plate"*. The first names the PRODUCT being designed, the second names the object the
             number is printed on — nobody says "a grip plate design as a 25lb grip plate". */
          const noun = TIP_NOUN[parseProductKey(state.ui.product).category] || TIP_NOUN.bumper;
          return {
            title: 'Now viewing',
            body: done
              ? `You are now viewing this ${noun.self} design as a ${say(w)} ${noun.target}.`
              : `Click here to view this ${noun.self} design as a ${say(w)} ${noun.target} instead.`,
          };
        }
        if (taken) {
          /* Nothing happened, so the TENSE is the same on both surfaces — there is no past tense for
             an action that was refused. The DIRECTIONS are not: this sentence sends the buyer to the
             product switcher, and the switcher is in a different place on each device (Ryan
             2026-08-10: *"on mobile the 'click the left/right arrow buttons…' message needs to be
             rewritten since these are at the top of the mobile display"*).
             ⛔ Directions that are wrong are worse than no directions — they send someone hunting
             below a 3D model for a control that is above it, and the whole point of this sentence is
             that the buyer cannot get where they want from the tile they just pressed. */
          return {
            title: 'Already assigned',
            body: `The ${say(w)} plate is already assigned. To edit that plate instead, `
              + `click the left/right arrow buttons ${isPhone() ? 'at the top of the screen' : 'below the 3D rendering'}`,
          };
        }
        if (hasSibling) {
          /* WITH a second set on the board, moving is a CHANGE: this configuration stops being the
             45 and becomes the 55, while the other sets stay exactly where they are. */
          return {
            title: 'Plate updated',
            body: done
              ? `You just changed your design from a ${say(fromW)} plate to a ${say(w)} plate. `
                + `If you'd like to add a separate ${say(w)} plate, ${PICK_TAB}`
              : `Click here to change your design from a ${say(state.ui.weight)} plate to a `
                + `${say(w)} plate instead. If you'd like to add a separate ${say(w)} plate, ${PICK_TAB}`,
          };
        }
        /* ⭐ WITH ONLY ONE SET IT IS A VIEW, NOT A CHANGE. The same tap, honestly described
           differently: there is no sibling to disturb and nothing is reassigned, so the buyer is
           simply looking at their one design printed on a different plate. "Change" would imply they
           are giving something up, which is only true once a second set exists. */
        return {
          title: 'Now viewing',
          body: done
            ? `You are now viewing this plate design as a ${say(w)} plate. To add a separate `
              + `${say(w)} plate with its own design, ${PICK_TAB}`
            : `Click here to view this plate design as a ${say(w)} plate instead. To add a separate `
              + `${say(w)} plate with its own design, ${PICK_TAB}`,
        };
      };

      /* ⛔ THE PHONE HAS NO HOVER, SO IT GETS A POP-UP INSTEAD (Ryan 2026-08-10). Without this, a
       * phone buyer gets none of the above: the free rungs change their plate with no hint that a
       * SEPARATE one is a different thing entirely, and a refused rung does nothing at all with no
       * reason given — the exact "dead control" the desktop tooltip exists to prevent.
       * ⚠ MOUNTED ON THE APP ROOT (`ctx.el`), never inside the panel: the sheet sits under a
       * `clip-path`, and a fixed scrim inside one of those cannot cover the viewport.
       * ⚠ Not awaited. The plate change is already under way; this only reports it, and blocking on
       * an OK the buyer may not press for ten seconds would hold nothing useful.
       * ⚠ `openModal` brings the focus trap, Esc, scrim-dismiss, scroll lock and focus-return with
       * it — all of which a hand-rolled scrim would have had to reimplement. */
      const explainOnPhone = (fromW) => {
        if (!isPhone() || !ctx.el) return;
        const { title, body } = tipCopy(true, fromW);
        /* `variant: 'glass'` — the translucent grey card with the centred action, added 2026-08-09
           for the quote step's enlarge preview and asked for here a day later (Ryan: *"make the
           pop-up glass-like with some transparency… then add the OK button to the center of it"*).
           Reused rather than restyled: a second dialog look would be two things to keep in step, and
           the grey tint already carries a decision worth keeping — the house glass fill is BLUE and
           gets lost on this tool's blue-black background, which is why that variant is neutral. */
        void openModal({ mount: ctx.el, title, body, variant: 'glass' });
      };

      const tile = h('button', {
        type: 'button',
        class: `wd-lockup wd-flat-glass wd-flat-glass--flush wd-size-tile${active ? ' is-active' : ''}${taken ? ' is-taken' : ''}${dye ? ' has-dye' : ''}${onLight ? ' is-onlight' : ''}${inLineup ? ' is-in' : ''}${notAdded ? ' is-free' : ''}${removable ? ' is-removable' : ''}`,
        'aria-pressed': active ? 'true' : 'false',
        /* ⛔ `aria-disabled`, NEVER THE `disabled` ATTRIBUTE (Ryan 2026-08-10, on making a sibling's
           rung unclickable). A real `disabled` button does not fire `pointerenter` in Chrome, so it
           would silently kill the very tooltip that explains why the tile is refusing — leaving a
           dead control with no reason given, which is precisely the "reads as broken" failure the
           2026-08-09 note was guarding against. `aria-disabled` states the fact for assistive tech,
           the guard in `onClick` enforces it, and hover and focus stay fully alive. */
        ...(taken ? { 'aria-disabled': 'true' } : {}),
        /* ⛔ NO `title`. The custom bubble below carries this copy, and a native tooltip would open a
           second later saying the same thing in a different visual language — see ui/tooltip.js. The
           fact still reaches assistive tech through `aria-label`, which already names the set. */
        ...(dye ? { style: `--wd-glass-fill: ${dye}` } : {}),
        /* ⭐ THE TILES CARRY THEIR UNIT (Ryan 2026-08-10: *"we need these cards to say 'LB'
           underneath them in small letters like the circles on desktop… (or KG, if that one is
           toggled on)"*). They used to read as a bare ladder — 5 · 10 · 15 — on the reasoning that
           the unit was already on the plate and owned by the Weight section below. That held while
           this was the only place denominations were picked; it stopped holding the day the gate
           grew its own labelled circles, because the buyer now meets "45 LB" before they ever reach
           this panel and a bare "45" here reads as a different kind of number.
           ⚠ The RUNG is always pounds (it is a plate, not a number — see design-profile's "UNIT IS A
           LABEL"); only what the tile SAYS follows the toggle, which is why every comparison in this
           function stays against the raw `w` and only the label strings convert. */
        'aria-label': (state.designProfile.unit === 'kg'
          ? `${displayWeightText(w, 'kg')} kilogram` : `${w} pound`)
          // The `title` is a pointer affordance; a screen reader gets the same fact in the name,
          // because "taken" is the whole reason this tile looks different from its neighbours.
          + (taken ? `, on your ${colorWord(held.color)} set` : '')
          + (removable ? ', in your lineup. Activate to remove it' : inLineup ? ', in your lineup' : notAdded ? ', not added. Activate to add it' : ''),
        onClick: (e) => {
          /* WD 496 — THE LINEUP CLICK: on = remove (not the last), in lineup = go to it, not added = add it and go there.
             Asked of LIVE state (the tile outlives its build-time truth, see the notes below). */
          if (lineupMode) {
            const liveW = Number(state.ui.weight);
            /* WD 502 (Ryan 11-02: "the one you clicked gets immediately highlighted or unhighlighted? instead of waiting
               for the 3D model to load"). The tile repaints NOW, the same "right early, never wrong" repaint the plain
               ladder below has always done: the panel rebuild after the hop re-derives every tile from live state. */
            const tile = e.currentTarget;
            const tiles = [...((tile.closest('.wd-size-grid') || tile).parentNode.querySelectorAll('.wd-size-tile'))];
            const badge = (t, glyph) => {
              t.querySelector('.wd-size-tile__badge')?.remove();
              if (glyph) t.append(h('span', { class: `wd-ol-x wd-size-tile__badge${glyph === '+' ? ' wd-size-tile__plus' : ''}`, 'aria-hidden': 'true' }, glyph));
            };
            const light = (fill, onLight) => {
              const cur = tiles.find((t) => t !== tile && t.classList.contains('is-active'));
              if (cur) { cur.classList.remove('is-active', 'is-removable'); cur.classList.add('is-in'); cur.setAttribute('aria-pressed', 'false'); badge(cur, null); }
              tile.classList.remove('is-free', 'is-in');
              tile.classList.add('is-active', 'is-removable');
              tile.setAttribute('aria-pressed', 'true');
              if (fill) { tile.style.setProperty('--wd-glass-fill', fill); tile.classList.add('has-dye'); tile.classList.toggle('is-onlight', !!onLight); }
              badge(tile, '✕');
            };
            if (Number(w) === liveW) {
              if (ctx.lineup.count() > 1) {
                tile.classList.remove('is-active', 'is-removable', 'has-dye', 'is-onlight');
                tile.style.removeProperty('--wd-glass-fill');
                tile.classList.add('is-free');
                tile.setAttribute('aria-pressed', 'false');
                badge(tile, '+');
                ctx.lineup.removeCurrent();
              }
              return;
            }
            const heldLive = ctx.siblingRungs ? ctx.siblingRungs().get(Number(w)) : null;
            if (heldLive) { light(null); void ctx.switchProduct?.(heldLive.key); return; }
            // an added plate copies the one you are on, colour included (WD 496) - so it takes that tile's fill
            const was = tiles.find((t) => t.classList.contains('is-active'));
            light(was && was.style.getPropertyValue('--wd-glass-fill'), was && was.classList.contains('is-onlight'));
            ctx.lineup.add(w);
            return;
          }
          /* ⛔ ASK THE LIVE STATE, NOT THE `active` THIS NODE WAS BUILT WITH.
             These chips outlive their own truth: the panel rebuilds on a schedule of its own, so a
             chip's build-time `active` is a stale closure the moment the weight moves. Reading it
             here made the control swallow a click — go 15 → 45 and then straight back, and the 15
             chip (built while 15 WAS live) still believed it was active and returned. Which is the
             exact bug the coalescing above exists to prevent, reintroduced one layer up. */
          if (Number(state.ui.weight) === Number(w)) return;
          /* ⛔ A RUNG A SIBLING HOLDS IS INERT (Ryan 2026-08-10). It used to be clickable on the
             reasoning that this ladder only decides WHICH PLATE YOU ARE LOOKING AT — but it does not
             do that: clicking a sibling's rung does not show you their plate, it moves YOURS onto it
             and leaves two configurations claiming one denomination. The only route to the other set
             is the switcher, which is exactly what the bubble now tells them. Letting the click
             through also meant refusing in words and complying in behaviour, and it created a
             double-count that had to be caught downstream on the quote form where it costs money.
             ⚠ ASKED OF LIVE STATE, not of the `taken` this node was built with — the same trap the
             note above this guard describes. These tiles outlive their own truth: a sibling can be
             moved off this rung (or onto it) while this chip sits in the DOM believing otherwise. */
          const heldNow = ctx.siblingRungs ? ctx.siblingRungs() : new Map();
          if (heldNow.has(Number(w))) { explainOnPhone(); return; }
          // BEFORE the change — see the note on `tipCopy`'s `fromW`.
          const cameFrom = Number(state.ui.weight);
          // Light the clicked rung NOW rather than waiting for the panel rebuild. A change occupies
          // the hand-off for the exit spin plus a texture build, and main.js coalesces clicks that
          // land inside it — so without this, a fast second tap looks ignored right up until the
          // plate turns. Derived state re-asserts itself on the next rebuild, so this can only ever
          // be right early, never wrong.
          const row = e.currentTarget.parentNode;
          for (const c of row.children) {
            const on = c === e.currentTarget;
            c.classList.toggle('is-active', on);
            c.setAttribute('aria-pressed', on ? 'true' : 'false');
          }
          ctx.changeWeight?.(w);
          explainOnPhone(cameFrom);
        },
      /* ⛔ `.wd-lockup-mini` + `.wd-lk-unit`, NOT a bespoke stack. That pair already IS this app's
         number-over-unit lockup — it is what the Number-style chips use to preview "45 LB" as it
         will print on the plate — so borrowing it means the caption's size, tracking and the 1px it
         sits below the numeral all arrive from the rule that already owns them (house rule
         `match-shared-ui-by-sharing-rules`: add your class to ITS rules, never copy the
         declarations). `.wd-size-tile__n` stays on the numeral because it carries three things the
         shared class must not: tabular figures, the smaller kg size, and the taken-rung strike. */
      }, h('span', { class: 'wd-lockup-mini' },
        h('span', { class: 'wd-size-tile__n' }, displayWeightText(w, state.designProfile.unit)),
        h('span', { class: 'wd-lk-unit wd-size-tile__u' }, state.designProfile.unit === 'kg' ? 'KG' : 'LB')),
      /* WD 496: the corner badge - the outline swatch's ✕ on the plate you are on (Ryan: *"an 'X' in the corner like the
         outline swatch has"*), a + that shows on hover on a weight not added. A span, never a button: a button inside a
         button is invalid and the whole tile is already the control. */
      removable ? h('span', { class: 'wd-ol-x wd-size-tile__badge', 'aria-hidden': 'true' }, '✕') : null,
      notAdded ? h('span', { class: 'wd-ol-x wd-size-tile__badge wd-size-tile__plus', 'aria-hidden': 'true' }, '+') : null);

      /* ── WHAT THIS RUNG WILL DO TO YOU (Ryan 2026-08-10) ────────────────────────────────────────
       * Two sentences, because this control does two very different things depending on the tile,
       * and neither was discoverable: clicking a free rung MOVES the plate you are standing on
       * (it does not add one), and clicking a rung a sibling holds does nothing you would want.
       *
       * ⭐ ONLY WHERE A SECOND CONFIGURATION IS EVEN POSSIBLE. On the nine products that cannot be
       * picked twice there is no "separate plate" to go and add and no sibling to collide with, so
       * the copy would be describing machinery those buyers do not have.
       * ⚠ RESOLVED AT HOVER TIME, not built into a string here. The wording names the rung you are
       * CURRENTLY on, and a tile repaints its own row optimistically on click — so a captured string
       * would describe the plate you just left. See `_wdTip` in ui/tooltip.js.
       * ⚠ Units follow the LB/KG toggle, for the same reason the tile's own caption does.
       * ⛔ No bubble on the tile you are already standing on: there is nothing to warn about and
       * nothing to explain, and a tooltip on the current selection is noise. */
      /* WD 496 (Ryan: *"keep pop-up messages when you mouseover the different buttons, but they can be much shorter and
         sweeter"*): one short line per lineup state. The last plate has nothing to offer, so no bubble. */
      if (lineupMode) {
        const say = () => `${displayWeightText(w, state.designProfile.unit)}${state.designProfile.unit === 'kg' ? 'kg' : 'lb'}`;
        if (removable) attachTip(tile, () => `Remove this ${say()} plate`);
        else if (inLineup) attachTip(tile, () => `Go to your ${say()} plate`);
        else if (notAdded) attachTip(tile, () => (ctx.lineup.wasRemoved?.(w) ? `Add your ${say()} plate back` : `Add a ${say()} plate`));
        return tile;
      }
      if (!active) attachTip(tile, () => tipCopy(false).body, taken ? { tone: 'warn' } : {});
      return tile;
    };

    const cap = SIZE_COLS * SIZE_COLLAPSED_ROWS;
    const paint = () => {
      const live = Number(state.ui.weight);
      /* Re-asked on EVERY paint, never captured. A sibling's parked rung moves whenever the buyer
         visits it and changes its denomination, and this panel is rebuilt on its own schedule — a
         map resolved once at build time would mark the rung a sibling used to be on. */
      const held = ctx.siblingRungs ? ctx.siblingRungs() : new Map();
      /* Is there a SECOND set to tell this one apart from? Derived from the map that was going to be
         computed anyway — a non-empty `siblingRungs` IS "more than one configuration of this
         product", so there is no count to keep in step with it. See the note in `tileFor`. */
      const hasSibling = held.size > 0;
      /* ⛔ NEVER HIDE THE RUNG THAT IS LIT. A collapsed ladder whose selected tile sits in row 4 is
         a control showing nothing selected on a design that plainly has a denomination — and it is
         reachable without touching the toggle at all: resume a 150 lb dumbbell, or let a coalesced
         burst land past the fold. So the fold OPENS ITSELF rather than the buyer having to discover
         that their plate is further down. It stays open afterwards, which is also why this reads
         `sizeRowsExpanded ||` rather than assigning: an auto-open is still an open. */
      const liveIdx = ladder.findIndex((w) => Number(w) === live);
      const open = sizeRowsExpanded || liveIdx >= cap;
      sizeRowsExpanded = open;
      sizeGridEl = h(
        'div',
        { class: `wd-size-grid${state.designProfile.unit === 'kg' ? ' wd-size-grid--kg' : ''}`, role: 'group', 'aria-label': 'Weight size shown' },
        ladder.slice(0, cap).map((w) => tileFor(w, live, held.get(Number(w)), hasSibling)),
      );
      const kids = [h('div', { class: 'wd-eyebrow' }, 'Size'), sizeGridEl];
      // Only when there is something folded away. Three of the six ladders never reach the cap.
      if (ladder.length > cap) {
        /* ⛔ THE EXTRA ROWS ARE ALWAYS IN THE DOM — that is what makes it animate (Ryan 2026-08-02:
           *"have it smoothly slide down to reveal the additional buttons or slide up to hide"*).
           The first version rebuilt the grid with a longer or shorter list, and you cannot tween
           between two different sets of children. So the overflow lives in its own collapsible
           wrapper on the `0fr → 1fr` grid-rows recipe this app already uses for the Advanced block
           and the Help & Guide accordion — it animates to the content's real height without anyone
           having to measure it, which a `max-height` guess never does honestly on a wrapping grid.
             ⚠ THE GAP ABOVE IT IS THE INNER GRID'S MARGIN, NOT THE WRAPPER'S PADDING. Padding on
           the collapsing element cannot collapse at `0fr` and would leave a permanent sliver of
           space under the third row — the exact trap changelog 203 hit on the Q&A panel. */
        const restGrid = h(
          'div',
          { class: `wd-size-grid wd-size-grid--rest${state.designProfile.unit === 'kg' ? ' wd-size-grid--kg' : ''}` },
          ladder.slice(cap).map((w) => tileFor(w, live, held.get(Number(w)), hasSibling)),
        );
        const rest = h('div', { class: 'wd-size-rest', 'data-open': open ? 'true' : 'false' },
          h('div', { class: 'wd-size-rest__inner' }, restGrid));
        // `inert` so the hidden tiles leave the tab order and the a11y tree entirely. `overflow:
        // hidden` only stops them being SEEN — it leaves them focusable, so tabbing through a
        // collapsed ladder would walk 20 buttons nobody can see.
        if (!open) rest.setAttribute('inert', '');
        const btn = h('button', {
          type: 'button',
          class: 'wd-size-more',
          'aria-expanded': open ? 'true' : 'false',
          onClick: () => {
            /* Flips the ATTRIBUTE and lets CSS tween it — deliberately not a repaint. Rebuilding
               here would replace the very nodes mid-transition and the slide would never render. */
            sizeRowsExpanded = !sizeRowsExpanded;
            rest.dataset.open = sizeRowsExpanded ? 'true' : 'false';
            if (sizeRowsExpanded) rest.removeAttribute('inert'); else rest.setAttribute('inert', '');
            btn.setAttribute('aria-expanded', sizeRowsExpanded ? 'true' : 'false');
            btn.textContent = sizeRowsExpanded ? 'Hide rows' : 'View more rows';
          },
        }, open ? 'Hide rows' : 'View more rows');
        kids.push(rest, h('div', { class: 'wd-size-more-row' }, btn));
      }
      replaceChildren(box, kids);
    };
    sizeSectionRender = paint;
    paint();
    return box;
  }

  /* The number's nudge pad — the twin of `logo-step.js`'s `nudgePad`, and deliberately its twin
     rather than a shared component: that one is BADGE-only and this one includes Full Face (see
     `canNudgeWeight`), that one writes every logo element because the layouts share one mark across
     slots while this writes the weight elements, and that one lives on a tab that is rebuilt on a
     different schedule. What they DO share is the vocabulary — the classes, the ±v-is-down sign
     convention, `clampBigNudge` as the single authority on the bound, and the step. */
  const WEIGHT_NUDGE_DIRS = [
    { glyph: '▲', axis: 'nudgeV', dir: -1, label: 'Nudge weight number up' },
    { glyph: '◀', axis: 'nudgeU', dir: -1, label: 'Nudge weight number left' },
    { glyph: '▶', axis: 'nudgeU', dir: +1, label: 'Nudge weight number right' },
    { glyph: '▼', axis: 'nudgeV', dir: +1, label: 'Nudge weight number down' },
  ];
  function weightNudgePad() {
    const weights = () => (state.designProfile.elements || []).filter((e) => e && e.type === 'weight');
    const valueOf = (axis) => clampBigNudge((weights()[0] || {})[axis], WEIGHT_NUDGE_MAX, WEIGHT_NUDGE_STEP);
    const btns = new Map();
    const sync = () => btns.forEach((b, d) => {
      // Greyed at the cap, the same honesty the size dials get: a button that cannot move the piece
      // must not look like it can.
      b.disabled = clampBigNudge(valueOf(d.axis) + d.dir * WEIGHT_NUDGE_STEP, WEIGHT_NUDGE_MAX, WEIGHT_NUDGE_STEP) === valueOf(d.axis);
    });
    const bump = (d) => {
      const targets = weights();
      if (!targets.length) { sync(); return; }   // nothing to move — see logo-step's own note
      const next = clampBigNudge(valueOf(d.axis) + d.dir * WEIGHT_NUDGE_STEP, WEIGHT_NUDGE_MAX, WEIGHT_NUDGE_STEP);
      if (next === valueOf(d.axis)) return;
      // EVERY weight element: a layout can print the number in more than one slot, and moving one of
      // a pair would tear the lockup in half — the same argument the logo pad makes for its slots.
      for (const e of targets) e[d.axis] = next;
      markStyleUserEdited(state.designProfile);
      /* The live value and its per-product record move together, or a hop away and back would
         restore the position as it was BEFORE this press — see `rememberWeightNudge`. */
      rememberWeightNudge(state, weightNudgeKey(state));
      ctx.focusWeightZoom?.();   // the Weight tab leans in on whatever you are adjusting
      valueCommit();
      sync();
    };
    const pad = h('div', { class: 'wd-nudge-pad', role: 'group', 'aria-label': 'Weight number position' },
      WEIGHT_NUDGE_DIRS.map((d) => {
        const b = attachHoldRepeat(h('button', {
          type: 'button', class: `wd-step-btn wd-nudge-btn wd-nudge-btn--${d.axis === 'nudgeV' ? (d.dir < 0 ? 'up' : 'down') : (d.dir < 0 ? 'left' : 'right')}`,
          'aria-label': d.label, title: d.label,
          onPointerdown: (e) => e.stopPropagation(),
        }, d.glyph), () => bump(d));
        btns.set(d, b);
        return b;
      }));
    sync();
    return pad;
  }

  function weightSection() {
    // ── ANY CONTROL HERE LEANS THE CAMERA IN (Ryan 2026-07-28) ──────────────────────────────────
    // "any button click on this tab should result in a close-up zoom of the visible weight… so you
    // can more easily see what you're doing." Everything in this section edits the number PRINTED ON
    // the plate — the unit, the lettering case, the number style — and at the default framing that
    // number is a few millimetres of a plate that is mostly black rubber.
    //   ONE listener on the section rather than a call inside each control's handler: the section
    // rebuilds itself wholesale on a unit flip (`renderAll`), so per-button wiring would have to be
    // re-attached every time and would silently miss any control added later. A delegated listener on
    // the container survives every rebuild — it IS the container — and "any button" stays literally
    // true. Capture phase so it still fires for handlers that stop propagation.
    const box = h('div', {});
    box.addEventListener('click', (e) => {
      if (!e.target.closest('button, [role="button"], input, select')) return;
      ctx.focusWeightZoom?.();
    }, true);
    // Rebuild BOTH the unit toggle and the number-style grid together — flipping LB/KG must
    // re-letter every style preview (-LB- → -KG-, etc.), so the whole section re-renders.
    const renderAll = () => {
      const u = state.designProfile.unit;
      // --lg sizes LB/KG to match the Number-style tiles below it (Ryan 2026-07-24). The Lettering
      // case toggle shares .wd-unit-toggle and deliberately does NOT get this.
      const unitToggle = h('div', { class: 'wd-unit-toggle wd-unit-toggle--lg wd-unit-toggle--gridalign', role: 'group', 'aria-label': 'Weight unit' },
        ['lb', 'kg'].map((unit) => {
          const active = unit === u;
          return h('button', {
            type: 'button',
            class: `wd-unit-btn${active ? ' is-active' : ''}`,
            'aria-pressed': active ? 'true' : 'false',
            onClick: () => {
              if (state.designProfile.unit === unit) return;
              /* ⛔ THE UNIT, AND NOTHING ELSE (Ryan 2026-08-02: *"when I select the KG toggle, the
                 plates often get smaller in size. This should never happen. Instead, the weight
                 that you're on needs to stay the same size and simply adjust the weight number
                 label accordingly."*)
                   This used to also call `setWeight(defaultWeightFor(product, unit))` — it did not
                 CONVERT the buyer's denomination, it threw it away and jumped to the category's kg
                 sample. A 45 lb plate became a 20, and since `gripPlateDims`/`gripZone` read a bare
                 number as pounds, a grip plate then rendered at its 20 LB size. `weightValue` is
                 pounds now and the unit only re-letters the face, so there is nothing to set: the
                 same plate stays on the stage and its number reads 20.4. */
              setUnit(state, unit);
              renderAll(); // re-letter the style previews for the new unit
              /* ⚠ AND THE SIZE LADDER, WHICH IS A DIFFERENT SECTION (2026-08-02). Its tiles now
                 read "20.4" or "45" off the same toggle, and `renderAll` only owns the Weight
                 section it lives in. Same escape hatch the nudge pad and the size dial each needed
                 when a control outside the design panel had to follow a change made inside it —
                 CLAUDE.md §0b, "a rule computed in one UI surface and cached in another". */
              sizeSectionRender?.();
              valueCommit();
            },
          }, unit.toUpperCase());
        }));
      const kids = [
        h('div', { class: 'wd-eyebrow' }, 'Weight'),
        subHead('Unit of Measurement'),
        h('div', { class: 'wd-denomination' }, unitToggle),
      ];
      // Number-style picker (moved here from Advanced 2026-07-22) — 1 click to pick pounds/
      // kilos above, 1 click to pick the number style here. Hidden when there's no weight number.
      // SIZE for the weight number (Ryan 2026-07-25) — moved out of the colour rows to sit with the
      // other two controls that act on the number: the unit above it and the style below. Hidden
      // when the layout has no weight number, same gate the style grid uses.
      if (hasPieceType('weight')) kids.push(subHead('Size', '12px 0 6px'), sizeRow(textSizeStepper('weight', 'Number')));
      /* ── AND THE NUMBER GETS A NUDGE PAD, ON THE SAME LAYOUTS THE LOGO HAS ONE (Ryan 2026-08-09) ──
         *"we also need the same thing on the weight tab/area to reposition the weight number, and
         that goes for both mobile and desktop."*
           Built HERE, in the Weight section, which is the whole of "both platforms": the phone shows
         this section as the Weight sub-tab and the desktop stacks it in the pane, so one construction
         serves both. It sits directly under Size for the same reason the logo's sits under its own
         dial — on these layouts the two are one job, sizing the piece and then placing it.
           ⚠ IT WEARS THE LOGO PAD'S CLASSES, not copies of its values. `.wd-nudge-pad` /
         `.wd-nudge-btn` / `.wd-step-btn` carry the clip-path wedges, the hit-testing they double as,
         the disabled treatment and the phone width — so the two pads cannot drift apart, and the 20%
         narrowing asked for in the same message applied to this one before it existed. */
      if (canNudgeWeight(state.designProfile)) kids.push(subHead('Position', '12px 0 6px'), weightNudgePad());
      const grid = weightStyleGrid();
      if (grid) kids.push(subHead('Number style', '12px 0 6px'), grid);
      // Last, so it reads as "reset everything above me" — the same position and the same control
      // "Reset colors" occupies at the foot of the Colors section.
      if (phoneMQ()) {
        kids.push(sectionResetBtn('Reset weight', 'Put the number style, size and position back to their defaults', resetWeightSection));
      }
      replaceChildren(box, kids);
    };
    // Held so a denomination change can re-letter the Number-style previews WITHOUT a panel
    // rebuild. They print the LIVE weight ("45 -LB-"), so they are as much a function of the
    // denomination as the plate is — see `refreshForWeight`. Re-assigned on every build, exactly
    // like `sizeGridEl` and the two size steppers.
    weightSectionRender = renderAll;
    renderAll();
    return box;
  }

  // ── shared controls ──────────────────────────────────────────────────────────
  // (The stepper itself moved to ui/stepper.js 2026-07-25 — the Add Logo tab needed one for Logo
  // size and it was a closure in here, reachable only from this panel. Same control, one copy.)

  // Wrap a ✎ / + tool chip so its WORD sits INSIDE the button, under the glyph (Ryan 2026-07-25).
  // Two gains: the label inherits the button's own colour instead of the badge's dimmed 0.72, so icon
  // and word read as one control; and the badge slot under the chip goes empty, which is what lets the
  // size steppers move up tight against the swatches.
  //   NO caption span is emitted (Ryan 2026-07-25). It used to be kept, empty, because the badge rule
  // reserved a min-height that held the column's box level. That min-height is gone, so an empty span
  // is now a zero-height flex item that still collects the wrap's 3px column gap — which is exactly
  // the padding that kept the size steppers from sitting tight under the swatches.
  function toolWrap(btn, label, kind = 'cchip') {
    const glyph = btn.textContent;
    btn.textContent = '';
    btn.append(h('span', { class: 'wd-tool__icon' }, glyph), h('span', { class: 'wd-tool__label' }, label));
    return h('div', { class: `wd-${kind}-wrap` }, btn);
  }

  // A Size & color stepper row: the CONTROL first, left-aligned with the swatch row above it, and its
  // name to the RIGHT (Ryan 2026-07-25). Deliberately NOT `controlRow`, which leads with a fixed-width
  // label — that's the Advanced convention, where a column of labels is what makes a long list of
  // unlike settings scannable, and it stays as it is.
  //   `label` is omitted when a group has only ONE stepper: the sub-heading right above it ("Logo")
  // already says what it acts on, so a second word would just be noise. Pass a label only where a
  // group has two (Text → Arch / Number), i.e. where the control genuinely needs telling apart.
  function sizeRow(control, label) {
    return h('div', { class: 'wd-sizerow' }, control, label ? h('span', { class: 'wd-sizerow__label' }, label) : null);
  }

  // Text/weight SIZE steppers. Hoisted to panel scope 2026-07-25 when the two moved out of the
  // colour rows: Arch is built by the Text section, Number by the Weight section, and a shared
  // builder is what keeps them one control with one range rather than two that drift apart.
  //   TWO steppers, not one, and that split is load-bearing (Ryan 2026-07-25). A single "Text size"
  // moved the arcs AND the weight number together, and the number is not a peer of the arcs —
  // `logoMatchBox` size-matches the LOGO to the weight lockup, so nudging the arc text silently
  // resized the number and the logo with it. Nobody asking for a smaller school name means "and
  // shrink the logo". Arch moves both arcs (that pairing is the whole point of the chain), Number
  // moves the weight, and only Number carries the knock-on to the logo — where it is at least the
  // obvious consequence of the control you touched.
  //   Per-piece sizing (one arc alone) stays in Advanced, along with the chain toggle.
  function textScaleOf(type) {
    const e = textWeightEls().find((x) => x.type === type);
    return Number.isFinite(e && e.sizeScale) ? e.sizeScale : 1;
  }
  function hasPieceType(type) { return textWeightEls().some((e) => e.type === type); }
  // Top of the dial, per piece type (Ryan 2026-07-25: "the number stopped me at 100%, but there was
  // still plenty of room on the left and right sides of it").
  //   WEIGHT 1.35: nothing geometric was stopping it at 1.0 — that was just where the dial ended.
  // `weightSizes` starts the number at 0.62 × the slot box and only width-caps it at the box edge,
  // so a two-digit number has room to roughly 1.45 before anything binds; 1.35 matches the logo's
  // ceiling and keeps the STACKED lockup (number + unit line) inside the slot box vertically, which
  // is the constraint that bites first for that format.
  //   ARC TEXT stays 1.0, and that one IS geometry: an arc's `maxFont` is the ring BAND's height ×
  // the scale, so anything above 1 lets letters grow taller than the band they sit in and spill over
  // its edges. The span fit usually binds long before that anyway — which is what the greying below
  // now makes visible instead of leaving people clicking a dead button.
  // ⭐ WD 487 (Ryan 2026-11-01): arc text now runs past 1.0 until its letters' INK fills the band — see
  // `arcMaxFont` in render-engine.js. 100% still reads the old wall (`edgeFraction` below re-bases it).
  const TEXT_SCALE_MAX = { arctext: ARC_TEXT_SCALE_DIAL_MAX, weight: 1.35 };

  // The live arc-text size dial, so a Wording edit can re-sync it WITHOUT a panel rebuild — typing
  // deliberately never rebuilds (it would tear out the field being typed in), which is exactly why
  // this control went stale. Set on every build; cleared implicitly by the next one.
  let arcSizeStepper = null;
  let weightSizeStepper = null;
  /** The number style currently on the plate — what `weightOpenPctFor` needs to answer the share.
   *  Read at BUILD time, which is correct because changing the style commits and rebuilds the
   *  panel; the same rebuild is what re-seeds the dial to the new style's default. */
  const weightFormatNow = () => {
    const e0 = state.designProfile.elements.find((e) => e.type === 'weight');
    return (e0 && e0.weightFormat) || 'stacked-dash';
  };
  function textSizeStepper(type, label, { enabled = true } = {}) {
    const stepper = createStepper({
      value: textScaleOf(type), min: 0.4, max: TEXT_SCALE_MAX[type] ?? 1, step: 0.05,
      fmt: (v) => Math.round(v * 100) + '%', ariaLabel: `${label} size`,
      onInput: (v, { seeded = false } = {}) => {
        // A manual move marks the piece hand-sized, so the 80%/max default is never re-seeded over
        // it on the next rebuild. A SEED must not — it is the default arriving, not a choice.
        //   ⚠ That distinction was documented here but never implemented (found 2026-07-28 while
        // chasing a shrunken weight number): `seedDefault` commits through this same handler, so the
        // construction-time seed marked the number and the arc text hand-sized on the FIRST render,
        // before the buyer had touched anything. Everything downstream then honoured the lie —
        // `seedDefaultSizes`, which re-pegs a piece when the room changes (a layout switch, a product
        // switch), skips anything flagged, so both pieces were frozen at whatever multiplier the
        // first layout happened to produce and no later geometry could correct them. Measured on a
        // fresh design: `sizeUserSet: true` with nothing ever hand-sized.
        //   The logo dial had hit this and was clearing the flag by hand afterwards (logo-step.js
        // ~556). Fixed in the stepper now, so neither has to.
        textWeightEls().forEach((e) => {
          if (e.type !== type) return;
          e.sizeScale = v;
          if (!seeded) e.sizeUserSet = true;
        });
        valueCommit();
      },
      // Grey the −/+ at the REAL wall, not just at the ends of the dial (Ryan 2026-07-25). Text
      // size is a ceiling: an arc is fitted to its span first, so a long word stops growing well
      // before 100% and every further click moved the number and nothing else. The engine answers
      // "what would you paint at X?" and the button greys when the answer stops changing — so this
      // covers the arc-span fit, the 72px ceiling, the top/bottom link-clamp and the weight's slot
      // box without the panel knowing which one is biting.
      paintedAt: (v) => paintedTextSize(state.designProfile, type, v, { product: modelFor(state.ui.product) }),
      // ARC TEXT reads as a % of its WRAP-AROUND limit (Ryan 2026-07-25): 100% = the line filling
      // the layout's arc exactly, so the number answers "how much of the available run am I
      // using?". It re-derives on every rebuild, which is what makes it move as you type — add
      // words and the same font stops fitting the span, the edge drops, and the percentage climbs
      // to say so.
      //   The NUMBER is deliberately left on the plain multiplier. Its four styles cap at wildly
      // different points (0.2x for `ruled` up to 1.2x for `plain`), and the default `stacked-dash`
      // has no enforced cap at all, so "100%" would mean a different thing per style and be
      // undefined for the common one. See the render-engine note in `weightSizes`.
      // BOTH read as a share of the room now. The number's edge is per style and per digit count —
      // `ruled` caps at 0.2x where `plain` runs to 1.2x — which is exactly why a raw multiplier was
      // meaningless there: 90% meant something different for every combination. As a fraction of the
      // edge it means the same thing everywhere.
      pctOfMax: true,
      // Arc text opens as LARGE as it fits (100% of its wrap-around limit, less when the words are
      // long); the number opens at 80%, matching the logo.
      //
      // ⛔ …TIMES THE PRODUCT'S OPENING FACTOR, WHICH THIS DIAL USED TO IGNORE (Ryan 2026-07-30:
      // *"on the grip plate Classic Grip layout, the logo still seems relatively small compared to
      // the weight numbers"*).
      //   TWO seeders set this piece's default and they disagreed. `seedDefaultSizes` scales the
      // opening share by `openShareFactorFor` — 65/80 on the grip, 1 everywhere else — and this
      // stepper then re-seeded on panel build at a FLAT `DEFAULT_SIZE_PCT`, overwriting it. The grip's
      // number therefore opened at 75% of its edge where the engine had placed it at 61%: **1.23×
      // larger than intended**. The logo dial never had the bug because it passes
      // `seedDefault: false` (logo-step.js) and simply wears what the engine wrote — which is exactly
      // why the two drifted apart on this one product and looked like a logo that was too small.
      //   ⚠ INVISIBLE ON THREE OF THE FOUR PRODUCTS BY CONSTRUCTION. The factor is 1 for the bumper,
      // dumbbell and barbell, so the two seeders agree there and always have. Only the grip
      // diverges, which is why this survived every earlier size pass.
      //
      // ⚠ THE FACTOR MOVES THE ROOM, NOT THE OPENING SHARE — see `edgeFraction` in stepper.js. An
      // earlier cut put it here, on `defaultPct`, so the grip's number opened reading 61%. Same
      // plate, and Ryan rejected the READOUT: *"it simply makes the weight numbers 60% and keeps the
      // logo at 75%, as opposed to changing what a 75% weight number looks like size wise."* That
      // rule still holds and is why 2026-08-06 raised the FACTOR to 1.08 rather than the percentage:
      // the reading stays 75 and 75 comes to mean 81% of the wall.
      //   ⚠ `defaultPct` below is nonetheless no longer the flat house 75 — that was a SECOND
      // disagreement hiding under the first, about the number STYLE rather than the product.
      /* ⛔ THE NUMBER'S DEFAULT IS PER STYLE, AND IT IS THE ENGINE'S ANSWER (2026-08-06). This was a
         flat `DEFAULT_SIZE_PCT`, while `seedDefaultSizes` opened the ruled banner at 90 and the
         "45LB" one-liner at 95 — so the dial re-seeded 75 over the engine's number on every panel
         rebuild and those two styles dropped 17% and 21% on every product. Same shape as the grip's
         hop, one axis over; measured by tools/probe-weight-style-seed-agreement.mjs.
           `weightOpenPctFor` is now the single place that answers "what share does this number open
         at", so the two cannot drift again. It also carries the cap that keeps the grip's >1 edge
         fraction from seeding a style past its own wall. */
      defaultPct: type === 'arctext' ? 100 : weightOpenPctFor(weightFormatNow(), modelFor(state.ui.product)),
      /* 100% for the NUMBER is the geometric wall everywhere but the grip, where it is now 1.08× the
         wall so the default reading of 75% paints 81% of it (Ryan 2026-08-06 — see
         `GRIP_NUM_OPEN_SHARE`). Arc text is always its own wall — its edge is the wrap-around limit,
         a fact about the words rather than a matter of taste — so it is never scaled.
           ⚠ ABOVE 1, WHICH REVERSES THE OLD TRADE. At 65/80 the dial's 100% sat BELOW the wall and
         the grid ran past 100 (to ~125) to keep the wall reachable; at 1.08 it sits ABOVE, so the
         grid stops at 100 and the + button greys at ~92% instead. `pctCeil` handles both by asking
         whether the fraction is under 1.
           ⚠ NO LONGER DENOMINATION-AWARE, and the staleness note that argued for a function is spent
         with it: the share is of each plate's OWN wall, and the wall is already per-denomination, so
         all six grip plates take the same factor. Kept as a function anyway — it is read on every
         repaint of the readout, and the PRODUCT can still change under a dial the Size ladder
         deliberately does not rebuild. */
      /* ⛔ THE NUMBER STAYS AT 100% ON THESE LAYOUTS (Ryan 2026-08-09, correcting himself: *"the size
         one is for the weight number, which should be capped at 100% on these layouts — only the
         logo should be allowed to go to 150%"*).
           It briefly took the same 1/BIG_LOGO_SCALE_MAX re-basing the logo dial has. Removed rather
         than re-pitched: the two marks are not symmetrical. A big logo is placed by a RULE against
         the ring or the face and clipped to it, so growing it past its nominal size just fills more
         of a surface it already owns. The number is fitted into a slot beside that logo — extra size
         there does not find more room, it walks into the mark. `numberEdgeFractionFor` stands alone
         again, which is the grip plate's own re-peg and nothing to do with big layouts. */
      /* ⭐ ARC TEXT: 100% STAYS THE OLD WALL, THE GRID RUNS ON TO THE INK WALL (WD 487, Ryan 2026-11-01: the
         text dial stopped at 100% with room left). The logo dial's WD 463 lever, same reason: the + now greys
         where the letters fill the band, past the old wall, and without re-basing every saved design's text
         would suddenly read less than 100%. `sizeEdgeFor` still scans [0.4, 1], so it IS the old wall. */
      edgeFraction: type === 'arctext'
        ? () => {
          const o = { product: modelFor(state.ui.product) }; // the same opts `paintedAt` probes with
          try {
            const old = sizeEdgeFor(state.designProfile, 'arctext', o);
            const now = edgeOf((v) => paintedTextSize(state.designProfile, 'arctext', v, o), 0.4, ARC_TEXT_SCALE_DIAL_MAX, 0.05);
            return now > 0 && old > 0 ? Math.min(1, old / now) : 1;
          } catch { return 1; }
        }
        : () => numberEdgeFractionFor(modelFor(state.ui.product)),
      // Gated on the plate actually being DRESSED (Ryan 2026-07-25). Seeding commits, and a commit
      // renders — and `refreshControls()` rebuilds this panel during the upload flow, well before
      // "Design my Weights". Without the gate the seed drew the plate and started the reveal spin
      // while the buyer was still looking at the "Who's this for?" card.
      // Nothing of this type on the plate → nothing to seed a size onto, and the seed commits
      // (which renders). `enabled` short-circuits it before it can fire on an empty set.
      seedDefault: enabled && state.ui.hasBuilt === true && !textWeightEls().some((e) => e.type === type && e.sizeUserSet === true),
    });
    if (type === 'arctext') arcSizeStepper = stepper;
    else if (type === 'weight') weightSizeStepper = stepper;
    // Visible but inert while the piece it sizes doesn't exist (Ryan 2026-07-26) — the same
    // treatment the Add Logo tab's Logo size dial gets before a logo lands. `setEnabled` greys
    // BOTH ends and blocks the tap-and-hold repeat, so it can't be nudged into committing a size
    // onto nothing; the probe would also grey them (it answers 0 at every value), but saying so
    // outright is what keeps that true if the probe ever learns to answer differently.
    if (!enabled) stepper.setEnabled(false);
    return stepper;
  }

  // A labeled row hosting one or more controls (stepper, chain toggle, …).
  function controlRow(label, ...controls) {
    return h('div', { style: { display: 'flex', gap: '8px', alignItems: 'center', margin: '5px 0' } },
      h('span', { style: { ...S.small, minWidth: '58px' } }, label), ...controls);
  }

  // Chain toggle — links top & bottom arc-text sizes (§2.7). All instances stay in
  // sync; toggling one flips the shared `textSizeLinked` flag.
  function chainBtn() {
    return h('button', {
      type: 'button',
      class: 'wd-chain-btn' + (textSizeLinked ? ' is-on' : ''),
      'aria-pressed': textSizeLinked ? 'true' : 'false',
      title: 'Top & bottom stay matched — unlink to size separately.',
      'aria-label': 'Link top and bottom text size',
      html: LINK_ICON,
      onClick: () => {
        textSizeLinked = !textSizeLinked;
        try { sessionStorage.setItem(TEXT_LINK_KEY, textSizeLinked ? '1' : '0'); } catch { /* storage off */ }
        root.querySelectorAll('.wd-chain-btn').forEach((b) => {
          b.classList.toggle('is-on', textSizeLinked);
          b.setAttribute('aria-pressed', textSizeLinked ? 'true' : 'false');
        });
      },
    });
  }

  // A compact editable color chip for the Advanced per-piece rows — tap opens the picker.
  function pieceColorChip(hex, ariaLabel, onOpen) {
    const chip = h('button', {
      type: 'button', class: 'wd-cchip wd-cchip--sm', style: { background: hex },
      title: hex, 'aria-label': ariaLabel, onClick: (e) => { e.stopPropagation(); onOpen(chip); },
    });
    return chip;
  }
  function outlineEditor(element) {
    const pal = state.designProfile.palette;
    const outlines = element.outlines || (element.outlines = []);
    const rows = outlines.map((o, idx) => {
      const hex = resolvePaletteRef(o.colorRef, pal);
      return h(
        'div',
        { style: { ...S.row, gap: '6px', margin: '3px 0' } },
        h('span', { style: { ...S.small, minWidth: '58px' } }, `Outline ${idx + 1}`),
        pieceColorChip(hex, `Outline ${idx + 1} color: ${hex}. Tap to edit.`, (anchor) => openElementOutlinePicker(element, idx, anchor)),
        createStepper({ value: clamp(Number(o.thickness) || 0, OUTLINE_MIN, outlineMaxFor(outlines, idx)), min: OUTLINE_MIN, max: outlineMaxFor(outlines, idx), step: OUTLINE_STEP, ariaLabel: `Outline ${idx + 1} thickness`, onInput: (v) => { o.thickness = v; noteOutlineEdit(element, o, { thickness: v }); valueCommit(); } }),
        h('button', { type: 'button', style: S.chip, onClick: () => { noteOutlineEdit(element, outlines[idx], { removed: true }); outlines.splice(idx, 1); structuralCommit(); } }, '×'),
      );
    });
    // No "+ outline" here any more (Ryan 2026-07-25). Adding an outline lived in TWO places — this
    // per-piece chip and the Size & color row's "+ Add" — and only the latter is restore-then-append
    // via `autoIdx`, so the two produced different results from the same gesture. Advanced keeps the
    // per-piece EDITS (colour, thickness, ×); the row owns adding.
    const addBtn = null;
    return h('div', {}, h('div', { style: { ...S.small, margin: '6px 0 2px' } }, 'Outlines (thin → thick)'), rows, addBtn);
  }

  // Buyer-language piece names (§2.8) — no "arctext / slot" jargon.
  function pieceName(element) {
    if (element.type === 'logo') return 'Logo';
    if (element.type === 'weight') return 'Weight number';
    if (element.slot === 'top') return 'Top text';
    if (element.slot === 'bottom') return 'Bottom text';
    return `${slotLabelsFor(modelFor(state.ui.product))[element.slot] || element.slot} text`;
  }

  /** The slots the CURRENT product has, not the bumper's four. See render-engine's productSlots. */
  const mySlots = () => productSlots(modelFor(state.ui.product), state.ui.weight);

  // Position control (§2.8) — a small ring diagram: tap a dot to move the piece.
  // Dots occupied by ANOTHER piece are disabled (one piece per slot).
  function positionRing(element) {
    const usedByOther = new Set(state.designProfile.elements.filter((e) => e.id !== element.id).map((e) => e.slot));
    const labels = slotLabelsFor(modelFor(state.ui.product));
    const dots = mySlots().map((s) => {
      const active = s === element.slot;
      const taken = usedByOther.has(s);
      return h('button', {
        type: 'button',
        class: `wd-posdot wd-posdot--${s}${active ? ' is-active' : ''}`,
        'aria-label': `Move to ${(labels[s] || s).toLowerCase()}`,
        'aria-pressed': active ? 'true' : 'false',
        // `labels`, not the bumper's own map: this line read `SLOT_LABELS[s]` outright, so the cap's
        // `center` dot came up with an empty tooltip (and a grip plate's lower gaps were titled
        // "Left"/"Right" while their aria-label two lines up said "lower left"). One source.
        title: taken ? `${labels[s] || s} (in use)` : (labels[s] || s),
        disabled: taken && !active ? true : null,
        onClick: () => { if (element.slot === s || (taken && !active)) return; updateElement(state, element.id, { slot: s }); structuralCommit(); },
      });
    });
    return h('div', { class: 'wd-posring', role: 'group', 'aria-label': 'Position' }, h('span', { class: 'wd-posring__ring', 'aria-hidden': 'true' }), ...dots);
  }

  // Advanced per-anchor angle control (§2.4): a degree stepper that DISENGAGES
  // auto-balance for this piece + an "Auto" chip that re-engages it. Point pieces
  // only (logo / weight); arc text is arc-anchored, not point-anchored.
  function anglePositionControl(element) {
    if (element.type !== 'logo' && element.type !== 'weight') return null;
    const base = slotBaseAngle(element.slot, modelFor(state.ui.product), state.ui.weight);
    const isAuto = !Number.isFinite(element.angleDeg);
    const startDeg = isAuto ? Math.round(effectivePointAngle(state.designProfile, element, modelFor(state.ui.product))) : element.angleDeg;
    let autoChip = null;
    const deg = createStepper({
      value: startDeg, min: base - 45, max: base + 45, step: 1,
      fmt: (v) => `${Math.round(((v % 360) + 360) % 360)}°`, ariaLabel: 'Position angle',
      onInput: (v) => {
        updateElement(state, element.id, { angleDeg: v }); // engages manual mode
        valueCommit();
        if (autoChip) { autoChip.classList.remove('is-on'); autoChip.setAttribute('aria-pressed', 'false'); }
      },
    });
    autoChip = h('button', {
      type: 'button', class: 'wd-auto-chip' + (isAuto ? ' is-on' : ''), 'aria-pressed': isAuto ? 'true' : 'false',
      title: 'Re-engage auto-balance for this piece',
      onClick: () => { updateElement(state, element.id, { angleDeg: undefined }); structuralCommit(); ctx.announce?.('Auto-balance re-engaged.'); },
    }, 'Auto');
    return controlRow('Angle', deg, autoChip);
  }

  // "⋯" overflow menu on a piece card (§2.8 — Remove is demoted here).
  function openPieceMenu(anchorEl, element) {
    const mount = root.closest('.weight-designer') || document.body;
    let menu = null;
    const close = () => {
      if (!menu) return;
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
      menu.remove();
      menu = null;
    };
    const onDown = (e) => { if (menu && !menu.contains(e.target) && e.target !== anchorEl) close(); };
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); close(); } };
    menu = h(
      'div',
      { class: 'wd-assign-menu', role: 'menu', 'aria-label': `${pieceName(element)} options` },
      h('button', { type: 'button', class: 'wd-assign-opt', role: 'menuitem', onClick: () => { close(); removeElement(state, element.id); structuralCommit(); } }, `Remove ${pieceName(element).toLowerCase()}`),
    );
    mount.append(menu);
    const r = anchorEl.getBoundingClientRect();
    const mw = menu.offsetWidth || 180, mh = menu.offsetHeight || 48;
    menu.style.left = Math.max(8, Math.min(r.right - mw, window.innerWidth - mw - 8)) + 'px';
    menu.style.top = (r.bottom + 6 + mh > window.innerHeight - 8 ? Math.max(8, r.top - mh - 6) : r.bottom + 6) + 'px';
    setTimeout(() => {
      document.addEventListener('pointerdown', onDown, true);
      document.addEventListener('keydown', onKey, true);
      const first = menu && menu.querySelector('.wd-assign-opt');
      if (first) first.focus();
    }, 0);
  }

  function textStyleControls(element) {
    const isArc = element.type === 'arctext';
    const sizeStepper = createStepper({
      value: element.sizeScale ?? 1, min: 0.4, max: isArc ? ARC_TEXT_SCALE_DIAL_MAX : 1, step: 0.05,
      fmt: (v) => Math.round(v * 100) + '%', ariaLabel: 'Size',
      onInput: (v) => {
        // Linked (§2.7): a size edit on either arc-text moves BOTH; otherwise just this piece.
        if (isArc && textSizeLinked) {
          state.designProfile.elements.forEach((e) => { if (e.type === 'arctext') updateElement(state, e.id, { sizeScale: v }); });
        } else {
          updateElement(state, element.id, { sizeScale: v });
        }
        valueCommit();
      },
    });
    const fillHex = resolvePaletteRef(element.fillRef, state.designProfile.palette);
    return [
      controlRow('Fill', pieceColorChip(fillHex, `Fill color: ${fillHex}. Tap to edit.`, (anchor) => openElementFillPicker(element, anchor))),
      controlRow('Size', sizeStepper, ...(isArc ? [chainBtn()] : [])),
      outlineEditor(element),
    ];
  }

  // Weight NUMBER STYLE picker (§2.7; moved into the basic Weight section 2026-07-22) — the
  // pound/kilo mark as mini-previews, not a word list, rendered in the plate's current font
  // so it's a true preview. Picking one fans the format out to EVERY weight number on the
  // plate (e.g. a grip plate's two) → a one-click style choice. Returns just the grid (the
  // Weight section supplies the label); null when the layout has no weight number.
  function weightStyleGrid() {
    const weightEls = state.designProfile.elements.filter((e) => e.type === 'weight');
    if (!weightEls.length) return null;
    const rep = weightEls[0];
    /* ⚠ THROUGH `displayWeightText`, LIKE THE PLATE (Ryan 2026-08-07: *"I changed the unit from LB
       to KG… the dumbbell properly adopted '22.7 KG', but the number-style cards changed to include
       'KG' and retained the '50'"*).
         This was `String(weightValue)` — the raw POUNDS, always. `weightValue` is pounds by design
       (design-profile: "UNIT IS A LABEL, NOT A WEIGHT"), so the mini-previews had half the
       conversion: the MARK below re-letters LB→KG / POUNDS→KILOS and the number never moved, which
       is a worse answer than not converting at all — every chip read "50 KG" for a 50 lb plate.
         Not a dumbbell bug: this is the shared Weight section, so every product's chips said it.
       The unit MARK and the NUMBER are one decision and now share one source. */
    const num = displayWeightText(state.designProfile.weightValue, state.designProfile.unit);
    const fam = fontFamilyFor(state.designProfile.font);
    const fw = fontWeightFor(state.designProfile.font);
    // The mini-previews follow the current unit (Ryan 2026-07-20) so they never contradict
    // the plate — kg re-letters LB→KG, LBS→KGS, POUNDS→KILOS, etc. (mirrors render-engine).
    const kg = state.designProfile.unit === 'kg';
    const KG_MARK = { 'stacked-dash': '-KG-', 'stacked-lb': 'KG', 'stacked-lbs': 'KGS', 'stacked-pounds': 'KILOS', 'stacked-lb-lower': 'kg' };
    const markFor = (lk) => (lk.unit == null ? null : (kg ? (KG_MARK[lk.fmt] || '-KG-') : lk.unit));
    const inlineU = kg ? 'KG' : 'LB';
    const grid = h('div', { class: 'wd-lockup-grid' }, WEIGHT_LOCKUPS.map((lk) => {
      const active = lk.fmt === (rep.weightFormat || 'stacked-dash');
      const mark = markFor(lk);
      const numText = lk.fmt === 'inline-lb' ? `${num}${inlineU}` : num;
      // The stacked unit line WIDTH-MATCHES the number on the plate, so long words shrink
      // (POUNDS ⟶ ~the "45" width). Mirror that here so the chip doesn't render POUNDS wider
      // than the number (Ryan 2026-07-22); short marks (LB/LBS/-LB-/lb) stay at the 8px cap.
      const unitPx = mark ? Math.min(8, 34 / mark.length) : 0;
      const preview = lk.fmt === 'ruled'
        ? h('span', { class: 'wd-lockup-mini wd-lk-ruled' }, h('span', { class: 'wd-lk-num' }, `${num}${inlineU}`))
        : mark
          ? h('span', { class: 'wd-lockup-mini' }, h('span', { class: 'wd-lk-num' }, num), h('span', { class: 'wd-lk-unit', style: { fontSize: unitPx + 'px' } }, mark))
          : h('span', { class: 'wd-lockup-mini' }, h('span', { class: 'wd-lk-num' }, numText));
      return h('button', {
        type: 'button',
        class: 'wd-lockup wd-flat-glass wd-flat-glass--flush' + (active ? ' is-active' : ''),
        'aria-pressed': active ? 'true' : 'false',
        'aria-label': `Weight number style ${mark || (lk.fmt === 'inline-lb' ? `NN ${inlineU}` : INLINE_LABEL[lk.fmt]) || lk.fmt}`,
        style: { fontFamily: fam, fontWeight: fw },
        onClick: () => {
          // ── CARRY THE SIZE ACROSS THE STYLE CHANGE (Ryan 2026-07-28) ───────────────────────────
          // "45LB on the right, change layouts so the number moves to the top, then change the style
          // to 45 over LB → it'll get super tiny!"
          //
          // `sizeScale` is an ABSOLUTE multiplier while every dial reads as a share of the room —
          // and the number styles do not share a room. They cap at wildly different points (see the
          // note by `pctOfMax`: ~0.2× for `ruled` up to ~1.2× for `plain`), so the same stored number
          // means a different size in each. `setLayoutPreset` already re-pegs for exactly this reason
          // when a LAYOUT moves the room; a style change moves it just as much and re-pegged nothing.
          //   Reproduced in pure state before fixing: right slot + `inline-lb` re-pegs to 0.338 on
          // the layout hop (correct for inline), then switching to stacked applies that 0.338 to a
          // lockup whose room is three times bigger — painted 16.91 against the 50.67 the same slot
          // and style reach when arrived at directly.
          //
          // Converting the SHARE rather than re-seeding a fixed default is what makes this right for
          // a hand-sized piece too: someone who set 90% gets 90% of the new style's room, not 75%.
          // Both cases go through one path, so they cannot drift apart.
          const sizeOpts = { product: modelFor(state.ui.product), weight: state.ui.weight };
          const beforeEdge = sizeEdgeFor(state.designProfile, 'weight', sizeOpts);
          const shares = beforeEdge > 0
            ? weightEls.map((e) => (e.sizeScale ?? 1) / beforeEdge)
            : null;
          const numeralBefore = paintedNumeralSize(state.designProfile, (weightEls[0] && weightEls[0].sizeScale) ?? 1, sizeOpts); // WD 487
          /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 494, 2026-11-01) — A STYLE CHANGE KEEPS THE NUMERALS' SIZE, HAND-SIZED OR NOT,
             AND SHRINKS ONLY WHEN THE NEW STYLE NO LONGER FITS. Ryan, UX run 3 on a dumbbell: *"when I change the Number Style
             to the one which is just "40" … the number shrinks… Shouldn't it stay the same size, but just drop the suffix?"*
             then *"The one written as "45LB" gets smaller too… on dumbbells the canvas is generally very wide. same with fixed
             barbells, and grip plates. So I'm not sure it makes sense for these number styles to get smaller on these just by
             changing to them, unless of course the weight number is now too wide for the space."*
             WD 487 kept the numerals for an UNTOUCHED number; a hand-sized one still went through the share conversion below,
             and 80% of one style's room is a different numeral size from 80% of another's (63.6 -> 56.6 on a 25 lb dumbbell).
             Now every piece takes the smallest scale that paints the numerals at least as big as before, capped at the new
             style's wall - which is exactly "unless it is now too wide": on the bumper's narrow ring the wall binds and "45LB"
             still shrinks as far as it must; on a wide face it does not. The share conversion is kept only as dead-letter
             fallback when the numerals cannot be measured. */
          const keepNumerals = numeralBefore > 0;
          /* ⛔ …AND IT MATCHES THE SIZE THE BUYER WANTED, NOT THE SIZE THE LAST STYLE WAS FORCED TO. Matching only the numerals
             on screen ratcheted: "45LB" on the bumper's narrow ring is wall-bound at 21.8 (vs 41.8 stacked), and the next switch
             back to a stacked style then matched 21.8 and stayed tiny. So each match leaves `_numeralKeep` {fam, target, scale}
             behind, and the next style change reuses its TARGET while the scale is still the one it set - any hand dial, layout
             or product change moves the scale and the note goes stale by itself, with nothing to clear. Not persisted: losing
             it only means the next change matches what is on screen. */
          const keepFam = sizeOpts.product || 'plate';
          const scaleBefore = weightEls.map((e) => e.sizeScale ?? 1);
          weightEls.forEach((e) => updateElement(state, e.id, { weightFormat: lk.fmt }));
          if (shares) {
            const afterEdge = sizeEdgeFor(state.designProfile, 'weight', sizeOpts);
            if (afterEdge > 0) {
              // ⚠ THE SHARE IS CONVERTED ONLY FOR A HAND-SIZED PIECE (Ryan 2026-07-28). That is the
              // case this conversion was built for — "someone who set 90% gets 90% of the new style's
              // room, not 75%" — and it is the only case where there is a share worth preserving.
              //   A piece nobody has touched is wearing a DEFAULT, and each style now opens at its
              // own (90% for the ruled banner, 95% for "45LB", 75% elsewhere). Carrying the old
              // style's share across meant picking the ruled style handed it the stacked lockup's
              // 75% and the new default never applied. `seedDefaultSizes` below re-seeds exactly
              // those pieces — it skips anything flagged hand-sized — so the two cases stay split
              // without either one needing to know about the other.
              weightEls.forEach((e, i) => {
                if (e.sizeUserSet !== true || keepNumerals) return; // WD 494: the numerals are matched below instead
                const next = Math.round(shares[i] * afterEdge * 1000) / 1000;
                if (next > 0) e.sizeScale = next;
              });
            }
          }
          // ⚠ NO LOGO CORRECTION HERE ANY MORE (Ryan 2026-07-28). A hand-sized logo used to need its
          // `sizeScale` rewritten on a style change to hold its painted size, because the base it
          // multiplied — the auto-fit — moved with the number style. `logoDrawSize` now scales a
          // style-INDEPENDENT room, so a stored scale paints the same size whatever the style is and
          // there is nothing left to correct. `seedDefaultSizes` still re-seeds the UNTOUCHED ones,
          // which is the half of the relationship that should stay connected.
          seedDefaultSizes(state.designProfile, { ...sizeOpts, pct: DEFAULT_SIZE_PCT });
          /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 487, 2026-11-01) — AN UNTOUCHED NUMBER KEEPS ITS NUMERALS' SIZE ACROSS A
             STYLE CHANGE. Ryan: *"when I change the number style from … POUNDS … to the plain number … the "45" decreases
             in size"* -> keep it ("yes"). The seed above opens each style at its own share of its own wall, so the same
             75% painted 41.7 on POUNDS and 37.1 on plain. Now the smallest scale that paints the numerals at least as big
             as before is taken, capped at the new style's wall (a style with less room shrinks only as far as it must).
             (WD 494 took away the "never below the opening size" floor and brought hand-sized pieces in here too.) */
          if (numeralBefore > 0) {
            const edge = sizeEdgeFor(state.designProfile, 'weight', sizeOpts) || 1;
            weightEls.forEach((e) => {
              const handSized = e.sizeUserSet === true;
              if (handSized && !keepNumerals) return;
              const kept = e._numeralKeep;
              const i0 = weightEls.indexOf(e);
              const target = kept && kept.fam === keepFam && Math.abs(kept.scale - scaleBefore[i0]) < 1e-6 && kept.target > 0
                ? kept.target : numeralBefore;
              let pick = edge;
              for (let sc = 0.4; sc <= edge + 1e-9; sc += 0.005) {
                if (paintedNumeralSize(state.designProfile, sc, sizeOpts) >= target - 0.05) { pick = sc; break; }
              }
              // ⛔ NEVER BELOW THE NEW STYLE'S OWN OPENING SIZE: "45LB" and the ruled style draw small numerals, and
              // matching them shrank a plain 45 to 45% on the first cut. Only ever keeps a bigger 45, never a smaller one.
              const seeded = e.sizeScale ?? 1;
              /* WD 494: EVERY number takes the match exactly - Ryan's rule is "stay the same size", so WD 487's floor at the new
                 style's opening size is gone too: on the grip it GREW an untouched 45 from 57 to 65 on a switch to plain, and a
                 round trip through the styles ratcheted it. `seeded` is only the fallback when no scale reaches the numerals. */
              e.sizeScale = Math.round(Math.min(pick > 0 ? pick : seeded, edge) * 1000) / 1000;
              e._numeralKeep = { fam: keepFam, target, scale: e.sizeScale };
            });
          }
          // ⚠ RE-PROBE THE DIAL (Ryan 2026-07-28: "it loads with a size of 75%, then when I click +
          // it's like it remembers it was supposed to be at 95%, so it jumps to 100%").
          //   Both lines above write `sizeScale` straight onto the elements, which is invisible to the
          // stepper: it holds its own `value` AND caches its own edge. So after a style change it was
          // showing the OLD value against the NEW style's room — 0.95 read against the stacked
          // lockup's edge of 1.35 is the 70% he saw — and the first + stepped from that stale value,
          // landing past the new edge and clamping to 100%. Two symptoms, one staleness.
          //   `setValue` is built for exactly this and says so in its own comment: it re-syncs the
          // readout, re-probes the wall, and re-judges the greyed ends, silently. Entry 112 added the
          // identical call for arc text when retyping moved its span; the number's dial simply had no
          // handle to call it on.
          if (weightSizeStepper) weightSizeStepper.setValue(textScaleOf('weight'));
          valueCommit();
          grid.querySelectorAll('.wd-lockup').forEach((b, i) => {
            const on = WEIGHT_LOCKUPS[i].fmt === lk.fmt;
            b.classList.toggle('is-active', on);
            b.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
        },
      }, preview);
    }));
    return grid;
  }

  // ── per-piece cards (§2.8) — buyer-named, ring-diagram position, ⋯ menu ───────
  function elementCard(element) {
    const menuBtn = h('button', { type: 'button', class: 'wd-piece-menu', 'aria-label': `${pieceName(element)} options`, title: 'More', html: '<span aria-hidden="true">⋯</span>', onClick: (e) => { e.stopPropagation(); openPieceMenu(menuBtn, element); } });
    const head = h(
      'div',
      { style: { ...S.row, justifyContent: 'space-between', margin: '0 0 8px' } },
      h('strong', {}, pieceName(element)),
      menuBtn,
    );
    const controls = [head];
    if (element.type === 'arctext') {
      controls.push(
        // Empty text field → the piece is hidden on the plate (render skips empty
        // arc text) and the card shows the ONLY placeholder that exists anywhere.
        // Twin of the Text section's arch-wording field — same flag, same two-way sync (see
        // archTextFields). Kept here because the piece card is where you edit a THIRD arc slot.
        h('input', { type: 'text', maxlength: arcMaxLen(), value: element.text || '', placeholder: 'Type to add', 'data-arctext': element.id, style: S.input, onInput: (e) => { const v = e.target.value; updateElement(state, element.id, { text: v, textUserSet: v.trim() !== '' }); syncArcInputs(element.id, v, e.target); valueCommit(); } }),
        controlRow('Spacing', createStepper({ value: element.letterSpacing ?? 3, min: 0, max: 12, step: 0.5, ariaLabel: 'Letter spacing', onInput: (v) => { updateElement(state, element.id, { letterSpacing: v }); valueCommit(); } })),
        ...textStyleControls(element),
      );
    } else if (element.type === 'weight') {
      // Number STYLE now lives in the basic Weight section (weightStyleGrid); the per-piece
      // card keeps just fill/size/outline (Ryan 2026-07-22).
      controls.push(...textStyleControls(element));
    }
    controls.push(controlRow('Position', positionRing(element)));
    const angleCtl = anglePositionControl(element); // point pieces get the §2.4 angle stepper + Auto
    if (angleCtl) controls.push(angleCtl);
    return h('div', { style: S.card }, controls);
  }

  function elementsSection() {
    const cards = state.designProfile.elements.map(elementCard);
    const used = new Set(state.designProfile.elements.map((e) => e.slot));
    const full = mySlots().every((s) => used.has(s));
    const hasLogo = state.designProfile.elements.some((e) => e.type === 'logo');
    // "+ Add text" / "+ Add logo spot" chips (§2.8) — no jargon.
    const addSpecs = [
      { type: 'arctext', label: '+ Add text' },
      { type: 'weight', label: '+ Add weight' },
      { type: 'logo', label: '+ Add logo spot' },
    ];
    const addRow = h(
      'div',
      { style: { ...S.row, gap: '6px' } },
      ...addSpecs.map(({ type, label }) => {
        // No free slot → nothing to add to; a plate carries at most one logo (WD-17).
        const disabled = full || (type === 'logo' && hasLogo);
        return h('button', {
          type: 'button',
          disabled: disabled || null,
          style: { ...S.chip, ...(disabled ? { opacity: '0.4', cursor: 'not-allowed' } : {}) },
          onClick: () => { if (disabled) return; addElement(state, type, freeSlot()); structuralCommit(); },
        }, label);
      }),
    );
    return h('div', {}, h('div', { class: 'wd-eyebrow' }, 'Text & logo details'), cards, addRow);
  }

  function freeSlot() {
    const used = new Set(state.designProfile.elements.map((e) => e.slot));
    // The PRODUCT's slots, never the bumper's four — "+ Add text" on a grip plate used to hand out
    // `bottom`, whose only meaning on that face is the 6 o'clock hand hole (2026-07-30).
    return mySlots().find((s) => !used.has(s)) || 'top';
  }

  // ── Simple/Advanced tier plumbing (§2.1) ─────────────────────────────────────
  // Panel header carries one quiet "Advanced" pill. Advanced extras live inside
  // `advReveal()` wrappers that unfold in place (CSS grid-rows + opacity) — no
  // second panel, no mode switch. Toggling never discards values (the controls
  // are always in the DOM, just collapsed + inert when Simple).
  // The Advanced toggle — a full-width, on-brand button that sits at the BOTTOM of the pane
  // (under the LB/KG toggle); clicking it unfolds all the advanced options directly below it
  // (Ryan 2026-07-20). Chevron rotates when open; label flips Show ⇄ Hide.
  let advPill = null;
  let advLabelSpan = null;
  function advancedToggle() {
    advLabelSpan = h('span', {}, advanced ? 'Hide advanced options' : 'Show advanced options');
    advPill = h(
      'button',
      {
        type: 'button',
        class: 'wd-adv-toggle' + (advanced ? ' is-open' : ''),
        'aria-expanded': advanced ? 'true' : 'false',
        onClick: toggleAdvanced,
      },
      advLabelSpan,
      h('span', { class: 'wd-adv-toggle__chev', 'aria-hidden': 'true' }, '▾'),
    );
    return advPill;
  }
  // The advanced options themselves — Lettering + the per-piece "Text & logo details".
  function advancedBlock() {
    // Lettering case moved up into the basic Font section 2026-07-24 — Advanced is now per-piece
    // element detail only.
    return h('div', { class: 'wd-adv-block' }, elementsSection());
  }

  // Wrap Advanced-only content so it unfolds in place. `i` staggers the reveal
  // (30ms per section, §2.1). Collapsed content is inert (not tabbable) until on.
  let advIndex = 0;
  function advReveal(...children) {
    const inner = h('div', { class: 'wd-adv-reveal__inner' }, children);
    const wrap = h('div', { class: 'wd-adv-reveal', style: { transitionDelay: `${advIndex * 30}ms` } }, inner);
    advIndex += 1;
    return wrap;
  }

  // Reflect the current tier onto the DOM. `animate:false` (used on every rebuild)
  // suppresses the unfold transition so a structural re-render doesn't re-animate;
  // the pill click passes `animate:true` for the real unfold.
  function applyAdvancedState({ animate } = { animate: false }) {
    if (!animate) root.classList.add('wd-no-anim');
    root.classList.toggle('is-advanced', advanced);
    root.querySelectorAll('.wd-adv-reveal').forEach((el) => {
      if (advanced) el.removeAttribute('inert');
      else el.setAttribute('inert', '');
    });
    if (advPill) {
      advPill.classList.toggle('is-open', advanced);
      advPill.setAttribute('aria-expanded', advanced ? 'true' : 'false');
    }
    if (advLabelSpan) advLabelSpan.textContent = advanced ? 'Hide advanced options' : 'Show advanced options';
    if (!animate) {
      void root.offsetWidth; // force reflow so the no-anim state is committed
      root.classList.remove('wd-no-anim');
    }
  }

  function toggleAdvanced() {
    advanced = !advanced;
    try { sessionStorage.setItem(ADVANCED_KEY, advanced ? '1' : '0'); } catch { /* storage off */ }
    applyAdvancedState({ animate: true });
    ctx.announce?.(advanced ? 'Advanced controls shown.' : 'Advanced controls hidden.');
  }

  // ── mobile section tab strip (Fable §7.1 — no scroll-hunting) ────────────────
  // On the mobile gate the four Simple sections show ONE at a time, switched by a
  // sticky horizontal tab strip (Layout · Colors · Font · Weight) instead of a long
  // vertical scroll. Desktop is unchanged (CSS hides the strip; all sections show).
  // The 'font' KEY is unchanged (it's the section's data-msection id, stored in sessionStorage);
  // only its label follows the section's Font → Text rename.
  // 'logo' is CONDITIONAL — the section only exists while this tab owns the borrowed controls (see
  // hasLogoSection), so the strip is built from a filtered list rather than this one.
  /* ── 'LOGO' GOES LAST (Ryan 2026-08-08) ────────────────────────────────────────────────────────
   * *"the order of the child pills should put 'LOGO' at the end. Chances are, the person just came
   * from the Add Logo tab where they would have played with the logo size adjuster, so it doesn't
   * need to circle back so soon."*
   *
   * 🔑 AND THE CODE MAKES THAT CASE HARDER THAN THE HUNCH DOES. This section is not a second set of
   * logo controls that happen to resemble the Add Logo tab's — `logoSection()` appends
   * `ctx.logoStep.logoLoan.sizeBlock` and `logoLoan.nudgeBlock`, the SAME instances, loaned and
   * relocated so the two tabs cannot drift. So the Logo pill is the only one on this strip that can
   * show a buyer nothing they have not just seen; Layout, Colors, Text and Weight are all new
   * decisions at this point. Second position was giving the most-recently-used control the
   * best seat.
   *   It stays on the strip rather than being dropped: a buyer who skipped past the size dial on the
   * way here still needs a route to it, and last is a route.
   *   ⚠ Order here is the PILL order only. The sections' own DOM order is built separately (see
   * `sections` ~3559), which is what desktop stacks — so this is a phone-only change by construction,
   * and nothing keys off the index (`setMobileSection` matches on `data-msec`, deliberately, because
   * the list is filtered). */
  const MSECTIONS = [['layout', 'Layout'], ['colors', 'Colors'], ['font', 'Text'], ['weight', 'Weight'], ['logo', 'Logo']];
  const shownMSections = () => MSECTIONS.filter(([k]) => k !== 'logo' || hasLogoSection());
  let mobileSection = 'layout';
  /* ── A MODEL YOU HAVE NEVER SEEN OPENS ON ITS LAYOUT (Ryan 2026-08-09) ────────────────────────
   * *"if someone is on mobile on the sub-tab of weight, then they go to the next 3D model along by
   * clicking the next pill button, if this is the first time they've landed on this model before, it
   * should default load them on LAYOUT sub-tab, instead of the WEIGHT sub-tab."*
   *
   * The sub-tab is sticky across a product hop, which is right for a model you have already dressed
   * — you were adjusting numbers, you still are. It is wrong for one you have never seen, where the
   * first decision is the arrangement and the Weight tab is three decisions in.
   *   FIRST VISIT, not "coming from Weight": the same argument holds arriving from Colors or Text,
   * and a rule that fires from one tab only would leave the other four landing somewhere arbitrary.
   * Once a model has been visited its tab is sticky again, so this can never override a buyer who
   * has come back to finish something.
   *   The set is per-SESSION and deliberately not persisted: it is about orientation within a
   * sitting, and a resumed design a week later deserves the same introduction. */
  const seenProducts = new Set();
  /**
   * Slide the pill rail so the strip walks left as the buyer walks right (Ryan 2026-08-09).
   *
   * *"with each click this entire bar of 5x pills moves slightly to the left, incrementally, a
   * little more the further down the line you get. Then, by the time you click on the 2nd to last
   * tab for WEIGHT, the bar will have fully slid across revealing the full 'LOGO' pill so you can
   * easily click on that one next."*
   *
   * 🔑 THE POSITION IS A FRACTION OF THE AVAILABLE SCROLL, NOT A PILL WIDTH. Advancing by one pill
   * per click sounds like the same thing and is not: it either runs out of travel early or leaves
   * some at the end, depending on how many items overflow — and that overflow changes with the
   * screen. Mapping index → `i / (n − 2)` of `scrollWidth − clientWidth` makes the LAST reachable
   * position land exactly on the second-to-last tab, at every width and any number of items, which
   * is the rule Ryan actually stated. Five pills: 0, ⅓, ⅔, full, full.
   *   `n − 2` rather than `n − 1` is the whole of "so you can easily click on that one next" — at the
   * second-to-last tab there is nothing left to reveal, so the last pill is already fully on screen
   * and reachable without a second gesture.
   *
   * ⚠ IT NEVER FIGHTS A FINGER. This runs only from a pill CLICK; a drag sets `scrollLeft` directly
   * and nothing here re-asserts it, so *"anyone at any time can put their finger on the bar and move
   * it to any position they want"* stays true and stays put until the next click.
   * ⚠ `max <= 0` MEANS NOTHING OVERFLOWS — a four-pill strip on a wide phone — and must be a no-op
   * rather than a scroll to 0, which would be a pointless animation on every tab change.
   */
  function railScrollTo(key) {
    const rail = root.querySelector('.wd-msection-tabs');
    if (!rail) return;
    const tabs = [...rail.querySelectorAll('.wd-msection-tab')];
    const i = tabs.findIndex((t) => t.dataset.msec === key);
    const max = rail.scrollWidth - rail.clientWidth;
    if (i < 0 || max <= 0) return;
    const lastReveal = Math.max(1, tabs.length - 2);
    rail.scrollTo({ left: max * Math.min(1, i / lastReveal) });
  }

  function setMobileSection(key) {
    // Leaving the sub-tab pulls the camera back out (Ryan 2026-07-28). Unconditional rather than
    // `if (mobileSection === 'weight')` — `endFocusZoom` already no-ops unless the Weight tab was
    // what started the zoom, and keeping the knowledge in ONE place is what stops the two ends of
    // this feature from drifting apart.
    ctx.endFocusZoom?.();
    // ── THE SHEET EASES TO THE NEW SECTION'S HEIGHT (Ryan 2026-07-28) ───────────────────────────
    // The four sections are very different heights — Layout is a carousel plus a nav row, Weight is a
    // stepper — and switching them is a `display` swap, so the pane's content height changes between
    // one frame and the next with nothing to transition. Handing the swap to the sheet lets it pin
    // its height first and drive it explicitly, the same way it already does for a step change.
    //   ⚠ Everything the swap touches goes INSIDE the callback, not just the data attribute. The
    // measurement is taken around whatever this function does, so a pill left to light up afterwards
    // would repaint outside the eased window — and the strip would flick to the new tab a beat before
    // the pane agreed with it.
    const apply = () => {
      mobileSection = key;
      root.dataset.msection = key;
      railScrollTo(key);
      // Keyed off the pill's OWN section id, not its index — the strip is now a filtered list, so an
      // index into MSECTIONS stops meaning the same thing the moment 'logo' drops out of it.
      root.querySelectorAll('.wd-msection-tab').forEach((t) => {
        const on = t.dataset.msec === key;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    };
    /* ── AND THE CAROUSEL WAITS ITS TURN (Ryan 2026-08-08) ──────────────────────────────────────
     * *"when you click from the Logo sub tab and click on the Layout sub tab, there's a lot of
     * flashing/distortions… I can only assume it's happening due to the SVGs loading or populating
     * at the same time?"* — that is exactly it.
     *
     * The sections are all BUILT; switching them is a `display` swap. So while Layout is hidden its
     * sixteen carousel slides are not intersecting and none has been drawn — and the instant the
     * attribute flips they all intersect at once, handing the IntersectionObserver sixteen
     * synchronous `renderDesignToSVGString` builds ("tens of milliseconds" each, per the carousel's
     * own measured note) to drain into the opening frames of the sheet's 300ms height ease.
     *
     * `setThumbsBusy` exists for precisely this collision — `switchProduct` claims the frames before
     * its hand-off for the same reason (2026-08-02) — and it had simply never been wired to a
     * SUB-SECTION change, because until the sheet eased its height there was no animation here to
     * protect. Refused tiles are deferred, not dropped, and the release drains them one per frame.
     *
     * ⚠ UNCONDITIONAL, not `if (key === 'layout')`. The deferred set is empty for a section with no
     * thumbnails, so the other pills pay nothing — and a future heavy section inherits the hold
     * instead of needing someone to remember it. */
    setThumbsBusy(true);
    const release = () => setThumbsBusy(false);
    // `easeSheet` resolves when the height has SETTLED, chases included — a fixed duration here
    // would release mid-flight on exactly the content-heavy swaps this protects. It always resolves
    // (see `easeAround`), and `setThumbsBusy` is idempotent, so a double release is a no-op.
    Promise.resolve(ctx.easeSheet ? ctx.easeSheet(apply) : apply()).then(release, release);
  }
  function mobileTabs() {
    return h('div', { class: 'wd-msection-tabs', role: 'tablist', 'aria-label': 'Design sections' },
      shownMSections().map(([key, label]) =>
        h('button', {
          type: 'button', class: 'wd-msection-tab' + (mobileSection === key ? ' is-active' : ''),
          role: 'tab', 'aria-selected': mobileSection === key ? 'true' : 'false',
          'data-msec': key,
          onClick: () => setMobileSection(key),
        }, label)));
  }

  // ── assemble ─────────────────────────────────────────────────────────────────
  // Simple tier = four sections in Fable's order: Layout → Colors → Font → Weight.
  // The per-piece "Text & logo details" section is Advanced-only (§2.1).
  function build() {
    advIndex = 0;
    const secs = { size: sizeSection(), layout: layoutSection(), logo: logoSection(), colors: paletteSection(), font: fontSection(), weight: weightSection() };
    // `size` is the one section that can decline to exist (see sizeSection) — hence the guard.
    Object.entries(secs).forEach(([k, elm]) => elm && elm.classList.add('wd-dsection', `wd-dsection--${k}`));
    /* First sight of this model on a phone → open on Layout. Before the `logo` fallback below, so a
       first visit that also lacks a logo section still resolves to the same place rather than
       through it. `build()` runs on every rebuild, which is why the SET is what makes this fire once
       per model rather than on every repaint. */
    if (phoneMQ()) {
      const prod = String(state.ui.product || '');
      if (prod && !seenProducts.has(prod)) {
        seenProducts.add(prod);
        mobileSection = 'layout';
      }
    }
    // A phone parked on Logo when the section goes away would land on an empty pane. It can only
    // happen on a path that leaves this tab, but the fallback costs one line.
    if (mobileSection === 'logo' && !hasLogoSection()) mobileSection = 'layout';
    root.dataset.msection = mobileSection; // drives the mobile single-section view
    // THE SECTIONS SCROLL, THE STRIP DOES NOT (Ryan 2026-07-28: "the scroll bar itself will overlap
    // the banner on tab 3 which includes the 4 pills… it should butt up against the bottom of it").
    // A scrollbar is drawn down the whole of its scroll container, so while the strip was merely
    // STICKY INSIDE that container, no amount of z-index could get it out from under the bar — the
    // only fix is for the strip not to be in the scrolling box at all. This wrapper is that box; the
    // phone CSS makes it the scroller and leaves the strip above it as fixed chrome.
    //   On desktop it is an unstyled div and nothing changes: the strip is `display: none` there,
    // the sections stack, and the panel body scrolls exactly as before.
    return [
      mobileTabs(),
      h('div', { class: 'wd-dsection-scroll' },
        /* Size has NO tab of its own — the strip is already at five and a sixth would not clear a
           narrow phone — so the stylesheet shows it alongside Layout, the section it belongs with.
           ⭐ SIZE SITS ABOVE LAYOUT ON BOTH DEVICES AGAIN (Ryan 2026-08-10). It went BELOW on the
           phone on 2026-08-08, when Size was five bare numerals and Layout was the thing you came
           for — *"the carousel is what you want, the dial is the adjustment you make after"*. That
           reading expired today: the rungs now carry their unit, wear each set's plate colour, mark
           what a sibling has claimed and refuse a click that would collide, so Size stopped being an
           afterthought and became where a multi-plate design is steered from. *"Now that we've got
           all this added technical stuff for bumpers, it makes sense that they're back on top."*
           ⚠ DOM ORDER, NOT CSS `order` — kept from the previous arrangement and still the reason
           this is one array rather than a stylesheet rule. The two are plain block boxes in a
           scroller, so `order` would mean making that scroller a flex column, and margins do not
           collapse between flex items: every gap between the six sections would silently grow. */
        secs.size,
        secs.layout,
        secs.logo,
        secs.colors,
        secs.font,
        secs.weight,
        // Both omitted while SHOW_ADVANCED_TOGGLE is off — the block is not rendered at all rather
        // than rendered-and-hidden, so its controls can't be reached by tabbing either.
        ...(SHOW_ADVANCED_TOGGLE ? [
          advancedToggle(),          // under the Weight (LB/KG) section
          advReveal(advancedBlock()), // unfolds directly below the toggle
        ] : []),
      ),
    ];
  }
  // Colours present at the LAST render. A rebuild replaces every chip node, so the browser replays any
  // entry animation on all of them; diffing against this is what tells a genuinely NEW colour from one
  // that was simply re-created. Seeded empty, so the first paint does spring in.
  let seenSwatchHexes = new Set();
  function markNewSwatches() {
    const now = new Set();
    for (const chip of root.querySelectorAll('.wd-cchip:not(.wd-cchip--tool)')) {
      const hex = chip.style.background || chip.style.backgroundImage || '';
      now.add(hex);
      if (!seenSwatchHexes.has(hex)) chip.classList.add('wd-cchip--new');
    }
    seenSwatchHexes = now;
  }

  /**
   * Run a rebuild and EASE the Size section's height across it (Ryan 2026-08-02: *"when you use the
   * arrow keys to switch between models, should the list of size options get bigger or smaller from
   * one model to another, I want the list to grow smoothly like it does when you click view more
   * rows"*).
   *
   * ⚠ IT CANNOT BE THE VIEW-MORE MECHANISM, AND THAT IS THE WHOLE DIFFICULTY. "View more rows" keeps
   * its nodes and tweens ONE grid track from `0fr` to `1fr` — the browser interpolates because both
   * ends belong to the same element. A product switch calls `replaceChildren`: the old section is
   * destroyed and a different one is born, so there is no element that exists at both ends to
   * transition. This measures the OLD height, lets the rebuild happen, measures the NEW one, and
   * animates the survivor between the two — a height FLIP.
   *
   * ⚠ `height`, NOT `max-height`. A `max-height` guess has to overshoot to be safe, and the overshoot
   * is dead time at the end of every transition — the ladder would visibly finish early on a bumper
   * and late on a dumbbell. Both ends are measured here, so the real value is available.
   *
   * Timing and easing are the View-more button's, deliberately: the buyer is meant to read these as
   * the same control doing the same thing. Kept in the stylesheet (`--wd-size-ease`) so they cannot
   * drift apart.
   */
  /**
   * @returns {Promise<void>} resolves when the tween has FINISHED (immediately when there was none).
   *
   * ⚠ THE RETURN VALUE IS NOT A COURTESY — IT IS THE SEQUENCING (Ryan 2026-08-02: *"we need the menu
   * to slide first then the layout SVG 2D images to update, or vice-versa. If they both try to
   * update at the same time we run into issues with lagging again."*). The first cut released the
   * thumbnail drain on the line after this call, so a 320ms height tween and a run of full SVG
   * builds started on the same frame — the exact collision the hand-off pause was written to stop,
   * reintroduced one beat later. The caller now awaits this before letting the thumbnails go.
   */
  function easeSizeAcross(mutate) {
    const sel = '.wd-dsection--size';
    const before = root.querySelector(sel);
    const h0 = before ? before.getBoundingClientRect().height : 0;
    const reduced = typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    mutate();
    if (reduced) return Promise.resolve();
    const after = root.querySelector(sel);
    if (!after || !h0) return Promise.resolve(); // appeared from nothing, or vanished — nothing to tween
    const h1 = after.getBoundingClientRect().height;
    if (!h1 || Math.abs(h1 - h0) < 2) return Promise.resolve(); // same ladder — a tween would be noise
    after.style.overflow = 'hidden';
    after.style.height = `${h0}px`;
    void after.offsetHeight;                   // commit h0 as the start, or the browser coalesces both
    after.classList.add('is-sizing');
    after.style.height = `${h1}px`;
    return new Promise((resolve) => {
      const done = (e) => {
        if (e && e.target !== after) return;    // a child's own transition, not ours
        after.removeEventListener('transitionend', done);
        clearTimeout(timer);
        after.classList.remove('is-sizing');
        after.style.height = '';                // back to auto — a pinned height would freeze the
        after.style.overflow = '';              // section against a later View-more expand
        resolve();
      };
      // A belt-and-braces timer: `transitionend` never fires if the element is display:none'd
      // mid-flight (the mobile single-section view does exactly that), and a section left pinned at
      // a stale height — or a promise that never settles, stranding the thumbnails behind it — is a
      // worse bug than a missed animation.
      const timer = setTimeout(done, 600);
      after.addEventListener('transitionend', done);
    });
  }

  function render() {
    // ── HOLD THE SCROLL POSITION ACROSS THE REBUILD (Ryan 2026-07-28) ─────────────────────────
    // "clicking the 'Reset colors' button auto scrolls you back to the top. Don't want this."
    // Nothing was scrolling anywhere — `replaceChildren` DESTROYS the scrolling element, and a
    // fresh one starts at 0. It shows up on Reset colors because that button sits at the bottom of
    // the longest section, so the whole pane visibly snaps; every other rebuild does it too, just
    // from somewhere less obvious.
    //   Both scrollers are captured because which one is live depends on the breakpoint: on a phone
    // it is the inner `.wd-dsection-scroll` (the strip has to stay clear of the bar — see the
    // stylesheet), on desktop the pane body itself. Restoring a 0 costs nothing, so neither needs a
    // media query to decide.
    const outer = root.closest('.ds-side-panel-body');
    const prevOuter = outer ? outer.scrollTop : 0;
    const inner = root.querySelector('.wd-dsection-scroll');
    const prevInner = inner ? inner.scrollTop : 0;

    colorPicker.close(); // the popover lives outside the panel — close it so a
    replaceChildren(root, build()); // rebuild can't strand it with a stale swatch ref
    applyAdvancedState({ animate: false }); // restore the tier without re-animating
    markNewSwatches(); // only a colour that wasn't here before is allowed to pop

    // AFTER the tier + swatch passes, not before: both can change the content's height, and a
    // scrollTop set against a shorter document is clamped on the spot and cannot be un-clamped.
    // A section that genuinely got shorter still clamps, which is the correct outcome.
    if (prevInner) {
      const sc = root.querySelector('.wd-dsection-scroll');
      if (sc) sc.scrollTop = prevInner;
    }
    if (prevOuter && outer) outer.scrollTop = prevOuter;
  }
  render();

  // Re-draw the layout thumbnails IN PLACE (Ryan 2026-07-24 bug: the 2D preview drifted from the 3D).
  // The thumbnails draw `logo.renderUrl`, but structuralCommit() runs ctx.requestRender() — whose
  // logo composite is ASYNC — and then render()s the panel SYNCHRONOUSLY, so every thumbnail was
  // drawn with the PREVIOUS composite and nothing repainted it when the new one landed. The panel
  // therefore sat exactly one edit behind the stage and the 3D model.
  //   Deliberately NOT a full render(): this fires from the render pipeline, which also runs during a
  // live colour DRAG (the picker's `preview` callback), and rebuilding the controls under an open
  // picker would tear out the element it is anchored to. Swapping the SVG inside each tagged tile
  // touches no control, no carousel transform, and no drag handler. Covers the peek carousel and the
  // "View all" grid alike — both tag their tile with `data-wd-thumb=<presetId>`.
  //   ⚠ Via `drawThumbInto`, which keeps the tile's overlays. This ran on RESUME — the restored
  // design rebuilds its logo composite asynchronously, so this fires a beat after load and used to
  // take the selected tick down with the old drawing (Ryan 2026-07-28: "I refreshed and clicked
  // resume… the layout had the blue tint but the checkmark was missing on load").
  /**
   * Redraw every layout thumbnail against the current composite.
   *
   * ⛔ IT HONOURS `thumbsBusy` — it did not, and that was a hole straight through the one mechanism
   * this app has for keeping SVG builds out of an animation (Ryan 2026-08-07: *"the rotate out of
   * the weight you're exiting is always sluggish, laggy, jumpy… the model is already there loaded
   * on the screen! I can spin it with my mouse just fine"*).
   *   `switchProduct` claims the frames with `setThumbsBusy(true)` before its hand-off, and the
   * IntersectionObserver drain respects that. This function did not: it walked every `[data-wd-thumb]`
   * in the tool and drew it synchronously — ~16 full `renderDesignToSVGString` builds in one task.
   * And it is reached from `ctx.requestRender()`, which `adoptProduct` fires on every product hop, so
   * the busiest possible moment was the one moment it ignored the flag.
   *   Queued rather than skipped: the composite really has changed, and a thumbnail showing the
   * previous product's artwork is the bug this function was written to fix. `setThumbsBusy(false)`
   * drains the map one per frame once the hand-off is over.
   */
  function refreshThumbs() {
    const scope = (root.closest && root.closest('.weight-designer')) || document;
    for (const el of scope.querySelectorAll('[data-wd-thumb]')) {
      const id = el.getAttribute('data-wd-thumb');
      if (!id) continue;
      if (thumbsBusy) deferredThumbs.set(el, id);   // newest id wins, deduped by node
      else drawThumbInto(el, id);
    }
  }

  /**
   * A DENOMINATION CHANGED — update the three things that actually moved, and rebuild NOTHING.
   *
   * Ryan 2026-08-02: *"every click results in all the 2D renders on the layout cards disappearing,
   * then all reappearing at once when they reload… can this be lightened up a bit, since the only
   * thing on the 2D layout renders that actually needs to load is the weight number?"* He is exactly
   * right, and the cause was `main.js` reaching for `refreshControls()` — a full `render()`, which
   * `replaceChildren`s the panel and therefore destroys all ~16 layout cards. Their thumbnails are
   * drawn lazily by an IntersectionObserver, one per frame, so the carousel blanked and trickled
   * back on every rung click to change one numeral inside each tile.
   *
   * Four things are a function of the denomination:
   *   • which rung is lit — re-lit in place on the existing buttons;
   *   • the NUMBER-STYLE previews, which print the live weight ("45 -LB-", "45LB", …). Ryan
   *     2026-08-02: *"go from the 10 to the 25/35/45 and back again… the number style cards don't
   *     update."* They were only re-lettering on the crossings that happened to move the LAYOUT and
   *     therefore took the heavy path — the same shape of bug as the thumbnails, one control over;
   *   • the two size dials' readouts — `setValue` RE-PROBES the room, which genuinely moved on the
   *     grip / dumbbell / barbell (their faces resize with the load), so a stale % would be a lie;
   *   • the number drawn inside each layout thumbnail — `refreshThumbs` swaps the SVG inside each
   *     surviving tile and, in its own words, "touches no control, no carousel transform, and no
   *     drag handler".
   *
   * Everything else — the swatch rows, the font list, the scroll position, the carousel's transform,
   * an open Advanced block — is left exactly where the buyer had it, which is both cheaper and more
   * correct than rebuilding it identically.
   *
   * ⚠ A HEAL IS NOT THIS. If the denomination forced a LAYOUT change (a grip dropping to 2.5 lb off
   * a cutout-only preset), the SELECTION moved and main.js calls the full `refreshControls()` — that
   * path, and only that path.
   *
   * ⛔ BUT THE SELECTION MOVING IS NOT THE ONLY WAY THE PICKER GOES STALE, and the gap between those
   * two statements is a shipped bug (Ryan 2026-08-07: *"on the grip plate, say the 45 lb one, the
   * 'View all 4 layouts' button reads correctly. Change to the 2.5 lb plate and the button reads the
   * same. It should update to 'View all 6 layouts'."*).
   *   On the grip, the eligible list is a function of the DENOMINATION — 2.5 and 5 lb are cutout-free
   * and wear six layouts where the bigger faces wear four. Crossing that boundary always restores the
   * new face's remembered layout, but when the remembered id is the one already on, `healed` is false,
   * main.js takes this light path, and the picker keeps the OTHER face's list: a stale count on the
   * button and, worse, a carousel offering four choices when six exist.
   *   So this asks the honest question — *has what is on offer changed?* — rather than main.js's
   * question, *has the selection changed?* The two came apart because `layoutSection` captures
   * `presets` in a `const` at build time and every consumer of it (the count, the track, the arrows,
   * `resyncLayoutPicker`) inherited that snapshot. Rebuilding the section re-points all of them at
   * once, which is why this is a section swap and not a relabelled button.
   *   GATED ON THE LIST ACTUALLY MOVING, so the no-blanking rule above survives intact: every bumper
   * denomination shares one face, so the key is unchanged and nothing here fires. It costs a carousel
   * rebuild exactly when the carousel's contents are genuinely different.
   */
  function refreshForWeight() {
    if (layoutBoxEl && layoutBoxEl.isConnected && layoutKeyNow() !== layoutSectionKey) {
      const outgoing = layoutBoxEl;
      outgoing.replaceWith(layoutSection()); // re-points layoutBoxEl + resyncLayoutPicker at the new build
    }
    /* Repaints the ladder rather than hand-toggling `is-active` on the tiles that happen to be in
       the DOM — because with the 3-row fold the lit rung may not BE in the DOM. Walking the visible
       children would then light nothing and quietly leave the section looking unselected, on a
       design that has a perfectly good denomination. `paint` owns that rule (it opens the fold when
       the live rung is below it) and this is the one path that has to honour it. */
    if (sizeSectionRender) sizeSectionRender();
    // ⚠ BEFORE the stepper syncs below. `renderAll` rebuilds the Weight section, which CREATES a
    // fresh number-size stepper and re-points `weightSizeStepper` at it — sync the old one first
    // and you have re-probed a control that is about to be thrown away. Cheap either way: this
    // section is a few text tiles, with none of the lazy SVG that makes a full render expensive.
    if (weightSectionRender) weightSectionRender();
    if (arcSizeStepper) arcSizeStepper.setValue(textScaleOf('arctext'));
    if (weightSizeStepper) weightSizeStepper.setValue(textScaleOf('weight'));
    refreshThumbs();
  }

  return {
    el: root,
    refresh: render,
    refreshThumbs,
    /* ⛔ THE DEBOUNCED ONE, FOR CONTROLS THAT LIVE OUTSIDE THIS PANEL (Ryan 2026-08-09: *"the 2D SVGs
       aren't updating as I move the logos or resize them"*).
         `valueCommit` calls this after every edit made INSIDE the panel — wired there rather than on
       each control precisely so the next one added cannot forget. The Add Logo tab's size dial and
       nudge pad are the case that comment did not cover: they are real value edits on a different
       surface, committing through `ctx.requestRender` instead, so the carousel and View-all cards
       kept drawing a mark at its old size and position. Same rule, one tab over. */
    scheduleThumbRefresh: () => scheduleThumbRefresh(),
    refreshForWeight,
    /** Hold the lazy layout-thumbnail drain while something else owns the frames (the product
     *  hand-off). Wrapped rather than passed directly: the carousel reassigns `resumeThumbDrain` on
     *  every rebuild, so handing main.js a captured copy would pin the FIRST carousel's. */
    setThumbsBusy: (on) => setThumbsBusy(on),
    /** Run a rebuild with the Size ladder's height tweened across it — see easeSizeAcross. */
    easeSizeAcross,
  };
}
